/**
 * Ximilar Results Renderer
 * Shared gorgeous result display for all card analysis services
 * Designed to stay above the fold and look consistent across pages
 */

(function (window) {
    'use strict';

function pgcT(k, en) { var t = (window.PGC_T && window.PGC_T[k]); var s = (t != null ? t : en); if (arguments.length > 2) { var a = Array.prototype.slice.call(arguments, 2); var i = 0; s = s.replace(/%s/g, function() { return a[i++]; }); } return s; }


    // Store rendered records so action buttons (eBay listing, print, etc.) can access full data
    const resultRegistry = new Map();
    let resultIdCounter = 0;

    // Economics data for the interactive "What If It Grades" explorer
    const econRegistry = new Map();
    let econIdCounter = 0;

    // Context of the most recent render — used by the certificate PDF builder
    let lastRenderContext = null;

    // Promo surfaces that used to live here (post-result upsell card) are
    // rendered by promo-engine.js into the [data-promo-mount] containers
    // emitted below.

    const SERVICE_CONFIG = {
        grade: {
            title: pgcT('rr.grade.analysis.complete', 'Grade Analysis Complete'),
            subtitle: 'Your card has been professionally graded',
            icon: '🏆',
            color: '#8b5cf6',
            colorLight: '#f3e8ff',
            credits: 1
        },
        condition: {
            title: pgcT('rr.condition.analysis.complete', 'Condition Analysis Complete'),
            subtitle: 'Your card condition has been assessed',
            icon: '✅',
            color: '#10b981',
            colorLight: '#f0fdf4',
            credits: 0.5
        },
        centering: {
            title: pgcT('rr.centering.analysis.complete', 'Centering Analysis Complete'),
            subtitle: 'Your card centering has been measured',
            icon: '🎯',
            color: '#3b82f6',
            colorLight: '#eff6ff',
            credits: 0.5
        },
        crop: {
            title: pgcT('rr.card.cropped.successfully', 'Card Cropped Successfully'),
            subtitle: 'Your card has been perfectly isolated',
            icon: '✂️',
            color: '#06b6d4',
            colorLight: '#ecfeff',
            credits: 0.5
        },
        localize: {
            title: pgcT('rr.card.located.successfully', 'Card Located Successfully'),
            subtitle: 'Card position and boundaries detected',
            icon: '📍',
            color: '#f97316',
            colorLight: '#fff7ed',
            credits: 0.5
        },
        'sport-id': {
            title: pgcT('rr.card.identified', 'Card Identified'),
            subtitle: 'Your sports card has been recognized',
            icon: '🏷️',
            color: '#8b5cf6',
            colorLight: '#f3e8ff',
            credits: 0.5
        },
        'tcg-id': {
            title: pgcT('rr.tcg.card.identified', 'TCG Card Identified'),
            subtitle: 'Your trading card has been recognized',
            icon: '🎴',
            color: '#ec4899',
            colorLight: '#fce7f3',
            credits: 0.5
        },
        'unified-identify': {
            title: pgcT('rr.card.identified', 'Card Identified'),
            subtitle: 'AI detected the card type automatically',
            icon: '🔍',
            color: '#6366f1',
            colorLight: '#eef2ff',
            credits: 0.5
        },
        'slab-id': {
            title: pgcT('rr.slab.successfully.identified', 'Slab Successfully Identified'),
            subtitle: 'Your slab label has been read and analyzed',
            icon: '✅',
            color: '#10b981',
            colorLight: '#f0fdf4',
            credits: 0.5
        },
        'slab-grade': {
            title: pgcT('rr.slab.grade.detected', 'Slab Grade Detected'),
            subtitle: 'Your slab grade has been identified',
            icon: '🏆',
            color: '#f59e0b',
            colorLight: '#fef3c7',
            credits: 0.5
        },
        'coin-id': {
            title: pgcT('rr.coin.identification.complete', 'Coin Identification Complete'),
            subtitle: 'Your coin has been identified by AI',
            icon: '<svg width="36" height="36" viewBox="0 0 36 36" style="display:block"><circle cx="18" cy="18" r="15" fill="#fbbf24" stroke="#b45309" stroke-width="2"/><circle cx="18" cy="18" r="11" fill="none" stroke="#b45309" stroke-width="1.4" stroke-dasharray="2.2 2.2"/><text x="18" y="23" text-anchor="middle" font-size="13" font-weight="700" fill="#b45309">$</text></svg>',
            color: '#d97706',
            colorLight: '#fef3c7',
            credits: 1
        }
    };

    function formatConfidence(value) {
        if (value === null || value === undefined || value === '') return 'N/A';
        const num = parseFloat(value);
        if (isNaN(num)) return value;
        if (num > 1) return Math.round(num) + '%';
        return Math.round(num * 100) + '%';
    }

    function formatValue(value) {
        if (value === null || value === undefined) return 'N/A';
        return value;
    }

    function capitalizeFirst(value) {
        if (!value || typeof value !== 'string') return value;
        return value.charAt(0).toUpperCase() + value.slice(1);
    }

    function normalizeCategory(value) {
        if (!value) return value;
        const str = String(value).trim();
        return /^\s*(Card\/Sport Card|Sport Card|Card)\s*$/i.test(str) ? 'Card' : str;
    }

    function formatImageSrc(src) {
        if (!src) return '';
        if (src.startsWith('data:')) return src;
        if (src.startsWith('http') || src.startsWith('/')) return src;
        if (src.startsWith('UklGR')) return 'data:image/webp;base64,' + src;
        if (src.startsWith('/9j/')) return 'data:image/jpeg;base64,' + src;
        if (src.startsWith('iVBOR')) return 'data:image/png;base64,' + src;
        return src;
    }

    // Derive a human-readable processing time. The card-grader/recognition APIs
    // do not put it on the record; it lives in the statistics object as
    // "processing time" (seconds) or a time_stats breakdown. Returns e.g.
    // "173ms", or null when genuinely unknown (so callers can show "N/A"
    // WITHOUT a stray "ms" suffix — the cause of the "N/Ams" bug).
    function formatProcessingTime(record, statistics) {
        const status = record._status || {};
        let ms = null;

        // Record-level fields are already in milliseconds when present.
        if (record._processing_time != null && !isNaN(parseFloat(record._processing_time))) {
            ms = parseFloat(record._processing_time);
        } else if (status.processing_time != null && !isNaN(parseFloat(status.processing_time))) {
            ms = parseFloat(status.processing_time);
        } else if (statistics && typeof statistics === 'object') {
            // Ximilar canonical total is in SECONDS.
            const pt = (function(_o){return _o != null ? _o : statistics.processing_time})(statistics['processing time']);
            if (pt != null && !isNaN(parseFloat(pt))) {
                ms = parseFloat(pt) * 1000;
            } else if (statistics.time_stats && typeof statistics.time_stats === 'object') {
                const sum = Object.values(statistics.time_stats)
                    .reduce((acc, v) => acc + (parseFloat(v) || 0), 0);
                if (sum > 0) ms = sum * 1000;
            }
        }

        if (ms == null || isNaN(ms)) return null;
        return Math.round(ms).toLocaleString() + 'ms';
    }

    function renderHeader(service, record, config, creditsUsed, statistics) {
        const status = record._status || {};
        return `
            <div class="ximilar-result-header" style="
                background: var(--xg-panel);
                color: var(--xg-text);
                padding: 20px;
                border-radius: 8px;
                margin-bottom: 20px;
                border: 1px solid var(--xg-border);
                box-shadow: var(--xg-shadow);
                border-left: 4px solid ${config.color};
            ">
                <div class="ximilar-header-inner" style="display: flex; align-items: center; gap: 18px; flex-wrap: wrap;">
                    <div class="ximilar-header-icon" style="font-size: 36px; flex-shrink: 0;">${config.icon}</div>
                    <div class="ximilar-header-title" style="flex: 1; min-width: 260px;">
                        <h1 class="ximilar-header-h1" style="font-size: 24px; font-weight: 700; margin: 0 0 4px 0; color: var(--xg-text);">${config.title}</h1>
                        <p style="font-size: 14px; color: var(--xg-muted); margin: 0;">${config.subtitle}</p>
                    </div>
                </div>
            </div>
        `;
    }

    function darken(hex, percent) {
        const num = parseInt(hex.replace('#', ''), 16);
        const amt = Math.round(2.55 * percent);
        const R = Math.max((num >> 16) - amt, 0);
        const G = Math.max((num >> 8 & 0x00FF) - amt, 0);
        const B = Math.max((num & 0x0000FF) - amt, 0);
        return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
    }

    function renderImages(record, originalImageUrl, service, meta) {
        meta = meta || {};
        const images = [];
        const seen = new Set();

        // Temporarily hidden image types. Remove a label from this array to show it again.
        const HIDDEN_IMAGE_LABELS = [];

        const hiddenLabels = HIDDEN_IMAGE_LABELS;

        function addImage(src, label, border, detailMeta = null, zoomSrc = null) {
            if (hiddenLabels.includes(label)) return;
            const normalized = formatImageSrc(src);
            if (!normalized || seen.has(normalized)) return;
            seen.add(normalized);
            images.push({ label, src: normalized, border, detailMeta, zoom: zoomSrc ? formatImageSrc(zoomSrc) : null });
        }

        function boxFromPoints(points) {
            if (!Array.isArray(points) || points.length < 2) return null;
            const xs = points.map(point => Number((point != null ? point[0] : void 0))).filter(Number.isFinite);
            const ys = points.map(point => Number((point != null ? point[1] : void 0))).filter(Number.isFinite);
            if (!xs.length || !ys.length) return null;
            return {
                x: Math.min(...xs),
                y: Math.min(...ys),
                width: Math.max(...xs) - Math.min(...xs),
                height: Math.max(...ys) - Math.min(...ys)
            };
        }

        const hasCornerDetails = Array.isArray((record._images != null ? record._images.corner_details : void 0)) && record._images.corner_details.length === 4;
        const hasEdgeDetails = Array.isArray((record._images != null ? record._images.edge_details : void 0)) && record._images.edge_details.length === 4;

        const cornerLabels = ['Top Left', 'Top Right', 'Bottom Left', 'Bottom Right'];
        const edgeLabels = ['Left Edge', 'Right Edge', 'Top Edge', 'Bottom Edge'];

        // Uploaded / original images
        addImage(originalImageUrl || record.original_image_url || (record._images != null ? record._images.original : void 0) || record._url, 'Original Image', 'var(--xg-border)');

        // Visualization variants. Locally-saved _images.* paths are permanent and
        // preferred over Ximilar S3 temp URLs (which expire ~24h after analysis).
        // The history page merges result_images into record._images before rendering.
        // _clean_url_card = isolated card crop (no lines) → "Cropped Card"
        // _exact_url_card = centering line overlay (thin lines on white) → "Centering Overlay"
        // _img_url        = alias for clean/crop used by /crop endpoint
        // api/grade-v3 injects the signed lazy-render slab URL at grade time;
        // the history page renders the tile PHP-side.
        const _pgcCleanSrc = (record._images != null ? record._images.clean : void 0) || record._clean_url_card || (record._images != null ? record._images.cropped : void 0) || record._img_url;
        addImage(_pgcCleanSrc, 'Cropped Card', '#06b6d4');
        // Stamped PGC records get our own overlay (renderPgcHero); Ximilar's
        // full/exact images carry Ximilar's numbers → admin-only.
        const ximAdminOnly = pgcOverlaySubset(record) !== null;
        // PGC Analysis Overlay: our grades on Ximilar's detected geometry over
        // the raw photo — replaces the burned-in Ximilar overlay (api injects
        // the signed lazy-render URL at grade time; history renders it PHP-side).
        const pgcRawSrc = record._pgc_overlay_raw_url || null;
        // if (pgcRawSrc) addImage(pgcRawSrc, 'PGC Analysis Overlay', '#facc15'); // tile hidden 2026-10-24 pending rework — keep pgcRawSrc for ximSuppressed
        const ximSuppressed = ximAdminOnly || pgcRawSrc !== null;
        // Ximilar centering tile restored for all users 2026-10-24 — the PGC
        // overlay replacement is hidden pending rework, leaving this the only
        // baked-overlay image in the gallery.
        addImage((record._images != null ? record._images.exact : void 0) || record._exact_url_card, 'Centering Overlay', '#3b82f6');
        // Full Analysis Overlay: prefer the locally-saved _images.full (permanent).
        // The Ximilar _full_url_card is an S3 temp URL that expires ~24h after
        // analysis, so skip it to avoid showing a broken/placeholder image on
        // older reports.
        // Ximilar's burned-in full overlay is removed from the gallery
        // entirely (owner, 2026-10-29) — no tile, no placeholder, for anyone.

        addImage(record.back_image_url || (record._images != null ? record._images.back : void 0), service === 'coin-id' ? 'Reverse Side' : 'Back Image', '#64748b');
        // PGC Slab last (2026-10-24, was first): the share-ready representation
        // of the whole grade (card + label + QR to the public report) — a
        // summary tile, so it closes the gallery.
        const pgcSlabSrc = record._pgc_slab_url || null;
        if (pgcSlabSrc) addImage(pgcSlabSrc + (pgcSlabSrc.includes('?') ? '&' : '?') + 'w=400', 'PGC Slab', '#0f2a5c', null, pgcSlabSrc);

        // Corner/edge detail crops rendered as inline OpenSeadragon card grids
        // matching the test-corner-capture.php format and site color palette.
        const detailCards = [];
        // Stamped records show these crops in the hero's tap-to-inspect
        // close-up (with the same zoom modal) — the big grids would repeat them.
        if (hasCornerDetails && !ximAdminOnly) {
            detailCards.push({
                title: pgcT('rr.corner.details', 'Corner Details'),
                color: '#1a468e',
                items: record._images.corner_details.map((src, i) => ({ src, label: cornerLabels[i] }))
            });
        }
        if (hasEdgeDetails && !ximAdminOnly) {
            detailCards.push({
                title: pgcT('rr.edge.details', 'Edge Details'),
                color: '#2c5282',
                items: record._images.edge_details.map((src, i) => ({ src, label: edgeLabels[i] }))
            });
        }

        function renderDetailCards(cards, cardName) {
            if (!cards.length) return '';
            const namePrefix = cardName ? cardName + ' ' : '';
            return cards.map(card => {
                const isCorners = card.title.toLowerCase().includes('corner');
                const itemClass = isCorners ? 'ximilar-image-corner-detail' : 'ximilar-image-edge-detail';
                return `
                <div class="ximilar-detail-section">
                    <h3 style="font-size: 14px; font-weight: 800; color: var(--xg-text); margin: 0 0 14px 0; text-transform: uppercase; letter-spacing: 0.04em;">${card.title}</h3>
                    <div class="ximilar-detail-grid">
                        ${card.items.map(item => `
                        <div class="ximilar-detail-card ${itemClass}">
                            <h4>${item.label}</h4>
                            <div class="ximilar-zoomable" data-zoom-src="${item.src}" data-zoom-label="${namePrefix}${item.label}" title="Click to zoom">
                                <img src="${pgcThumb(item.src, 640)}" alt="${namePrefix}${item.label} detail" loading="lazy" onerror="window.PGCImgRetry && PGCImgRetry(this)">
                            </div>
                        </div>
                        `).join('')}
                    </div>
                </div>
                `;
            }).join('');
        }

        // "Annotated Analysis" overlay removed 2026-09-25 — never rendered again.

        // Build a card name for descriptive alt text on detail/gallery images
        const _imgId = (record.card && record.card.identification) || record.identification || {};
        const _imgCardName = buildAltText({
            year: _imgId.year,
            name: _imgId.name || _imgId.full_name,
            setName: _imgId.set_name || _imgId.set,
            setCode: _imgId.set_code,
            cardNumber: cleanCardNum(_imgId.card_number || _imgId.card_no)
        });

        const detailCardsHtml = renderDetailCards(detailCards, _imgCardName);

        const gallery = images.length === 0 ? '' : `
            <div class="ximilar-result-card" style="
                background: var(--xg-panel);
                border-radius: 8px;
                padding: 20px;
                box-shadow: var(--xg-shadow);
                margin-bottom: 16px;
                border: 1px solid var(--xg-border);
            ">
                <h3 style="font-size: 19px; font-weight: 800; color: var(--xg-text); margin: 0 0 14px 0; letter-spacing: -0.01em;">Analysis Images</h3>
                <div class="ximilar-images-grid">
                    ${images.map(img => {
                        const labelSlug = img.label.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                        const _galleryAlt = _imgCardName ? _imgCardName + ' — ' + img.label : img.label;
                        // Owner 2026-10-29: cropped-card tile matches the
                        // centering overlay — no special hug/radius styling.
                        const tileImgStyle = 'width: 92%; height: 148px; object-fit: contain; display: block; margin: 0 auto; pointer-events: none;';
                        // Crosshair-split centering overlay (owner 2026-11-02): the PGC
                        // endpoint renders OUR measurements onto the clean crop —
                        // replaces Ximilar's burned-in numbers wherever present.
                        const _pgcCtr = img.label === 'Centering Overlay' ? (record._pgc_centering_overlay_url || null) : null;
                        const _tileSrc = _pgcCtr || img.src;
                        const _tileZoom = _pgcCtr || img.zoom || img.src;
                        return `
                        <div class="ximilar-image-item ximilar-image-${labelSlug}" style="text-align: center;">
                            <div style="font-size: 11px; color: var(--xg-muted); margin-bottom: 6px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em;">${img.label}</div>
                            <div class="ximilar-zoomable" data-zoom-src="${_tileZoom}" data-zoom-label="${_imgCardName ? _imgCardName + ' — ' + img.label : img.label}"${img.label === 'PGC Slab' ? ' data-slab="1"' : ''}${img.detailMeta ? ` data-detail-source="${encodeURIComponent(img.detailMeta.source)}" data-detail-region="${encodeURIComponent(JSON.stringify(img.detailMeta.region))}"` : ''} title="Click to zoom" style="position: relative; background: var(--xg-subtle); border-radius: 12px; padding: 16px 18px; border: 1px solid ${img.border}; cursor: zoom-in;">
                                <img src="${pgcThumb(_tileSrc, 640)}" alt="${escapeHtml(_galleryAlt)}" style="${tileImgStyle}" loading="lazy" onerror="window.PGCImgRetry && PGCImgRetry(this)">
                                <div style="position: absolute; bottom: 12px; right: 12px; background: rgba(15,23,42,0.72); color: #fff; border-radius: 999px; width: 26px; height: 26px; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,0.25);">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                                </div>
                            </div>
                        </div>
                        `;
                    }).join('')}
                </div>
            </div>
        `;

        return `${gallery}${detailCardsHtml}`;
    }

    // Record subset for the PGC overlay, or null — same gate as
    // ResultRenderer::pgcOverlaySubset (grade_pgc.overlay stamp).
    function pgcOverlaySubset(record) {
        const gp = (record && record.grade_pgc) || {};
        const ov = gp.overlay;
        if (!record || record.grader !== 'pgc' || !ov || !ov.v || !ov.w || !ov.h) return null;
        const im = record._images || {};
        if (!im.clean && !im.cropped) return null;
        const f = gp.front || {}, c = f.corners || {}, e = f.edges || {};
        if ([c.ul, c.ur, c.ll, c.lr, e.top, e.right, e.bottom, e.left].some(v => !isFinite(parseFloat(v)))) return null;
        return {
            grader: 'pgc',
            grades: { centering: record.grades ? record.grades.centering : null },
            grade_pgc: { overlay: ov, front: { corners: c, edges: e, centering: f.centering || null } },
            centering: record.centering ? { bound_box: record.centering.bound_box, left_right: record.centering.left_right, top_bottom: record.centering.top_bottom } : {},
            _images: { clean: im.clean, cropped: im.cropped, corner_details: im.corner_details, edge_details: im.edge_details },
            // Tapping the middle of the overlay card opens this in the zoom modal.
            original_image_url: record.original_image_url || im.original || record._url || null
        };
    }

    // pgc-overlay.js mounts [data-pgc-overlay] nodes itself (MutationObserver);
    // load it on pages that didn't include it.
    function ensurePgcOverlay() {
        if (window.PGCOverlay) { setTimeout(() => window.PGCOverlay.mountAll(), 0); return; }
        if (document.getElementById('pgc-overlay-js')) return;
        const s = document.createElement('script');
        s.id = 'pgc-overlay-js';
        s.src = '/assets/js/pgc-overlay.js';
        s.onload = () => { if (window.PGCOverlay) window.PGCOverlay.mountAll(); };
        document.head.appendChild(s);
    }

    // Top-of-report "AI Grading Results" hero for stamped PGC records —
    // mirrors ResultRenderer::renderPgcHero; keep the markup in sync.

    function renderPgcHero(record, subset, grades, overall, overallColor, ringPct, chipsHtml, iqNotice) {
        const bg = record.back_grading;
        const crop = subset._images.clean || subset._images.cropped;
        const f = subset.grade_pgc.front;
        const tiles = [['' + pgcT('subgrade.corners', 'Corners') + '', 'corners'], ['' + pgcT('subgrade.edges', 'Edges') + '', 'edges'], ['' + pgcT('subgrade.surface', 'Surface') + '', 'surface'], ['' + pgcT('subgrade.centering', 'Centering') + '', 'centering']].map(([label, k]) => {
            const v = grades[k];
            if (v === null || v === undefined || v === '') return '';
            return `<div class="pgc-pt" style="--c:${gradeColor(v)};"><div class="pgc-pt-k">${label}</div><div class="pgc-pt-v">${escapeHtml(String(v))}</div>${getSeverityBadge(v)}<div class="pgc-pt-bar"><i style="width:${Math.min((parseFloat(v) || 0) / 10 * 100, 100)}%;"></i></div></div>`;
        }).join('');
        const fc = f.centering || {}, cm = subset.centering || {};
        const lr = Array.isArray(fc.lr) ? fc.lr.join('/') : (cm.left_right || '');
        const tb = Array.isArray(fc.tb) ? fc.tb.join('/') : (cm.top_bottom || '');
        const meta = (lr || tb) ? `<div class="pgc-ov-meta">${lr ? `<span>Left/Right <b>${escapeHtml(lr)}</b></span>` : ''}${tb ? `<span>Top/Bottom <b>${escapeHtml(tb)}</b></span>` : ''}</div>` : '';
        ensurePgcOverlay();
        return `<div class="ximilar-result-card pgc-hero" data-pgc-overlay="${escapeHtml(JSON.stringify(subset))}" style="background:var(--xg-panel);border-radius:12px;padding:20px;box-shadow:var(--xg-shadow);margin-bottom:16px;border:1px solid var(--xg-border);border-top:4px solid ${overallColor};">`
            // Top strip: title + compact final grade (small ring, condition, front/back).
            + `<div class="pgc-hero-top">`
            + `<div class="xg-ring" style="--pct: ${ringPct}; --ring-color: ${overallColor};"><div class="xg-ring-inner">`
            + `<span class="xg-ring-grade">${overall != null ? escapeHtml(String(overall)) : 'N/A'}</span></div></div>`
            + `<div class="pgc-hero-head"><div class="pgc-hero-title">AI Grading Results${bg ? '<span class="pgc-hero-badge">ENHANCED FRONT + BACK</span>' : ''}</div>`
            + `<div class="pgc-hero-cond" style="color:${overallColor};">${grades.condition ? escapeHtml(grades.condition) : 'AI Predicted'}<span>${bg ? 'AI calculated grade' : 'Scale 1–10 · AI'}</span></div>`
            + (bg ? `<div class="pgc-hero-fb"><span class="xim-chip"><span class="xim-chip__dot" style="background: #3b82f6;"></span>Front: <b>${grades.front != null ? escapeHtml(String(grades.front)) : '—'}</b></span><span class="xim-chip"><span class="xim-chip__dot" style="background: #059669;"></span>Back: <b>${bg.final != null ? escapeHtml(String(bg.final)) : '—'}</b></span></div>` : '')
            + `</div></div>`
            + `<div class="pgc-hero-sub">Corners, edges, surface &amp; centering &mdash; powered by computer vision.${bg ? ' Combined front &amp; back AI assessment.' : ''}</div>`
            // Card left, close-up + pillars right.
            + `<div class="pgc-hero-grid">`
            + `<div class="pgc-ov-stage pgc-ov-slot"><img src="${escapeHtml(pgcThumb(crop, 640))}" alt="Graded card" style="display:block;width:100%;max-width:220px;height:auto;margin:auto;"></div>`
            + `<div class="pgc-hero-side">`
            + `<div class="pgc-ov-detail" aria-live="polite"></div>`
            + (window.PGCOverlay && window.PGCOverlay.HINT ? window.PGCOverlay.HINT : '<div class="pgc-ov-hint"><div class="t"><b>Close-up view</b>Select any corner or edge on the card to inspect it here, then tap the close-up to zoom in. Tap the middle of the card to see your original photo.</div></div>')
            + (bg ? '<div class="pgc-hero-label">Front of card</div>' : '')
            + `<div class="pgc-hero-pillars">${tiles}</div>`
            + meta + `</div></div>`
            + (iqNotice || '')
            + (chipsHtml ? `<div class="pgc-hero-chips">${chipsHtml}</div>` : '')
            + `</div>`;
    }

    function injectStyles() {
        if (document.getElementById('ximilar-result-styles')) return;
        const style = document.createElement('style');
        style.id = 'ximilar-result-styles';
        style.textContent = /* __REPORT_CSS_BEGIN__ */`
/* Ximilar result renderer styles — served statically (was injectStyles() in
   ximilar-results.js). Loaded by server-rendered record pages; the JS file
   keeps its copy for the live grading flow. */
/* ── Command Center design system ─────────────────────────────
   Dark is the default (matches the Command Center layout).
   [data-xim-theme="light"] flips every token to the light palette. */
.ximilar-result {
    --xg-bg: #0b1424;
    --xg-panel: #121c31;
    --xg-subtle: #0e1728;
    --xg-border: #243356;
    --xg-text: #e9effc;
    --xg-text-2: #b9c5dc;
    --xg-muted: #8291ad;
    --xg-accent: #22d3ee;
    --xg-accent-contrast: #062a30;
    --xg-accent-soft: rgba(34,211,238,0.10);
    --xg-accent-line: rgba(34,211,238,0.35);
    --xg-good: #34d399;
    --xg-good-soft: rgba(52,211,153,0.10);
    --xg-good-line: rgba(52,211,153,0.35);
    --xg-good-strong: #6ee7a7;
    --xg-warn: #fbbf24;
    --xg-warn-soft: rgba(251,191,36,0.10);
    --xg-warn-line: rgba(251,191,36,0.35);
    --xg-warn-strong: #fcd34d;
    --xg-bad: #f87171;
    --xg-bad-soft: rgba(248,113,113,0.10);
    --xg-bad-line: rgba(248,113,113,0.35);
    --xg-info: #60a5fa;
    --xg-info-soft: rgba(96,165,250,0.10);
    --xg-info-line: rgba(96,165,250,0.35);
    --xg-shadow: 0 12px 32px rgba(0,0,0,0.35);
    --xg-ring-track: #1c2a47;
    --xg-chip-bg: rgba(255,255,255,0.05);
    background: var(--xg-bg);
    color: var(--xg-text);
    border: 1px solid var(--xg-border);
    border-radius: 20px;
    padding: 22px;
}
.ximilar-result[data-xim-theme="light"] {
    --xg-bg: #eef2f7;
    --xg-panel: #ffffff;
    --xg-subtle: #f6f8fb;
    --xg-border: #dfe6ef;
    --xg-text: #0f172a;
    --xg-text-2: #3b4a63;
    --xg-muted: #64748b;
    --xg-accent: #0891b2;
    --xg-accent-soft: rgba(8,145,178,0.08);
    --xg-accent-line: rgba(8,145,178,0.30);
    --xg-good: #059669;
    --xg-good-soft: #ecfdf5;
    --xg-good-line: #bbf7d0;
    --xg-good-strong: #047857;
    --xg-warn: #d97706;
    --xg-warn-soft: #fffbeb;
    --xg-warn-line: #fde68a;
    --xg-warn-strong: #92400e;
    --xg-bad: #dc2626;
    --xg-bad-soft: #fef2f2;
    --xg-bad-line: #fecaca;
    --xg-info: #2563eb;
    --xg-info-soft: #eff6ff;
    --xg-info-line: #bfdbfe;
    --xg-shadow: 0 6px 20px rgba(15,23,42,0.08);
    --xg-ring-track: #e6ecf4;
    --xg-chip-bg: #f1f5f9;
}
.ximilar-result .ximilar-result-card,
.ximilar-result .ximilar-result-header {
    background: var(--xg-panel);
    border-color: var(--xg-border);
}
.ximilar-result .ximilar-result-card > h3,
.ximilar-result .ximilar-result-card h3:first-child {
    text-transform: uppercase;
    letter-spacing: 0.07em;
    font-size: 12.5px !important;
    color: var(--xg-accent) !important;
}
.ximilar-result a { color: var(--xg-info); }
/* theme toggle — segmented Light | Dark switch */
.xim-theme-toggle {
    display: inline-flex; align-items: center; gap: 0;
    border-radius: 8px; cursor: pointer; overflow: hidden;
    background: var(--xg-subtle); border: 1px solid var(--xg-border);
    flex-shrink: 0; transition: all .2s;
}
.xim-theme-toggle:hover { border-color: var(--xg-accent-line); }
.xim-theme-toggle span {
    padding: 5px 14px; font-size: 12px; font-weight: 700;
    color: var(--xg-muted); transition: all .15s; user-select: none;
    display: inline-flex; align-items: center; gap: 5px;
}
.xim-theme-toggle span.active {
    background: var(--xg-accent); color: #fff;
}
.ximilar-result[data-xim-theme="dark"] .xim-theme-toggle .xt-light { color: var(--xg-muted); }
.ximilar-result[data-xim-theme="dark"] .xim-theme-toggle .xt-dark { color: var(--xg-text); background: var(--xg-subtle); }
.ximilar-result[data-xim-theme="light"] .xim-theme-toggle .xt-light { color: var(--xg-text); background: var(--xg-subtle); }
.ximilar-result[data-xim-theme="light"] .xim-theme-toggle .xt-dark { color: var(--xg-muted); }
/* grade ring */
.xg-ring {
    --pct: 0; --ring-color: var(--xg-accent);
    width: 168px; height: 168px; border-radius: 50%;
    background: conic-gradient(var(--ring-color) calc(var(--pct) * 1%), var(--xg-ring-track) 0);
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto;
}
.xg-ring-inner {
    width: calc(100% - 22px); height: calc(100% - 22px); border-radius: 50%;
    background: var(--xg-panel); border: 1px solid var(--xg-border);
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    text-align: center; padding: 10px;
}
.xg-ring-grade { font-size: 44px; font-weight: 800; line-height: 1; color: var(--ring-color); }
.xg-ring-label { font-size: 11px; font-weight: 800; letter-spacing: 0.16em; text-transform: uppercase; color: var(--xg-accent); margin-top: 6px; }
.xg-ring-sub { font-size: 8.5px; letter-spacing: 0.1em; text-transform: uppercase; color: var(--xg-muted); margin-top: 3px; }
/* KPI strip */
.xg-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px; margin-bottom: 16px; }
.xg-kpi { background: var(--xg-panel); border: 1px solid var(--xg-border); border-top: 2px solid var(--xg-accent-line); border-radius: 12px; padding: 12px 14px; }
.xg-kpi-label { font-size: 9.5px; font-weight: 800; letter-spacing: 0.12em; text-transform: uppercase; color: var(--xg-muted); }
.xg-kpi-value { font-size: 22px; font-weight: 800; color: var(--xg-text); margin-top: 3px; line-height: 1.15; }
.xg-kpi-value small { font-size: 12px; color: var(--xg-muted); font-weight: 700; }
.xg-kpi-sub { font-size: 10.5px; color: var(--xg-muted); margin-top: 2px; }
/* subgrade rows with severity */
.xg-sgrow { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
.xg-sgrow:last-child { margin-bottom: 0; }
.xg-sgrow .xim-bar-label { width: 92px; flex-shrink: 0; }
.xg-sgrow .xim-bar-val { width: 40px; flex-shrink: 0; text-align: right; }
/* Fixed-width severity badge so every row aligns identically
   regardless of label length (Fair / Very Good / Excellent ...). */
.xg-sev { display: inline-flex; align-items: center; justify-content: center;
    width: 92px; flex-shrink: 0; padding: 3px 8px; border-radius: 999px;
    font-size: 10px; font-weight: 800; letter-spacing: 0.02em; color: #fff;
    text-align: center; white-space: nowrap; box-sizing: border-box; }
/* subgrade mobile rules are in the main mobile block below */
.xim-metric { background: var(--xg-panel); padding: 12px 10px 10px; border-radius: 6px; text-align: center; border: 1px solid var(--xg-border); overflow: hidden; }
.xim-metric-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(132px, 1fr)); gap: 12px; margin-bottom: 16px; }
.xim-mkt-links { display: flex; flex-wrap: wrap; gap: 8px; }
.xim-mkt-links a { flex: 0 0 auto; }
@media (max-width: 640px) { .xim-metric-grid { grid-template-columns: repeat(2, 1fr); gap: 8px; } }
@media (max-width: 640px) { .xim-metric { padding: 8px 6px 7px; } }
@media (max-width: 640px) { .xim-metric__value { font-size: 14px; } }
@media (max-width: 640px) { .xim-metric__label { font-size: 10px; } }
.xim-metric__label { font-size: 11px; color: var(--xg-muted); font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; margin-bottom: 4px; }
.xim-metric__value { font-size: 17px; font-weight: 700; line-height: 1.2; color: var(--xg-text); }
.xim-metric__sub { font-size: 11px; color: var(--xg-muted); margin-top: 2px; font-weight: 500; }
.xim-chip { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 999px; font-size: 12px; font-weight: 700; background: var(--xg-subtle); color: var(--xg-text-2); border: 1px solid var(--xg-border); }
.ucid-chip, .ucid-stat { display: inline-flex; align-items: center; padding: 4px 10px; border-radius: 999px; font-size: 13px; font-weight: 600; line-height: 1.3; border: 1px solid transparent; }
.ucid-link { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border-radius: 999px; font-size: 13px; font-weight: 700; text-decoration: none; }
.xim-chip b { font-weight: 700; color: var(--xg-text); }
.xim-chip__dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.xim-bar-row { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
.xim-bar-row:last-child { margin-bottom: 0; }
.xim-bar-label { width: 92px; flex-shrink: 0; font-size: 12px; font-weight: 700; color: var(--xg-text-2); }
.xim-bar-track { flex: 1; height: 9px; background: var(--xg-ring-track); border-radius: 999px; overflow: hidden; }
.xim-bar-fill { height: 100%; border-radius: 999px; transition: width .6s cubic-bezier(.22,.61,.36,1); }
.xim-bar-val { width: 40px; flex-shrink: 0; text-align: right; font-size: 13px; font-weight: 700; color: var(--xg-text); }
.xim-kv { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 8px 18px; min-width: 0; }
.xim-kv > div { font-size: 13px; color: var(--xg-text-2); line-height: 1.5; padding: 4px 0; border-bottom: 1px dashed var(--xg-border); word-break: break-word; overflow-wrap: break-word; min-width: 0; }
.xim-kv strong { color: var(--xg-text); font-weight: 700; }
.xim-hero-grid { display: grid; grid-template-columns: minmax(170px, 220px) 1fr; gap: 18px; align-items: stretch; margin-bottom: 18px; }
.ximilar-images-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.ximilar-detail-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; }
.ximilar-detail-section { background: var(--xg-panel); border: 1px solid var(--xg-border); border-radius: 8px; padding: 8px; margin-bottom: 8px; box-shadow: var(--xg-shadow); }
.ximilar-detail-card { background: var(--xg-subtle); border: 1px solid var(--xg-border); border-radius: 8px; overflow: hidden; }
.ximilar-detail-card h4 { margin: 0; padding: 5px 6px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--xg-muted); border-bottom: 1px solid var(--xg-border); }
.ximilar-detail-card .ximilar-zoomable {
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--xg-subtle);
    border-top: 1px solid var(--xg-border);
    border-radius: 0 0 6px 6px;
    padding: 8px;
    cursor: zoom-in;
    min-height: 96px;
}
.ximilar-detail-card .ximilar-zoomable img {
    max-width: 100%;
    max-height: 96px;
    object-fit: contain;
    display: block;
    margin: 0 auto;
}
@media (max-width: 1100px) { .ximilar-detail-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 640px) { .ximilar-detail-grid { grid-template-columns: 1fr; } }
/* Base expander rules — apply at ALL viewport widths. The mobile
   block below overrides display to grid for expanded rows. */
.xim-expandable-rows tr.xim-extra-row { display: none; }
.xim-expandable-rows.xim-expanded tr.xim-extra-row { display: table-row; }
/* Replaces inline onmouseover/onmouseout handlers (CSP) — hover styles */
.xim-expandable-rows tbody tr:hover { background: var(--xg-subtle, rgba(255,255,255,0.05)); }
.xim-hover-link:hover { opacity: 0.85; }
.xim-hover-card:hover { transform: translateY(-2px); }
/* Image-matched listings (Google Lens "looks like your card") — compact
   card grid; the whole card is the link to the listing. */
.xim-imgmatch-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 10px; }
.xim-imgmatch-item {
    display: block;
    padding: 8px;
    border-radius: 10px;
    background: var(--xg-subtle);
    border: 1px solid var(--xg-border);
    text-decoration: none;
    transition: border-color .15s ease, box-shadow .15s ease;
}
a.xim-imgmatch-item:hover { border-color: #4285f4; box-shadow: 0 2px 10px rgba(66,133,244,0.25); }
a.xim-imgmatch-item:hover .xim-imgmatch-title { color: var(--xg-text); text-decoration: underline; }
a.xim-imgmatch-item:focus-visible { outline: 2px solid #4285f4; outline-offset: 2px; }
.xim-imgmatch-thumb {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 104px;
    object-fit: contain;
    border-radius: 6px;
    background: var(--xg-panel);
    border: 1px solid var(--xg-border);
    margin-bottom: 8px;
    color: var(--xg-muted);
}
.xim-imgmatch-title {
    font-size: 12px;
    font-weight: 600;
    line-height: 1.35;
    color: var(--xg-text-2);
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
}
.xim-imgmatch-ext { color: var(--xg-muted); margin-left: 3px; font-weight: 700; }
@media (max-width: 560px) { .xim-imgmatch-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
.ucid-ocr > summary { list-style: none; }
.ucid-ocr > summary::-webkit-details-marker { display: none; }
.ucid-ocr[open] > summary .ucid-ocr-chev { transform: rotate(180deg); }
.hybrid-forecast-panel { background: var(--xg-subtle); border: 1px solid var(--xg-border); border-radius: 8px; padding: 20px; margin-top: 16px; }
.hybrid-forecast-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
.hybrid-forecast-card { background: var(--xg-panel); padding: 18px 12px; border-radius: 8px; border: 1px solid var(--xg-border); text-align: center; display: flex; flex-direction: column; align-items: center; justify-content: flex-start; min-width: 0; }
.xim-corner-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
.xim-profit-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
@media (max-width: 768px) { .ximilar-images-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 640px) { .hybrid-forecast-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 480px) { .ximilar-images-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 560px) { .xim-hero-grid { grid-template-columns: 1fr; } }
@media (max-width: 640px) { .xim-kv { grid-template-columns: repeat(2, 1fr); } }
/* Image Quality Analysis: stack the thumbnail above the commentary on narrow screens */
.xim-iq-thumb { flex-shrink: 0; width: 180px; margin-right: 16px; }
.xim-iq-image-trigger,
.xim-iq-zone-trigger { display: block; width: 100%; padding: 0; border: 0; background: transparent; color: inherit; font: inherit; text-align: left; cursor: zoom-in; }
.xim-iq-image-trigger img { display: block; width: 180px; max-width: 38vw; height: auto; border-radius: 8px; border: 1px solid var(--xg-border); object-fit: cover; }
.xim-iq-image-trigger:focus-visible,
.xim-iq-zone-trigger:focus-visible { outline: 3px solid #0891b2; outline-offset: 3px; border-radius: 8px; }
.xim-iq-zonemap { flex: 0 0 200px; width: 200px; margin-right: 16px; }
.xim-iq-zone-visual { position: relative; display: block; width: 100%; overflow: hidden; border-radius: 8px; border: 1px solid var(--xg-border); background: var(--xg-panel); }
.xim-iq-image-cta,
.xim-iq-map-cta { display: block; margin-top: 7px; color: var(--xg-text-2); font-size: 12px; font-weight: 700; text-align: center; }
.xim-iq-zone-legend { display: flex; flex-direction: column; gap: 5px; margin-top: 8px; color: var(--xg-text-2); font-size: 12px; line-height: 1.35; }
.xim-iq-zone-legend > div { display: flex; align-items: center; gap: 6px; }
.xim-iq-zone-swatch { display: inline-block; flex: 0 0 13px; width: 13px; height: 13px; border: 2px solid rgba(255,255,255,0.96); border-radius: 3px; box-sizing: border-box; }
.xim-iq-zone-swatch.is-blown { background: rgba(245,158,11,0.52); box-shadow: inset 0 0 0 1px rgba(180,83,9,0.98); }
.xim-iq-zone-swatch.is-shadow { background: rgba(8,145,178,0.48); box-shadow: inset 0 0 0 1px rgba(14,116,144,0.98); }
.xim-iq-zone-lightbox { position: fixed; inset: 0; z-index: 100001; display: none; align-items: center; justify-content: center; padding: 64px 24px 24px; background: rgba(8,12,24,0.94); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px); }
.xim-iq-zone-lightbox[aria-hidden="false"] { display: flex; }
.xim-iq-zone-dialog { position: relative; display: flex; flex-direction: column; align-items: center; gap: 14px; max-width: 100%; max-height: 100%; }
.xim-iq-zone-close { position: absolute; top: -48px; right: 0; width: 42px; height: 42px; border: 1px solid rgba(255,255,255,0.45); border-radius: 50%; background: rgba(15,23,42,0.8); color: #fff; font-size: 26px; line-height: 1; cursor: pointer; }
.xim-iq-zone-viewer { display: flex; flex-direction: column; align-items: center; gap: 12px; max-width: 100%; max-height: calc(100vh - 110px); overflow: auto; }
.xim-iq-zone-viewer .xim-iq-zone-visual { border-color: rgba(255,255,255,0.55); border-radius: 10px; box-shadow: 0 20px 60px rgba(0,0,0,0.5); }
.xim-iq-zone-viewer .xim-iq-zone-legend { flex-direction: row; flex-wrap: wrap; justify-content: center; gap: 8px 18px; color: #e2e8f0; font-size: 14px; }
@media (max-width: 640px) {
    .xim-iq-layout { flex-direction: column !important; }
    .xim-iq-thumb { width: 170px; margin-right: 0 !important; margin-bottom: 14px !important; }
    .xim-iq-thumb img { width: 170px !important; max-width: 76vw; }
    .xim-iq-zonemap { flex-basis: auto; width: min(190px, 72vw); margin-right: 0 !important; margin-bottom: 14px !important; }
    .xim-iq-zone-lightbox { padding: 58px 14px 18px; }
    .xim-iq-zone-viewer .xim-iq-zone-legend { flex-direction: column; gap: 5px; font-size: 12px; }
}
@media (max-width: 640px) { .xim-corner-grid { grid-template-columns: repeat(2, 1fr); gap: 8px; } }
@media (max-width: 640px) { .xim-corner-grid > div { padding: 8px 6px !important; } }
@media (max-width: 640px) { .xim-corner-grid > div > div:nth-child(2) { font-size: 1.2rem !important; } }
@media (max-width: 640px) { .xim-profit-grid { grid-template-columns: repeat(3, 1fr); gap: 8px; } }
@media (max-width: 400px) { .xim-profit-grid { grid-template-columns: 1fr; gap: 8px; } }
@media (max-width: 400px) { .xim-profit-grid > div { border-right: none !important; border-bottom: 1px solid var(--xg-border); padding-right: 0 !important; padding-bottom: 10px; } }
@media (max-width: 768px) { .ximilar-price-grid { grid-template-columns: repeat(4, 1fr) !important; gap: 4px !important; } }
@media (max-width: 768px) { .ximilar-price-grid > div { padding: 6px 4px !important; } }
@media (max-width: 768px) { .ximilar-price-grid > div > div:first-child { font-size: 8px !important; } }
@media (max-width: 768px) { .ximilar-price-grid > div > div:last-child { font-size: 13px !important; } }
@media (max-width: 480px) { .ximilar-price-grid { grid-template-columns: repeat(3, 1fr) !important; } }
@media (max-width: 768px) { .ximilar-price-trend > div:last-child { gap: 6px !important; } }
@media (max-width: 768px) { .ximilar-price-trend > div:last-child > div { padding: 4px 8px !important; } }
/* PSA pop ladder keeps its 5-col grid on mobile (header.php blanket-collapses inline grids at 768px) */
@media (max-width: 768px) { .ximilar-result .xg-pop-row { grid-template-columns: 24px 1fr 30px 44px 54px !important; gap: 6px !important; } }
@media (max-width: 400px) { .ximilar-result .xg-pop-row { grid-template-columns: 22px 1fr 26px 40px 48px !important; font-size: 10px !important; } }
@media (max-width: 480px) { .ximilar-result .xg-pop-hd-grade { visibility: hidden; } }
@media (max-width: 768px) {
    .ximilar-listings-table thead { display: none; }
    .ximilar-listings-table, .ximilar-listings-table tbody, .ximilar-listings-table tr { display: block; width: 100% !important; }
    .ximilar-listings-table tr {
        display: grid !important;
        grid-template-columns: 1fr auto !important;
        grid-template-rows: auto auto;
        gap: 4px 10px !important;
        align-items: center !important;
        border: 1px solid var(--xg-border) !important;
        border-radius: 8px !important;
        margin-bottom: 6px !important;
        padding: 8px 10px !important;
        background: var(--xg-panel) !important;
    }
    .xim-expandable-rows tr.xim-extra-row {
        display: none !important;
    }
    .xim-expandable-rows.xim-expanded tr.xim-extra-row {
        display: grid !important;
    }
    .ximilar-listings-table td {
        display: block !important;
        border: none !important;
        padding: 0 !important;
        width: auto !important;
    }
    .ximilar-listings-table td::before { display: none !important; }
    .ximilar-listings-table td[data-label="Listing"] {
        grid-column: 1 / -1;
        font-size: 12px !important;
        line-height: 1.35 !important;
        margin-bottom: 2px;
    }
    .ximilar-listings-table td[data-label="Listing"] > div:first-child {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        font-size: 12px !important;
    }
    .ximilar-listings-table td[data-label="Listing"] > div:last-child {
        margin-top: 2px !important;
    }
    .ximilar-listings-table td[data-label="Price"] {
        grid-column: 1;
        font-weight: 800 !important;
        font-size: 14px !important;
    }
    .ximilar-listings-table td[data-label="Source"] {
        grid-column: 2;
        text-align: right;
    }
    .ximilar-listings-table td[data-label="Source"] a {
        padding: 3px 8px !important;
        font-size: 11px !important;
        border-radius: 4px !important;
    }
    .ximilar-listings-table td[data-label="Date"] {
        grid-column: 1 / -1;
        font-size: 10px !important;
        color: var(--xg-muted) !important;
        margin-top: -2px;
    }
}

/* ===== Economics card mobile optimization ===== */
@media (max-width: 640px) {
    .xim-econ-card { padding: 12px !important; border-radius: 10px; margin: 0 10px 12px !important; }
    .xim-econ-header { flex-direction: row !important; align-items: center !important; justify-content: space-between !important; gap: 10px !important; margin-bottom: 12px !important; }
    .xim-econ-header > div:first-child { display: flex !important; align-items: center !important; gap: 10px !important; flex: 1 1 auto !important; min-width: 0 !important; width: auto !important; }
    .xim-econ-header > div:first-child > div:first-child { flex-shrink: 0 !important; width: 40px !important; height: 40px !important; font-size: 20px !important; }
    .xim-econ-header > div:first-child > div:last-child { min-width: 0 !important; }
    .xim-econ-header > span { width: auto !important; text-align: right !important; flex-shrink: 0 !important; font-size: 11px !important; padding: 4px 10px !important; }
    .xim-econ-header h3 { font-size: 17px !important; font-weight: 800 !important; margin: 0 !important; line-height: 1.2 !important; }
    .xim-econ-stats { display: grid !important; grid-template-columns: 1fr 1fr; gap: 8px !important; margin-bottom: 12px !important; }
    .xim-econ-stat { min-width: 0 !important; padding: 8px 10px !important; border-radius: 8px !important; }
    .xim-econ-stat > div:first-child { font-size: 10px !important; }
    .xim-econ-stat > div:nth-child(2) { font-size: 16px !important; }
    /* Compact company cards instead of stacked table rows */
    .xim-econ-table { min-width: 0 !important; width: 100% !important; }
    .xim-econ-table thead { display: none; }
    .xim-econ-table, .xim-econ-table tbody { display: block; width: 100% !important; }
    .xim-econ-table tr { display: grid !important; grid-template-columns: 1fr auto; gap: 2px 8px; align-items: center; border: 1px solid var(--xg-border) !important; border-radius: 10px; margin-bottom: 8px; padding: 10px 12px; width: 100%; }
    .xim-econ-table td { display: block !important; border: none !important; padding: 0 !important; font-size: 13px !important; width: auto !important; }
    .xim-econ-table td::before { display: none !important; }
    .xim-econ-table td[data-label="Company"] { grid-column: 1; font-weight: 700; font-size: 14px !important; }
    .xim-econ-table td[data-label="Company"] > span { font-size: 11px; }
    .xim-econ-table td[data-label="Best Grade"] { grid-column: 2; text-align: right; font-weight: 700; font-size: 16px !important; }
    .xim-econ-table td[data-label="Total Cost"] { grid-column: 1; font-size: 11px !important; color: var(--xg-muted); }
    .xim-econ-table td[data-label="Net Profit"] { grid-column: 1; font-size: 13px !important; font-weight: 700; }
    .xim-econ-table td[data-label="ROI"] { grid-column: 2; text-align: right; font-size: 13px !important; font-weight: 700; }
}

body.ximilar-lightbox-open .back-to-top { display: none !important; }

@media (max-width: 640px) {
    /* Edge-to-edge: outer .container already provides 24px margin.
       Remove .ximilar-result padding to reclaim ~28px of width. */
    .ximilar-result { padding: 0 !important; border-radius: 0; border: none; }
    .ximilar-result-header { padding: 14px !important; border-radius: 10px; margin: 10px 10px 12px; }
    .ximilar-header-inner { display: flex !important; flex-wrap: wrap !important; align-items: center !important; gap: 12px !important; }
    .ximilar-header-icon { font-size: 32px !important; order: 0 !important; flex-shrink: 0 !important; }
    .ximilar-header-title { min-width: 0 !important; flex: 1 1 auto !important; order: 1 !important; }
    .ximilar-header-h1 { font-size: 20px !important; font-weight: 800; margin: 0 0 2px 0 !important; }
    .ximilar-header-title p { font-size: 12px !important; margin: 0 !important; }
    .ximilar-header-stats { order: 2 !important; gap: 8px !important; width: 100% !important; margin-top: 4px !important; }
}

/* ===== Mobile layout optimization (iPhone-first) =====
   Edge-to-edge layout: outer .container provides the margin.
   Inner panels use 12px padding; fonts enlarged to use the space. */
@media (max-width: 640px) {
    .ximilar-result { overflow-x: hidden; }
    /* Remove body card padding — outer container handles it */
    .ximilar-results-body {
        padding: 0 !important; background: transparent !important;
        border: none !important; box-shadow: none !important;
        margin-bottom: 0 !important;
    }
    /* Other result cards: 12px padding, small margin for separation */
    .ximilar-result-card:not(.ximilar-results-body) {
        padding: 12px !important; margin: 0 10px 12px !important; border-radius: 10px;
    }
    /* Hero grid: reduce gap + panel padding */
    .xim-hero-grid { gap: 10px; margin: 0 10px 12px; }
    .xim-hero-grid > div { padding: 14px !important; border-radius: 10px; }
    /* Grade ring: compact but readable */
    /* Grade ring: compact — shrink inner ring border so grade+label fit */
    .xg-ring { width: 128px; height: 128px; }
    .xg-ring-inner { width: calc(100% - 14px); height: calc(100% - 14px); padding: 6px; }
    .xg-ring-grade { font-size: 34px; }
    .xg-ring-label { font-size: 10px; margin-top: 4px; }
    .xg-ring-sub { font-size: 7.5px; margin-top: 2px; }
    /* KPI strip: 2 columns, larger fonts w/ extra width */
    .xg-kpis { grid-template-columns: repeat(2, 1fr) !important; gap: 8px; margin: 0 10px 12px; }
    .xg-kpi { padding: 10px 12px; }
    .xg-kpi-value { font-size: 20px; }
    .xg-kpi-label { font-size: 10px; }
    .xg-kpi-sub { font-size: 11px; }
    /* Subgrade rows: enlarge fonts w/ extra width */
    .xg-sgrow { gap: 10px; margin-bottom: 11px; }
    .xg-sgrow .xim-bar-label { width: 72px; font-size: 13px; }
    .xg-sgrow .xim-bar-val { width: 34px; font-size: 15px; }
    .xg-sev { width: 80px; font-size: 10px; padding: 3px 8px; }
    .xim-bar-track { height: 10px; }
    /* Data blocks: 12px padding, larger body text */
    .xg-block { padding: 12px !important; margin: 0 10px 12px !important; border-radius: 10px; }
    .xg-block h4 { font-size: 13px !important; }
    .xg-block > div { font-size: 14px !important; line-height: 1.55 !important; }
    /* Hybrid forecast: reduce padding, enlarge grades */
    .hybrid-forecast-panel { padding: 14px; margin: 0 10px 12px; border-radius: 10px; }
    .hybrid-forecast-card { padding: 14px 8px; }
    .hybrid-forecast-grade { font-size: 2.4rem !important; }
    .hybrid-forecast-label { font-size: 12px !important; }
    .hybrid-forecast-note { font-size: 11px !important; }
    /* Profit grid: reduce gap, keep 3 cols */
    .xim-profit-grid { gap: 8px; }
    .xim-profit-grid > div > div:first-child { font-size: 11px !important; }
    .xim-profit-grid > div > div:nth-child(2) { font-size: 1.8rem !important; }
    /* Images: 2 columns, taller thumbnails */
    .ximilar-images-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 10px; margin: 0 10px 12px; }
    .ximilar-image-item img { height: 130px !important; }
    /* Detail grids: 2 columns */
    .ximilar-detail-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .ximilar-detail-section { margin: 0 10px 12px; }
    /* Corner/edge grids: 2 columns, larger grade numbers */
    .xim-corner-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
    .xim-corner-grid > div { padding: 12px 10px !important; }
    .xim-corner-grid > div > div:nth-child(2) { font-size: 1.5rem !important; }
    /* Chips: slightly larger */
    .xim-chip { font-size: 13px; padding: 7px 13px; }
    /* KV grid: larger text */
    .xim-kv > div { font-size: 14px !important; line-height: 1.5 !important; }
    /* Identification section: clean single-column list on mobile */
    .xim-id-hero { margin: 0 10px 12px !important; padding: 14px 16px !important; border-radius: 10px; }
    .xim-id-hero > div:nth-child(2) { font-size: 20px !important; }
    .xim-id-hero > div:nth-child(3) { font-size: 14px !important; }
    .xim-id-grid { display: grid !important; grid-template-columns: repeat(2, 1fr) !important; gap: 0 !important; margin: 0 10px 12px !important; background: var(--xg-panel); border: 1px solid var(--xg-border); border-radius: 10px; overflow: hidden; }
    .xim-id-item { background: transparent !important; border: none !important; border-bottom: 1px solid var(--xg-border) !important; border-right: 1px solid var(--xg-border) !important; border-radius: 0 !important; padding: 8px 10px !important; text-align: left !important; display: flex; flex-direction: column; align-items: flex-start; gap: 2px; min-width: 0; }
    .xim-id-item:nth-child(2n) { border-right: none !important; }
    .xim-id-item .xim-metric__label { margin-bottom: 0 !important; font-size: 10px !important; }
    .xim-id-item .xim-metric__value { font-size: 14px !important; text-align: left; word-break: break-word; overflow-wrap: break-word; }
    .xim-id-kv { grid-template-columns: repeat(2, 1fr) !important; gap: 0 !important; margin-top: 12px !important; }
    .xim-id-kv > div { border-bottom: 1px solid var(--xg-border) !important; padding: 8px 10px !important; }
    /* Attribute chips (Side/Category/Damaged/Detection): 2-up on mobile.
       A nowrap pill + 999px radius turns into an oval blob with wrapped
       text (#11442 iPhone screenshot) — let the content wrap inside a
       rounded tile instead. */
    .xim-attr-chips { display: grid !important; grid-template-columns: repeat(2, 1fr) !important; gap: 6px !important; align-items: stretch; }
    .xim-attr-chips .xim-chip { display: flex; flex-wrap: wrap; justify-content: center; align-items: center; align-content: center; gap: 3px 5px; width: 100%; box-sizing: border-box; font-size: 11.5px; line-height: 1.3; padding: 7px 8px; border-radius: 12px; text-align: center; white-space: normal; }
    .xim-attr-chips .xim-chip > span { white-space: normal; }
    /* Centering pair (Ximilar overlay + SVG): keep side-by-side on mobile */
    .xim-centering-pair { flex-wrap: nowrap !important; gap: 8px !important; }
    .xim-centering-pair .xim-centering-fig,
    .xim-centering-pair .xim-centering-svg { flex: 1 1 0 !important; min-width: 0 !important; }
    /* SportsDB player/team profile: full-width cards, side-by-side image+text */
    .ximilar-sportsdb-content { display: flex !important; flex-direction: column !important; gap: 10px !important; align-items: stretch !important; justify-content: flex-start !important; }
    .ximilar-sportsdb-content > div { flex: 1 1 auto !important; min-width: 0 !important; width: 100% !important; }
    .ximilar-sportsdb-content > div > div:first-child { flex-wrap: nowrap !important; }
    .ximilar-sportsdb-content img { width: 60px !important; height: 60px !important; flex-shrink: 0 !important; }
    /* Share buttons: uniform 2-column grid on mobile */
    .xim-share-buttons { display: grid !important; grid-template-columns: repeat(2, 1fr) !important; gap: 8px !important; }
    .xim-share-buttons > a, .xim-share-buttons > button {
        width: 100% !important; justify-content: center !important; box-sizing: border-box !important;
        padding: 10px 8px !important; font-size: 12px !important;
    }
    /* Share section + referral promo: edge-to-edge w/ margin */
    .ximilar-share-section, .ximilar-referral-promo { margin: 0 10px 12px !important; padding: 14px 16px !important; border-radius: 10px; }
    /* Grade tips + upsell cards: override inline padding/margin */
    .ximilar-grade-tips, .ximilar-post-result-upsell { padding: 12px !important; margin: 0 10px 12px !important; border-radius: 10px; }
    /* Upsell "Ready for your next card?" — stack text + button vertically on mobile */
    .ximilar-post-result-upsell > div[style*="space-between"] {
        flex-direction: column !important; align-items: stretch !important; gap: 12px !important;
    }
    .ximilar-post-result-upsell > div[style*="space-between"] > div[style*="min-width"] { min-width: 0 !important; }
    .ximilar-post-result-upsell > div[style*="space-between"] > a,
    .ximilar-post-result-upsell > div[style*="space-between"] > button {
        width: 100% !important; text-align: center !important; box-sizing: border-box !important;
    }
    /* Next steps: 2-column grid on mobile */
    .xim-next-steps-grid { display: grid !important; grid-template-columns: repeat(2, 1fr) !important; gap: 8px !important; }
    .xim-next-step { width: 100% !important; text-align: center !important; box-sizing: border-box !important; justify-content: center !important; }
    /* Price grid: 4 columns on mobile (override global 1fr rule) */
    .xg-price-grid { grid-template-columns: repeat(4, 1fr) !important; gap: 6px !important; }
    .xg-price-grid > div { padding: 6px 2px !important; }
    .xg-price-grid > div > div:first-child { font-size: 9px !important; }
    .xg-price-grid > div > div:last-child { font-size: 14px !important; }
    /* Unified Card Identification card mobile overrides */
    .xim-unified-inner { padding: 12px 10px !important; }
    .xim-unified-header { gap: 10px !important; margin-bottom: 10px !important; }
    .xim-details-section { margin-bottom: 8px !important; }
    .xim-info-grid { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; }
    .xim-info-cell { padding: 4px 6px !important; }
    .xim-info-label { font-size: 9px !important; }
    .xim-info-value { font-size: 12.5px !important; }
    .ucid-chip, .ucid-stat { font-size: 12px !important; padding: 3px 9px !important; }
    /* Centering details panel mobile overrides */
    .xim-border-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 6px !important; }
    .xim-border-cell { padding: 5px 9px !important; text-align: left !important; }
    .xim-border-cell span { font-size: 10.5px !important; }
    .xim-border-cell strong { font-size: 12.5px !important; }
    .xim-ratio-row { grid-template-columns: 1fr 1fr !important; gap: 6px !important; margin-top: 6px !important; }
    .xim-ratio-pill { padding: 5px 9px !important; flex: 1 1 0 !important; }
    .xim-ratio-pill span:first-child { font-size: 8.5px !important; }
    .xim-ratio-pill span:last-child { font-size: 13.5px !important; }
    .xim-centering-desc { margin-top: 6px !important; font-size: 11px !important; }
    /* Section headings mobile scale */
    .xim-econ-header h3,
    .hybrid-forecast-panel h4,
    .xg-block h4 { font-size: 16px !important; }
    /* Ensure flex/grid children don't overflow */
    .ximilar-result * { min-width: 0; box-sizing: border-box; }
}
@media (max-width: 360px) {
    .xg-kpis { grid-template-columns: 1fr !important; }
    .ximilar-images-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
    .xg-price-grid { grid-template-columns: repeat(3, 1fr) !important; }
    .xg-ring { width: 110px; height: 110px; }
    .xg-ring-inner { width: calc(100% - 12px); height: calc(100% - 12px); padding: 4px; }
    .xg-ring-grade { font-size: 30px; }
    .xg-ring-label { font-size: 9px; margin-top: 3px; }
    .xg-ring-sub { font-size: 7px; }
    .xg-sgrow .xim-bar-label { width: 60px; font-size: 12px; }
    .xg-sgrow .xim-bar-val { width: 28px; font-size: 13px; }
    .xg-sev { width: 68px; font-size: 9px; }
}

@media print {
    .ximilar-result {
        --xg-bg: #ffffff; --xg-panel: #ffffff; --xg-subtle: #f8fafc; --xg-border: #e5e7eb;
        --xg-text: #111827; --xg-text-2: #374151; --xg-muted: #6b7280;
        --xg-shadow: none; border: none; border-radius: 0; padding: 0;
    }
    .xim-theme-toggle { display: none !important; }
    .ximilar-result-header,
    .ximilar-result-card,
    .ximilar-results-body,
    .ximilar-images-grid,
    .ximilar-image-item,
    .ximilar-print-banner,
    .ximilar-result {
        break-inside: avoid-page !important;
        page-break-inside: avoid !important;
    }
    .ximilar-print-banner { display: block !important; }
    .ximilar-result-header,
    .ximilar-result-actions,
    .ximilar-report-section,
    .ximilar-sportsdb-lookup,
    .ximilar-tcg-lookup,
    .ximilar-post-result-upsell,
    .ximilar-technical-details,
    .ximilar-share-section,
    .ximilar-lightbox,
    #ximilar-lightbox,
    .ximilar-zoomable > div,
    .ximilar-zoom-icon,
    button { display: none !important; }
    .ximilar-image-item img {
        max-height: 1.8in !important;
        width: auto !important;
        height: auto !important;
        object-fit: contain !important;
    }
    .ximilar-images-grid {
        grid-template-columns: repeat(4, 1fr) !important;
        gap: 10px !important;
    }
    .ximilar-image-corner-detail img,
    .ximilar-image-edge-detail img {
        max-height: 1.5in !important;
    }
}

/* === Variant C identity panel (2026-10-23, owner-approved) =================
   Compact "spec-sheet" layout for the unified card identity block:
   one merged minicell strip (Card/Game/Subject ticks) replaces the stacked
   Card Details + Game Details sections. Used by both renderers
   (includes/ResultRenderer.php + assets/js/services/ximilar-results.js) —
   keep the emitted class names in sync with this sheet.
============================================================================= */
.ucid-headrow{display:flex;align-items:flex-start;gap:10px;flex-wrap:wrap}
.ucid-headrow .ucid-title{font-size:20px;font-weight:800;color:var(--xg-text);line-height:1.2;letter-spacing:-.01em}
.ucid-headrow .ucid-rar{font-size:13px;color:var(--xg-muted);font-weight:500}
.ucid-headrow .ucid-full{font-size:13px;color:var(--xg-text-2);margin:2px 0 6px}
.ucid-headrow .ucid-chips{display:flex;flex-wrap:wrap;gap:5px;gap:5px;justify-content:flex-end;margin-left:auto;min-width:220px;max-width:46%}
.ucid-chips-col{display:flex;flex-wrap:wrap;gap:5px;margin-top:8px}
.ucid-headrow .ucid-chips-col{margin-left:0;min-width:0;max-width:none;justify-content:flex-start}
.ucid-main2{display:grid;grid-template-columns:1fr;gap:14px}
/* Slab in the identity rail: taller than the card crop, so it gets its own
   column width and no forced aspect-ratio. user-select + draggable are killed
   here — on Windows a mouse drag otherwise paints a selection highlight over
   the image and fights the tilt gesture. */
.ucid-main2.has-img.has-slab{grid-template-columns:172px 1fr}.ucid-slab-frame{display:block;position:relative;cursor:grab;user-select:none;-webkit-user-select:none;-ms-user-select:none;touch-action:none}
.ucid-slab-frame:active{cursor:grabbing}
.ucid-slab-frame img.ucid-slabimg{width:100%;max-width:170px;height:auto;display:block;border-radius:10px;border:1px solid var(--xg-border);background:var(--xg-subtle);pointer-events:none;user-select:none;-webkit-user-drag:none;transition:transform 450ms cubic-bezier(.22,1,.36,1)}
@media (max-width:700px){.ucid-main2.has-img.has-slab{grid-template-columns:1fr}.ucid-slab-frame img.ucid-slabimg{max-width:190px;margin:0 auto}}
.ucid-main2.has-img{grid-template-columns:140px 1fr}
.ucid-rail{display:flex;flex-direction:column;gap:8px;min-width:0}
.ucid-rail img.ucid-cardimg{width:100%;max-width:140px;height:auto;aspect-ratio:5/7;object-fit:contain;background:var(--xg-subtle);border:1px solid var(--xg-border);border-radius:9px}
/* Not scoped to .ucid-rail: the header wordmark renders outside the rail, and
   an unscoped rule is the only thing that keeps a wide set logo (Scarlet &
   Violet) from filling the panel. */
img.ucid-setlogo{max-height:44px;max-width:130px;width:auto;height:auto;object-fit:contain}
.ucid-strip{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:1px;background:var(--xg-border);border:1px solid var(--xg-border);border-radius:10px;overflow:hidden;align-content:start;margin-bottom:14px}
.ucid-cell{background:var(--xg-panel);padding:6px 10px 7px;border-top:2px solid #2563eb;min-width:0}
.ucid-cell[data-g="game"]{border-top-color:#7c3aed}
.ucid-cell[data-g="subj"]{border-top-color:#059669}
.ucid-cell .k{display:block;font-size:9px;font-weight:800;letter-spacing:.05em;text-transform:uppercase;color:var(--xg-muted)}
.ucid-cell .v{display:block;font-size:12.5px;font-weight:650;color:var(--xg-text)}
.ucid-legend{display:flex;flex-wrap:wrap;gap:12px;font-size:10px;font-weight:600;color:var(--xg-muted);margin:0 0 6px}
.ucid-legend i{display:inline-block;width:8px;height:8px;border-radius:2px;margin-right:4px;vertical-align:-1px}
.ucid-sec{font-size:10px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;color:var(--xg-muted);margin:12px 0 6px}
.ucid-atks{margin-top:10px;border:1px solid var(--xg-border);border-radius:10px;overflow:hidden}
.ucid-atk{display:grid;grid-template-columns:auto 1fr;gap:2px 8px;align-items:start;padding:7px 10px;font-size:12.5px}
.ucid-atk:nth-child(even){background:var(--xg-subtle)}
.ucid-atk .pips{display:inline-flex;gap:3px;padding-top:2px}
.ucid-atk .pips .pip,.pip{width:12px;height:12px;border-radius:50%;display:inline-block;border:1px solid rgba(15,23,42,.15)}
.pip.el-water{background:#3b82f6}.pip.el-fire{background:#ef4444}.pip.el-grass{background:#22c55e}.pip.el-lightning{background:#facc15}
.pip.el-fighting{background:#b45309}.pip.el-psychic{background:#a855f7}.pip.el-darkness{background:#334155}.pip.el-metal{background:#94a3b8}
.pip.el-colorless{background:#cbd5e1}.pip.el-dragon{background:#f97316}.pip.el-fairy{background:#f472b6}.pip.el-other{background:#e2e8f0}
.pip-txt{display:inline-block;padding:1px 6px;background:var(--xg-subtle);border:1px solid var(--xg-border);border-radius:6px;font-size:10px;font-weight:600;color:var(--xg-text-2)}
.ucid-atk .at{display:flex;gap:6px;align-items:baseline;flex-wrap:wrap}
.ucid-atk .at .n{font-weight:750;color:var(--xg-text)}
.ucid-atk .at .dmg{font-weight:800;color:#b91c1c}
.ucid-atk .at .cost{font-size:11px;color:var(--xg-muted)}
.ucid-atk .at .t{width:100%;color:var(--xg-text-2);font-size:12px;line-height:1.4}
@media (max-width:700px){
  .ucid-main2.has-img{grid-template-columns:1fr}
  .ucid-rail{align-items:center;max-width:190px;margin:0 auto}
  .ucid-headrow .ucid-chips{margin-left:0;max-width:none;justify-content:flex-start}
  .ucid-strip{grid-template-columns:repeat(auto-fill,minmax(120px,1fr))}
}
        `/* __REPORT_CSS_END__*/;
        document.head.appendChild(style);
    }

    function renderMetricGrid(items) {
        if (!items || items.length === 0) return '';
        const visible = items.filter(i => i.value !== null && i.value !== undefined && i.value !== '');
        if (visible.length === 0) return '';
        return `
            <div class="xim-metric-grid xim-id-grid">
                ${visible.map(item => `
                    <div class="xim-metric xim-id-item" style="--xim-accent: ${item.color || '#94a3b8'};${item.bg ? ` background: ${item.bg};` : ''}">
                        <div class="xim-metric__label">${item.label}</div>
                        <div class="xim-metric__value" style="color: ${item.color || 'var(--xg-text)'};">${item.value}</div>
                        ${item.sub ? `<div class="xim-metric__sub">${item.sub}</div>` : ''}
                    </div>
                `).join('')}
            </div>
        `;
    }

    function renderDataBlock(title, content, color = 'var(--xg-text)', bg = 'var(--xg-subtle)') {
        if (!content) return '';
        return `
            <div class="xg-block" style="background: var(--xg-panel); padding: 16px 18px; border-radius: 8px; margin-top: 16px; margin-bottom: 12px; border: 1px solid var(--xg-border); border-left: 4px solid ${color}; box-shadow: var(--xg-shadow);">
                <h4 style="font-size: 15px; font-weight: 800; color: ${color}; margin: 0 0 12px 0; text-transform: uppercase; letter-spacing: 0.05em; display: flex; align-items: center; gap: 8px;">
                    <span style="width: 8px; height: 8px; border-radius: 50%; background: ${color}; flex-shrink: 0;"></span>${title}
                </h4>
                <div style="font-size: 13px; color: var(--xg-text-2); line-height: 1.5;">${content}</div>
            </div>
        `;
    }

    // ── Market Price Analysis (shared across services) ─────────────────────────
    function renderPriceStats(stats) {
        if (!stats || !Array.isArray(stats) || stats.length === 0) return '';

        // Remove 'overall' stats_type and sort: ungraded first, then graded
        const filtered = stats.filter(s => s.stats_type !== 'overall');
        filtered.sort((a, b) => {
            const order = { ungraded: 0, graded: 1 };
            return ((function(_o){return _o != null ? _o : 2})(order[a.stats_type])) - ((function(_o){return _o != null ? _o : 2})(order[b.stats_type]));
        });

        const panels = filtered.map(stat => {
            const v = stat.value || {};
            const trend = v.trend || {};
            const typeColor  = stat.stats_type === 'graded' ? '#7c3aed' : stat.stats_type === 'ungraded' ? '#0891b2' : '#1e293b';
            const typeLabel  = stat.stats_type === 'graded' ? '🏅 Graded' : stat.stats_type === 'ungraded' ? '📦 Raw / Ungraded' : '📊 Overall Market';
            const trendDir   = trend.slope_per_day > 0 ? '↗' : trend.slope_per_day < 0 ? '↘' : '→';
            const trendColor = trend.slope_per_day > 0 ? '#10b981' : trend.slope_per_day < 0 ? '#ef4444' : '#64748b';
            const volatility = trend.volatility != null ? (parseFloat(trend.volatility) * 100).toFixed(0) + '%' : null;
            const forecast   = trend.forecast_30d != null ? '$' + parseFloat(trend.forecast_30d).toFixed(2) : null;

            return `
            <div style="
                background: var(--xg-panel);
                border-radius: 8px;
                border: 1px solid var(--xg-border);
                padding: 18px;
                margin-bottom: 14px;
                box-shadow: var(--xg-shadow);
            ">
                <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; flex-wrap: wrap; gap: 8px;">
                    <span style="font-size: 15px; font-weight: 700; color: ${typeColor};">${typeLabel}</span>
                    ${v.latest_date ? `<span style="font-size: 11px; color: var(--xg-muted); background: var(--xg-subtle); padding: 4px 10px; border-radius: 20px;">Latest sale: ${v.latest_date}</span>` : ''}
                </div>

                <div class="ximilar-price-grid xg-price-grid" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(72px, 1fr)); gap: 6px; margin-bottom: 12px;">
                    ${v.mean != null ? `<div style="background: var(--xg-panel); padding: 8px 6px; border-radius: 6px; text-align: center; border: 1px solid var(--xg-border);">
                        <div style="font-size: 9px; color: var(--xg-muted); font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em;">Mean</div>
                        <div style="font-size: 17px; font-weight: 700; color: #059669;">$${parseFloat(v.mean).toFixed(2)}</div>
                    </div>` : ''}
                    ${v.median != null ? `<div style="background: var(--xg-panel); padding: 8px 6px; border-radius: 6px; text-align: center; border: 1px solid var(--xg-border);">
                        <div style="font-size: 9px; color: var(--xg-muted); font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em;">Median</div>
                        <div style="font-size: 17px; font-weight: 700; color: #059669;">$${parseFloat(v.median).toFixed(2)}</div>
                    </div>` : ''}
                    ${v.min != null ? `<div style="background: var(--xg-panel); padding: 8px 6px; border-radius: 6px; text-align: center; border: 1px solid var(--xg-border);">
                        <div style="font-size: 9px; color: var(--xg-muted); font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em;">Min</div>
                        <div style="font-size: 15px; font-weight: 700; color: var(--xg-text-2);">$${parseFloat(v.min).toFixed(2)}</div>
                    </div>` : ''}
                    ${v.max != null ? `<div style="background: var(--xg-panel); padding: 8px 6px; border-radius: 6px; text-align: center; border: 1px solid var(--xg-border);">
                        <div style="font-size: 9px; color: var(--xg-muted); font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em;">Max</div>
                        <div style="font-size: 15px; font-weight: 700; color: var(--xg-text-2);">$${parseFloat(v.max).toFixed(2)}</div>
                    </div>` : ''}
                    ${v.q1 != null ? `<div style="background: var(--xg-panel); padding: 8px 6px; border-radius: 6px; text-align: center; border: 1px solid var(--xg-border);">
                        <div style="font-size: 9px; color: var(--xg-muted); font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em;">Q1</div>
                        <div style="font-size: 15px; font-weight: 700; color: var(--xg-text-2);">$${parseFloat(v.q1).toFixed(2)}</div>
                    </div>` : ''}
                    ${v.q3 != null ? `<div style="background: var(--xg-panel); padding: 8px 6px; border-radius: 6px; text-align: center; border: 1px solid var(--xg-border);">
                        <div style="font-size: 9px; color: var(--xg-muted); font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em;">Q3</div>
                        <div style="font-size: 15px; font-weight: 700; color: var(--xg-text-2);">$${parseFloat(v.q3).toFixed(2)}</div>
                    </div>` : ''}
                    ${v.latest != null ? `<div style="background: var(--xg-panel); padding: 8px 6px; border-radius: 6px; text-align: center; border: 1px solid var(--xg-border);">
                        <div style="font-size: 9px; color: var(--xg-muted); font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em;">Latest</div>
                        <div style="font-size: 15px; font-weight: 700; color: #2563eb;">$${parseFloat(v.latest).toFixed(2)}</div>
                    </div>` : ''}
                </div>

                ${(trend.slope_per_day != null || forecast || volatility) ? `
                <div class="ximilar-price-trend" style="background: var(--xg-subtle); border-radius: 6px; padding: 10px 12px; border: 1px solid var(--xg-border);">
                    <div style="font-size: 11px; font-weight: 600; color: var(--xg-muted); text-transform: uppercase; letter-spacing: 0.03em; margin-bottom: 8px;">Price Trend</div>
                    <div style="display: flex; flex-wrap: wrap; gap: 8px; align-items: center;">
                        ${trend.slope_per_day != null ? `<div style="display: flex; align-items: center; gap: 4px;">
                            <span style="font-size: 18px; font-weight: 700; color: ${trendColor};">${trendDir}</span>
                            <div>
                                <div style="font-size: 11px; font-weight: 700; color: ${trendColor};">${trend.slope_per_day > 0 ? '+' : ''}$${parseFloat(trend.slope_per_day).toFixed(4)}/day</div>
                                <div style="font-size: 9px; color: var(--xg-muted);">${trend.slope_per_day > 0 ? 'Trending up' : trend.slope_per_day < 0 ? 'Trending down' : 'Flat'}</div>
                            </div>
                        </div>` : ''}
                        ${forecast ? `<div style="background: var(--xg-panel); border: 1px solid var(--xg-border); border-radius: 6px; padding: 6px 10px; text-align: center;">
                            <div style="font-size: 9px; color: var(--xg-muted); font-weight: 600;">30-Day Forecast</div>
                            <div style="font-size: 14px; font-weight: 700; color: #7c3aed;">${forecast}</div>
                        </div>` : ''}
                        ${volatility ? `<div style="background: var(--xg-panel); border: 1px solid var(--xg-border); border-radius: 6px; padding: 6px 10px; text-align: center;">
                            <div style="font-size: 9px; color: var(--xg-muted); font-weight: 600;">Volatility</div>
                            <div style="font-size: 14px; font-weight: 700; color: #f59e0b;">${volatility}</div>
                        </div>` : ''}
                        ${trend.r_squared != null ? `<div style="background: var(--xg-panel); border: 1px solid var(--xg-border); border-radius: 6px; padding: 6px 10px; text-align: center;">
                            <div style="font-size: 9px; color: var(--xg-muted); font-weight: 600;">Trend R²</div>
                            <div style="font-size: 14px; font-weight: 700; color: var(--xg-text-2);">${(parseFloat(trend.r_squared)*100).toFixed(1)}%</div>
                        </div>` : ''}
                    </div>
                </div>` : ''}
            </div>`;
        }).join('');

        return `
        <div class="ximilar-result-card" style="
            background: var(--xg-panel); border-radius: 8px; padding: 20px;
            box-shadow: var(--xg-shadow); margin-bottom: 20px; border: 1px solid var(--xg-border);
        ">
            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 16px;">
                <div style="width: 44px; height: 44px; background: var(--xg-good-soft); border: 1.5px solid var(--xg-good-line); border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 22px; flex-shrink: 0; box-shadow: 0 2px 6px rgba(5,150,105,0.15);">📈</div>
                <div>
                    <h3 style="font-size: 19px; font-weight: 800; color: var(--xg-text); margin: 0; line-height: 1.25; letter-spacing: -0.01em;">Market Price Analysis</h3>
                    <p style="font-size: 12px; color: var(--xg-muted); margin: 2px 0 0 0; font-weight: 500;">Aggregated from eBay, COMC, Beckett &amp; more</p>
                </div>
            </div>
            ${panels}
        </div>`;
    }

    // Renders the marketplace listings table body (rows + table markup, no card wrapper).
    // Used inside renderLiveComps so marketplace listings appear in the same card as
    // the eBay active listings, rather than as a separate standalone card.
    function renderListingsTable(list) {
        if (!list || !Array.isArray(list) || list.length === 0) return '';

        const sourceColors = {
            'eBay': '#e53e3e', 'Mercari': '#4f46e5', 'COMC': '#0369a1',
            'Beckett': '#6d28d9', 'Rakuten': '#b91c1c', 'Rakuma': '#d97706'
        };

        const rows = list.map((item, idx) => {
            const srcColor = sourceColors[item.source] || '#475569';
            const gradeTag  = item.grade && item.grade_company
                ? `<span style="background: #7c3aed; color: white; font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 20px; white-space: nowrap;">${item.grade_company} ${item.grade}</span>`
                : `<span style="background: var(--xg-subtle); color: var(--xg-muted); font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 20px;">Raw</span>`;

            const versionTag = item.version && item.version !== 'Non-Foil'
                ? `<span style="background: var(--xg-warn-soft); color: var(--xg-warn-strong); font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 20px; margin-left: 4px;">${item.version}</span>`
                : '';

            const dateStr = item.date_of_sale
                ? `<span style="color: #10b981; font-size: 10px;">Sold ${item.date_of_sale}</span>`
                : item.date_of_creation
                    ? `<span style="color: var(--xg-muted); font-size: 10px;">Listed ${item.date_of_creation}</span>`
                    : '';

            return `
            <tr class="${idx >= 3 ? 'xim-extra-row' : ''}" style="border-bottom: 1px solid var(--xg-border);" onmouseover="this.style.background='var(--xg-subtle)'" onmouseout="this.style.background='var(--xg-panel)'">
                <td style="padding: 8px; vertical-align: top; border: 1px solid var(--xg-border);" data-label="Listing">
                    <div style="font-size: 13px; color: var(--xg-text); font-weight: 500; line-height: 1.4; word-break: break-word;">${item.name || 'Unknown listing'}</div>
                    <div style="margin-top: 4px;">${gradeTag}${versionTag}</div>
                </td>
                <td style="padding: 8px; white-space: nowrap; vertical-align: top; border: 1px solid var(--xg-border);" data-label="Price">
                    <span style="font-size: 14px; font-weight: 600; color: #059669;">
                        ${item.currency === 'USD' ? '$' : (item.currency || '')}${item.price != null ? parseFloat(item.price).toFixed(2) : 'N/A'}
                    </span>
                </td>
                <td style="padding: 8px; white-space: nowrap; vertical-align: top; border: 1px solid var(--xg-border);" data-label="Source">
                    <a href="${item.item_link}" target="_blank" rel="noopener" style="
                        display: inline-flex; align-items: center; gap: 5px;
                        background: ${srcColor}; color: white;
                        padding: 4px 10px; border-radius: 6px; text-decoration: none;
                        font-size: 12px; font-weight: 600;
                        transition: opacity 0.2s;
                    " onmouseover="this.style.opacity='0.85'" onmouseout="this.style.opacity='1'">
                        ${item.source || 'View'} ↗
                    </a>
                </td>
                <td style="padding: 8px; vertical-align: top; border: 1px solid var(--xg-border);" data-label="Date">${dateStr}</td>
            </tr>`;
        }).join('');

        const expandBtn = list.length > 3 ? `
            <button type="button" class="xim-expand-btn" data-target="mkt-tbody" style="width: 100%; margin-top: 8px; padding: 8px 12px; background: var(--xg-subtle); border: 1px solid var(--xg-border); border-radius: 8px; font-size: 12px; font-weight: 700; color: var(--xg-text); cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px;">
                <span>View all ${list.length} marketplace listings</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>
            </button>` : '';

        return `
            <div class="ximilar-listings-table-wrap">
                <table class="ximilar-listings-table" style="width: 100%; border-collapse: collapse; font-family: inherit; table-layout: fixed; font-size: 14px;">
                    <thead>
                        <tr style="background: var(--xg-subtle); border-bottom: 1px solid var(--xg-border);">
                            <th style="padding: 8px; text-align: left; font-size: 12px; font-weight: 600; color: var(--xg-text-2); border: 1px solid var(--xg-border);">Listing</th>
                            <th style="padding: 8px; text-align: left; font-size: 12px; font-weight: 600; color: var(--xg-text-2); border: 1px solid var(--xg-border); width: 80px;">Price</th>
                            <th style="padding: 8px; text-align: left; font-size: 12px; font-weight: 600; color: var(--xg-text-2); border: 1px solid var(--xg-border); width: 90px;">Source</th>
                            <th style="padding: 8px; text-align: left; font-size: 12px; font-weight: 600; color: var(--xg-text-2); border: 1px solid var(--xg-border); width: 100px;">Date</th>
                        </tr>
                    </thead>
                    <tbody class="xim-expandable-rows" id="xim-mkt-tbody">${rows}</tbody>
                </table>
            </div>
            ${expandBtn}`;
    }

    function renderListings(list) {
        if (!list || !Array.isArray(list) || list.length === 0) return '';
        return `
        <div class="ximilar-result-card" style="
            background: var(--xg-panel); border-radius: 8px; padding: 20px;
            box-shadow: var(--xg-shadow); margin-bottom: 20px; border: 1px solid var(--xg-border);
        ">
            <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 16px;">
                <div style="width: 36px; height: 36px; background: var(--xg-bad-soft); border: 1px solid var(--xg-bad-line); border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 18px;">🛒</div>
                <div>
                    <h3 style="font-size: 16px; font-weight: 700; color: var(--xg-text); margin: 0;">Live Marketplace Listings</h3>
                    <p style="font-size: 12px; color: var(--xg-muted); margin: 0;">${list.length} listing${list.length !== 1 ? 's' : ''} found across marketplaces</p>
                </div>
            </div>
            ${renderListingsTable(list)}
        </div>`;
    }

    // Pull identification + market data from any record (grade or sport-id shaped)
    function extractMarketData(record) {
        const card = record.card || {};
        const cardObj = (record._objects || []).find(o => o && o.name === 'Card') || {};
        const id = card.identification
            || record.identification
            || (cardObj._identification != null ? cardObj._identification.best_match : void 0)
            || {};
        let listings = card.listings
            || record.listings
            || (id.pricing != null ? id.pricing.list : void 0)
            || (function(_o){return _o == null ? void 0 : _o.list})((function(_o){return _o == null ? void 0 : _o.pricing})((cardObj._identification != null ? cardObj._identification.best_match : void 0)))
            || null;
        let priceStats = card.price_stats
            || record.price_stats
            || id.price_stats
            || (function(_o){return _o == null ? void 0 : _o.price_stats})((cardObj._identification != null ? cardObj._identification.best_match : void 0))
            || null;
        // Ximilar's marketplace listings and price stats were matched to the
        // PRE-correction identity — on a player-corrected record they're the
        // wrong player's market data. Suppress rather than mislead.
        if (id.player_corrected_from) { listings = null; priceStats = null; }
        return { id, listings, priceStats };
    }

    // Identification summary card used on the grade page
    function renderIdentificationCard(id, config, web) {
        if (!id || (!id.full_name && !id.name && !id.year && !id.set_name)) return '';
        const rows = [
            id.year ? `<div><strong>Year:</strong> ${id.year}</div>` : '',
            (id.set_name || id.set) ? `<div><strong>Set:</strong> ${id.set_name || id.set}</div>` : '',
            cleanCardNum(id.card_number || id.card_no) ? `<div><strong>Card #:</strong> ${cleanCardNum(id.card_number || id.card_no)}</div>` : '',
            id.company ? `<div><strong>Company:</strong> ${id.company}</div>` : '',
            id.team ? `<div><strong>Team:</strong> ${id.team}</div>` : '',
            id.card_type ? `<div><strong>Type:</strong> ${id.card_type}</div>` : ''
        ].filter(Boolean).join('');
        // Google Lens verification evidence — three states: verified
        // (corroborated by online listings), web-corrected (the Lens titles
        // named the card the internal judges missed), or unverified (couldn't
        // be confirmed — don't present it as a confident answer).
        let webHtml = '';
        if (web && typeof web === 'object') {
            const wv = !!web.verified;
            const wReason = String(web.reason || '');
            const wTitles = Array.isArray(web.titles) ? web.titles.length : 0;
            if (wv && wReason === 'winner_corroborated') {
                webHtml = `<div style="display:flex;align-items:center;gap:8px;margin-top:12px;padding:8px 12px;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;">
                    <span style="font-size:15px;">✅</span>
                    <span style="font-size:12px;color:#166534;font-weight:700;">Card verified against ${wTitles ? wTitles : ''} online listing${wTitles === 1 ? '' : 's'}</span>
                </div>`;
            } else if (wReason === 'web_detect_third_identity' && web.adopted) {
                webHtml = `<div style="display:flex;align-items:center;gap:8px;margin-top:12px;padding:8px 12px;background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;">
                    <span style="font-size:15px;">🔍</span>
                    <span style="font-size:12px;color:#1e40af;font-weight:600;line-height:1.45;">Identified via Google Lens listing titles — ${escapeHtml(String(web.adopted))}</span>
                </div>`;
            } else if (!wv) {
                webHtml = `<div style="display:flex;align-items:center;gap:8px;margin-top:12px;padding:8px 12px;background:#fffbeb;border:1px solid #fde68a;border-radius:8px;">
                    <span style="font-size:15px;">⚠️</span>
                    <span style="font-size:12px;color:#92400e;font-weight:600;line-height:1.45;">AI suggestion — this identification could not be verified against online listings. Compare the year, set and card number with the card before relying on it.</span>
                </div>`;
            }
        }
        return `
            <div class="xim-id-hero" style="background: var(--xg-panel); border-radius: 8px; padding: 18px 20px; margin-bottom: 16px; border: 1px solid var(--xg-border); border-left: 4px solid ${config.color}; box-shadow: var(--xg-shadow);">
                <div style="font-size: 11px; color: var(--xg-muted); font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 6px;">${id.status === 'partial' ? 'Partial identification — pricing withheld' : 'Identified Card'}</div>
                <div style="font-size: 18px; font-weight: 700; color: var(--xg-text); line-height: 1.3;">${id.full_name || id.name || 'Card identified'}</div>
                ${id.sub_set ? `<div style="font-size: 13px; color: var(--xg-muted); margin-top: 4px;">${id.sub_set}</div>` : ''}
                ${rows ? `<div class="xim-kv xim-id-kv" style="margin-top: 14px; color: var(--xg-text-2);">${rows}</div>` : ''}
                ${webHtml}
            </div>`;
    }

    // ════════════════════════════════════════════════════════════════════════
    // UNIFIED CARD IDENTIFICATION — merges Ximilar recognition + TCG databases +
    // sports APIs into a single gorgeous component. Replaces the separate
    // identification card, TCG lookup, and sports profile sections.
    // ════════════════════════════════════════════════════════════════════════
    // Resolve + verify the tcg_card_data enrichment card against the provider
    // identification. Shared by renderUnifiedCardId (card display) and the
    // standalone Market Data card.
    //
    // ── Enrichment authoritativeness gate ──
    // The tcg_card_data enrichment is a SECOND lookup (Pokemon TCG API,
    // Scryfall, tcgapi.dev, etc.) done on top of Ximilar's own
    // identification. It must NOT override Ximilar's correct identification
    // unless its set matches what Ximilar identified.
    //
    // We can't use the source field alone to detect fallbacks because
    // tcgapi.dev ("TCG API") is the PRIMARY source for some games (Weiss
    // Schwarz, Dragon Ball Super, etc.) but a FALLBACK for others (Pokemon,
    // YuGiOh, MTG) — and in both cases the stored card.source is just "TCG
    // API". The set-name match is the reliable signal: a wrong fuzzy match
    // returns a different set than Ximilar identified.
    //
    // If the set doesn't match (or Ximilar didn't identify a set and the
    // card name has no overlap with Ximilar's name), discard the enrichment
    // so Ximilar's identification is shown as-is.
    function _resolveTcgEnrichment(record) {
        const isSports = !isTCGCard(record);
        const ximilarRec = isSports ? (record.sport_id || record.tcg_id) : (record.tcg_id || record.sport_id);
        const ximilarId = (ximilarRec && ximilarRec.card && ximilarRec.card.identification) || record.identification || {};
        const tcgData = record.tcg_card_data || null;
        let tcgCard = (tcgData && tcgData.success && tcgData.card) ? (Array.isArray(tcgData.card) ? tcgData.card[0] : tcgData.card) : null;
        // Set-logo fallback: fetched by grade-v3.php when the full card lookup
        // failed or returned a wrong-set match. Used when tcgCard is discarded.
        const setLogoFallback = (tcgData && tcgData.set_logo_fallback) || null;
        if (tcgCard) {
            // ── Set-name matching with token overlap + abbreviation expansion ──
            // Different APIs use different names for the same set:
            //   "XY Promos"          ↔ "XY Black Star Promos"
            //   "Black & White Promos" ↔ "BW Black Star Promos"
            //   "Sun & Moon Promos"   ↔ "SM Black Star Promos"
            // Pure substring matching fails on these. Token overlap
            // (all tokens of the shorter name appear in the longer) is
            // far more robust, plus we expand common TCG abbreviations.
            const SET_ABBREVIATIONS = {
                'bw': ['black', 'white'],
                'sm': ['sun', 'moon'],
                'hgss': ['heartgold', 'soulsilver'],
                'dp': ['diamond', 'pearl'],
                'pl': ['platinum'],
                'swsh': ['sword', 'shield'],
                'sv': ['scarlet', 'violet'],
            };
            const tokenizeSet = (s) => {
                const tokens = s.toLowerCase().split(/[^a-z0-9]+/).filter(t => t.length >= 2);
                const expanded = [];
                for (const t of tokens) {
                    if (SET_ABBREVIATIONS[t]) {
                        expanded.push(...SET_ABBREVIATIONS[t]);
                    } else {
                        expanded.push(t);
                    }
                }
                return expanded;
            };
            const xSetTokens = tokenizeSet(ximilarId.set_name || ximilarId.set || '');
            const tSetTokens = tokenizeSet(tcgCard.set_name || '');
            const setMatches = !xSetTokens.length || !tSetTokens.length
                || xSetTokens.every(t => tSetTokens.includes(t))
                || tSetTokens.every(t => xSetTokens.includes(t))
                || (() => { // fallback: substring on normalized
                    const x = xSetTokens.join('');
                    const t = tSetTokens.join('');
                    return x.includes(t) || t.includes(x);
                })();
            // Name overlap check (only used when Ximilar has no set to compare)
            const xNameNorm = (ximilarId.name || ximilarId.full_name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
            const tNameNorm = (tcgCard.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
            const nameOverlaps = !xNameNorm || !tNameNorm || xNameNorm.includes(tNameNorm) || tNameNorm.includes(xNameNorm)
                || xNameNorm.slice(0, 4) === tNameNorm.slice(0, 4);
            // Card number match: if the TCG card's number matches Ximilar's
            // card number, it's almost certainly the right card even when the
            // set name differs (different APIs use different set names, e.g.
            // "XY Promos" vs "XY Black Star Promos"). Don't discard in that case.
            const xCardNum = (ximilarId.card_number || ximilarId.card_no || '').toString().toLowerCase().trim();
            const tCardNum = (tcgCard.number || '').toString().toLowerCase().trim();
            const cardNumMatches = xCardNum && tCardNum && xCardNum === tCardNum;
            if ((!setMatches && !cardNumMatches) || (!xSetTokens.length && !nameOverlaps && !cardNumMatches)) {
                tcgCard = null;  // discard non-authoritative enrichment
            }
        }
        return { tcgCard, setLogoFallback };
    }

    function renderUnifiedCardId(record, config) {
        if (!record) return '';
        const _wdInfo = (record.identification_resolution && record.identification_resolution.web_detect) || null;
        if ((record.identification_resolution != null ? record.identification_resolution.status : void 0) === 'partial') {
            return renderIdentificationCard(record.identification, config, _wdInfo);
        }

        // Format ISO date (YYYY-MM-DD) as "Mon DD, YYYY"
        function fmtDate(d) {
            if (!d) return null;
            const dt = new Date(d);
            if (isNaN(dt)) return d;
            return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        }

        // ── Gather data from all sources ──
        // For sports cards, prefer sport_id; for TCG cards, prefer tcg_id
        const isSports = !isTCGCard(record);
        const ximilarRec = isSports ? (record.sport_id || record.tcg_id) : (record.tcg_id || record.sport_id);
        const ximilarCard = (ximilarRec != null ? ximilarRec.card : void 0) || {};
        const ximilarId = ximilarCard.identification || record.identification || {};
        const ximilarTags = ximilarCard.tags || {};
        const ximilarListings = ximilarCard.listings || record.listings || [];
        const ximilarPriceStats = ximilarCard.price_stats || record.price_stats || [];
        const ximilarAlts = ximilarCard.alternatives || record.alternatives || [];
        const { tcgCard, setLogoFallback } = _resolveTcgEnrichment(record);
        const sportsProfile = record.sports_profile || null;
        const subcategory = extractSubcategory(record);

        // ── Slab detection (Scrydex Vision graded_details) ──
        // When the uploaded photo is an already-graded slab, Vision returns the
        // company/grade/cert from the label. The AI grade below evaluates the
        // card *through* the slab — surface that context prominently.
        const slabInfo = (ximilarCard.graded_details && ximilarCard.graded_details.company)
            ? ximilarCard.graded_details
            : (((function(_o){return _o == null ? void 0 : _o.graded_details})((record.tcg_id != null ? record.tcg_id.card : void 0)) && record.tcg_id.card.graded_details.company)
                ? record.tcg_id.card.graded_details : null);

        // ── Autograph detection (60% confidence threshold, sports cards only) ──
        // TCG cards almost never have autographs — Ximilar's autograph classifier
        // is unreliable for TCG, so we only show/autograph data for sports cards.
        const autographData = record.autograph || {};
        const hasAutograph = isSports && (autographData.has_autograph === true
            || (autographData.value === 'Yes' && ((autographData.confidence != null ? autographData.confidence : 0)) >= 0.60));
        const autographConfidence = (autographData.confidence != null ? autographData.confidence : 0);

        // Determine if we have enough data to show anything
        const hasXimilarId = !!(ximilarId.name || ximilarId.full_name || ximilarId.set_name);
        const hasTcg = !!(tcgCard && tcgCard.name);
        const hasSports = !!(sportsProfile && sportsProfile.success && (sportsProfile.player || sportsProfile.team));
        if (!hasXimilarId && !hasTcg && !hasSports) {
            // Try fallback identification
            const { id } = extractMarketData(record);
            if (!id || (!id.name && !id.full_name && !id.set_name)) return '';
            return renderIdentificationCard(id, config) + renderPriceDataSection(record, config);
        }

        // ── Merge & dedupe fields ──
        // Priority: TCG database > Ximilar (for TCG cards)
        //           Ximilar > sports API (for sports cards)
        const name = (tcgCard && tcgCard.name) || ximilarId.name || ximilarId.full_name || '';
        const fullName = ximilarId.full_name || '';
        const setName = (tcgCard && tcgCard.set_name) || ximilarId.set_name || ximilarId.set || '';
        const setCode = (tcgCard && tcgCard.set_id) || ximilarId.set_code || '';
        const series = (tcgCard && tcgCard.set_series) || ximilarId.series || '';
        const cardNumber = cleanCardNum((tcgCard && tcgCard.number) || ximilarId.card_number || ximilarId.card_no);
        const outOf = (tcgCard && tcgCard.set_total) || ximilarId.out_of || '';
        const rarity = (tcgCard && tcgCard.rarity) || ximilarId.rarity || '';
        const year = ximilarId.year || (tcgCard && tcgCard.year ? String(tcgCard.year).slice(0,4) : '');
        const company = ximilarId.company || '';
        const team = ximilarId.team || ((record.identification != null ? record.identification.team : void 0) || '');
        // Multi-entity detection (mirror of ResultRenderer): merge resolved
        // sports_profile arrays with the printed name/team parts so every
        // subject on a multi-player card is represented.
        let multiPlayers = ((sportsProfile != null ? sportsProfile.player : void 0) || []).filter(function(p){return p && typeof p === 'object';});
        let multiTeams = ((sportsProfile != null ? sportsProfile.team : void 0) || []).filter(function(t){return t && typeof t === 'object';});
        let isMultiEntity = false, isMultiPlayer = false, isMultiTeam = false;
        if (isSports) {
            let subjectNames = pgcSplitCardSubjects(String(name || ''), false);
            let subjectTeams = team ? pgcSplitCardSubjects(String(team), true) : [];
            // ximilarId can hold a generic card title while the primary
            // identification carries the real subject list — try both.
            const altName = (record.identification != null ? record.identification.name : void 0) || '';
            if (altName && altName !== name) {
                const alt = pgcSplitCardSubjects(String(altName), false);
                if (alt.length > subjectNames.length) subjectNames = alt;
            }
            const altTeam = (record.identification != null ? record.identification.team : void 0) || '';
            if (altTeam && altTeam !== team) {
                const alt = pgcSplitCardSubjects(String(altTeam), true);
                if (alt.length > subjectTeams.length) subjectTeams = alt;
            }
            const normK = function (s) { return String(s || '').toLowerCase().replace(/[^a-z0-9]/g, ''); };
            // Order the merged lists by printed card order: each subject claims
            // its matching profile; leftover profiles append after.
            const mergeOrdered = function (subjects, profiles) {
                if (!subjects.length) return profiles;
                const ordered = [];
                const used = {};
                subjects.forEach(function (s) {
                    const sk = normK(s);
                    let hit = null;
                    profiles.forEach(function (p, i) {
                        if (hit || used[i]) return;
                        const pk = normK(p.printed_name || p.name || '');
                        if (pk && (pk === sk || pk.indexOf(sk) !== -1 || sk.indexOf(pk) !== -1)) { hit = p; used[i] = true; }
                    });
                    ordered.push(hit || {name: s, printed_name: s, unresolved: true});
                });
                profiles.forEach(function (p, i) { if (!used[i]) ordered.push(p); });
                return ordered;
            };
            multiPlayers = mergeOrdered(subjectNames, multiPlayers);
            multiTeams = mergeOrdered(subjectTeams, multiTeams);
            isMultiEntity = multiPlayers.length > 1 || multiTeams.length > 1;
            // Per-side flags: a lone player on a multi-team card keeps the
            // full bio/stats/awards (record 11201 — one player, two teams).
            isMultiPlayer = multiPlayers.length > 1;
            isMultiTeam = multiTeams.length > 1;
        }
        const teamProfile = (function(_o){return _o == null ? void 0 : _o[0]})((sportsProfile != null ? sportsProfile.team : void 0)) || {};
        // Prefer the alias-resolved franchise name ("Dallas Mavericks") over
        // the mascot/city fragment read off the card ("Mavericks"). The raw
        // extraction stays visible as "Team (on card)" in Team Info. On
        // multi-team cards keep the composite printed string.
        const displayTeam = isMultiEntity ? team : ((teamProfile.name && teamProfile.name !== team) ? teamProfile.name : team);
        const subSet = ximilarId.sub_set || '';
        const cardType = ximilarId.card_type || '';
        const artist = (tcgCard && tcgCard.artist) || '';
        const side = capitalizeFirst((ximilarTags.side != null ? ximilarTags.side.name : void 0));
        const alphabet = capitalizeFirst((ximilarTags.alphabet != null ? ximilarTags.alphabet.name : void 0));
        const confidence = ximilarCard.confidence ? formatConfidence(ximilarCard.confidence) : '';
        const rawCategory = (ximilarTags.category != null ? ximilarTags.category.name : void 0) || (record.category != null ? record.category.value : void 0) || '';
        // Non-sport collectibles (Star Wars, Garbage Pail Kids...) are not
        // sport cards — the provider taxonomy mislabels them "Sport Card".
        const nonSportLabel = nonSportCollectibleLabel(record);
        // Non-sport collectibles (Disney, GPK, Star Wars…) have characters,
        // not players — the label below follows the same classification.
        const subjectTerm = nonSportLabel ? 'Character' : 'Player';
        const categoryLabel = nonSportLabel
            ? `Non-Sport · ${subcategory || 'Collectible'}`
            : isSports
                ? `Sport Card · ${subcategory || 'Sports'}`
                : `TCG · ${subcategory || 'Trading Card'}`;

        // Image: prefer TCG database image, then locally-saved crops, then uploaded image.
        // Avoid Ximilar S3 temp URLs (record._img_url, ximilarCard._img_url) — they expire
        // after ~24h and may show a broken/placeholder image on older reports.
        const cardImage = (tcgCard && (tcgCard.image || tcgCard.thumbnail))
            || (record._images != null ? record._images.clean : void 0)
            || (record._images != null ? record._images.cropped : void 0)
            || record._clean_url_card
            || record.original_image_url
            || ximilarCard._img_url
            || record._img_url
            || null;

        // Set logo: prefer TCG database, fall back to set-logo-only lookup
        const setLogo = (tcgCard && (tcgCard.set_logo || tcgCard.set_symbol))
            || (setLogoFallback && (setLogoFallback.set_logo || setLogoFallback.set_symbol))
            || null;

        // ── Build chips ──
        const chips = [];
        chips.push(`<span class="ucid-chip category" style="background:${isSports ? '#d1fae5;color:#065f46' : '#dbeafe;color:#1e40af'}">${escapeHtml(categoryLabel)}</span>`);
        if (rarity) chips.push(`<span class="ucid-chip rarity" style="background:#fef3c7;color:#92400e">${escapeHtml(rarity)}</span>`);
        if (cardType && cardType !== rarity) chips.push(`<span class="ucid-chip rarity" style="background:#fef3c7;color:#92400e">${escapeHtml(cardType)}</span>`);
        if (cardNumber) chips.push(`<span class="ucid-chip number" style="background:var(--xg-chip-bg);color:var(--xg-text-2)">#${escapeHtml(cardNumber)}${outOf ? ' / ' + escapeHtml(outOf) : ''}</span>`);
        // Serial-numbered chip — IQ serial first, special_features prose as
        // fallback for records graded before the field normalization existed.
        const serialVal = serialForRecord(record);
        if (serialVal) {
            chips.push(`<span class="ucid-chip serial" title="Serial-numbered card" style="background:#ede9fe;color:#5b21b6">${/^\d/.test(String(serialVal)) ? 'Serial #' + escapeHtml(String(serialVal)) : 'Serial numbered'}</span>`);
        }
        if (year) chips.push(`<span class="ucid-chip year" style="background:var(--xg-chip-bg);color:var(--xg-text-2)">${escapeHtml(String(year))}</span>`);
        if (artist) chips.push(`<span class="ucid-chip artist" style="background:#fce7f3;color:#9f1239">Art: ${escapeHtml(artist)}</span>`);
        if (side) chips.push(`<span class="ucid-chip side" style="background:#e0e7ff;color:#3730a3">${escapeHtml(side)}</span>`);
        if (alphabet) chips.push(`<span class="ucid-chip alphabet" style="background:#f3e8ff;color:#6b21a8">${escapeHtml(alphabet)}</span>`);
        if (confidence) chips.push(`<span class="ucid-chip confidence" style="background:#d1fae5;color:#065f46">${escapeHtml(confidence)} Confidence</span>`);
        // Language chip — from the catalog card (tcgCard) when present, else
        // the provider identification's language_code (Ximilar _ocr.lang via
        // lang:true, or Scrydex Vision). Covers Latin + CJK languages.
        const langCode = (ximilarId.language_code || (tcgCard && tcgCard.language_code) || '').toUpperCase();
        if (langCode && langCode !== 'EN' && langCode !== 'EN-US' && langCode !== 'EN_US') {
            const LANG_NAMES = {
                JA: 'Japanese', KO: 'Korean', 'ZH-CN': 'Chinese (Simplified)', 'ZH-TW': 'Chinese (Traditional)',
                FR: 'French', DE: 'German', ES: 'Spanish', IT: 'Italian', PT: 'Portuguese', NL: 'Dutch',
            };
            chips.push(`<span class="ucid-chip lang" style="background:#fef9c3;color:#854d0e">${escapeHtml(LANG_NAMES[langCode] || langCode)}</span>`);
        }
        // OCR corroboration chip — printed card text agrees/disagrees with
        // the identification (CardIdentifier::annotateOcrEvidence).
        const ocrEv = (record.identification_resolution != null ? record.identification_resolution.ocr_evidence : void 0) || null;
        if ((ocrEv != null ? ocrEv.card_number : void 0) === 'match') {
            chips.push(`<span class="ucid-chip ocr-ok" title="Text printed on the card corroborates this identification" style="background:#d1fae5;color:#065f46">✓ Card text verified</span>`);
        }
        // Back corroboration chip — the card-back record's own read agrees or
        // filled identity fields (CardIdentifier::annotateBackEvidence).
        const backEv = (record.identification_resolution != null ? record.identification_resolution.back_evidence : void 0) || null;
        if ((backEv != null ? backEv.promoted : void 0)) {
            chips.push(`<span class="ucid-chip back-id" title="The front couldn't be identified — the card back provided this identification" style="background:#dbeafe;color:#1e40af">ID'd from card back</span>`);
        } else if ((backEv != null ? backEv.back_id : void 0) === 'match' || (backEv != null ? backEv.back_ocr : void 0) === 'match') {
            chips.push(`<span class="ucid-chip back-ok" title="Text printed on the card back corroborates this identification" style="background:#d1fae5;color:#065f46">✓ Back confirms ID</span>`);
        }


        // ── Build info grids (separated by category) ──
        const seenLabels = new Set();
        function makeGrid() { return []; }
        function addInfo(grid, label, value) {
            if (!value || seenLabels.has(label)) return;
            seenLabels.add(label);
            grid.push({ label, value: String(value) });
        }
        // Variant C (2026-10-23): compact spec-sheet — merged minicell strip
        // with group ticks; overline section labels. Mirrors ResultRenderer.
        function renderInfoGrid(items, group) {
            if (!items.length) return '';
            return items.map(item => `<div class="ucid-cell" data-g="${group || 'card'}"><span class="k">${escapeHtml(item.label)}</span><span class="v">${escapeHtml(item.value)}</span></div>`).join('');
        }
        function renderSection(title, gridHtml, color) {
            if (!gridHtml) return '';
            return `<div class="xim-details-section" style="margin-bottom:10px;">
                <div class="ucid-sec" style="color:${color || 'var(--xg-muted)'}">${escapeHtml(title)}</div>
                ${gridHtml}
            </div>`;
        }

        // Card details grid (applies to all card types)
        const cardGrid = makeGrid();
        addInfo(cardGrid, 'Name', name);
        if (fullName && fullName !== name) addInfo(cardGrid, 'Full Name', fullName);
        addInfo(cardGrid, 'Set', setName);
        if (setCode) addInfo(cardGrid, 'Set Code', setCode);
        if (series) addInfo(cardGrid, 'Series', series);
        addInfo(cardGrid, 'Card #', cardNumber + (outOf ? ' / ' + outOf : ''));
        addInfo(cardGrid, 'Rarity', rarity);
        addInfo(cardGrid, 'Manufacturer', company);
        addInfo(cardGrid, 'Year', year);
        addInfo(cardGrid, 'Sub Set', subSet);
        addInfo(cardGrid, 'Card Type', cardType);
        // Provider-observed attributes ("1st Bowman, Serial Numbered 4/5,
        // Red Shimmer Parallel") — the only place these details live.
        addInfo(cardGrid, 'Features', ximilarId.special_features || (record.identification && record.identification.special_features) || '');
        if (hasAutograph) addInfo(cardGrid, 'Autograph', 'Signed');

        // TCG game details grid
        const tcgGrid = makeGrid();
        if (!isSports && tcgCard) {
            if (tcgCard.supertype) addInfo(tcgGrid, 'Supertype', tcgCard.supertype);
            if (tcgCard.subtypes) addInfo(tcgGrid, 'Subtypes', tcgCard.subtypes.join(' · '));
            if (tcgCard.type_line || tcgCard.type) addInfo(tcgGrid, 'Type', tcgCard.type_line || tcgCard.type);
            if (tcgCard.types && tcgCard.types.length) addInfo(tcgGrid, 'Types', tcgCard.types.join(' · '));
            if (tcgCard.attribute) addInfo(tcgGrid, 'Attribute', tcgCard.attribute);
            if (tcgCard.level != null) addInfo(tcgGrid, 'Level', tcgCard.level);
            if (tcgCard.atk != null) addInfo(tcgGrid, 'ATK', tcgCard.atk);
            if (tcgCard.def != null) addInfo(tcgGrid, 'DEF', tcgCard.def);
            if (tcgCard.hp) addInfo(tcgGrid, 'HP', tcgCard.hp);
            if (tcgCard.mana_cost) addInfo(tcgGrid, 'Mana Cost', tcgCard.mana_cost);
            if (tcgCard.cmc != null) addInfo(tcgGrid, 'CMC', tcgCard.cmc);
            if (tcgCard.cost != null) addInfo(tcgGrid, 'Cost', tcgCard.cost);
            if (tcgCard.strength) addInfo(tcgGrid, 'Strength', tcgCard.strength);
            if (tcgCard.willpower) addInfo(tcgGrid, 'Willpower', tcgCard.willpower);
            if (tcgCard.lore) addInfo(tcgGrid, 'Lore', tcgCard.lore);
            if (tcgCard.pitch) addInfo(tcgGrid, 'Pitch', tcgCard.pitch);
            if (tcgCard.defense) addInfo(tcgGrid, 'Defense', tcgCard.defense);
            if (tcgCard.power) addInfo(tcgGrid, 'Power', tcgCard.power);
            if (tcgCard.toughness) addInfo(tcgGrid, 'Toughness', tcgCard.toughness);
            if (tcgCard.class) addInfo(tcgGrid, 'Class', tcgCard.class);
            if (tcgCard.ink) addInfo(tcgGrid, 'Ink', tcgCard.ink);
            if (tcgCard.regulation_mark) addInfo(tcgGrid, 'Regulation Mark', tcgCard.regulation_mark);
            if (tcgCard.artist) addInfo(tcgGrid, 'Artist', tcgCard.artist);
            if (tcgCard.legalities) addInfo(tcgGrid, 'Legalities', Object.entries(tcgCard.legalities).map(([k,v]) => `${k}: ${v}`).join(' · '));
            if (tcgCard.finishes) addInfo(tcgGrid, 'Finishes', tcgCard.finishes.join(', '));
            if (tcgCard.evolves_from) addInfo(tcgGrid, 'Evolves From', tcgCard.evolves_from);
            if (tcgCard.evolves_to && tcgCard.evolves_to.length) addInfo(tcgGrid, 'Evolves To', tcgCard.evolves_to.join(', '));
            if (tcgCard.weaknesses && tcgCard.weaknesses.length) addInfo(tcgGrid, 'Weakness', tcgCard.weaknesses.map(w => w.type + (w.value ? ' ' + w.value : '')).join(', '));
            if (tcgCard.resistances && tcgCard.resistances.length) addInfo(tcgGrid, 'Resistance', tcgCard.resistances.map(r => r.type + (r.value ? ' ' + r.value : '')).join(', '));
            if (tcgCard.retreatCost != null) addInfo(tcgGrid, 'Retreat Cost', tcgCard.retreatCost);
            if (tcgCard.printings && tcgCard.printings.length > 1) addInfo(tcgGrid, 'Printings', tcgCard.printings.length + ' sets');
            if (tcgCard.national_pokedex && tcgCard.national_pokedex.length) addInfo(tcgGrid, 'Pokédex', '#' + tcgCard.national_pokedex.join(', #'));
        }

        // Sports: player profile grid
        const playerGrid = makeGrid();
        if (isSports && !isMultiPlayer) {
            const playerData = (function(_o){return _o == null ? void 0 : _o[0]})((sportsProfile != null ? sportsProfile.player : void 0)) || {};
            // Fallback: when the external sports API has no player record,
            // show the identified player name and team from the card itself.
            if (!playerData.name && name) {
                addInfo(playerGrid, subjectTerm, name);
                if (displayTeam) addInfo(playerGrid, 'Team', displayTeam);
            }
            if (playerData.position) addInfo(playerGrid, 'Position', playerData.position);
            if (playerData.height) addInfo(playerGrid, 'Height', playerData.height);
            if (playerData.weight) addInfo(playerGrid, 'Weight', playerData.weight + ' lb');
            if (playerData.number) addInfo(playerGrid, 'Number', '#' + playerData.number);
            if (playerData.batSide) addInfo(playerGrid, 'Bats', playerData.batSide);
            if (playerData.throwArm) addInfo(playerGrid, 'Throws', playerData.throwArm);
            if (playerData.birth) addInfo(playerGrid, 'Born', fmtDate(playerData.birth));
            if (playerData.age != null) addInfo(playerGrid, 'Age', String(playerData.age));
            if (playerData.debut) addInfo(playerGrid, 'Debut', fmtDate(playerData.debut));
            if (playerData.birthCity || playerData.birthState) addInfo(playerGrid, 'Birthplace', [playerData.birthCity, playerData.birthState].filter(Boolean).join(', '));
            if (playerData.nationality) addInfo(playerGrid, 'Nationality', playerData.nationality);
            if (playerData.active !== null && playerData.active !== undefined) addInfo(playerGrid, 'Status', playerData.active ? 'Active' : 'Retired');
            if (playerData.draftYear) addInfo(playerGrid, 'Draft', String(playerData.draftYear));
            if (playerData.deathDate) addInfo(playerGrid, 'Died', fmtDate(playerData.deathDate));
        }

        // Sports: team info — kept as a SEPARATE grid so player bio facts
        // (Born, Position, etc.) don't mix with team facts (Founded, League, etc.)
        const teamGrid = makeGrid();
        if (isSports && !isMultiTeam) {
            const teamData = (function(_o){return _o == null ? void 0 : _o[0]})((sportsProfile != null ? sportsProfile.team : void 0)) || {};
            if (team && teamData.name && teamData.name !== team) {
                addInfo(teamGrid, 'Team (on card)', team);
                const renamed = isTeamRename(teamData.original_name || team, teamData.name, teamData.alias_type);
                addInfo(teamGrid, renamed ? 'Team (current)' : 'Team (full name)', teamData.name + (renamed ? ' (renamed)' : ''));
            } else {
                addInfo(teamGrid, 'Team', teamData.name || team);
            }
            if (teamData.league) addInfo(teamGrid, 'League', teamData.league);
            if (teamData.stadium) addInfo(teamGrid, 'Stadium', teamData.stadium);
            if (teamData.founded) addInfo(teamGrid, 'Founded', teamData.founded);
            if (teamData.country) addInfo(teamGrid, 'Country', teamData.country);
        }

        // Detection details grid (from Ximilar)
        const detectionGrid = makeGrid();
        if (isSports) addInfo(detectionGrid, nonSportLabel ? 'Franchise' : 'Sport', subcategory);
        if (side) addInfo(detectionGrid, 'Side', side);
        if (alphabet) addInfo(detectionGrid, 'Alphabet', alphabet);
        if ((ximilarTags.foil_holo != null ? ximilarTags.foil_holo.name : void 0)) addInfo(detectionGrid, 'Foil/Holo', ximilarTags.foil_holo.name);
        if ((ximilarTags.graded != null ? ximilarTags.graded.name : void 0)) addInfo(detectionGrid, 'Graded', ximilarTags.graded.name);
        if (hasAutograph) addInfo(detectionGrid, 'Autograph', `Signed (${formatConfidence(autographConfidence)})`);
        if (confidence) addInfo(detectionGrid, 'Detection Confidence', confidence);

        // ── Text/attacks/abilities (TCG) — merged INTO the spec strip as
        // full-width rows below the cells (separate section box removed,
        // 2026-10-24 — mirrors ResultRenderer). ──
        let textRows = '';
        if (!isSports && tcgCard) {
            // Energy-cost pips: known tokens get colored dots, anything else a
            // text chip — cost info is never dropped. Mirrors ResultRenderer.
            const EL = { water: 'water', fire: 'fire', grass: 'grass', lightning: 'lightning', fighting: 'fighting', psychic: 'psychic', darkness: 'darkness', metal: 'metal', colorless: 'colorless', dragon: 'dragon', fairy: 'fairy', ener: 'other' };
            function costHtml(cost) {
                return (cost || []).map(function (t) {
                    const key = String(t).trim().toLowerCase();
                    return EL[key] ? '<i class="pip el-' + EL[key] + '" title="' + escapeHtml(t) + '"></i>' : '<span class="pip-txt">' + escapeHtml(t) + '</span>';
                }).join('');
            }
            // Attacks (Pokemon)
            if (tcgCard.attacks && tcgCard.attacks.length) {
                const attacksHtml = tcgCard.attacks.map(a => `
                    <div class="ucid-atk"><span class="pips">${a.cost && a.cost.length ? costHtml(a.cost) : ''}</span><div class="at">
                        <span class="n">${escapeHtml(a.name || '')}</span>
                        ${a.damage ? `<span class="dmg">${escapeHtml(a.damage)}</span>` : ''}
                        ${a.text ? `<span class="t">${escapeHtml(a.text)}</span>` : ''}
                    </div></div>`).join('');
                textRows += `<div style="grid-column:1/-1;background:var(--xg-panel);padding-bottom:4px"><div class="ucid-sec" style="margin:8px 10px 4px">Attacks</div>${attacksHtml}</div>`;
            }
            // Abilities (Pokemon)
            if (tcgCard.abilities && tcgCard.abilities.length) {
                const abHtml = tcgCard.abilities.map(a => `
                    <div class="ucid-atk"><span class="pips"></span><div class="at">
                        <span class="n">${escapeHtml(a.name || '')}</span>
                        ${a.text ? `<span class="t">${escapeHtml(a.text)}</span>` : ''}
                    </div></div>`).join('');
                textRows += `<div style="grid-column:1/-1;background:var(--xg-panel);padding-bottom:4px"><div class="ucid-sec" style="margin:8px 10px 4px">Abilities</div>${abHtml}</div>`;
            }
            // Card text (MTG, YuGiOh, etc.)
            const cardText = tcgCard.oracle_text || tcgCard.desc || tcgCard.text || '';
            if (cardText) textRows += `<div style="grid-column:1/-1;background:var(--xg-panel);padding:8px 12px;font-size:13px;color:var(--xg-text);line-height:1.5">${escapeHtml(cardText)}</div>`;
            // Flavor text
            if (tcgCard.flavor_text) textRows += `<div style="grid-column:1/-1;background:var(--xg-panel);padding:6px 12px;font-size:12px;color:var(--xg-muted);font-style:italic;line-height:1.5">${escapeHtml(tcgCard.flavor_text)}</div>`;
            // Rules (Pokemon)
            if (tcgCard.rules && tcgCard.rules.length) textRows += `<div style="grid-column:1/-1;background:var(--xg-panel);padding:6px 12px;font-size:12px;color:var(--xg-text-2);line-height:1.4">${tcgCard.rules.map(escapeHtml).join('<br>')}</div>`;
        }

        // ── Sports: player bio + career stats + awards + team info ──
        let sportsBioHtml = '';
        let teamInfoHtml = '';
        let playerBioHtml = '';
        // Multi-entity compact rendering (mirror of ResultRenderer): per-player
        // thumbnail + role meta + top-3 stat chips + one-line bio; logo strip
        // for teams. Full bio/awards/socials stay single-entity only.
        const spOk = isSports && sportsProfile && sportsProfile.success;
        const teamData = spOk ? ((sportsProfile.team != null ? sportsProfile.team[0] : void 0) || null) : null;
        if (isSports && isMultiPlayer) {
            const compactPlayer = function (p) {
                const img = p.thumbnail || p.render || null;
                const bits = [p.position, p.sport, (p.active === true ? 'Active' : (p.active === false ? 'Retired' : null))].filter(Boolean);
                const unresolved = p.unresolved || (!p.sport && !p.position && !p.description);
                let chips = '';
                const cs = p.career_stats || {};
                const top = [];
                const fields = [['era','ERA'],['wins','W'],['strikeOuts','SO'],['homeRuns','HR'],['rbi','RBI'],['avg','AVG'],['ops','OPS'],['pts','PPG'],['reb','RPG'],['ast','APG']];
                [cs.pitching, cs.hitting, cs.basketball].forEach(function (group) {
                    if (!group) return;
                    fields.forEach(function (f) {
                        if (group[f[0]] != null && group[f[0]] !== '' && top.length < 3) top.push([f[1], group[f[0]]]);
                    });
                });
                top.forEach(function (t) {
                    chips += `<span style="background:var(--xg-subtle);border:1px solid var(--xg-border);padding:2px 7px;border-radius:6px;font-size:10px;font-weight:700;color:var(--xg-text-2)">${escapeHtml(t[0])} ${escapeHtml(String(t[1]))}</span>`;
                });
                let desc = String(p.description || '').trim();
                if (desc.length > 110) desc = desc.slice(0, 110).trim() + '…';
                return `<div style="padding:10px 12px;background:var(--xg-subtle);border:1px solid var(--xg-border);border-radius:10px">
                    <div style="display:flex;align-items:center;gap:10px">
                        ${img ? `<img src="${escapeHtml(proxyImg(img))}" alt="${escapeHtml(p.name||'')}" style="width:44px;height:44px;border-radius:8px;object-fit:cover;border:1px solid var(--xg-border);flex:none" onerror="this.style.display='none'">` : ''}
                        <div style="min-width:0">
                            <div style="font-size:13px;font-weight:700;color:var(--xg-text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${escapeHtml(p.name || '')}</div>
                            <div style="font-size:11px;color:var(--xg-muted);margin-top:1px">${escapeHtml(bits.join(' · '))}${unresolved ? '<span style="font-style:italic"> · no profile found</span>' : ''}</div>
                        </div>
                    </div>
                    ${chips ? `<div style="display:flex;gap:4px;flex-wrap:wrap;margin-top:8px">${chips}</div>` : ''}
                    ${desc ? `<div style="font-size:11px;color:var(--xg-text-2);line-height:1.45;margin-top:8px">${escapeHtml(desc)}</div>` : ''}
                </div>`;
            };
            const pcells = multiPlayers.map(compactPlayer).join('');
            if (pcells) playerBioHtml = `<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:8px">${pcells}</div>`;
        } else if (spOk) {
            const playerData = (sportsProfile.player != null ? sportsProfile.player[0] : void 0) || null;
            const cardTeam = ximilarId.team || team || '';

            // Fallback: show identified player name when external API has no record
            if (!playerData && name) {
                playerBioHtml = `<div style="display:grid;grid-template-columns:auto 1fr;gap:16px;margin-bottom:16px;padding:14px;background:var(--xg-subtle);border-radius:10px;border:1px solid var(--xg-border)">
                    <div style="width:64px;height:64px;border-radius:10px;background:var(--xg-panel);border:1px solid var(--xg-border);display:flex;align-items:center;justify-content:center;font-size:28px;">&#127942;</div>
                    <div>
                        <div style="font-size:14px;font-weight:700;color:var(--xg-text)">${escapeHtml(name)}</div>
                        <div style="font-size:12px;color:var(--xg-muted);margin-bottom:6px">${escapeHtml([team, 'Identified from card'].filter(Boolean).join(' · '))}</div>
                        <div style="font-size:12px;color:var(--xg-text-2);line-height:1.5">${escapeHtml(subjectTerm)} identified from card recognition.</div>
                    </div>
                </div>`;
            }

            // Player bio (rendered inside the Player Profile section)
            if (playerData) {
                const pImg = playerData.thumbnail || playerData.render || null;
                playerBioHtml = `<div style="display:grid;grid-template-columns:auto 1fr;gap:16px;margin-bottom:16px">
                    ${pImg ? `<img src="${escapeHtml(proxyImg(pImg))}" alt="${escapeHtml(playerData.name||'')}" style="width:64px;height:64px;border-radius:10px;object-fit:cover;border:1px solid var(--xg-border)" onerror="this.style.display='none'">` : ''}
                    <div>
                        <div style="font-size:14px;font-weight:700;color:var(--xg-text)">${escapeHtml(playerData.name || '')}</div>
                        <div style="font-size:12px;color:var(--xg-muted);margin-bottom:6px">${escapeHtml([playerData.position, playerData.sport, (playerData.active === true ? 'Active' : (playerData.active === false ? 'Retired' : null))].filter(Boolean).join(' · '))}</div>
                        ${playerData.description ? `<div style="font-size:12px;color:var(--xg-text-2);line-height:1.5">${escapeHtml(playerData.description)}</div>` : ''}
                    </div>
                </div>`;
            }

            // Career stats
            if (playerData && playerData.career_stats) {
                const cs = playerData.career_stats;
                const hitting = cs.hitting || {};
                const pitching = cs.pitching || {};
                const hittingFields = hitting && (hitting.homeRuns != null || hitting.avg != null) ? [
                    ['HR', hitting.homeRuns], ['RBI', hitting.rbi], ['H', hitting.hits],
                    ['AVG', hitting.avg], ['OBP', hitting.obp], ['SLG', hitting.slg],
                    ['OPS', hitting.ops], ['GP', hitting.gamesPlayed]
                ] : null;
                const pitchingFields = pitching && (pitching.era != null || pitching.wins != null) ? [
                    ['ERA', pitching.era], ['W', pitching.wins], ['L', pitching.losses],
                    ['SV', pitching.saves], ['SO', pitching.strikeOuts], ['WHIP', pitching.whip]
                ] : null;

                if (pitchingFields) {
                    const statsWithValues = pitchingFields.filter(([,v]) => v != null);
                    if (statsWithValues.length) {
                        sportsBioHtml += `<div style="padding:8px 0;border-top:1px solid var(--xg-border)">
                            <div style="font-size:10px;font-weight:700;color:var(--xg-muted);text-transform:uppercase;letter-spacing:0.06em;margin-bottom:6px">Career Pitching</div>
                            <div style="display:flex;flex-wrap:wrap;gap:4px">
                                ${statsWithValues.map(([k,v]) => `<div style="text-align:center;padding:4px 8px;background:var(--xg-subtle);border-radius:6px;min-width:48px"><div style="font-size:9px;color:var(--xg-muted);font-weight:600;text-transform:uppercase">${escapeHtml(k)}</div><div style="font-size:15px;font-weight:800;color:var(--xg-text)">${escapeHtml(String(v))}</div></div>`).join('')}
                            </div>
                        </div>`;
                    }
                }
                if (hittingFields) {
                    const statsWithValues = hittingFields.filter(([,v]) => v != null);
                    if (statsWithValues.length) {
                        sportsBioHtml += `<div style="padding:8px 0;border-top:1px solid var(--xg-border)">
                            <div style="font-size:10px;font-weight:700;color:var(--xg-muted);text-transform:uppercase;letter-spacing:0.06em;margin-bottom:6px">Career Hitting</div>
                            <div style="display:flex;flex-wrap:wrap;gap:4px">
                                ${statsWithValues.map(([k,v]) => `<div style="text-align:center;padding:4px 8px;background:var(--xg-subtle);border-radius:6px;min-width:48px"><div style="font-size:9px;color:var(--xg-muted);font-weight:600;text-transform:uppercase">${escapeHtml(k)}</div><div style="font-size:15px;font-weight:800;color:var(--xg-text)">${escapeHtml(String(v))}</div></div>`).join('')}
                            </div>
                        </div>`;
                    }
                }
                // NBA career/season averages (ESPN / BALLDONTLIE)
                const bb = cs.basketball || null;
                const bbFields = bb && (bb.pts != null || bb.reb != null) ? [
                    ['PPG', bb.pts], ['RPG', bb.reb], ['APG', bb.ast],
                    ['SPG', bb.stl], ['BPG', bb.blk], ['FG%', bb.fg_pct],
                    ['3P%', bb.fg3_pct], ['FT%', bb.ft_pct],
                    ['GP', bb.games_played], ['MIN', bb.minutes_per_game]
                ] : null;
                if (bbFields) {
                    const statsWithValues = bbFields.filter(([,v]) => v != null);
                    if (statsWithValues.length) {
                        sportsBioHtml += `<div style="padding:8px 0;border-top:1px solid var(--xg-border)">
                            <div style="font-size:10px;font-weight:700;color:var(--xg-muted);text-transform:uppercase;letter-spacing:0.06em;margin-bottom:6px">${escapeHtml(cs.basketball_label || 'NBA Season Averages')}</div>
                            <div style="display:flex;flex-wrap:wrap;gap:4px">
                                ${statsWithValues.map(([k,v]) => `<div style="text-align:center;padding:4px 8px;background:var(--xg-subtle);border-radius:6px;min-width:48px"><div style="font-size:9px;color:var(--xg-muted);font-weight:600;text-transform:uppercase">${escapeHtml(k)}</div><div style="font-size:15px;font-weight:800;color:var(--xg-text)">${escapeHtml(String(v))}</div></div>`).join('')}
                            </div>
                        </div>`;
                    }
                }
            }

            // Awards
            if (playerData && playerData.awards && playerData.awards.length) {
                const awards = playerData.awards.slice(0, 8);
                const more = playerData.awards.length - 8;
                sportsBioHtml += `<div style="padding:8px 0;border-top:1px solid var(--xg-border)">
                    <div style="font-size:10px;font-weight:700;color:var(--xg-muted);text-transform:uppercase;letter-spacing:0.06em;margin-bottom:6px">Awards & Honors (${playerData.awards.length})</div>
                    <div style="display:flex;flex-wrap:wrap;gap:4px">
                        ${awards.map(a => `<span style="background:#fef3c7;color:#92400e;padding:3px 8px;border-radius:6px;font-size:10px;font-weight:600">${escapeHtml(typeof a === 'string' ? a : (a.name || a.award || JSON.stringify(a)))}</span>`).join('')}
                        ${more > 0 ? `<span style="background:var(--xg-chip-bg);color:var(--xg-text-2);padding:3px 8px;border-radius:6px;font-size:10px;font-weight:600">+${more} more</span>` : ''}
                    </div>
                </div>`;
            }

        } else if (isSports && name) {
            // No sports_profile data at all — still show the identified player
            playerBioHtml = `<div style="display:grid;grid-template-columns:auto 1fr;gap:16px;margin-bottom:16px;padding:14px;background:var(--xg-subtle);border-radius:10px;border:1px solid var(--xg-border)">
                <div style="width:64px;height:64px;border-radius:10px;background:var(--xg-panel);border:1px solid var(--xg-border);display:flex;align-items:center;justify-content:center;font-size:28px;">&#127942;</div>
                <div>
                    <div style="font-size:14px;font-weight:700;color:var(--xg-text)">${escapeHtml(name)}</div>
                    <div style="font-size:12px;color:var(--xg-muted);margin-bottom:6px">${escapeHtml([team, 'Identified from card'].filter(Boolean).join(' · '))}</div>
                    <div style="font-size:12px;color:var(--xg-text-2);line-height:1.5">${escapeHtml(subjectTerm)} identified from card recognition.</div>
                </div>
            </div>`;
        }

        if (isSports && isMultiTeam) {
            const tcells = multiTeams.map(function (t) {
                const tLogo = t.logo || t.badge || null;
                const tmeta = [t.league, t.sport, t.stadium].filter(Boolean).join(' · ');
                return `<div style="display:flex;align-items:center;gap:10px;padding:8px 12px;background:var(--xg-subtle);border:1px solid var(--xg-border);border-radius:10px;min-width:0">
                    ${tLogo ? `<img src="${escapeHtml(proxyImg(tLogo))}" alt="${escapeHtml(t.name||'')}" style="width:28px;height:28px;object-fit:contain;flex:none" onerror="this.style.display='none'">` : ''}
                    <div style="min-width:0">
                        <div style="font-size:12px;font-weight:700;color:var(--xg-text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${escapeHtml(t.name || '')}</div>
                        ${tmeta ? `<div style="font-size:11px;color:var(--xg-muted)">${escapeHtml(tmeta)}</div>` : ''}
                    </div>
                </div>`;
            }).join('');
            if (tcells) teamInfoHtml = `<div style="display:flex;flex-wrap:wrap;gap:8px">${tcells}</div>`;
        } else if (spOk && teamData && teamData.name) {
            // Team info (rendered inside the Team Info section, not the bio)
            {
                const tLogo = teamData.logo || teamData.badge || null;
                const socials = [];
                if (teamData.website) socials.push(`<a href="${escapeHtml(ensureUrl(teamData.website))}" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:4px;padding:4px 10px;background:var(--xg-panel);border:1px solid var(--xg-border);border-radius:100px;color:var(--xg-text-2);text-decoration:none;font-size:11px;font-weight:600">🌐 Website</a>`);
                if (teamData.facebook) socials.push(`<a href="${escapeHtml(ensureUrl(teamData.facebook))}" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:4px;padding:4px 10px;background:#1877f2;border-radius:100px;color:white;text-decoration:none;font-size:11px;font-weight:600">f Facebook</a>`);
                if (teamData.twitter) socials.push(`<a href="${escapeHtml(ensureUrl(teamData.twitter))}" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:4px;padding:4px 10px;background:#000;border-radius:100px;color:white;text-decoration:none;font-size:11px;font-weight:600">𝕏 Twitter</a>`);
                if (teamData.instagram) socials.push(`<a href="${escapeHtml(ensureUrl(teamData.instagram))}" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:4px;padding:4px 10px;background:linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888);border-radius:100px;color:white;text-decoration:none;font-size:11px;font-weight:600">📷 Instagram</a>`);
                if (teamData.youtube) socials.push(`<a href="${escapeHtml(ensureUrl(teamData.youtube))}" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:4px;padding:4px 10px;background:#ff0000;border-radius:100px;color:white;text-decoration:none;font-size:11px;font-weight:600">▶ YouTube</a>`);
                teamInfoHtml = `<div style="display:grid;grid-template-columns:auto 1fr;gap:16px;margin-bottom:16px;padding:14px;background:var(--xg-subtle);border-radius:10px;border:1px solid var(--xg-border)">
                    ${tLogo ? `<img src="${escapeHtml(proxyImg(tLogo))}" alt="${escapeHtml(teamData.name)}" style="width:64px;height:64px;object-fit:contain;border-radius:10px;background:var(--xg-panel);border:1px solid var(--xg-border);padding:6px" onerror="this.style.display='none'">` : ''}
                    <div>
                        <div style="font-size:14px;font-weight:700;color:var(--xg-text)">${escapeHtml(teamData.name)}</div>
                        ${isTeamRename(teamData.original_name, teamData.name, teamData.alias_type) ? `<div style="font-size:12px;color:var(--xg-muted);margin-bottom:4px;font-style:italic">Formerly: ${escapeHtml(teamData.original_name)}</div>` : ''}
                        <div style="font-size:12px;color:var(--xg-text-2)">${escapeHtml([teamData.stadium, teamData.league, teamData.sport].filter(Boolean).join(' · '))}</div>
                        ${socials.length ? `<div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:8px">${socials.join('')}</div>` : ''}
                    </div>
                </div>`;
            }
        }

        // ── Price stats ──
        let priceStatsHtml = '';
        const priceStats = (ximilarPriceStats && ximilarPriceStats.length) ? ximilarPriceStats : [];
        if (priceStats.length) {
            const statCards = priceStats.slice(0, 3).map(ps => {
                const v = ps.value || {};
                const label = ps.stats_type ? capitalizeFirst(ps.stats_type) : 'Overall';
                const median = v.median != null ? '$' + Number(v.median).toFixed(2) : 'N/A';
                const range = (v.min != null && v.max != null) ? `$${Number(v.min).toFixed(2)} – $${Number(v.max).toFixed(2)}` : '';
                const color = ps.stats_type === 'graded' ? '#059669' : (ps.stats_type === 'ungraded' ? 'var(--xg-text-2)' : 'var(--xg-text)');
                return `<div style="padding:12px;background:var(--xg-subtle);border-radius:10px;border:1px solid var(--xg-border);text-align:center">
                    <div style="font-size:10px;color:var(--xg-muted);font-weight:600;text-transform:uppercase;margin-bottom:4px">${escapeHtml(label)}</div>
                    <div style="font-size:20px;font-weight:800;color:${color}">${escapeHtml(median)}</div>
                    ${range ? `<div style="font-size:10px;color:var(--xg-muted);margin-top:4px">${escapeHtml(range)}</div>` : ''}
                </div>`;
            }).join('');
            priceStatsHtml = `<div style="margin-bottom:16px">
                <div style="font-size:10px;font-weight:700;color:var(--xg-muted);text-transform:uppercase;letter-spacing:0.06em;margin-bottom:8px">Market Price Statistics</div>
                <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:10px">${statCards}</div>
            </div>`;
        }

        // ── Links ──
        const links = [];
        // TCG marketplace links (TCGPlayer/Cardmarket/Scryfall/YGOPRODeck/eBay)
        // render in the "Find this card" footer — renderMarketSearchLinks().
        // Ximilar links (eBay, COMC, Beckett)
        if (ximilarId.links) {
            const linkMap = { 'ebay.com': ['eBay ↗', '#e53238'], 'comc.com': ['COMC ↗', '#059669'], 'beckett.com': ['Beckett ↗', '#4b5563'] };
            for (const [key, url] of Object.entries(ximilarId.links)) {
                const [label, bg] = linkMap[key] || [key + ' ↗', '#4b5563'];
                links.push(`<a class="ucid-link" style="background:${bg};color:white" href="${escapeHtml(url)}" target="_blank" rel="noopener">${escapeHtml(label)}</a>`);
            }
        }
        // Sports links (MLB.com, Wikipedia)
        if (isSports && (function(_o){return _o == null ? void 0 : _o[0]})((sportsProfile != null ? sportsProfile.player : void 0))) {
            const p = sportsProfile.player[0];
            if (p.mlb_url) links.push(`<a class="ucid-link" style="background:#059669;color:white" href="${escapeHtml(p.mlb_url)}" target="_blank" rel="noopener">MLB.com ↗</a>`);
            if (p.wiki_url) links.push(`<a class="ucid-link" style="background:#4b5563;color:white" href="${escapeHtml(p.wiki_url)}" target="_blank" rel="noopener">Wikipedia ↗</a>`);
        }
        const linksHtml = links.length ? `<div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:16px;padding-top:16px;border-top:1px solid var(--xg-border)">${links.join('')}</div>` : '';

        // ── Source tags ──
        const sources = ['Ximilar Recognition'];
        if (tcgCard) sources.push('TCG Database');
        if (isSports && sportsProfile) sources.push('ESPN API', 'MLB Stats API', 'TheSportsDB');
        const sourcesHtml = `<div style="display:flex;align-items:center;gap:6px;margin-top:12px;font-size:10px;color:var(--xg-muted)">
            <span>Sources:</span>
            ${sources.map(s => `<span style="background:var(--xg-chip-bg);padding:2px 8px;border-radius:100px;font-weight:600">${escapeHtml(s)}</span>`).join('')}
        </div>`;

        // ── Banner color ──
        const bannerStyle = isSports
            ? 'background:linear-gradient(90deg,#d97706,#dc2626,#1e40af)'
            : `background:linear-gradient(90deg,#667eea,#764ba2)`;

        // ── Variant C merge: Card + Game + Player/Team grids → one strip ──
        const cardCells = renderInfoGrid(cardGrid, 'card');
        const gameCells = !isSports ? renderInfoGrid(tcgGrid, 'game') : '';
        const multiGrid = makeGrid();
        if (isSports && isMultiEntity && displayTeam) addInfo(multiGrid, 'Team', displayTeam);
        const subjCells = isSports ? renderInfoGrid([...playerGrid, ...teamGrid, ...multiGrid], 'subj') : '';
        const stripCells = cardCells + gameCells + subjCells;
        let stripHtml = '';
        if (stripCells || textRows) {
            let leg = '<span><i style="background:#2563eb"></i>Card</span>';
            if (gameCells) leg += '<span><i style="background:#7c3aed"></i>Game</span>';
            if (subjCells) leg += '<span><i style="background:#059669"></i>Player &amp; team</span>';
            stripHtml = `${stripCells ? `<div class="ucid-legend">${leg}</div>` : ''}<div class="ucid-strip">${stripCells}${textRows}</div>`;
        }
        const playerBioOnly = playerBioHtml + sportsBioHtml;
        const playerSection = isSports && playerBioOnly ? renderSection(isMultiPlayer ? 'Players on this card (' + multiPlayers.length + ')' : (subjectTerm + ' Profile'), playerBioOnly, '#059669') : '';
        const teamSection = isSports && teamInfoHtml ? renderSection(isMultiTeam ? 'Teams (' + multiTeams.length + ')' : 'Team Info', teamInfoHtml, '#7c3aed') : '';
        // Non-sport / TCG character bio — mirrors ResultRenderer::$characterSection.
        const charP = (record && record.character_profile && typeof record.character_profile === 'object') ? record.character_profile : null;
        let characterSection = '';
        if (!hasSports && charP && charP.success && String(charP.extract || '').trim() !== '') {
            const cpImg = String(charP.thumbnail || '');
            const cpSrc = charP.source === 'pokeapi' ? 'PokéAPI · Bulbapedia' : 'Wikipedia';
            characterSection = renderSection('About ' + String(charP.title || charP.name || 'this character'),
                '<div class="pgc-char-bio" style="display:flex;flex-wrap:nowrap !important;align-items:flex-start;gap:14px">'
                + (cpImg ? `<img src="${escapeHtml(cpImg)}" alt="${escapeHtml(String(charP.title || ''))}" loading="lazy" style="flex:0 0 64px;width:64px;height:64px;border-radius:10px;object-fit:contain;background:var(--xg-subtle);border:1px solid var(--xg-border)" onerror="this.style.display='none'">` : '')
                + '<div style="min-width:0;flex:1 1 auto">'
                + (charP.description ? `<div style="font-size:12px;color:var(--xg-muted);margin-bottom:6px">${escapeHtml(String(charP.description))}</div>` : '')
                + `<div style="font-size:13px;color:var(--xg-text-2);line-height:1.6">${escapeHtml(String(charP.extract))}</div>`
                + (charP.url ? `<a href="${escapeHtml(String(charP.url))}" target="_blank" rel="noopener nofollow" style="display:inline-block;margin-top:8px;font-size:12px;font-weight:600;color:var(--xg-accent, #2563eb);text-decoration:none">Source: ${escapeHtml(cpSrc)} &#8599;</a>` : '')
                + '</div></div>', '#7c3aed');
        }

        // Rail visual: the PGC slab when we can render one, else the card crop.
        // No download link here — the zoom modal carries it.
        const slabFull = record._pgc_slab_url || null;
        const slabSep = slabFull ? (slabFull.indexOf('?') === -1 ? '?' : '&') : '';
        const cardImgHtmlC = slabFull
            ? `<div class="ximilar-zoomable ucid-slab-frame" data-zoom-src="${escapeHtml(slabFull)}" data-zoom-label="${escapeHtml((name ? name + ' — ' : '') + 'PGC Slab')}" data-slab="1" title="Click to enlarge — drag to tilt">`
                + `<img class="ucid-slabimg" src="${escapeHtml(slabFull + slabSep + 'w=400')}" alt="${escapeHtml(buildAltText({ name, setName, setCode, cardNumber, rarity, gradeLabel: record.final_grade_label, grade: record.grades && record.grades.final }))}" draggable="false" loading="lazy"></div>`
            : (cardImage ? `<img class="ucid-cardimg" src="${escapeHtml(pgcThumb(proxyImg(cardImage), 320))}" alt="${escapeHtml(buildAltText({ name, setName, setCode, cardNumber, rarity, gradeLabel: record.final_grade_label, grade: record.grades && record.grades.final }))}" onerror="this.style.display='none'">` : '');
        // Set logo/wordmark lives in the header beside the card (2026-10-23),
        // not stacked under the card image in the rail. Size is inline, not
        // left to CSS: this img sits OUTSIDE .ucid-rail, so the old
        // `.ucid-rail img.ucid-setlogo` rule never matched and wide wordmarks
        // (Scarlet & Violet) rendered at natural size across the whole panel.
        const setBlockFull = (`
            ${setLogo ? `<img class="ucid-setlogo" src="${escapeHtml(proxyImg(setLogo))}" alt="${escapeHtml(setName ? setName + ' set logo' : 'Set logo')}" style="max-height:44px;max-width:130px;width:auto;height:auto;object-fit:contain" onerror="this.style.display='none'">` : ''}
            ${!setLogo && setName ? `<span style="font-size:13px;font-weight:600;color:var(--xg-text-2)">${escapeHtml(setName)}</span>` : ''}
            ${!setLogo && setCode ? `<span style="font-size:11px;color:var(--xg-muted);background:var(--xg-chip-bg);padding:2px 8px;border-radius:100px;font-weight:600;letter-spacing:0.03em">${escapeHtml(setCode)}${series ? ' · ' + escapeHtml(series) : ''}</span>` : ''}`).trim();

        // ── Assemble the component ──
        return `
        <div class="ximilar-result-card xim-unified-card" style="background:var(--xg-panel);border-radius:16px;overflow:hidden;box-shadow:var(--xg-shadow);border:1px solid var(--xg-border);margin-bottom:20px">
            <div style="height:6px;${bannerStyle}"></div>
            <div class="xim-unified-inner" style="padding:18px 22px">
                <div class="xim-unified-header ucid-main2${cardImgHtmlC !== '' ? ' has-img' : ''}${slabFull ? ' has-slab' : ''}" style="margin-bottom:14px">
                    ${cardImgHtmlC !== '' ? `<div class="ucid-rail">${cardImgHtmlC}</div>` : ''}
                    <div style="min-width:0">
                        <div class="ucid-headrow">
                            <div style="min-width:0">
                                <div><span class="ucid-title">${escapeHtml(name)}</span>${rarity && rarity !== name ? ` <span class="ucid-rar">— ${escapeHtml(rarity)}</span>` : ''}</div>
                                ${fullName && fullName !== name ? `<div class="ucid-full">${escapeHtml(fullName)}</div>` : ''}
                                ${setBlockFull ? `<div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">${setBlockFull}</div>` : ''}
                                <!-- Chips sit under the set logo in the space beside the card -->
                                ${chips.length ? `<div class="ucid-chips ucid-chips-col">${chips.join('')}</div>` : ''}
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Slab banner: card is already professionally graded -->
                ${slabInfo ? `<div style="margin-bottom:16px;padding:12px 16px;background:linear-gradient(135deg,#eff6ff,#dbeafe);border:1px solid #93c5fd;border-radius:10px;display:flex;align-items:center;gap:12px;flex-wrap:wrap">
                    <span style="font-size:20px">🛡️</span>
                    <div style="flex:1;min-width:200px">
                        <div style="font-size:13px;font-weight:800;color:#1e40af">Already slabbed — ${escapeHtml(slabInfo.company)} ${escapeHtml([slabInfo.grade_label, slabInfo.grade_number].filter(Boolean).join(' '))}</div>
                        <div style="font-size:12px;color:#1e3a8a;margin-top:2px">
                            ${slabInfo.cert ? `Cert <strong>#${escapeHtml(slabInfo.cert)}</strong>${slabInfo.year ? ' · ' + escapeHtml(slabInfo.year) : ''} — ` : ''}the AI grade below assesses the card as photographed through the slab.
                        </div>
                    </div>
                    <a href="/tools/slab-auditor" style="font-size:12px;font-weight:700;color:#1d4ed8;text-decoration:none;white-space:nowrap">Audit this slab →</a>
                </div>` : ''}
                <!-- OCR disclosure: the raw text vision read off the card -->
                ${ximilarId.ocr_text ? `
                <details class="ucid-ocr" style="margin-bottom:14px;border:1px solid var(--xg-border);border-radius:10px;background:var(--xg-subtle)">
                    <summary style="cursor:pointer;padding:10px 14px;font-size:12px;font-weight:700;color:var(--xg-text-2);display:flex;align-items:center;gap:6px">
                        <svg class="ucid-ocr-chev" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="flex:none;transition:transform .15s"><polyline points="6 9 12 15 18 9"/></svg>
                        AI read this text from your card
                    </summary>
                    <div style="padding:0 14px 12px;font-size:12.5px;color:var(--xg-text-2);line-height:1.6;white-space:pre-wrap;word-break:break-word">${escapeHtml(String(ximilarId.ocr_text).slice(0, 1500))}</div>
                </details>` : ''}
                <!-- Stats row removed (Variant C follow-up): values live in the merged spec strip -->
                <!-- Market Price Statistics -->
                ${priceStatsHtml}
                <!-- Ident merged strip (Card/Game/Player+Team + TCG text) -->
                ${stripHtml}
                <!-- Sports: Player Profile (bio + stats + awards) -->
                ${playerSection}
                <!-- Sports: Team Info -->
                ${teamSection}
                <!-- Non-sport / TCG: About this character -->
                ${characterSection}
                <!-- Links -->
                ${linksHtml}
            </div>
        </div>`;
    }

    // Shared section: identification + price stats + listings, for any service.
    // If a full sport-id or tcg-id record is attached (e.g. from the grade
    // secondary lookup), render the type-appropriate comprehensive output.
    function renderMarketSection(record, config) {
        return renderIdentificationSection(record, config) + renderPriceDataSection(record, config);
    }

    function _hasIdentification(rec) {
        if (!rec || !rec.card || !rec.card.identification) return false;
        const id = rec.card.identification;
        return !!(id.name || id.full_name || id.set_name);
    }

    function renderIdentificationSection(record, config) {
        // Google Lens verification notice — rendered above whichever
        // identification component follows (verified badge / web-corrected /
        // unverified). Absent evidence (gate disabled or no verdict) renders
        // nothing, preserving today's behavior.
        const _wd = (record && record.identification_resolution && record.identification_resolution.web_detect) || null;
        let wdNotice = '';
        if (_wd && typeof _wd === 'object') {
            const _wv = !!_wd.verified;
            const _wr = String(_wd.reason || '');
            const _wn = Array.isArray(_wd.titles) ? _wd.titles.length : 0;
            if (_wv && _wr === 'winner_corroborated') {
                wdNotice = `<div style="display:flex;align-items:center;gap:8px;margin-bottom:12px;padding:8px 12px;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;"><span style="font-size:15px;">✅</span><span style="font-size:12px;color:#166534;font-weight:700;">Card verified against ${_wn ? _wn : ''} online listing${_wn === 1 ? '' : 's'}</span></div>`;
            } else if (_wr === 'web_detect_third_identity' && _wd.adopted) {
                wdNotice = `<div style="display:flex;align-items:center;gap:8px;margin-bottom:12px;padding:8px 12px;background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;"><span style="font-size:15px;">🔍</span><span style="font-size:12px;color:#1e40af;font-weight:600;line-height:1.45;">Identified via Google Lens listing titles — ${escapeHtml(String(_wd.adopted))}</span></div>`;
            } else if (!_wv) {
                wdNotice = `<div style="display:flex;align-items:center;gap:8px;margin-bottom:12px;padding:8px 12px;background:#fffbeb;border:1px solid #fde68a;border-radius:8px;"><span style="font-size:15px;">⚠️</span><span style="font-size:12px;color:#92400e;font-weight:600;line-height:1.45;">AI suggestion — this identification could not be verified against online listings. Compare the year, set and card number with the card before relying on it.</span></div>`;
            }
        }
        // Gemini identification (stored at top-level record.identification) takes
        // priority over Ximilar's tcg_id/sport_id. When present, override the
        // Ximilar identification in tcg_id/sport_id so the TCG/sports-specific
        // rendering (tags, alternatives, price stats) still works with Gemini's data.
        const geminiId = record && record.identification && record.identification.source === 'gemini'
            ? record.identification : null;
        if (geminiId) {
            if (record.tcg_id && record.tcg_id.card) {
                record.tcg_id.card.identification = geminiId;
            } else if (record.sport_id && record.sport_id.card) {
                record.sport_id.card.identification = geminiId;
            }
        }

        const tcgHasId = _hasIdentification(record && record.tcg_id);
        const sportHasId = _hasIdentification(record && record.sport_id);
        if (tcgHasId) {
            const tcgConfig = (typeof SERVICE_CONFIG !== 'undefined' && SERVICE_CONFIG['tcg-id']) || config;
            return wdNotice + renderTcgIdIdentification(record.tcg_id, tcgConfig);
        } else if (sportHasId) {
            const sidConfig = (typeof SERVICE_CONFIG !== 'undefined' && SERVICE_CONFIG['sport-id']) || config;
            return wdNotice + renderSportIdIdentification(record.sport_id, sidConfig);
        }
        // Fallback: if tcg_id has a card object (even with empty id), use it; otherwise try sport_id
        if (record && record.tcg_id && record.tcg_id.card) {
            const tcgConfig = (typeof SERVICE_CONFIG !== 'undefined' && SERVICE_CONFIG['tcg-id']) || config;
            return wdNotice + renderTcgIdIdentification(record.tcg_id, tcgConfig);
        } else if (record && record.sport_id && record.sport_id.card) {
            const sidConfig = (typeof SERVICE_CONFIG !== 'undefined' && SERVICE_CONFIG['sport-id']) || config;
            return wdNotice + renderSportIdIdentification(record.sport_id, sidConfig);
        }
        const { id } = extractMarketData(record);
        return wdNotice + renderIdentificationCard(id, config, _wd);
    }

    function renderPriceDataSection(record, config) {
        const tcgHasId = _hasIdentification(record && record.tcg_id);
        const sportHasId = _hasIdentification(record && record.sport_id);
        if (tcgHasId) {
            const tcgConfig = (typeof SERVICE_CONFIG !== 'undefined' && SERVICE_CONFIG['tcg-id']) || config;
            return renderTcgIdPriceData(record.tcg_id, tcgConfig);
        } else if (sportHasId) {
            const sidConfig = (typeof SERVICE_CONFIG !== 'undefined' && SERVICE_CONFIG['sport-id']) || config;
            return renderSportIdPriceData(record.sport_id, sidConfig);
        }
        if (record && record.tcg_id && record.tcg_id.card) {
            const tcgConfig = (typeof SERVICE_CONFIG !== 'undefined' && SERVICE_CONFIG['tcg-id']) || config;
            return renderTcgIdPriceData(record.tcg_id, tcgConfig);
        } else if (record && record.sport_id && record.sport_id.card) {
            const sidConfig = (typeof SERVICE_CONFIG !== 'undefined' && SERVICE_CONFIG['sport-id']) || config;
            return renderSportIdPriceData(record.sport_id, sidConfig);
        }
        const { listings, priceStats } = extractMarketData(record);
        const statsHtml = renderPriceStats(priceStats);
        // Marketplace listings now rendered inside renderLiveComps (same card as
        // eBay active listings) — no separate standalone card here.
        return statsHtml;
    }

    function renderGradeResult(record, config, meta) {
        meta = meta || {};
        const grades = record.grades || {};
        const centering = record.centering || {};
        const card = record.card || {};
        const tags = card._tags || {};
        let corners = record.corners || [];
        let edges = record.edges || [];
        // When the PGC grader produced the headline grades, the individual
        // corner/edge cards must come from the SAME grader — mixing Ximilar's
        // per-corner values under PGC headline bars showed "Corners 8" above
        // four corners graded 4.5-5.0 (record 10877).
        const _pgcSub = pgcOwnSubgrades(record);
        if (_pgcSub) { corners = _pgcSub.corners; edges = _pgcSub.edges; }
        const surface = record.surface || {};
        const detection = record.detection || {};
        const damaged = record.damaged || {};

        // Autograph detection (60% confidence threshold, sports cards only)
        const _isSports = !isTCGCard(record);
        const autographData = record.autograph || {};
        const hasAutograph = _isSports && (autographData.has_autograph === true
            || (autographData.value === 'Yes' && ((autographData.confidence != null ? autographData.confidence : 0)) >= 0.60));
        const autographConfidence = (autographData.confidence != null ? autographData.confidence : 0);

        const overall = grades.final;
        const overallColor = gradeColor(overall);

        // Detect "Damaged" with no sub-grades — usually means the card wasn't
        // fully in frame or the image was too poor for the grader to segment.
        const isDamagedNoGrades = (grades.condition === 'Damaged' || grades.condition === 'damaged')
            && (overall === null || overall === undefined || overall === '')
            && (!corners || corners.length === 0)
            && (!edges || edges.length === 0);

        const damagedWarningHtml = isDamagedNoGrades ? `
            <div style="background: var(--xg-warn-soft); border: 1px solid var(--xg-warn-line); border-left: 4px solid var(--xg-warn); border-radius: 8px; padding: 16px 20px; margin-bottom: 16px; display: flex; gap: 12px; align-items: flex-start;">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--xg-warn)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink: 0; margin-top: 2px;"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                <div>
                    <div style="font-weight: 700; color: var(--xg-warn-strong); font-size: 15px; margin-bottom: 4px;">Unable to Grade — Card Not Fully Visible</div>
                    <div style="color: var(--xg-warn-strong); font-size: 13px; line-height: 1.5;">The AI couldn't complete a full grading analysis. This usually happens when the card isn't fully in frame, the image is too dark/blurry, or the card back was uploaded instead of the front. Please retake the photo with the entire card visible on a plain background and try again.</div>
                </div>
            </div>
        ` : '';

        // Sub-grade progress bars (graded out of 10)
        const subGrades = [
            { label: pgcT('subgrade.corners', 'Corners'), value: grades.corners },
            { label: pgcT('subgrade.edges', 'Edges'), value: grades.edges },
            { label: pgcT('subgrade.surface', 'Surface'), value: grades.surface },
            { label: pgcT('subgrade.centering', 'Centering'), value: grades.centering }
        ].filter(g => g.value !== null && g.value !== undefined && g.value !== '');

        // Subgrade radar chart (SVG)
        const radarHtml = subGrades.length >= 3 ? (() => {
            const cx = 80, cy = 80, r = 60;
            const n = subGrades.length;
            const angle = (i) => -Math.PI / 2 + (i * 2 * Math.PI / n);
            const pt = (i, radius) => [cx + radius * Math.cos(angle(i)), cy + radius * Math.sin(angle(i))];
            // Grid rings at 5, 7.5, 10
            const rings = [5, 7.5, 10].map(g => {
                const pts = Array.from({length: n}, (_, i) => pt(i, (g / 10) * r).join(',')).join(' ');
                return `<polygon points="${pts}" fill="none" stroke="var(--xg-border)" stroke-width="0.5" opacity="0.5"/>`;
            }).join('');
            // Axis lines
            const axes = Array.from({length: n}, (_, i) => {
                const [x, y] = pt(i, r);
                return `<line x1="${cx}" y1="${cy}" x2="${x.toFixed(1)}" y2="${y.toFixed(1)}" stroke="var(--xg-border)" stroke-width="0.5" opacity="0.4"/>`;
            }).join('');
            // Data polygon
            const dataPts = subGrades.map((g, i) => pt(i, (parseFloat(g.value) / 10) * r).join(',')).join(' ');
            // Data points
            const dataDots = subGrades.map((g, i) => {
                const [x, y] = pt(i, (parseFloat(g.value) / 10) * r);
                return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="3" fill="${gradeColor(g.value)}" stroke="white" stroke-width="1"/>`;
            }).join('');
            // Labels
            const labels = subGrades.map((g, i) => {
                const [x, y] = pt(i, r + 14);
                return `<text x="${x.toFixed(1)}" y="${y.toFixed(1)}" text-anchor="middle" font-size="9" font-weight="600" fill="var(--xg-muted)">${escapeHtml(g.label)}</text>`;
            }).join('');
            // Value labels
            const valLabels = subGrades.map((g, i) => {
                const [x, y] = pt(i, (parseFloat(g.value) / 10) * r + 10);
                return `<text x="${x.toFixed(1)}" y="${y.toFixed(1)}" text-anchor="middle" font-size="8" font-weight="700" fill="${gradeColor(g.value)}">${g.value}</text>`;
            }).join('');
            return `<div style="display: flex; align-items: center; justify-content: center; padding: 10px;">
                <svg width="170" height="170" viewBox="0 0 170 170">
                    ${rings}${axes}
                    <polygon points="${dataPts}" fill="rgba(59,130,246,0.15)" stroke="#3b82f6" stroke-width="1.5"/>
                    ${dataDots}${labels}${valLabels}
                </svg>
            </div>`;
        })() : '';

        // ROI is computed up-front so the KPI strip can use it
        const { id: gradeMktId } = extractMarketData(record);
        const roi = calculateGradingROI(grades, gradeMktId);

        const ringPct = overall != null ? Math.min((parseFloat(overall) || 0) / 10 * 100, 100) : 0;
        const kpiHtml = `
            <div class="xg-kpis">
                ${roi && roi.graded_value != null ? `
                <div class="xg-kpi">
                    <div class="xg-kpi-label">Est. Graded Value</div>
                    <div class="xg-kpi-value">$${parseFloat(roi.graded_value).toFixed(0)}</div>
                    <div class="xg-kpi-sub">vs $${parseFloat(roi.raw_value).toFixed(0)} raw today</div>
                </div>
                <div class="xg-kpi">
                    <div class="xg-kpi-label">Projected ROI</div>
                    <div class="xg-kpi-value" style="color: var(--xg-good);">+${Math.round(roi.roi_percent)}%</div>
                    <div class="xg-kpi-sub">after $${roi.grading_fee != null ? roi.grading_fee : 25} fee</div>
                </div>` : ''}
            </div>
        `;

        let heroAndBars = damagedWarningHtml + `
            <div style="margin-bottom:16px;padding:18px 22px;border-radius:12px;background:linear-gradient(135deg,var(--xg-subtle),var(--xg-panel));border:1px solid var(--xg-border);border-left:4px solid ${overallColor};">
                <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;">
                    <div style="display:flex;align-items:center;justify-content:center;width:44px;height:44px;border-radius:10px;background:${overallColor}20;flex-shrink:0;">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="${overallColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                    </div>
                    <div>
                        <div style="font-size:18px;font-weight:800;color:var(--xg-text);line-height:1.2;letter-spacing:-0.01em;">AI Grading Results ${record.back_grading ? '<span style="display:inline-block;margin-left:6px;font-size:10px;font-weight:800;color:#059669;background:#ecfdf5;border:1px solid #a7f3d0;padding:2px 8px;border-radius:999px;vertical-align:middle;letter-spacing:0.04em;">ENHANCED FRONT + BACK</span>' : ''}</div>
                        <div style="font-size:12px;color:var(--xg-muted);margin-top:2px;">Corners, edges, surface & centering &mdash; powered by computer vision.${record.back_grading ? ' Combined front &amp; back AI assessment.' : ''}</div>
                    </div>
                </div>
            </div>
            <div class="xim-hero-grid">
                <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; padding: 22px 16px; border-radius: 12px; background: var(--xg-subtle); border: 1px solid var(--xg-border);">
                    <div class="xg-ring" style="--pct: ${ringPct}; --ring-color: ${overallColor};">
                        <div class="xg-ring-inner">
                            <span class="xg-ring-grade">${overall != null ? overall : 'N/A'}</span>
                            <span class="xg-ring-label">${grades.condition ? grades.condition : 'AI Predicted'}</span>
                            <span class="xg-ring-sub">${record.back_grading ? 'AI Calculated Grade' : 'Scale 1–10 · AI'}</span>
                        </div>
                    </div>
                    ${record.back_grading ? `<div style="display: flex; gap: 10px; flex-wrap: wrap; justify-content: center;">
                        <span class="xim-chip"><span class="xim-chip__dot" style="background: #3b82f6;"></span>Front: <b>${grades.front != null ? grades.front : '—'}</b></span>
                        <span class="xim-chip"><span class="xim-chip__dot" style="background: #059669;"></span>Back: <b>${record.back_grading.final != null ? record.back_grading.final : '—'}</b></span>
                    </div>` : ''}
                </div>
                <div style="display: flex; flex-direction: column; justify-content: center; padding: 20px; border-radius: 12px; background: var(--xg-subtle); border: 1px solid var(--xg-border);">
                    ${record.back_grading ? '<div style="font-size: 11px; font-weight: 800; color: var(--xg-muted); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 10px;">Front of Card</div>' : ''}
                    ${subGrades.length ? subGrades.map(g => {
                        const pct = Math.min((parseFloat(g.value) || 0) / 10 * 100, 100);
                        return `<div class="xg-sgrow">
                            <div class="xim-bar-label">${g.label}</div>
                            <div class="xim-bar-track"><div class="xim-bar-fill" style="width: ${pct}%; background: ${gradeColor(g.value)};"></div></div>
                            <div class="xim-bar-val">${g.value}</div>
                            ${getSeverityBadge(g.value)}
                        </div>`;
                    }).join('') : '<div style="color: var(--xg-muted); font-size:13px;">No sub-grades available</div>'}
                    ${radarHtml}
                </div>
            </div>
            ${renderIqCapsNotice(record)}
            ${kpiHtml}
        `;

        // Back-of-card panel — compact key elements only (the back gets its own
        // Ximilar grade call; no identification or market data is run on it).
        const bGrade = record.back_grading;
        const backImg = record.back_image_url || (record._images != null ? record._images.back : void 0) || '';
        const backGradingHtml = (bGrade && (bGrade.final != null || bGrade.corners != null || bGrade.edges != null || bGrade.surface != null)) ? (() => {
            const bf = bGrade.final;
            const bfColor = gradeColor(bf);
            const bfPct = bf != null ? Math.min((parseFloat(bf) || 0) / 10 * 100, 100) : 0;
            const bSubs = [
                { label: pgcT('subgrade.corners', 'Corners'), value: bGrade.corners },
                { label: pgcT('subgrade.edges', 'Edges'), value: bGrade.edges },
                { label: pgcT('subgrade.surface', 'Surface'), value: bGrade.surface },
                { label: pgcT('subgrade.centering', 'Centering'), value: bGrade.centering }
            ].filter(s => s.value !== null && s.value !== undefined && s.value !== '');
            return `
            <div class="xim-back-panel" style="margin-top:14px;display:flex;gap:18px;align-items:center;padding:16px 20px;border-radius:12px;background:var(--xg-subtle);border:1px solid var(--xg-border);border-left:4px solid ${bfColor};flex-wrap:wrap;">
                ${backImg ? `<img src="${escapeHtml(pgcThumb(proxyImg(backImg), 320))}" alt="Card back" loading="lazy" style="width:84px;height:118px;object-fit:contain;background:var(--xg-panel);border:1px solid var(--xg-border);flex-shrink:0;" onerror="this.style.display='none'">` : ''}
                <div style="flex:1;min-width:220px;">
                    <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:${bSubs.length ? '10px' : '0'};">
                        <div style="font-size:13px;font-weight:800;color:var(--xg-text);letter-spacing:-0.01em;">Back of Card</div>
                        <div style="display:inline-flex;align-items:center;gap:8px;">
                            <span style="font-size:20px;font-weight:800;color:${bfColor};">${bf != null ? bf : 'N/A'}</span>
                            <span style="font-size:11px;font-weight:700;color:${bfColor};background:${bfColor}1a;padding:2px 8px;border-radius:999px;">${bGrade.condition ? escapeHtml(bGrade.condition) : 'AI Predicted'}</span>
                        </div>
                        ${bGrade.damaged && bGrade.damaged.is_damaged ? `<span style="font-size:11px;font-weight:700;color:var(--xg-warn-strong);background:var(--xg-warn-soft);border:1px solid var(--xg-warn-line);padding:2px 8px;border-radius:999px;">Possible damage detected</span>` : ''}
                    </div>
                    ${bSubs.length ? `<div class="xim-back-subs" style="display:flex;gap:14px;flex-wrap:wrap;">
                        ${bSubs.map(s => `<div style="display:flex;flex-direction:column;gap:3px;min-width:64px;">
                            <span style="font-size:10px;font-weight:700;color:var(--xg-muted);text-transform:uppercase;letter-spacing:0.05em;">${s.label}</span>
                            <div style="display:flex;align-items:center;gap:6px;">
                                <div style="flex:1;height:5px;border-radius:3px;background:var(--xg-border);overflow:hidden;"><div style="height:100%;width:${Math.min((parseFloat(s.value) || 0) / 10 * 100, 100)}%;background:${gradeColor(s.value)};"></div></div>
                                <span style="font-size:12px;font-weight:800;color:${gradeColor(s.value)};min-width:22px;">${s.value}</span>
                            </div>
                        </div>`).join('')}
                    </div>` : ''}
                </div>
            </div>`;
        })() : (record.back_failed ? `
            <div class="xim-back-panel" style="margin-top:14px;display:flex;gap:18px;align-items:center;padding:16px 20px;border-radius:12px;background:var(--xg-warn-soft);border:1px solid var(--xg-warn-line);border-left:4px solid var(--xg-warn-strong);flex-wrap:wrap;">
                ${backImg ? `<img src="${escapeHtml(pgcThumb(proxyImg(backImg), 320))}" alt="Card back" loading="lazy" style="width:84px;height:118px;object-fit:contain;background:var(--xg-panel);border:1px solid var(--xg-border);flex-shrink:0;" onerror="this.style.display='none'">` : ''}
                <div style="flex:1;min-width:220px;">
                    <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:6px;">
                        <div style="font-size:13px;font-weight:800;color:var(--xg-text);letter-spacing:-0.01em;">Back of Card</div>
                        <span style="font-size:11px;font-weight:700;color:var(--xg-warn-strong);background:var(--xg-panel);border:1px solid var(--xg-warn-line);padding:2px 8px;border-radius:999px;">Not graded</span>
                    </div>
                    <div style="font-size:12px;color:var(--xg-muted);line-height:1.5;">We couldn't isolate the card in the back photo, so no back grade was produced — the add-on charge was refunded. Retake it straight-on with the entire card inside the frame on a plain contrasting background.</div>
                </div>
            </div>` : (record.back_skipped_low_credits ? `
            <div class="xim-back-panel" style="margin-top:14px;display:flex;gap:18px;align-items:center;padding:16px 20px;border-radius:12px;background:var(--xg-warn-soft);border:1px solid var(--xg-warn-line);border-left:4px solid var(--xg-warn-strong);flex-wrap:wrap;">
                <div style="flex:1;min-width:220px;">
                    <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:6px;">
                        <div style="font-size:13px;font-weight:800;color:var(--xg-text);letter-spacing:-0.01em;">Back of Card</div>
                        <span style="font-size:11px;font-weight:700;color:var(--xg-warn-strong);background:var(--xg-panel);border:1px solid var(--xg-warn-line);padding:2px 8px;border-radius:999px;">Not graded</span>
                    </div>
                    <div style="font-size:12px;color:var(--xg-muted);line-height:1.5;">Your back photo wasn't graded — the +${escapeHtml(String((record.back_cost != null ? record.back_cost : '')))}-credit add-on exceeds your current balance. <a href="/dashboard/credits" style="color:var(--xg-accent);font-weight:700;text-decoration:underline;">Add credits</a> and re-grade any time to include the back.</div>
                </div>
            </div>` : ''));

        // Attribute chips — autograph shown only when confidence >= 60%
        const sideVal = capitalizeFirst((function(_o){return _o == null ? void 0 : _o.name})((tags.Side != null ? tags.Side[0] : void 0)) || (record.side != null ? record.side.value : void 0));
        let categoryVal = normalizeCategory((function(_o){return _o == null ? void 0 : _o.name})((tags.Category != null ? tags.Category[0] : void 0)) || (record.category != null ? record.category.value : void 0));
        // Non-sport collectibles (Star Wars, Garbage Pail Kids...) get mislabeled
        // "Sport Card"/"Card" by the provider taxonomy — display the truth.
        if (nonSportCollectibleLabel(record)) categoryVal = 'Non-Sport';
        const isDamaged = damaged.is_damaged;
        const iq = record.image_quality || {};
        const relicChip = iq.has_relic
            ? { label: pgcT('rr.relic', 'Relic'), value: (iq.relic_type && iq.relic_type !== 'none') ? String(iq.relic_type) : 'Memorabilia', dot: '#3730a3' }
            : null;
        const serialChip = iq.is_numbered && !isCollectorNumberNotSerial(iq, record)
            ? { label: pgcT('rr.serial', 'Serial'), value: iq.serial ? '#' + String(iq.serial) : 'Numbered', dot: '#7c3aed' }
            : null;
        const chips = [
            sideVal ? { label: pgcT('metric.side', 'Side'), value: sideVal, sub: formatConfidence((record.side != null ? record.side.confidence : void 0)), dot: '#3b82f6' } : null,
            categoryVal ? { label: pgcT('metric.category', 'Category'), value: categoryVal, sub: formatConfidence((record.category != null ? record.category.confidence : void 0)), dot: '#8b5cf6' } : null,
            { label: pgcT('metric.damaged', 'Damaged'), value: isDamaged ? 'Yes' : (damaged.value || 'No'), sub: formatConfidence(damaged.confidence), dot: isDamaged ? '#ef4444' : '#10b981' },
            hasAutograph ? { label: pgcT('metric.autograph', 'Autograph'), value: 'Signed', sub: formatConfidence(autographConfidence), dot: '#f59e0b' } : null,
            relicChip,
            serialChip,
            detection.confidence ? { label: pgcT('metric.detection', 'Detection'), value: formatConfidence(detection.confidence), sub: detection.name, dot: '#06b6d4' } : null
        ].filter(Boolean);

        let chipsHtml = chips.length ? `
            <div class="xim-attr-chips" style="display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 18px; justify-content: center;">
                ${chips.map(c => `<span class="xim-chip"><span class="xim-chip__dot" style="background: ${c.dot};"></span>${c.label}: <b>${c.value}</b>${c.sub && c.sub !== 'N/A' ? ` <span style="color:#94a3b8; font-weight:600;">(${c.sub})</span>` : ''}</span>`).join('')}
            </div>
        ` : '';

        // Limiting-factor content sits INSIDE the results card right after the
        // attribute chips — no standalone warn section further down.
        const limitingFactorHtml = renderLimitingFactor(grades);

        // Stamped PGC records: ring, front/back, pillars and chips move into the
        // top-of-report hero (with the overlay card) instead of repeating here.
        let topHtml = '';
        const heroSubset = isDamagedNoGrades ? null : pgcOverlaySubset(record);
        const slabBlock = '';
        if (heroSubset) {
            topHtml = slabBlock + renderPgcHero(record, heroSubset, grades, overall, overallColor, ringPct, chipsHtml + limitingFactorHtml, renderIqCapsNotice(record));
            heroAndBars = damagedWarningHtml + kpiHtml;
            chipsHtml = '';
        }

        // The hero's overlay already shows every corner/edge grade (with
        // close-ups) — the list only stays on older, unstamped records.
        let cornersEdgesHtml = heroSubset ? '' : renderIndividualCorners(corners, edges);
        let centeringDetailsHtml = renderCenteringDetailsPanel(centering, config.color, record);

        // Unstamped records get the grading block above the photo gallery too —
        // the same top-of-report position the PGC hero has on stamped records.
        if (!heroSubset) {
            topHtml = slabBlock + '<div class="ximilar-result-card" style="background: var(--xg-panel); border-radius: 8px; padding: 20px; box-shadow: var(--xg-shadow); margin-bottom: 16px; border: 1px solid var(--xg-border);">'
                + heroAndBars + chipsHtml + limitingFactorHtml + cornersEdgesHtml + centeringDetailsHtml
                + '</div>';
            heroAndBars = chipsHtml = cornersEdgesHtml = centeringDetailsHtml = '';
        }

        const forecast = calculateHybridForecast(grades);
        const forecastHtml = renderHybridForecast(forecast);

        const roiHtml = renderProfitabilityMatrix(roi, grades.final);

        const identificationHtml = renderIdentificationSection(record, config);
        const priceDataHtml = renderPriceDataSection(record, config);
        const economicsPanel = renderEconomicsPanel(meta.economics || record.economics);
        const scrydexMarketHtml = renderScrydexMarketCard(record);
        const liveCompsHtml = renderLiveComps(record);
        const gradedMarketHtml = renderGradedMarket(record);

        // comps tail: when the row persisted with live_comps.comps_pending the
        // post-persist tail patches action_history in place. The record is a
        // snapshot — render the pending state statically, no comps-status poll.
        const compsPending = !!(record.live_comps && record.live_comps.comps_pending);
        let econMountHtml = economicsPanel;
        let compsMountHtml = liveCompsHtml + gradedMarketHtml;
        if (compsPending) {
            compsMountHtml = '<div class="pgc-comps-wait" style="display:flex;align-items:center;gap:10px;background:#eff6ff;border:1px solid #bfdbfe;border-radius:10px;padding:12px 16px;margin-bottom:16px;">'
                + '<div style="font-size:13px;color:#1e40af;font-weight:600;line-height:1.4;">Loading live market data — it will appear here automatically in a few seconds.</div>'
                + '</div>'
                + liveCompsHtml + gradedMarketHtml;
            // Fill it in by itself (2026-10-26, owner): re-check this page and
            // reload once the finished market snapshot has been folded in.
            if (typeof window !== 'undefined' && !window.__pgcCompsWatch) {
                window.__pgcCompsWatch = true;
                let tries = 0;
                const tick = function () {
                    if (++tries > 30) return; // ~3 min cap
                    fetch(location.href, { cache: 'no-store', credentials: 'same-origin' })
                        .then(function (r) { return r.ok ? r.text() : ''; })
                        .then(function (t) { if (t && t.indexOf('pgc-comps-wait') === -1) location.reload(); else setTimeout(tick, 6000); })
                        .catch(function () { setTimeout(tick, 6000); });
                };
                setTimeout(tick, 6000);
            }
        }

        // Population panel renders ONLY the snapshot embedded in the record
        // (population_records, written at grade time or server-side embedded
        // by the page) — never a live /api/population/lookup call.
        const popRecords = record.population_records
            || (record.live_comps && record.live_comps.population_records)
            || null;
        let populationPanel = '';
        if (Array.isArray(popRecords) && popRecords.length) {
            const pid = 'ps' + (++popStaticSeq);
            popStaticEmbed[pid] = popRecords;
            populationPanel = `
            <div class="ximilar-result-card" style="background: var(--xg-panel); border-radius: 8px; padding: 20px; box-shadow: var(--xg-shadow); margin-bottom: 16px; border: 1px solid var(--xg-border);">
                <div data-population-static="${pid}" style="min-height: 80px;"></div>
            </div>`;
        }

        // Market Price Analysis placed before Live Market Comps so the
        // Ximilar price data appears first, with the richer sold+active
        // eBay comps below.
        return { html: heroAndBars + chipsHtml + cornersEdgesHtml + centeringDetailsHtml + renderImageQualityAnalysis(record) + backGradingHtml + forecastHtml + econMountHtml + roiHtml + priceDataHtml + scrydexMarketHtml + compsMountHtml + populationPanel, marketHtml: identificationHtml, topHtml: topHtml };
    }

    // ── Live Market Comps (RapidAPI sold + eBay Browse API active) ────────
    // Renders sold comps + active listings fetched by includes/MarketComps.php.
    function renderLiveComps(record) {
        const comps = record.live_comps;
        if (!comps || typeof comps !== 'object') return '';
        const isSports = !isTCGCard(record);

        // Stale comps: the player identity was corrected AFTER pricing ran, so
        // the stored query and results name the superseded player (e.g. a card
        // corrected Mike Garcia → Bobby Avila kept Mike Garcia listings).
        // Suppress rather than show the wrong person's sales.
        const _idForStale = record.identification || (record.card && record.card.identification) || {};
        const _corrFrom = String(_idForStale.player_corrected_from || '').toLowerCase();
        const _corrTo = String(_idForStale.name || '').toLowerCase();
        const _cq = String(comps.query || '').toLowerCase();
        const staleComps = !!(_corrFrom && _cq.includes(_corrFrom) && !(_corrTo && _cq.includes(_corrTo)));
        if (staleComps) {
            return `<div class="xinfo-card" style="margin-top:16px;">
                <div style="font-size:12px;color:var(--xg-muted);line-height:1.5;">Sales comps were captured for an earlier identification of this card and aren't shown.</div>
            </div>`;
        }

        const sold = Array.isArray(comps.sold) ? comps.sold : [];
        const active = Array.isArray(comps.active) ? comps.active : [];
        const summary = comps.summary || {};
        // Vision rescue (identity-doomed pools): image-derived eBay queries
        // found listings the identity-derived query could not. Rendered in a
        // separately labeled block — the rows are NOT verified against the
        // identified card and must not be read as comps for it.
        const vr = (comps.vision_rescue && typeof comps.vision_rescue === 'object') ? comps.vision_rescue : null;
        const vrListings = vr && Array.isArray(vr.listings) ? vr.listings : [];
        const hasData = sold.length > 0 || active.length > 0 || vrListings.length > 0;

        // Possible misidentification: the comps pool rejected the stored
        // identity and pricing was withheld — render the verification notice
        // instead of pricing or the generic outage copy. A stale
        // identity_doomed flag survives on records the vision rescue later
        // cleared, so the flag only counts when no resolution status
        // contradicts it.
        const _idResStatus = String((record.identification_resolution && record.identification_resolution.status) || '');
        const identityDoomed = _idResStatus === 'possible_misidentification'
            || (_idResStatus === '' && comps.identity_doomed && typeof comps.identity_doomed === 'object');

        // Ximilar marketplace listings (from card.listings / record.listings).
        // Rendered inside this same card so they appear alongside the eBay
        // active listings rather than as a separate standalone card.
        const { listings: ximilarListings } = extractMarketData(record);
        let marketplaceListingsHtml = '';
        if (ximilarListings && Array.isArray(ximilarListings) && ximilarListings.length > 0) {
            marketplaceListingsHtml = `
                <div style="margin-top: 20px;">
                    <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 10px;">
                        <span style="background: #475569; color: white; font-size: 11px; font-weight: 800; padding: 4px 8px; border-radius: 6px; letter-spacing: 0.04em;">MARKET</span>
                        <h4 style="font-size: 16px; font-weight: 800; color: var(--xg-text); margin: 0; letter-spacing: -0.01em;">Live Marketplace Listings</h4>
                        <span style="font-size: 12px; color: var(--xg-muted);">${ximilarListings.length} listing${ximilarListings.length !== 1 ? 's' : ''} across marketplaces</span>
                    </div>
                    ${renderListingsTable(ximilarListings)}
                </div>`;
        }

        // Provider failure list — MarketComps stores per-source errors as an
        // object keyed 'sold_<src>'/'sup_<src>'/'ebay_active'/'deadline', and
        // whole-call failures as comps.error (string). Without this the card
        // silently vanished on provider outages (e.g. #7848).
        const compSourceLabels = {
            ebay_active: 'eBay Browse',
            soldcomps: 'SoldComps', rapidapi: 'RapidAPI', pkmnprices: 'PkmnPrices',
            cardsight: 'CardSight', hoodcar: 'HoodCar', parsebot: '130point',
            cardladder: 'CardLadder', sportscardspro: 'SportsCardsPro',
            cardmarket: 'Cardmarket', cardshowdata: 'CardShowData',
            scrydex: 'Scrydex', opentcg: 'OpenTCG', deadline: 'Request budget'
        };
        const compErrors = [];
        const compTerminalError = comps.error ? String(comps.error) : null;
        if (compTerminalError !== null) compErrors.push({ src: '', msg: compTerminalError });
        if (comps.errors && typeof comps.errors === 'object' && !Array.isArray(comps.errors)) {
            for (const [k, v] of Object.entries(comps.errors)) {
                if (v == null || v === '') continue;
                compErrors.push({
                    src: compSourceLabels[String(k).replace(/^(sold|sup)_/, '')] || k,
                    msg: String(v)
                });
            }
        }

        // Don't bail if we have marketplace listings, price data, or population
        // data to show even without sold/active comps
        const hasPriceData = comps.tcgplayer_prices || comps.opentcg_prices || comps.tcgpl_prices
            || comps.cardshowdata_market != null || comps.cardshowdata_recommended != null
            || comps.fair_value != null || comps.graded_prices || comps.tcgpl_graded_prices
            || comps.cardmarket_floor_eur != null || comps.cardsight_median != null
            || comps.parsebot_median != null || comps.psa_population_total != null
            || comps.price_trends;
        if (!hasData && !compErrors.length && !marketplaceListingsHtml && !hasPriceData && !identityDoomed) {
            // Indicative raw value from Google's AI Overview when the comps
            // pool is empty (mirrors ResultRenderer::renderLiveComps).
            const aioRv = record.identification_resolution && record.identification_resolution.ai_overview
                && record.identification_resolution.ai_overview.raw_value;
            if (aioRv && aioRv.low && aioRv.high) {
                const sym = { USD: '$', EUR: '€', GBP: '£' }[aioRv.currency || 'USD'] || '$';
                const f = v => sym + (Number(v) % 1 === 0 ? Number(v).toLocaleString() : Number(v).toFixed(2));
                return `<div class="ximilar-result-card xim-aio-value" style="background: var(--xg-panel); border-radius: 8px; padding: 20px; box-shadow: var(--xg-shadow); margin-bottom: 16px; border: 1px solid var(--xg-border); border-left: 4px solid #0ea5e9;">
                    <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;"><span style="background:#0ea5e9;color:#fff;font-size:11px;font-weight:800;padding:4px 8px;border-radius:6px;letter-spacing:.04em;">INDICATIVE</span>
                    <h4 style="font-size:16px;font-weight:800;color:var(--xg-text);margin:0;">Raw Value Estimate</h4></div>
                    <div style="font-size:28px;font-weight:900;color:var(--xg-text);letter-spacing:-0.02em;">${f(aioRv.low)} – ${f(aioRv.high)} <span style="font-size:13px;font-weight:700;color:var(--xg-muted);">${escapeHtml(aioRv.currency || 'USD')} ungraded</span></div>
                    <div style="font-size:12px;color:var(--xg-muted);line-height:1.5;margin-top:8px;">No sold listings matched this card yet (new or thinly traded set). This range is Google's AI summary of current marketplace asking prices — not a PGC comps valuation. Check back as sales accumulate.</div>
                </div>`;
            }
            return '';
        }

        const fmtPrice = (v, cur) => {
            if (v == null) return 'N/A';
            const sym = (cur === 'USD' || !cur) ? '$' : (cur === 'CAD' ? 'C$' : (cur + ' '));
            return sym + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        };

        // Summary strip
        let summaryHtml = '';
        const summaryItems = [];
        // Auto-specific pricing badge
        if (comps.auto_filtered) {
            summaryItems.push(`<span style="background:#fef3c7;color:#92400e;padding:4px 10px;border-radius:20px;font-size:12px;font-weight:600;border:1px solid #fde68a;">Pricing based on autograph comps</span>`);
        }
        // Name-level comps: provider identification was empty, so the query came
        // from the name read off the photo — the pool mixes all printings of the
        // card and must not be read as exact-card pricing (e.g. Slifer reprints
        // vs the GBI-001 promo share a name but differ 100x in value).
        if (comps.query_iq_fallback || comps.sold_name_match) {
            summaryItems.push(`<span style="background:#e0e7ff;color:#3730a3;padding:4px 10px;border-radius:20px;font-size:12px;font-weight:600;border:1px solid #c7d2fe;" title="Pricing may include other printings of this card — treat as an estimate">Estimate — comps may cover multiple printings of this card</span>`);
        }
        // Translated-search transparency: non-English cards are comped via
        // English marketplace titles.
        const _compId = record.identification || (record.card && record.card.identification) || {};
        const _compLang = String(_compId.language_code || '').toUpperCase();
        if (_compLang && _compLang !== 'EN' && (_compId.name_en || _compId.set_name_en) && comps.query) {
            summaryItems.push(`<span style="background:#fef9c3;color:#854d0e;padding:4px 10px;border-radius:20px;font-size:12px;font-weight:600;border:1px solid #fde68a;" title="This card is non-English — marketplace comps were searched under its English name">${_compLang} card — priced using its English name</span>`);
        }
        if (summary.sold_count > 0) {
            summaryItems.push(`<span style="background: var(--xg-good-soft); color: var(--xg-good-strong); padding: 4px 10px; border-radius: 20px; font-size: 12px; font-weight: 600;">${summary.sold_count} sold</span>`);
            if (summary.sold_median != null) summaryItems.push(`<span style="font-size: 12px; color: var(--xg-muted);">Median: <strong style="color: var(--xg-text);">${fmtPrice(summary.sold_median, summary.sold_currency)}</strong></span>`);
            if (summary.sold_low != null && summary.sold_high != null) summaryItems.push(`<span style="font-size: 12px; color: var(--xg-muted);">Range: ${fmtPrice(summary.sold_low, summary.sold_currency)} – ${fmtPrice(summary.sold_high, summary.sold_currency)}</span>`);
        }
        if (summary.active_count > 0) {
            summaryItems.push(`<span style="background: var(--xg-info-soft); color: var(--xg-info); padding: 4px 10px; border-radius: 20px; font-size: 12px; font-weight: 600;">${summary.active_count} active</span>`);
            if (summary.active_median != null) summaryItems.push(`<span style="font-size: 12px; color: var(--xg-muted);">Asking median: <strong style="color: var(--xg-text);">${fmtPrice(summary.active_median, summary.active_currency)}</strong></span>`);
        }
        // P0-3 (2026-11-02): live comps came back empty but the identification
        // carried a price signal — show the labeled median instead of a bare
        // outage hole (server sets summary.market_source + note).
        if ((summary.market_source === 'ximilar_price_stats' || summary.market_source === 'scrydex_market_price')
            && summary.sold_median != null) {
            const _msLabel = summary.market_source === 'scrydex_market_price' ? 'Card data median' : 'Ximilar market median';
            summaryItems.push(`<span style="font-size: 12px; color: var(--xg-muted);" title="${escapeHtml(summary.note || 'Live provider comps were unavailable — this median comes from the identification price signal.')}">${_msLabel}: <strong style="color: var(--xg-text);">${fmtPrice(summary.sold_median, summary.sold_currency)}</strong></span>`);
        }

        // Price distribution chart (sold comps histogram)
        let priceChartHtml = '';
        if (sold.length >= 3) {
            const prices = sold.map(s => Number(s.price)).filter(p => p > 0).sort((a, b) => a - b);
            if (prices.length >= 3) {
                const min = prices[0];
                const max = prices[prices.length - 1];
                const range = max - min;
                const buckets = 8;
                const bucketSize = range > 0 ? range / buckets : 1;
                const counts = new Array(buckets).fill(0);
                for (const p of prices) {
                    let idx = Math.floor((p - min) / bucketSize);
                    if (idx >= buckets) idx = buckets - 1;
                    counts[idx]++;
                }
                const maxCount = Math.max(...counts);
                const barW = 100 / buckets;
                const maxBarHeight = 60; // px, leaves room for count + label in 80px container
                const bars = counts.map((cnt, i) => {
                    const lo = min + i * bucketSize;
                    const hi = lo + bucketSize;
                    const h = maxCount > 0 ? (cnt / maxCount) * maxBarHeight : 0;
                    return `<div style="display: flex; flex-direction: column; align-items: center; flex: 1; min-width: 0; justify-content: flex-end;">
                        <div style="font-size: 9px; color: var(--xg-muted); margin-bottom: 2px;">${cnt || ''}</div>
                        <div style="width: 80%; background: linear-gradient(180deg, #059669, #047857); border-radius: 3px 3px 0 0; height: ${h}px; min-height: ${cnt > 0 ? '4px' : '0'}; transition: height 0.3s;"></div>
                        <div style="font-size: 8px; color: var(--xg-muted); margin-top: 3px; text-align: center; white-space: nowrap; overflow: hidden;">${fmtPrice(lo, summary.sold_currency).replace(/\.\d+$/, '')}</div>
                    </div>`;
                }).join('');
                priceChartHtml = `
                    <div style="margin-bottom: 16px; padding: 12px 16px; background: var(--xg-subtle); border-radius: 8px; border: 1px solid var(--xg-border);">
                        <div style="font-size: 12px; font-weight: 600; color: var(--xg-text-2); margin-bottom: 8px;">Sold Price Distribution</div>
                        <div style="display: flex; align-items: flex-end; gap: 2px; height: 80px;">${bars}</div>
                    </div>`;
            }
        }

        // Sold Price Trend — time series of dated sold comps (inline SVG, no deps)
        // Source priority: (1) live_comps.price_series — compact daily series the
        // backend builds from a source's FULL sale history (e.g. CardSight 30d);
        // (2) dated sold rows — daily collapse when they span ≥4 days, else the
        // individual sales plotted chronologically (always chart what we have).
        let priceTrendHtml = '';
        let soldDayCount = 0;
        let plottableSales = 0;
        {
            const parseSoldDate = (s) => {
                if (!s) return null;
                const str = String(s).trim();
                let t = Date.parse(str) || Date.parse(str.replace(' ', 'T'));
                if (!t) {
                    const m = str.match(/(\d{4})[-\/](\d{1,2})[-\/](\d{1,2})/) || str.match(/(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})/);
                    if (m) t = new Date(m[1].length === 4 ? m[1] : m[3], (m[1].length === 4 ? m[2] : m[1]) - 1, m[1].length === 4 ? m[3] : m[2]).getTime();
                }
                return t || null;
            };
            const dated = sold
                .map(s => ({ t: parseSoldDate(s.soldDate), p: Number(s.price) }))
                .filter(d => d.t && d.p > 0)
                .sort((a, b) => a.t - b.t);
            plottableSales = dated.length;
            const byDay = {};
            dated.forEach(d => {
                const day = new Date(d.t).toISOString().slice(0, 10);
                (byDay[day] = byDay[day] || []).push(d.p);
            });
            const days = Object.keys(byDay).sort();
            soldDayCount = days.length;

            const series = Array.isArray(comps.price_series) ? comps.price_series : [];
            let pts = [];
            let nSales = dated.length;
            let nDays = soldDayCount;
            if (series.length >= 4) {
                pts = series
                    .map(x => ({ day: String(x.d || ''), t: Date.parse(String(x.d) + 'T00:00:00Z') || 0, p: Number(x.p), label: String(x.d || '').slice(5), tip: String(x.d || '') }))
                    .filter(x => x.t && x.p > 0)
                    .sort((a, b) => a.t - b.t);
                if (pts.length >= 4) {
                    nSales = series.reduce((a, x) => a + (Number(x.n) || 0), 0) || nSales;
                    nDays = pts.length;
                } else {
                    pts = [];
                }
            }
            if (!pts.length && days.length >= 4) {
                pts = days.map(day => ({
                    day,
                    t: new Date(day + 'T00:00:00').getTime(),
                    p: byDay[day].reduce((a, b) => a + b, 0) / byDay[day].length,
                    label: day.slice(5),
                    tip: day
                }));
            } else if (!pts.length && dated.length >= 3) {
                // Fewer than 4 distinct days — plot individual sales chronologically
                // so a chart still renders. Time-based x axis with HH:mm labels
                // so same-day clusters stay readable.
                pts = dated.map(d => {
                    const iso = new Date(d.t).toISOString();
                    return {
                        day: iso.slice(0, 10),
                        t: d.t,
                        p: d.p,
                        label: iso.slice(5, 16).replace('T', ' '),
                        tip: iso.slice(0, 16).replace('T', ' ') + ' UTC'
                    };
                });
            }
            if (pts.length >= 3) {
                const tMin = pts[0].t, tMax = pts[pts.length - 1].t;
                const pVals = pts.map(x => x.p);
                const pMin = Math.min(...pVals), pMax = Math.max(...pVals);
                const pPad = (pMax - pMin) * 0.12 || pMax * 0.05 || 1;
                const cur = summary.sold_currency;
                const nPts = pts.length;
                const minIdx = pVals.indexOf(pMin), maxIdx = pVals.indexOf(pMax);

                const W = 660, H = 200, L = 50, R = 16, T = 22, B = 30;
                const x = t => L + (tMax > tMin ? (t - tMin) / (tMax - tMin) : 0.5) * (W - L - R);
                const y = p => T + (1 - (p - pMin + pPad) / (pMax - pMin + 2 * pPad)) * (H - T - B);
                const line = pts.map((pt, i) => (i === 0 ? 'M' : 'L') + x(pt.t).toFixed(1) + ',' + y(pt.p).toFixed(1)).join(' ');
                const areaPath = line + ` L${x(tMax).toFixed(1)},${H - B} L${x(tMin).toFixed(1)},${H - B} Z`;

                // Y gridlines + price ticks (min / mid / max)
                const gridVals = [pMin, (pMin + pMax) / 2, pMax];
                const grid = gridVals.map(v => {
                    const gy = y(v).toFixed(1);
                    return `<line x1="${L}" y1="${gy}" x2="${W - R}" y2="${gy}" stroke="var(--xg-border, #e2e8f0)" stroke-width="1" stroke-dasharray="3,3"/>`
                         + `<text x="${L - 6}" y="${(+gy) + 3}" text-anchor="end" style="font-size: 10px; fill: var(--xg-muted, #64748b);">${fmtPrice(v, cur)}</text>`;
                }).join('');

                // X axis: up to 5 evenly-spaced date labels (MM-DD), skipping ones that would collide
                const nX = Math.min(5, nPts);
                const xIdx = Array.from({ length: nX }, (_, i) => Math.round(i * (nPts - 1) / Math.max(1, nX - 1)));
                const keptX = [];
                xIdx.forEach((idx, i) => {
                    if (i === 0 || i === xIdx.length - 1) { keptX.push(idx); return; }
                    const prevX = x(pts[keptX[keptX.length - 1]].t);
                    const nextX = x(pts[xIdx[xIdx.length - 1]].t);
                    if (x(pts[idx].t) - prevX >= 48 && nextX - x(pts[idx].t) >= 48) keptX.push(idx);
                });
                const xLabels = keptX.map(i => {
                    const pt = pts[i];
                    const anchor = i === 0 ? 'start' : (i === nPts - 1 ? 'end' : 'middle');
                    return `<text x="${x(pt.t).toFixed(1)}" y="${H - 8}" text-anchor="${anchor}" style="font-size: 10px; fill: var(--xg-muted, #64748b);">${pt.label}</text>`;
                }).join('');

                // Dots: every point; min/max emphasized
                const dots = pts.map((pt, i) => {
                    const isExtreme = (i === minIdx || i === maxIdx);
                    const fill = i === maxIdx ? '#059669' : (i === minIdx ? '#dc2626' : '#059669');
                    return `<circle cx="${x(pt.t).toFixed(1)}" cy="${y(pt.p).toFixed(1)}" r="${isExtreme ? 4 : 2.5}" fill="${fill}" stroke="#fff" stroke-width="1.2"><title>${pt.tip}: ${fmtPrice(pt.p, cur)}</title></circle>`;
                }).join('');

                // Price labels: first, last, min, max + every ~nth point so it stays readable.
                // Drop candidates within 40px of an already-kept label — dense
                // same-day clusters would otherwise stack into mush. First/last
                // are always kept even when tight.
                const labelEvery = Math.ceil(nPts / 8);
                const labelIdx = new Set([0, nPts - 1, minIdx, maxIdx]);
                for (let i = 0; i < nPts; i += labelEvery) labelIdx.add(i);
                const keptV = [];
                const keptVx = [];
                [...labelIdx].sort((a, b) => a - b).forEach(i => {
                    const px = x(pts[i].t);
                    if (i === 0 || keptVx.every(kx => Math.abs(px - kx) >= 40)) {
                        keptV.push(i); keptVx.push(px);
                    } else if (i === nPts - 1) {
                        // Last point must show — drop the colliding label instead
                        for (let k = keptV.length - 1; k > 0; k--) {
                            if (Math.abs(px - keptVx[k]) < 40) { keptV.splice(k, 1); keptVx.splice(k, 1); }
                        }
                        keptV.push(i); keptVx.push(px);
                    }
                });
                const valLabels = keptV.map(i => {
                    const pt = pts[i];
                    const anchor = i === 0 ? 'start' : (i === nPts - 1 ? 'end' : 'middle');
                    const isLast = i === nPts - 1;
                    return `<text x="${x(pt.t).toFixed(1)}" y="${(y(pt.p) - 8).toFixed(1)}" text-anchor="${anchor}" style="font-size: 10px; font-weight: ${isLast || i === minIdx || i === maxIdx ? 700 : 500}; fill: ${isLast ? '#059669' : 'var(--xg-muted, #64748b)'};">${fmtPrice(pt.p, cur)}</text>`;
                }).join('');

                // Trend: mean of newest half vs oldest half
                const half = Math.floor(pts.length / 2);
                const oldAvg = pts.slice(0, pts.length - half).reduce((a, b) => a + b.p, 0) / (pts.length - half);
                const newAvg = pts.slice(pts.length - half).reduce((a, b) => a + b.p, 0) / half;
                const pct = oldAvg > 0 ? ((newAvg - oldAvg) / oldAvg) * 100 : 0;
                const up = pct > 2, down = pct < -2;
                const trendColor = up ? '#059669' : (down ? '#dc2626' : '#64748b');
                const trendArrow = up ? '▲' : (down ? '▼' : '→');
                const spanDays = Math.max(1, Math.round((tMax - tMin) / 86400000));

                priceTrendHtml = `
                    <div style="margin-bottom: 16px; padding: 12px 16px; background: var(--xg-subtle); border-radius: 8px; border: 1px solid var(--xg-border);">
                        <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; flex-wrap: wrap; gap: 6px;">
                            <div style="font-size: 12px; font-weight: 600; color: var(--xg-text-2);">Sold Price Trend <span style="font-weight: 400; color: var(--xg-muted);">· ${nSales} sales, ${nDays} day${nDays === 1 ? '' : 's'}</span></div>
                            <div style="font-size: 12px; font-weight: 800; color: ${trendColor};">${trendArrow} ${pct > 0 ? '+' : ''}${pct.toFixed(1)}% <span style="font-weight: 500; color: var(--xg-muted);">last ${spanDays} day${spanDays === 1 ? '' : 's'}</span></div>
                        </div>
                        <svg viewBox="0 0 ${W} ${H}" style="width: 100%; height: auto; display: block;" role="img" aria-label="Sold price trend chart">
                            ${grid}
                            <path d="${areaPath}" fill="#059669" opacity="0.07"/>
                            <path d="${line}" fill="none" stroke="#059669" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" opacity="0.8"/>
                            ${dots}
                            ${valLabels}
                            ${xLabels}
                        </svg>
                    </div>`;
            }
        }

        // Explain the missing trend chart when sold rows exist but too few
        // carry a usable date+price to plot — never leave a silent gap.
        let trendNoteHtml = '';
        if (!priceTrendHtml && sold.length >= 3 && plottableSales < 3) {
            const noteMsg = plottableSales > 0
                ? `Only <strong>${plottableSales}</strong> sold comp${plottableSales === 1 ? '' : 's'} include a sale date — at least 3 are needed to draw a price trend chart.`
                : 'Sold comps are missing sale dates — a price trend chart can\'t be drawn yet.';
            trendNoteHtml = `
                <div style="margin-bottom: 16px; padding: 10px 16px; background: var(--xg-subtle); border-radius: 8px; border: 1px dashed var(--xg-border); font-size: 12px; color: var(--xg-muted);">
                    ${noteMsg}
                </div>`;
        }

        if (summaryItems.length > 0) {
            summaryHtml = `
                <div style="display: flex; flex-wrap: wrap; gap: 10px; align-items: center; margin-bottom: 16px; padding: 12px 16px; background: var(--xg-subtle); border-radius: 8px; border: 1px solid var(--xg-border);">
                    ${summaryItems.join('')}
                </div>`;
        }

        // Sold comps table (RapidAPI)
        let soldHtml = '';
        if (sold.length > 0) {
            const rows = sold.map((item, idx) => {
                // 130point/ParseBot rows often carry no itemUrl — link those to
                // an eBay sold-search on the comp title instead of a dead '#'
                // (which reloads this page); non-eBay rows show source as text.
                const srcName = item.source || 'sold comp';
                const href = item.itemUrl
                    || (/ebay/i.test(srcName)
                        ? `https://www.ebay.com/sch/i.html?_nkw=${encodeURIComponent(item.title || '')}&LH_Sold=1&LH_Complete=1`
                        : null);
                const srcCell = href
                    ? `<a href="${escapeHtml(href)}" target="_blank" rel="noopener" style="display: inline-flex; align-items: center; gap: 5px; background: #059669; color: white; padding: 4px 10px; border-radius: 6px; text-decoration: none; font-size: 12px; font-weight: 600;">Sold ↗</a>`
                    : `<span style="font-size: 11px; color: var(--xg-muted);">${escapeHtml(srcName)}</span>`;
                return `
                <tr class="${idx >= 3 ? 'xim-extra-row' : ''}" style="border-bottom: 1px solid var(--xg-border);" onmouseover="this.style.background='var(--xg-subtle)'" onmouseout="this.style.background='var(--xg-panel)'">
                    <td style="padding: 8px; vertical-align: top;" data-label="Listing">
                        <div style="font-size: 13px; color: var(--xg-text); font-weight: 500; line-height: 1.4; word-break: break-word;">${escapeHtml(item.title || 'Unknown')}</div>
                        ${item.soldDate ? `<div style="font-size: 11px; color: var(--xg-muted); margin-top: 2px;">Sold ${escapeHtml(item.soldDate)}</div>` : ''}
                    </td>
                    <td style="padding: 8px; white-space: nowrap; vertical-align: top;" data-label="Price">
                        <span style="font-size: 14px; font-weight: 600; color: #059669;">${fmtPrice(item.price, item.currency)}</span>
                    </td>
                    <td style="padding: 8px; white-space: nowrap; vertical-align: top;" data-label="Source">
                        ${srcCell}
                    </td>
                </tr>`;
            }).join('');
            const expandBtn = sold.length > 3 ? `
                <button type="button" class="xim-expand-btn" data-target="sold-tbody" style="width: 100%; margin-top: 8px; padding: 8px 12px; background: var(--xg-subtle); border: 1px solid var(--xg-border); border-radius: 8px; font-size: 12px; font-weight: 700; color: var(--xg-text); cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px;">
                    <span>View all ${sold.length} sold comps</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                </button>` : '';
            soldHtml = `
                <div style="margin-bottom: 20px;">
                    <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 10px;">
                        <span style="background: #059669; color: white; font-size: 11px; font-weight: 800; padding: 4px 8px; border-radius: 6px; letter-spacing: 0.04em;">SOLD</span>
                        <h4 style="font-size: 16px; font-weight: 800; color: var(--xg-text); margin: 0; letter-spacing: -0.01em;">Recent Sold Comps</h4>
                        <span style="font-size: 12px; color: var(--xg-muted); margin-left: 2px;">via eBay sold data</span>
                    </div>
                    <div class="ximilar-listings-table-wrap">
                        <table class="ximilar-listings-table" style="width: 100%; border-collapse: collapse; font-family: inherit; table-layout: fixed; font-size: 14px;">
                            <thead>
                                <tr style="background: var(--xg-subtle); border-bottom: 1px solid var(--xg-border);">
                                    <th style="padding: 8px; text-align: left; font-size: 12px; font-weight: 600; color: var(--xg-text-2);">Listing</th>
                                    <th style="padding: 8px; text-align: left; font-size: 12px; font-weight: 600; color: var(--xg-text-2); width: 90px;">Price</th>
                                    <th style="padding: 8px; text-align: left; font-size: 12px; font-weight: 600; color: var(--xg-text-2); width: 80px;">Source</th>
                                </tr>
                            </thead>
                            <tbody class="xim-expandable-rows" id="xim-sold-tbody">${rows}</tbody>
                        </table>
                    </div>
                    ${expandBtn}
                </div>`;
        } else if (hasData) {
            soldHtml = `<div style="margin-bottom: 16px; font-size: 13px; color: var(--xg-muted);">No recent sold listings found for this card — active listings are shown below.</div>`;
        }

        // Active listings table (eBay Browse API)
        let activeHtml = '';
        if (active.length > 0) {
            const rows = active.map((item, idx) => {
                const condTag = item.condition ? `<span style="background: var(--xg-subtle); color: var(--xg-muted); font-size: 10px; font-weight: 600; padding: 2px 6px; border-radius: 4px; margin-left: 4px;">${escapeHtml(item.condition)}</span>` : '';
                const buyOpts = (item.buyingOptions || []).map(o => {
                    const label = o === 'FIXED_PRICE' ? 'BIN' : (o === 'BEST_OFFER' ? 'OBO' : o);
                    return `<span style="background: var(--xg-warn-soft); color: var(--xg-warn-strong); font-size: 10px; font-weight: 600; padding: 2px 6px; border-radius: 4px; margin-left: 4px;">${label}</span>`;
                }).join('');
                return `
                    <tr class="${idx >= 3 ? 'xim-extra-row' : ''}" style="border-bottom: 1px solid var(--xg-border);" onmouseover="this.style.background='var(--xg-subtle)'" onmouseout="this.style.background='var(--xg-panel)'">
                        <td style="padding: 8px; vertical-align: top;" data-label="Listing">
                            <div style="font-size: 13px; color: var(--xg-text); font-weight: 500; line-height: 1.4; word-break: break-word;">${escapeHtml(item.title || 'Unknown')}</div>
                            <div style="margin-top: 3px;">${condTag}${buyOpts}</div>
                        </td>
                        <td style="padding: 8px; white-space: nowrap; vertical-align: top;" data-label="Price">
                            <span style="font-size: 14px; font-weight: 600; color: var(--xg-info);">${fmtPrice(item.price, item.currency)}</span>
                        </td>
                        <td style="padding: 8px; white-space: nowrap; vertical-align: top;" data-label="Source">
                            ${item.itemUrl
                                ? `<a href="${escapeHtml(item.itemUrl)}" target="_blank" rel="noopener" style="display: inline-flex; align-items: center; gap: 5px; background: #e53e3e; color: white; padding: 4px 10px; border-radius: 6px; text-decoration: none; font-size: 12px; font-weight: 600;">eBay ↗</a>`
                                : `<a href="https://www.ebay.com/sch/i.html?_nkw=${encodeURIComponent(item.title || '')}" target="_blank" rel="noopener" style="display: inline-flex; align-items: center; gap: 5px; background: #e53e3e; color: white; padding: 4px 10px; border-radius: 6px; text-decoration: none; font-size: 12px; font-weight: 600;">eBay ↗</a>`}
                        </td>
                    </tr>`;
            }).join('');
            const expandBtn = active.length > 3 ? `
                <button type="button" class="xim-expand-btn" data-target="active-tbody" style="width: 100%; margin-top: 8px; padding: 8px 12px; background: var(--xg-subtle); border: 1px solid var(--xg-border); border-radius: 8px; font-size: 12px; font-weight: 700; color: var(--xg-text); cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px;">
                    <span>View all ${active.length} active listings</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                </button>` : '';
            activeHtml = `
                <div>
                    <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 10px;">
                        <span style="background: #e53e3e; color: white; font-size: 11px; font-weight: 800; padding: 4px 8px; border-radius: 6px; letter-spacing: 0.04em;">ACTIVE</span>
                        <h4 style="font-size: 16px; font-weight: 800; color: var(--xg-text); margin: 0; letter-spacing: -0.01em;">Live eBay Listings</h4>
                        <span style="font-size: 12px; color: var(--xg-muted); margin-left: 2px;">via eBay Browse API</span>
                    </div>
                    <div class="ximilar-listings-table-wrap">
                        <table class="ximilar-listings-table" style="width: 100%; border-collapse: collapse; font-family: inherit; table-layout: fixed; font-size: 14px;">
                            <thead>
                                <tr style="background: var(--xg-subtle); border-bottom: 1px solid var(--xg-border);">
                                    <th style="padding: 8px; text-align: left; font-size: 12px; font-weight: 600; color: var(--xg-text-2);">Listing</th>
                                    <th style="padding: 8px; text-align: left; font-size: 12px; font-weight: 600; color: var(--xg-text-2); width: 90px;">Price</th>
                                    <th style="padding: 8px; text-align: left; font-size: 12px; font-weight: 600; color: var(--xg-text-2); width: 80px;">Source</th>
                                </tr>
                            </thead>
                            <tbody class="xim-expandable-rows" id="xim-active-tbody">${rows}</tbody>
                        </table>
                    </div>
                    ${expandBtn}
                </div>`;
        }

        // Vision-rescue listings table — eBay rows surfaced by the IQ pass's
        // image-derived query ladder when the identified card's own pool was
        // empty/doomed. Same row shape as active comps (title/price/itemUrl).
        let vrHtml = '';
        if (vrListings.length > 0) {
            const vrRows = vrListings.map((item, idx) => `
                <tr class="${idx >= 5 ? 'xim-extra-row' : ''}" style="border-bottom: 1px solid var(--xg-border);" onmouseover="this.style.background='var(--xg-subtle)'" onmouseout="this.style.background='var(--xg-panel)'">
                    <td style="padding: 8px; vertical-align: top;" data-label="Listing">
                        <div style="font-size: 13px; color: var(--xg-text); font-weight: 500; line-height: 1.4; word-break: break-word;">${escapeHtml(item.title || 'Unknown')}</div>
                    </td>
                    <td style="padding: 8px; white-space: nowrap; vertical-align: top;" data-label="Price">
                        <span style="font-size: 14px; font-weight: 600; color: var(--xg-info);">${fmtPrice(item.price, item.currency)}</span>
                    </td>
                    <td style="padding: 8px; white-space: nowrap; vertical-align: top;" data-label="Source">
                        ${item.itemUrl
                            ? `<a href="${escapeHtml(item.itemUrl)}" target="_blank" rel="noopener" style="display: inline-flex; align-items: center; gap: 5px; background: #e53e3e; color: white; padding: 4px 10px; border-radius: 6px; text-decoration: none; font-size: 12px; font-weight: 600;">eBay ↗</a>`
                            : `<a href="https://www.ebay.com/sch/i.html?_nkw=${encodeURIComponent(item.title || '')}" target="_blank" rel="noopener" style="display: inline-flex; align-items: center; gap: 5px; background: #e53e3e; color: white; padding: 4px 10px; border-radius: 6px; text-decoration: none; font-size: 12px; font-weight: 600;">eBay ↗</a>`}
                    </td>
                </tr>`).join('');
            const vrExpand = vrListings.length > 5 ? `
                <button type="button" class="xim-expand-btn" data-target="vr-tbody" style="width: 100%; margin-top: 8px; padding: 8px 12px; background: var(--xg-subtle); border: 1px solid var(--xg-border); border-radius: 8px; font-size: 12px; font-weight: 700; color: var(--xg-text); cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px;">
                    <span>View all ${vrListings.length} similar listings</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                </button>` : '';
            const vrCons = vr.consensus;
            const vrConsNote = vrCons && vrCons.differs_from_identified
                ? `<div style="margin-top: 8px; font-size: 11px; color: #92400e; line-height: 1.5;">These listings most often describe <strong>#${escapeHtml(String(vrCons.card_number))}${vrCons.year ? ' · ' + escapeHtml(String(vrCons.year)) : ''}</strong> (${vrCons.support} of ${vrCons.of_titles}).</div>`
                : '';
            vrHtml = `
                <div style="margin-top: 16px; padding: 14px 16px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px;">
                    <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 6px;">
                        <span style="background: #d97706; color: white; font-size: 11px; font-weight: 800; padding: 4px 8px; border-radius: 6px; letter-spacing: 0.04em;">VISUAL MATCH</span>
                        <h4 style="font-size: 16px; font-weight: 800; color: var(--xg-text); margin: 0; letter-spacing: -0.01em;">Similar Listings Found</h4>
                    </div>
                    <div style="font-size: 12px; color: #92400e; line-height: 1.5; margin-bottom: 10px;">
                        No verified comps matched the identified card. These listings were found by searching eBay from the card's <em>image</em> — they may be the same card under a different identification. Not used in pricing.
                    </div>
                    <div class="ximilar-listings-table-wrap">
                        <table class="ximilar-listings-table" style="width: 100%; border-collapse: collapse; font-family: inherit; table-layout: fixed; font-size: 14px;">
                            <thead>
                                <tr style="background: var(--xg-subtle); border-bottom: 1px solid var(--xg-border);">
                                    <th style="padding: 8px; text-align: left; font-size: 12px; font-weight: 600; color: var(--xg-text-2);">Listing</th>
                                    <th style="padding: 8px; text-align: left; font-size: 12px; font-weight: 600; color: var(--xg-text-2); width: 90px;">Price</th>
                                    <th style="padding: 8px; text-align: left; font-size: 12px; font-weight: 600; color: var(--xg-text-2); width: 80px;">Source</th>
                                </tr>
                            </thead>
                            <tbody class="xim-expandable-rows" id="xim-vr-tbody">${vrRows}</tbody>
                        </table>
                    </div>
                    ${vrExpand}
                    ${vrConsNote}
                </div>`;
        }

        // Error / no-data notice
        let noticeHtml = '';
        if (!hasData && compTerminalError !== null) {
            noticeHtml = `
                <div style="padding: 12px 16px; background: var(--xg-subtle); border-radius: 8px; color: var(--xg-muted); font-size: 13px; text-align: center;">
                    Pricing isn't available for this card.
                </div>`;
        } else if (!hasData && compErrors.length) {
            noticeHtml = `
                <div style="padding: 12px 16px; background: var(--xg-subtle); border-radius: 8px;">
                    <div style="color: var(--xg-text); font-size: 13px; font-weight: 700; text-align: center; margin-bottom: 4px;">Marketplace data temporarily unavailable</div>
                    <div style="color: var(--xg-muted); font-size: 12px; text-align: center;">Live pricing couldn't be loaded right now — check back shortly.</div>
                </div>`;
        } else if (!hasData) {
            noticeHtml = `
                <div style="padding: 12px 16px; background: var(--xg-subtle); border-radius: 8px; color: var(--xg-muted); font-size: 13px; text-align: center;">
                    No live comps found for this card. Try a more specific search.
                </div>`;
        }

        // Fair value + per-grade ladder (from HoodCar)
        let fairValueHtml = '';
        if (comps.fair_value != null) {
            const fvItems = [`<span style="background: var(--xg-good-soft); color: var(--xg-good-strong); padding: 4px 10px; border-radius: 20px; font-size: 12px; font-weight: 600;">Fair Value: ${fmtPrice(comps.fair_value, 'USD')}</span>`];
            if (comps.confidence != null) {
                const conf = typeof comps.confidence === 'number' ? Math.round(comps.confidence) + '%' : escapeHtml(String(comps.confidence));
                fvItems.push(`<span style="font-size: 12px; color: var(--xg-muted);">Confidence: <strong style="color: var(--xg-text);">${conf}</strong></span>`);
            }
            if (comps.verdict) {
                fvItems.push(`<span style="font-size: 12px; color: var(--xg-muted);">${escapeHtml(comps.verdict)}</span>`);
            }
            fairValueHtml = `
                <div style="display: flex; flex-wrap: wrap; gap: 10px; align-items: center; margin-bottom: 12px; padding: 12px 16px; background: var(--xg-good-soft); border-radius: 8px; border: 1px solid var(--xg-good-line);">
                    ${fvItems.join('')}
                </div>`;
        }

        // Per-grade price ladder (from HoodCar)
        let gradeLadderHtml = '';
        if (comps.per_grade_ladder && typeof comps.per_grade_ladder === 'object' && !Array.isArray(comps.per_grade_ladder)) {
            const ladderEntries = Object.entries(comps.per_grade_ladder).filter(([k, v]) => v != null && v > 0);
            if (ladderEntries.length > 0) {
                const ladderRows = ladderEntries.map(([grade, price]) => {
                    const isCurrent = record.grades && record.grades.final != null && Math.abs(parseFloat(grade) - parseFloat(record.grades.final)) < 0.01;
                    return `<div style="display: flex; justify-content: space-between; padding: 6px 12px; ${isCurrent ? 'background: var(--xg-good-soft); border-radius: 6px; font-weight: 700;' : ''}">
                        <span style="font-size: 13px; color: var(--xg-text-2);">${escapeHtml(grade)}</span>
                        <span style="font-size: 13px; font-weight: 600; color: var(--xg-text);">${fmtPrice(price, 'USD')}</span>
                        ${isCurrent ? '<span style="font-size: 10px; color: var(--xg-good-strong); font-weight: 700;">← YOUR GRADE</span>' : ''}
                    </div>`;
                }).join('');
                gradeLadderHtml = `
                    <div style="margin-bottom: 16px; padding: 12px 16px; background: var(--xg-subtle); border-radius: 8px; border: 1px solid var(--xg-border);">
                        <div style="font-size: 12px; font-weight: 600; color: var(--xg-text-2); margin-bottom: 8px;">Per-Grade Value Ladder</div>
                        ${ladderRows}
                    </div>`;
            }
        }

        // TCGplayer prices (from PkmnPrices)
        let tcgHtml = '';
        if (comps.tcgplayer_prices && typeof comps.tcgplayer_prices === 'object' && !isSports) {
            const tp = comps.tcgplayer_prices;
            const tpItems = [];
            if (tp.market_price != null) tpItems.push(['Market', fmtPrice(tp.market_price, 'USD')]);
            if (tp.low_price != null) tpItems.push(['Low', fmtPrice(tp.low_price, 'USD')]);
            if (tp.mid_price != null) tpItems.push(['Mid', fmtPrice(tp.mid_price, 'USD')]);
            if (tp.high_price != null) tpItems.push(['High', fmtPrice(tp.high_price, 'USD')]);
            if (tpItems.length > 0) {
                tcgHtml = `
                    <div style="margin-bottom: 16px; padding: 12px 16px; background: var(--xg-subtle); border-radius: 8px; border: 1px solid var(--xg-border);">
                        <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 8px;">
                            <span style="font-size: 12px; font-weight: 600; color: var(--xg-text-2);">TCGplayer Prices</span>
                        </div>
                        <div style="display: flex; flex-wrap: wrap; gap: 16px;">
                            ${tpItems.map(([label, val]) => `<div><span style="font-size: 11px; color: var(--xg-muted);">${label}</span><div style="font-size: 15px; font-weight: 700; color: var(--xg-text);">${val}</div></div>`).join('')}
                        </div>
                    </div>`;
            }
        }

        // Open TCG API prices (supplemental TCGPlayer prices — TCG cards only)
        if (comps.opentcg_prices && typeof comps.opentcg_prices === 'object' && !tcgHtml && !isSports) {
            const op = comps.opentcg_prices;
            const opItems = [];
            if (op.market_price != null) opItems.push(['Market', fmtPrice(op.market_price, 'USD')]);
            if (op.low_price != null) opItems.push(['Low', fmtPrice(op.low_price, 'USD')]);
            if (op.high_price != null) opItems.push(['High', fmtPrice(op.high_price, 'USD')]);
            if (opItems.length > 0) {
                tcgHtml = `
                    <div style="margin-bottom: 16px; padding: 12px 16px; background: var(--xg-subtle); border-radius: 8px; border: 1px solid var(--xg-border);">
                        <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 8px;">
                            <span style="font-size: 12px; font-weight: 600; color: var(--xg-text-2);">TCGplayer Prices (Open TCG API)</span>
                        </div>
                        <div style="display: flex; flex-wrap: wrap; gap: 16px;">
                            ${opItems.map(([label, val]) => `<div><span style="font-size: 11px; color: var(--xg-muted);">${label}</span><div style="font-size: 15px; font-weight: 700; color: var(--xg-text);">${val}</div></div>`).join('')}
                        </div>
                    </div>`;
            }
        }

        // TCGPriceLookup prices (backstop — TCG cards only)
        if (comps.tcgpl_prices && typeof comps.tcgpl_prices === 'object' && !tcgHtml && !isSports) {
            const tp = comps.tcgpl_prices;
            const tpItems = [];
            if (tp.market_price != null) tpItems.push(['Market', fmtPrice(tp.market_price, 'USD')]);
            if (tp.low_price != null) tpItems.push(['Low', fmtPrice(tp.low_price, 'USD')]);
            if (tpItems.length > 0) {
                tcgHtml = `
                    <div style="margin-bottom: 16px; padding: 12px 16px; background: var(--xg-subtle); border-radius: 8px; border: 1px solid var(--xg-border);">
                        <div style="font-size: 12px; font-weight: 600; color: var(--xg-text-2); margin-bottom: 8px;">TCGplayer Prices (TCGPriceLookup)</div>
                        <div style="display: flex; flex-wrap: wrap; gap: 16px;">
                            ${tpItems.map(([label, val]) => `<div><span style="font-size: 11px; color: var(--xg-muted);">${label}</span><div style="font-size: 15px; font-weight: 700; color: var(--xg-text);">${val}</div></div>`).join('')}
                        </div>
                    </div>`;
            }
        }

        // CardShowData market price + recommended price (TCG cards only)
        let csdMarketHtml = '';
        if (!isSports && (comps.cardshowdata_market != null || comps.cardshowdata_recommended != null)) {
            const csdItems = [];
            if (comps.cardshowdata_market != null) csdItems.push(['Market', fmtPrice(comps.cardshowdata_market, 'USD')]);
            if (comps.cardshowdata_recommended != null) csdItems.push(['Recommended', fmtPrice(comps.cardshowdata_recommended, 'USD')]);
            csdMarketHtml = `
                <div style="margin-bottom: 16px; padding: 12px 16px; background: var(--xg-subtle); border-radius: 8px; border: 1px solid var(--xg-border);">
                    <div style="font-size: 12px; font-weight: 600; color: var(--xg-text-2); margin-bottom: 8px;">CardShowData Market</div>
                    <div style="display: flex; flex-wrap: wrap; gap: 16px;">
                        ${csdItems.map(([label, val]) => `<div><span style="font-size: 11px; color: var(--xg-muted);">${label}</span><div style="font-size: 15px; font-weight: 700; color: var(--xg-text);">${val}</div></div>`).join('')}
                    </div>
                </div>`;
        }

        // Graded prices (CardShowData/TCGPriceLookup for TCG; HoodCar ladder or sold-comp bucketing for sports)
        let gradedPricesHtml = '';
        const gradedPrices = (comps.graded_prices || comps.tcgpl_graded_prices) || null;
        if (gradedPrices && typeof gradedPrices === 'object') {
            const graderEntries = Object.entries(gradedPrices);
            if (graderEntries.length > 0) {
                const graderRows = graderEntries.map(([grader, grades]) => {
                    if (!grades || typeof grades !== 'object') return '';
                    const gradeEntries = Object.entries(grades).filter(([g, v]) => v != null && v > 0)
                        .sort(([a], [b]) => parseFloat(a) - parseFloat(b));
                    if (gradeEntries.length === 0) return '';
                    const gradeChips = gradeEntries.map(([grade, price]) => {
                        const isCurrent = record.grades && record.grades.final != null && Math.abs(parseFloat(grade) - parseFloat(record.grades.final)) < 0.01;
                        return `<span style="display:inline-flex;align-items:center;white-space:nowrap;background:${isCurrent ? 'var(--xg-good-soft)' : 'var(--xg-panel)'};padding:3px 10px;border-radius:100px;font-size:11px;${isCurrent ? 'font-weight:700;color:var(--xg-good-strong);' : 'color:var(--xg-text-2);'}">${escapeHtml(grade)}:&nbsp;${fmtPrice(price, 'USD')}${isCurrent ? ' ←' : ''}</span>`;
                    }).join('');
                    return `<div style="display:flex;align-items:flex-start;gap:8px;margin-bottom:8px;"><span style="flex:0 0 40px;padding-top:3px;font-size:11px;font-weight:600;color:var(--xg-muted);text-transform:uppercase;">${escapeHtml(grader)}</span><span style="display:flex;flex-wrap:wrap;gap:4px;">${gradeChips}</span></div>`;
                }).join('');
                if (graderRows) {
                    gradedPricesHtml = `
                        <div style="margin-bottom: 16px; padding: 12px 16px; background: var(--xg-subtle); border-radius: 8px; border: 1px solid var(--xg-border);">
                            <div style="font-size: 12px; font-weight: 600; color: var(--xg-text-2); margin-bottom: 8px;">Graded Prices (by Grader)</div>
                            ${graderRows}
                        </div>`;
                }
            }
        }

        // Population report (from CardShowData — TCG only today, but render whenever data exists)
        let populationHtml = '';
        if (comps.population_report && typeof comps.population_report === 'object') {
            const popEntries = Object.entries(comps.population_report);
            if (popEntries.length > 0) {
                const popRows = popEntries.map(([grader, grades]) => {
                    if (!grades || typeof grades !== 'object') return '';
                    const total = grades.total || 0;
                    if (total === 0) return '';
                    const gradeCounts = Object.entries(grades)
                        .filter(([g, v]) => g !== 'total' && g !== 'as_of' && v != null && v > 0)
                        .sort((a, b) => parseFloat(b[0]) - parseFloat(a[0]))
                        .slice(0, 5);
                    const gradeChips = gradeCounts.map(([grade, count]) => {
                        const isCurrent = record.grades && record.grades.final != null && Math.abs(parseFloat(grade) - parseFloat(record.grades.final)) < 0.01;
                        return `<span style="background: ${isCurrent ? 'var(--xg-good-soft)' : 'var(--xg-subtle)'}; padding: 3px 8px; border-radius: 100px; font-size: 11px; ${isCurrent ? 'font-weight: 700; color: var(--xg-good-strong);' : 'color: var(--xg-text-2);'}">${escapeHtml(grade)}: ${count}${isCurrent ? ' ←' : ''}</span>`;
                    }).join(' ');
                    return `<div style="margin-bottom: 6px;"><span style="font-size: 11px; font-weight: 600; color: var(--xg-muted); text-transform: uppercase; margin-right: 6px;">${escapeHtml(grader)}</span><span style="font-size: 11px; color: var(--xg-muted); margin-right: 8px;">Total: ${total}</span>${gradeChips}</div>`;
                }).join('');
                if (popRows) {
                    populationHtml = `
                        <div style="margin-bottom: 16px; padding: 12px 16px; background: var(--xg-subtle); border-radius: 8px; border: 1px solid var(--xg-border);">
                            <div style="font-size: 12px; font-weight: 600; color: var(--xg-text-2); margin-bottom: 8px;">Grading Population Report</div>
                            ${popRows}
                        </div>`;
                }
            }
        }

        // EU Cardmarket prices (EUR)
        let euHtml = '';
        if (comps.cardmarket_floor_eur != null || comps.cardmarket_trend_eur != null || comps.cardmarket_avg7_eur != null) {
            const euItems = [];
            if (comps.cardmarket_floor_eur != null) euItems.push(['Floor', fmtPrice(comps.cardmarket_floor_eur, 'EUR')]);
            if (comps.cardmarket_trend_eur != null) euItems.push(['Trend', fmtPrice(comps.cardmarket_trend_eur, 'EUR')]);
            if (comps.cardmarket_avg7_eur != null) euItems.push(['7d Avg', fmtPrice(comps.cardmarket_avg7_eur, 'EUR')]);
            if (comps.cardmarket_avg30_eur != null) euItems.push(['30d Avg', fmtPrice(comps.cardmarket_avg30_eur, 'EUR')]);
            euHtml = `
                <div style="margin-bottom: 16px; padding: 12px 16px; background: var(--xg-subtle); border-radius: 8px; border: 1px solid var(--xg-border);">
                    <div style="font-size: 12px; font-weight: 600; color: var(--xg-text-2); margin-bottom: 8px;">EU Cardmarket Prices (EUR)</div>
                    <div style="display: flex; flex-wrap: wrap; gap: 16px;">
                        ${euItems.map(([label, val]) => `<div><span style="font-size: 11px; color: var(--xg-muted);">${label}</span><div style="font-size: 15px; font-weight: 700; color: var(--xg-text);">${val}</div></div>`).join('')}
                    </div>
                </div>`;
        }

        // CardSight AI stats
        let cardSightHtml = '';
        // Only show if there are actual sales — backend may set 0 when no sales found
        const cardSightHasData = comps.cardsight_count != null && comps.cardsight_count > 0;
        if (cardSightHasData && (comps.cardsight_median != null || comps.cardsight_avg != null)) {
            const csItems = [];
            if (comps.cardsight_median != null) csItems.push(['Median', fmtPrice(comps.cardsight_median, 'USD')]);
            if (comps.cardsight_avg != null) csItems.push(['Avg', fmtPrice(comps.cardsight_avg, 'USD')]);
            if (comps.cardsight_low != null) csItems.push(['Low', fmtPrice(comps.cardsight_low, 'USD')]);
            if (comps.cardsight_high != null) csItems.push(['High', fmtPrice(comps.cardsight_high, 'USD')]);
            if (comps.cardsight_count != null) csItems.push(['Sales', comps.cardsight_count]);
            cardSightHtml = `
                <div style="margin-bottom: 16px; padding: 12px 16px; background: var(--xg-subtle); border-radius: 8px; border: 1px solid var(--xg-border);">
                    <div style="font-size: 12px; font-weight: 600; color: var(--xg-text-2); margin-bottom: 8px;">CardSight AI (eBay + Fanatics + COMC)</div>
                    <div style="display: flex; flex-wrap: wrap; gap: 16px;">
                        ${csItems.map(([label, val]) => `<div><span style="font-size: 11px; color: var(--xg-muted);">${label}</span><div style="font-size: 15px; font-weight: 700; color: var(--xg-text);">${val}</div></div>`).join('')}
                    </div>
                </div>`;
        }

        // 130point multi-marketplace stats (Goldin+Heritage+MySlabs+Pristine+Fanatics Collect)
        let parsebotHtml = '';
        // Only show if there are actual sales — backend sets parsebot_* to 0
        // (not null) when 130point found no sales, so != null isn't enough.
        const parsebotHasData = comps.parsebot_count != null && comps.parsebot_count > 0;
        if (parsebotHasData && (comps.parsebot_median != null || comps.parsebot_avg != null)) {
            const pbItems = [];
            if (comps.parsebot_median != null) pbItems.push(['Median', fmtPrice(comps.parsebot_median, 'USD')]);
            if (comps.parsebot_avg != null) pbItems.push(['Avg', fmtPrice(comps.parsebot_avg, 'USD')]);
            if (comps.parsebot_low != null) pbItems.push(['Low', fmtPrice(comps.parsebot_low, 'USD')]);
            if (comps.parsebot_high != null) pbItems.push(['High', fmtPrice(comps.parsebot_high, 'USD')]);
            if (comps.parsebot_count != null) pbItems.push(['Sales', comps.parsebot_count]);
            if (comps.parsebot_trend) {
                const trendColor = comps.parsebot_trend === 'up' ? 'var(--xg-good-strong)' : (comps.parsebot_trend === 'down' ? 'var(--xg-bad)' : 'var(--xg-muted)');
                const trendPct = comps.parsebot_trend_pct != null ? ' (' + (comps.parsebot_trend_pct > 0 ? '+' : '') + comps.parsebot_trend_pct + '%)' : '';
                pbItems.push(['Trend', `<span style="color: ${trendColor};">${comps.parsebot_trend}${trendPct}</span>`]);
            }
            parsebotHtml = `
                <div style="margin-bottom: 16px; padding: 12px 16px; background: var(--xg-subtle); border-radius: 8px; border: 1px solid var(--xg-border);">
                    <div style="font-size: 12px; font-weight: 600; color: var(--xg-text-2); margin-bottom: 8px;">130point (Goldin + Heritage + MySlabs + Fanatics Collect)</div>
                    <div style="display: flex; flex-wrap: wrap; gap: 16px;">
                        ${pbItems.map(([label, val]) => `<div><span style="font-size: 11px; color: var(--xg-muted);">${label}</span><div style="font-size: 15px; font-weight: 700; color: var(--xg-text);">${val}</div></div>`).join('')}
                    </div>
                </div>`;
        }

        // PSA Population Report (via ParseBot) — grade distribution ladder.
        // Card-level data (by_grade/total) renders the full ladder; set-level
        // only (set_total) renders the compact fallback since per-card rarity
        // is the interesting part.
        let psaPopHtml = '';
        const psaByGrade = (comps.psa_population_by_grade && typeof comps.psa_population_by_grade === 'object')
            ? comps.psa_population_by_grade : null;
        const psaTotal = (comps.psa_population_total != null && comps.psa_population_total > 0)
            ? comps.psa_population_total : (psaByGrade ? Object.values(psaByGrade).reduce((s, v) => s + (Number(v) || 0), 0) : null);
        if (psaTotal != null && psaTotal > 0 && psaByGrade) {
            // Merge price sources per grade: actual sold (graded_prices) beats
            // the FMV guide. Guide keys are PSA labels ("NM-MT 8") → parse the
            // trailing number.
            const priceByGrade = {};
            if (comps.psa_price_guide && typeof comps.psa_price_guide === 'object') {
                Object.entries(comps.psa_price_guide).forEach(([label, v]) => {
                    const m = String(label).match(/(\d+(?:\.\d+)?)\s*$/);
                    if (m && v != null) priceByGrade[m[1]] = v;
                });
            }
            if (comps.graded_prices && comps.graded_prices.psa && typeof comps.graded_prices.psa === 'object') {
                Object.entries(comps.graded_prices.psa).forEach(([g, v]) => { if (v != null) priceByGrade[String(parseFloat(g))] = v; });
            }

            const aiGrade = (record.grades && record.grades.final != null) ? parseFloat(record.grades.final) : null;
            const estBucket = aiGrade != null ? Math.max(1, Math.min(10, Math.round(aiGrade))) : null;
            const maxCount = Math.max(1, ...Object.values(psaByGrade).map(v => Number(v) || 0));
            const gem = Number(psaByGrade['10']) || 0;
            const gemRate = (gem / psaTotal) * 100;
            let modalG = null, modalN = 0;
            Object.entries(psaByGrade).forEach(([g, n]) => { n = Number(n) || 0; if (n > modalN) { modalN = n; modalG = g; } });
            const popAbove = estBucket != null
                ? Object.entries(psaByGrade).reduce((s, [g, n]) => s + (parseFloat(g) > estBucket ? (Number(n) || 0) : 0), 0)
                : null;

            const subject = [comps.psa_population_subject, comps.psa_population_card_number ? '#' + escapeHtml(String(comps.psa_population_card_number)) : null].filter(Boolean).join(' ');
            const subLine = [comps.psa_population_set ? escapeHtml(comps.psa_population_set) : null,
                comps.psa_population_set_total != null ? comps.psa_population_set_total.toLocaleString() + ' set graded' : null].filter(Boolean).join(' · ');

            const statCell = (label, val) => `<div><div style="font-size:10px;color:var(--xg-muted);text-transform:uppercase;letter-spacing:0.4px;">${label}</div><div style="font-size:16px;font-weight:800;color:var(--xg-text);line-height:1.3;">${val}</div></div>`;
            const statsRow = `<div style="display:flex;flex-wrap:wrap;gap:20px;margin-bottom:10px;">
                ${statCell('Total graded', psaTotal.toLocaleString())}
                ${statCell('PSA 10s', `<span style="color:var(--xg-good-strong);">${gem.toLocaleString()}</span> <span style="font-size:11px;font-weight:500;color:var(--xg-muted);">${gemRate.toFixed(1)}%</span>`)}
                ${modalG != null ? statCell('Most common', `PSA ${escapeHtml(modalG)} — ${modalN.toLocaleString()}`) : ''}
                ${popAbove != null ? statCell('Grading higher', (popAbove / psaTotal * 100).toFixed(0) + '%') : ''}
            </div>`;

            // Ladder rows, 10→1; show every non-zero grade plus the AI bucket.
            const ladderRows = [];
            for (let g = 10; g >= 1; g--) {
                const n = Number(psaByGrade[String(g)]) || 0;
                const isEst = estBucket === g;
                if (n === 0 && !isEst) continue;
                const pct = (n / maxCount) * 100;
                const share = (n / psaTotal) * 100;
                const barColor = g === 10 ? 'var(--xg-good-strong)' : (g >= 8 ? 'var(--xg-info)' : (g >= 6 ? 'var(--xg-muted)' : 'var(--xg-bad)'));
                const price = priceByGrade[String(g)];
                ladderRows.push(`
                    <div class="xg-pop-row" style="display:grid;grid-template-columns:34px 1fr 46px 48px 62px;gap:8px;align-items:center;font-size:11px;${isEst ? 'background:var(--xg-info-soft);border-radius:6px;padding:2px 4px;margin:0 -4px;' : 'padding:2px 4px;'}">
                        <div style="font-weight:700;color:${isEst ? 'var(--xg-info)' : 'var(--xg-text-2)'};">${g}${isEst ? ' ←' : ''}</div>
                        <div style="background:var(--xg-border);border-radius:3px;height:9px;overflow:hidden;"><div style="height:100%;width:${Math.max(pct, n > 0 ? 1.5 : 0).toFixed(1)}%;background:${barColor};border-radius:3px;"></div></div>
                        <div style="text-align:right;font-weight:600;color:var(--xg-text);">${n > 0 ? n.toLocaleString() : '—'}</div>
                        <div style="text-align:right;color:var(--xg-muted);">${n > 0 ? share.toFixed(1) + '%' : ''}</div>
                        <div style="text-align:right;font-weight:600;color:var(--xg-text);">${price != null ? fmtPrice(price, 'USD') : ''}</div>
                    </div>`);
            }
            const ladderHeader = `
                <div class="xg-pop-row" style="display:grid;grid-template-columns:34px 1fr 46px 48px 62px;gap:8px;font-size:10px;color:var(--xg-muted);text-transform:uppercase;letter-spacing:0.4px;padding:0 4px 3px;border-bottom:1px solid var(--xg-border);white-space:nowrap;">
                    <div class="xg-pop-hd-grade">Grade</div><div>Dist.</div><div style="text-align:right;">Pop</div><div style="text-align:right;">Share</div><div style="text-align:right;">Value</div>
                </div>`;

            const estNote = aiGrade != null && popAbove != null
                ? `<div style="margin-top:10px;font-size:11px;color:var(--xg-muted);line-height:1.5;">Your card's AI estimate is <strong style="color:var(--xg-text);">${aiGrade}</strong> — ${popAbove > 0 ? `<strong style="color:var(--xg-text);">${popAbove.toLocaleString()}</strong> of ${psaTotal.toLocaleString()} graded copies sit higher` : 'no graded copy on record sits higher'}.</div>`
                : '';

            psaPopHtml = `
                <div style="margin-bottom: 16px; padding: 14px 16px; background: var(--xg-subtle); border-radius: 8px; border: 1px solid var(--xg-border);">
                    <div style="display:flex;justify-content:space-between;align-items:baseline;flex-wrap:wrap;gap:4px;margin-bottom:10px;">
                        <div style="font-size:12px;font-weight:600;color:var(--xg-text-2);">PSA Population${subject ? ' — ' + subject : ''}</div>
                        ${subLine ? `<div style="font-size:11px;color:var(--xg-muted);">${subLine}</div>` : ''}
                    </div>
                    ${statsRow}
                    ${ladderHeader}
                    ${ladderRows.join('')}
                    ${estNote}
                    <div style="margin-top:6px;font-size:10px;color:var(--xg-muted);">Value = actual sold where available, else PSA price guide.</div>
                </div>`;
        } else if (comps.psa_population_set_total != null) {
            // Set-level fallback — no per-card rarity, so keep it compact.
            let psaPriceRow = '';
            if (comps.psa_price_guide && typeof comps.psa_price_guide === 'object') {
                const priceEntries = Object.entries(comps.psa_price_guide).filter(([g, v]) => v != null);
                if (priceEntries.length > 0) {
                    psaPriceRow = `<div style="display:flex;flex-wrap:wrap;gap:14px;margin-top:8px;padding-top:8px;border-top:1px solid var(--xg-border);">
                        ${priceEntries.map(([grade, value]) => `<div><span style="font-size:11px;color:var(--xg-muted);">${escapeHtml(grade)}</span><div style="font-size:14px;font-weight:700;color:var(--xg-text);">${fmtPrice(value, 'USD')}</div></div>`).join('')}
                    </div>`;
                }
            }
            psaPopHtml = `
                <div style="margin-bottom: 16px; padding: 12px 16px; background: var(--xg-subtle); border-radius: 8px; border: 1px solid var(--xg-border);">
                    <div style="display:flex;justify-content:space-between;align-items:baseline;flex-wrap:wrap;gap:4px;">
                        <div style="font-size:12px;font-weight:600;color:var(--xg-text-2);">PSA Population${comps.psa_population_set ? ' — ' + escapeHtml(comps.psa_population_set) : ''}</div>
                        <div style="font-size:11px;color:var(--xg-muted);">set-level — per-card counts unavailable</div>
                    </div>
                    <div style="margin-top:6px;"><span style="font-size:16px;font-weight:800;color:var(--xg-text);">${comps.psa_population_set_total.toLocaleString()}</span> <span style="font-size:11px;color:var(--xg-muted);">copies graded across the set</span></div>
                    ${psaPriceRow}
                </div>`;
        }

        // Price trends (from CardShowData)
        let trendsHtml = '';
        if (comps.price_trends && typeof comps.price_trends === 'object') {            const trendEntries = Object.entries(comps.price_trends).filter(([k, v]) => v && typeof v === 'object');
            if (trendEntries.length > 0) {
                const trendChips = trendEntries.map(([period, data]) => {
                    const pct = data.pct != null ? (data.pct > 0 ? '+' : '') + data.pct + '%' : null;
                    const color = data.pct != null ? (data.pct > 0 ? 'var(--xg-good-strong)' : 'var(--xg-bad)') : 'var(--xg-muted)';                    return `<span style="font-size: 11px; color: var(--xg-muted);">${escapeHtml(period)}: <strong style="color: ${color};">${pct || 'N/A'}</strong></span>`;
                }).join(' ');
                trendsHtml = `
                    <div style="margin-bottom: 16px; padding: 12px 16px; background: var(--xg-subtle); border-radius: 8px; border: 1px solid var(--xg-border);">
                        <div style="font-size: 12px; font-weight: 600; color: var(--xg-text-2); margin-bottom: 8px;">Price Trends</div>
                        <div style="display: flex; flex-wrap: wrap; gap: 12px;">${trendChips}</div>
                    </div>`;
            }
        }

        // Identity-doomed: the identified card's own pool hard-rejected it, so
        // pricing was withheld upstream. Show the verification notice (plus any
        // image-derived similar listings) — never the generic outage copy and
        // never the rejected pool.
        if (identityDoomed) {
            const _doom = (comps.identity_doomed && typeof comps.identity_doomed === 'object') ? comps.identity_doomed : {};
            const _unmatched = parseInt(_doom.unmatched, 10) || 0;
            const doomNote = _unmatched > 0
                ? ` ${_unmatched} marketplace listings were checked against this identification and none matched.`
                : '';
            return `
        <div class="ximilar-result-card" style="
            background: var(--xg-panel); border-radius: 8px; padding: 20px;
            box-shadow: var(--xg-shadow); margin-bottom: 16px; border: 1px solid var(--xg-border);
        ">
            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 16px;">
                <div style="width: 44px; height: 44px; background: var(--xg-warn-soft); border: 1.5px solid var(--xg-warn); border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 22px; flex-shrink: 0;">⚠</div>
                <div>
                    <h3 style="font-size: 19px; font-weight: 800; color: var(--xg-text); margin: 0; line-height: 1.25; letter-spacing: -0.01em;">Live Market Comps</h3>
                    <p style="font-size: 12px; color: var(--xg-muted); margin: 2px 0 0 0; font-weight: 500;">Recent sales &amp; marketplace listings</p>
                </div>
            </div>
            <div style="padding: 12px 16px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px;">
                <div style="color: #92400e; font-size: 13px; font-weight: 700; margin-bottom: 4px;">Card identification needs verification.</div>
                <div style="color: var(--xg-muted); font-size: 12px; line-height: 1.5;">Market data could not be matched to this identification, so pricing has been withheld.${escapeHtml(doomNote)}</div>
            </div>
            ${vrHtml}
        </div>`;
        }

        // Source attribution — provider plumbing names stay internal
        const sourceLabel = 'Recent sales & marketplace listings';

        return `
        <div class="ximilar-result-card" style="
            background: var(--xg-panel); border-radius: 8px; padding: 20px;
            box-shadow: var(--xg-shadow); margin-bottom: 16px; border: 1px solid var(--xg-border);
        ">
            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 16px;">
                <div style="width: 44px; height: 44px; background: var(--xg-info-soft); border: 1.5px solid var(--xg-info-line); border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 22px; flex-shrink: 0; box-shadow: 0 2px 6px rgba(14,165,233,0.15);">📊</div>
                <div>
                    <h3 style="font-size: 19px; font-weight: 800; color: var(--xg-text); margin: 0; line-height: 1.25; letter-spacing: -0.01em;">Live Market Comps</h3>
                    <p style="font-size: 12px; color: var(--xg-muted); margin: 2px 0 0 0; font-weight: 500;">${escapeHtml(sourceLabel)}</p>
                </div>
            </div>
            ${fairValueHtml}
            ${gradeLadderHtml}
            ${tcgHtml}
            ${csdMarketHtml}
            ${gradedPricesHtml}
            ${populationHtml}
            ${euHtml}
            ${cardSightHtml}
            ${parsebotHtml}
            ${psaPopHtml}
            ${trendsHtml}
            ${summaryHtml}
            ${priceChartHtml}
            ${priceTrendHtml}
            ${trendNoteHtml}
            ${soldHtml}
            ${activeHtml}
            ${vrHtml}
            ${marketplaceListingsHtml}
            ${noticeHtml}
        </div>`;
    }

    // ── Graded Card Market — dedicated multi-source slab section ─────────
    // Aggregates every already-fetched provider extra stored on live_comps:
    // per-grader price ladders (CardLadder/SCP/CSD/TCGPL/Scrydex/HoodCar),
    // population reports (PSA, Scrydex pop_reports, CSD multi-grader,
    // CardLadder cl_pop), raw condition curves (TCGPL/CSD/Scrydex), trend
    // deltas, sell-channel guidance, recent graded sales + live listings,
    // and deterministic commentary bullets. Stored data only — no
    // render-time API calls.
    function renderGradedMarket(record) {
        const comps = record.live_comps;
        if (!comps || typeof comps !== 'object') return '';
        const { tcgCard } = _resolveTcgEnrichment(record);
        const userGrade = (record.grades && record.grades.final != null) ? parseFloat(record.grades.final) : null;
        const srcSet = new Set();
        const parts = [];

        const GRADER_ORDER = ['PSA', 'BGS', 'CGC', 'SGC', 'ACE', 'AGS', 'TAG', 'PGC'];
        const graderRank = g => {
            const i = GRADER_ORDER.indexOf(String(g).toUpperCase());
            return i === -1 ? GRADER_ORDER.length : i;
        };
        const gradeNum = g => { const n = parseFloat(g); return isNaN(n) ? -1 : n; };
        const num = v => (v == null || isNaN(v)) ? null : Number(v);
        const fmtM = (v, cur) => {
            if (v == null || isNaN(v)) return '—';
            const sym = (cur === 'USD' || !cur) ? '$' : (cur === 'EUR' ? '€' : (cur === 'CAD' ? 'C$' : cur + ' '));
            return sym + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        };
        const sec = (label, body) =>
            `<div style="font-size:10px;font-weight:700;color:var(--xg-muted);text-transform:uppercase;letter-spacing:0.06em;margin-bottom:6px">${label}</div>${body}`;
        const box = inner =>
            `<div style="background:var(--xg-panel);border:1px solid var(--xg-border);border-radius:8px;padding:10px 12px;margin-bottom:10px">${inner}</div>`;

        // ── 1. Unified per-grader price ladder ────────────────────────────
        // graderMap[grader][grade] = {price, src} — first non-null wins.
        const graderMap = {};
        const addGraded = (grader, grade, price, src) => {
            if (!grader || grade == null || num(price) == null) return;
            const g = String(grader).toUpperCase();
            const gr = String(grade);
            if (!graderMap[g]) graderMap[g] = {};
            if (graderMap[g][gr] == null) { graderMap[g][gr] = num(price); srcSet.add(src); }
        };
        if (comps.graded_prices && typeof comps.graded_prices === 'object') {
            for (const [g, grades] of Object.entries(comps.graded_prices)) {
                if (!grades || typeof grades !== 'object') continue;
                for (const [gr, v] of Object.entries(grades)) addGraded(g, gr, v, 'graded comps');
            }
        }
        if (comps.tcgpl_graded_windows && typeof comps.tcgpl_graded_windows === 'object') {
            for (const [g, grades] of Object.entries(comps.tcgpl_graded_windows)) {
                if (!grades || typeof grades !== 'object') continue;
                for (const [gr, w] of Object.entries(grades)) {
                    if (!w || typeof w !== 'object') continue;
                    addGraded(g, gr, (w.ebay && w.ebay.avg_7d) || (w.tcgplayer && w.tcgplayer.market), 'TCGPriceLookup');
                }
            }
        }
        if (comps.csd_graded_detail && typeof comps.csd_graded_detail === 'object') {
            for (const [g, detail] of Object.entries(comps.csd_graded_detail)) {
                if (!detail || typeof detail !== 'object') continue;
                const rp = detail.reference_prices;
                if (rp && typeof rp === 'object') {
                    for (const [gr, v] of Object.entries(rp)) {
                        const p = (v && typeof v === 'object')
                            ? (v.median_cents || v.market_cents || v.price_cents ? v[v.median_cents != null ? 'median_cents' : (v.market_cents != null ? 'market_cents' : 'price_cents')] / 100 : null)
                            : v;
                        addGraded(g, gr, p, 'CardShowData');
                    }
                }
            }
        }
        // /v1/grading deep-call payload — shape varies; walk grader→grade→
        // {median|market|price|high} structures defensively.
        if (comps.csd_grading && typeof comps.csd_grading === 'object') {
            const pool = comps.csd_grading.graders || comps.csd_grading.prices || comps.csd_grading;
            if (pool && typeof pool === 'object') {
                for (const [g, grades] of Object.entries(pool)) {
                    if (!grades || typeof grades !== 'object') continue;
                    for (const [gr, v] of Object.entries(grades)) {
                        const p = (v && typeof v === 'object')
                            ? ((function(_o){return _o != null ? _o : v.median_cents != null ? v.median_cents / 100 : null})((function(_o){return _o != null ? _o : v.high})((function(_o){return _o != null ? _o : v.price})((v.median != null ? v.median : v.market)))))
                            : v;
                        addGraded(g, gr, p, 'CardShowData');
                    }
                }
            }
        }
        if (tcgCard && Array.isArray(tcgCard.variants)) {
            let primary = tcgCard.variants[0] || {};
            for (const v of tcgCard.variants) {
                if ((v.prices || []).length > (primary.prices || []).length) primary = v;
            }
            for (const p of (primary.prices || [])) {
                if (p && p.type === 'graded') addGraded(p.company, p.grade, p.market, 'Scrydex');
            }
        }
        // HoodCar per-grade ladder — keys are either "PSA 8" (grader-prefixed)
        // or bare grades; unlabeled keys land on the market-estimate row.
        if (comps.per_grade_ladder && typeof comps.per_grade_ladder === 'object') {
            for (const [k, v] of Object.entries(comps.per_grade_ladder)) {
                const m = String(k).match(/^([A-Za-z]{2,4})\s+(.+)$/);
                if (m && GRADER_ORDER.includes(m[1].toUpperCase())) {
                    addGraded(m[1], m[2], v, 'HoodCar');
                } else {
                    addGraded('Market', k, v, 'HoodCar');
                }
            }
        }

        const graderNames = Object.keys(graderMap).sort((a, b) => graderRank(a) - graderRank(b) || a.localeCompare(b));
        const allGrades = [...new Set(graderNames.flatMap(g => Object.keys(graderMap[g])))]
            .sort((a, b) => gradeNum(b) - gradeNum(a) || a.localeCompare(b));
        let ladderHtml = '';
        if (graderNames.length && allGrades.length) {
            // Real table — the site's mobile CSS collapses any *inline*
            // grid-template-columns to 1fr; a table keeps the matrix and the
            // overflow-x wrapper scrolls when there are many grade columns.
            const headCells = allGrades.map(gr => {
                const isCur = userGrade != null && Math.abs(gradeNum(gr) - userGrade) < 0.01;
                return `<th style="text-align:center;font-size:10px;font-weight:700;color:${isCur ? '#b45309' : 'var(--xg-muted)'};padding:2px 4px;white-space:nowrap">${escapeHtml(gr)}${isCur ? ' ⭐' : ''}</th>`;
            }).join('');
            const rows = graderNames.map(g => {
                const cells = allGrades.map(gr => {
                    const v = graderMap[g][gr];
                    const isCur = userGrade != null && Math.abs(gradeNum(gr) - userGrade) < 0.01;
                    const is10 = gradeNum(gr) === 10;
                    return `<td style="text-align:center;font-size:12px;font-weight:${v != null ? 800 : 400};color:${v != null ? (is10 ? '#b45309' : 'var(--xg-text)') : 'var(--xg-border)'};background:${isCur ? '#fef3c7' : 'transparent'};border-radius:4px;padding:4px 3px;white-space:nowrap">${v != null ? fmtM(v) : '·'}</td>`;
                }).join('');
                return `<tr style="border-top:1px solid var(--xg-border)">
                    <td style="font-size:11px;font-weight:800;color:var(--xg-text-2);padding:4px 4px 4px 0;white-space:nowrap">${escapeHtml(g)}</td>${cells}</tr>`;
            }).join('');
            ladderHtml = sec('Graded market values by grade', `<div style="overflow-x:auto;margin-bottom:10px">
                <div style="background:var(--xg-panel);border:1px solid var(--xg-border);border-radius:8px;padding:10px 12px;min-width:fit-content">
                <table style="width:100%;border-collapse:collapse">
                    <thead><tr><th style="text-align:left;padding:2px 4px 2px 0"></th>${headCells}</tr></thead>
                    <tbody>${rows}</tbody></table></div></div>`);
        }

        // ── 2. Multi-grader population ────────────────────────────────────
        // popMap[grader] = {byGrade: {grade:count}, total}
        const popMap = {};
        const addPop = (grader, byGrade, total, src) => {
            const g = String(grader || 'PSA').toUpperCase();
            if (!popMap[g]) popMap[g] = { byGrade: {}, total: 0 };
            for (const [gr, c] of Object.entries(byGrade || {})) {
                const n = parseInt(c, 10);
                if (n > 0 && !(popMap[g].byGrade[gr] > 0)) popMap[g].byGrade[gr] = n;
            }
            const t = parseInt(total, 10) || Object.values(popMap[g].byGrade).reduce((s, v) => s + v, 0);
            if (t > popMap[g].total) popMap[g].total = t;
            if (t > 0 || Object.keys(byGrade || {}).length) srcSet.add(src);
        };
        if (comps.psa_population_by_grade || comps.psa_population_total) {
            addPop('PSA', comps.psa_population_by_grade, comps.psa_population_total, 'PSA Pop');
        }
        for (const [key, src] of [['csd_population', 'CardShowData'], ['population_report', 'CardShowData']]) {
            const pop = comps[key];
            if (pop && typeof pop === 'object') {
                for (const [g, v] of Object.entries(pop)) {
                    if (v && typeof v === 'object' && Array.isArray(v.grades)) {
                        const bg = {};
                        v.grades.forEach(e => { if (e && e.grade != null) bg[e.grade] = e.count || 0; });
                        addPop(g, bg, v.total, src);
                    } else if (v && typeof v === 'object') {
                        // {grade: count, ..., total: n} map form
                        const { total, ...grades } = v;
                        addPop(g, grades, total, src);
                    } else if (typeof v === 'number') {
                        addPop(g, {}, v, src);
                    }
                }
            }
        }
        if (tcgCard && Array.isArray(tcgCard.variants)) {
            for (const v of tcgCard.variants) {
                for (const r of (v.pop_reports || [])) {
                    const bg = {};
                    (r.grades || []).forEach(e => { if (e && e.grade != null) bg[e.grade] = e.count || 0; });
                    addPop(r.company || 'PSA', bg, r.total, 'Scrydex');
                }
            }
        }
        if (comps.cl_pop && typeof comps.cl_pop === 'object') {
            addPop('PSA', comps.cl_pop, null, 'CardLadder');
        }
        const popGraders = Object.keys(popMap).filter(g => popMap[g].total > 0 || Object.keys(popMap[g].byGrade).length)
            .sort((a, b) => graderRank(a) - graderRank(b) || a.localeCompare(b));
        let popHtml = '';
        if (popGraders.length) {
            const blocks = popGraders.map(g => {
                const pm = popMap[g];
                const numeric = Object.entries(pm.byGrade)
                    .filter(([gr, c]) => gradeNum(gr) >= 0 && c > 0)
                    .sort((a, b) => gradeNum(a[0]) - gradeNum(b[0]));
                const total = pm.total || numeric.reduce((s, [, c]) => s + c, 0);
                let body;
                if (numeric.length) {
                    const maxC = Math.max(...numeric.map(([, c]) => c));
                    const bars = numeric.map(([gr, c]) => {
                        const is10 = gradeNum(gr) === 10;
                        const w = Math.max(2, Math.round((c / maxC) * 100));
                        return `<div style="display:flex;align-items:center;gap:8px;margin-bottom:3px">
                            <span style="font-size:10px;font-weight:700;color:var(--xg-muted);width:26px;text-align:right">${escapeHtml(gr)}</span>
                            <div style="flex:1;background:var(--xg-subtle);border-radius:3px;height:12px;overflow:hidden">
                                <div style="width:${w}%;height:100%;background:${is10 ? '#f59e0b' : '#0d9488'};border-radius:3px"></div>
                            </div>
                            <span style="font-size:10px;color:var(--xg-text-2);width:52px">${c.toLocaleString()}</span>
                        </div>`;
                    }).join('');
                    const g10 = numeric.find(([gr]) => gradeNum(gr) === 10);
                    const scarce = g10 && total ? `<div style="font-size:11px;color:var(--xg-text-2);margin-top:6px">Only <strong>${g10[1].toLocaleString()}</strong> at grade 10 — <strong>${(g10[1] / total * 100).toFixed(1)}%</strong> of ${total.toLocaleString()} graded copies.</div>` : '';
                    body = bars + scarce;
                } else {
                    body = `<div style="font-size:16px;font-weight:800;color:var(--xg-text)">${total.toLocaleString()} <span style="font-size:11px;font-weight:600;color:var(--xg-muted)">graded copies</span></div>`;
                }
                return `<div style="margin-bottom:${popGraders.length > 1 ? '10px' : '0'}">
                    <div style="font-size:11px;font-weight:800;color:var(--xg-text-2);margin-bottom:4px">${escapeHtml(g)}${total ? ' · ' + total.toLocaleString() : ''}</div>${body}</div>`;
            }).join('');
            popHtml = sec('Graded population reports', box(blocks));
        }

        // ── 3. Raw condition curve ────────────────────────────────────────
        const CONDS = [['mint', 'M'], ['near_mint', 'NM'], ['lightly_played', 'LP'],
            ['moderately_played', 'MP'], ['heavily_played', 'HP'], ['damaged', 'DM']];
        const condSeries = [];
        const pickCond = (obj, src, label, cur) => {
            if (!obj || typeof obj !== 'object') return;
            const vals = CONDS.map(([k, lab]) => {
                const row = obj[k] || obj[lab.toLowerCase()] || obj[lab] || null;
                return row && num(row.market) != null ? { lab, v: num(row.market) } : null;
            }).filter(Boolean);
            if (vals.length >= 2) { condSeries.push({ label, src, vals, cur: cur || 'USD' }); srcSet.add(src); }
        };
        pickCond(comps.tcgpl_conditions, 'TCGPriceLookup', 'TCGplayer');
        pickCond(comps.csd_conditions, 'CardShowData', 'CardShowData');
        pickCond(comps.csd_eur, 'CardShowData', 'CardShowData (EUR)', 'EUR');
        let condHtml = '';
        if (condSeries.length) {
            condHtml = condSeries.map(s => {
                const maxV = Math.max(...s.vals.map(x => x.v));
                const bars = s.vals.map(x => {
                    const w = Math.max(4, Math.round((x.v / maxV) * 100));
                    return `<div style="display:flex;align-items:center;gap:8px;margin-bottom:3px">
                        <span style="font-size:10px;font-weight:700;color:var(--xg-muted);width:26px;text-align:right">${x.lab}</span>
                        <div style="flex:1;background:var(--xg-subtle);border-radius:3px;height:14px;overflow:hidden">
                            <div style="width:${w}%;height:100%;background:#2563eb;border-radius:3px"></div>
                        </div>
                        <span style="font-size:11px;font-weight:700;color:var(--xg-text);width:64px">${fmtM(x.v, s.cur)}</span>
                    </div>`;
                }).join('');
                return `<div style="margin-bottom:${condSeries.length > 1 ? '10px' : '0'}">
                    <div style="font-size:11px;font-weight:800;color:var(--xg-text-2);margin-bottom:4px">${escapeHtml(s.label)}</div>${bars}</div>`;
            }).join('');
            condHtml = sec('Raw market by condition', box(condHtml));
        }

        // ── 4. eBay rolling averages (TCGPL Trader tier) ──────────────────
        let ebayHtml = '';
        if (comps.tcgpl_ebay && typeof comps.tcgpl_ebay === 'object') {
            const rows = CONDS.map(([k, lab]) => {
                const e = comps.tcgpl_ebay[k];
                if (!e || typeof e !== 'object') return '';
                const f = v => num(v) != null ? fmtM(num(v)) : '—';
                return `<tr><td style="padding:4px 6px;font-size:11px;font-weight:700;color:var(--xg-text-2)">${lab}</td>
                    <td style="padding:4px 6px;font-size:11px;text-align:right">${f(e.avg_1d)}</td>
                    <td style="padding:4px 6px;font-size:11px;text-align:right">${f(e.avg_7d)}</td>
                    <td style="padding:4px 6px;font-size:11px;text-align:right">${f(e.avg_30d)}</td></tr>`;
            }).filter(Boolean);
            if (rows.length) {
                srcSet.add('TCGPriceLookup');
                ebayHtml = sec('eBay rolling averages', box(
                    `<table style="width:100%;border-collapse:collapse"><thead><tr>
                        <th style="text-align:left;font-size:10px;color:var(--xg-muted);padding:0 6px 4px">Cond</th>
                        <th style="text-align:right;font-size:10px;color:var(--xg-muted);padding:0 6px 4px">1D</th>
                        <th style="text-align:right;font-size:10px;color:var(--xg-muted);padding:0 6px 4px">7D</th>
                        <th style="text-align:right;font-size:10px;color:var(--xg-muted);padding:0 6px 4px">30D</th>
                    </tr></thead><tbody>${rows.join('')}</tbody></table>`));
            }
        }

        // ── 5. Trends ─────────────────────────────────────────────────────
        const trendChips = [];
        if (comps.price_trends && typeof comps.price_trends === 'object') {
            for (const [period, d] of Object.entries(comps.price_trends)) {
                const pct = d && typeof d === 'object' ? num((d.pct != null ? d.pct : d.percent_change)) : num(d);
                if (pct == null) continue;
                const c = pct > 0 ? '#059669' : (pct < 0 ? '#dc2626' : '#64748b');
                const arrow = pct > 0 ? '▲' : (pct < 0 ? '▼' : '—');
                trendChips.push(`<span style="display:inline-flex;align-items:center;gap:4px;background:${c}12;border:1px solid ${c}40;color:${c};border-radius:100px;padding:3px 9px;font-size:11px;font-weight:700">${escapeHtml(period)} ${arrow} ${Math.abs(pct).toFixed(1)}%</span>`);
            }
            if (trendChips.length) srcSet.add('CardShowData');
        }
        if (num(comps.cl_pct_30d) != null) {
            const pct = num(comps.cl_pct_30d);
            const c = pct > 0 ? '#059669' : (pct < 0 ? '#dc2626' : '#64748b');
            const arrow = pct > 0 ? '▲' : (pct < 0 ? '▼' : '—');
            trendChips.push(`<span style="display:inline-flex;align-items:center;gap:4px;background:${c}12;border:1px solid ${c}40;color:${c};border-radius:100px;padding:3px 9px;font-size:11px;font-weight:700">CardLadder 30D ${arrow} ${Math.abs(pct).toFixed(1)}%</span>`);
            srcSet.add('CardLadder');
        }
        const trendsHtml = trendChips.length
            ? sec('Price momentum', `<div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:12px">${trendChips.join('')}</div>`)
            : '';

        // ── 6. Sell-channel guide + recent graded sales + live listings ───
        const chanVal = v => {
            if (v == null) return null;
            if (typeof v === 'object') {
                for (const k of ['market_cents', 'price_cents', 'median_cents']) {
                    if (v[k] != null) return num(v[k]) / 100;
                }
                for (const k of ['market', 'price', 'median', 'value']) {
                    if (v[k] != null) return num(v[k]);
                }
                return null;
            }
            const n = num(v);
            return n != null && n > 10000 ? n / 100 : n; // cents-looking scalars
        };
        let sellHtml = '';
        if (comps.csd_sell && typeof comps.csd_sell === 'object') {
            const CH = [['online', 'Online'], ['show', 'Card shows'], ['lcs', 'Local shop']];
            const cells = CH.map(([k, lab]) => {
                const v = chanVal(comps.csd_sell[k]);
                if (v == null) return '';
                return `<div style="flex:1;background:var(--xg-panel);border:1px solid var(--xg-border);border-radius:8px;padding:8px 10px;text-align:center;min-width:0">
                    <div style="font-size:9px;font-weight:700;color:var(--xg-muted);text-transform:uppercase;letter-spacing:0.05em">${lab}</div>
                    <div style="font-size:15px;font-weight:800;color:var(--xg-text)">${fmtM(v)}</div></div>`;
            }).filter(Boolean);
            if (cells.length) {
                srcSet.add('CardShowData');
                sellHtml = sec('Where to sell — recommended ask', `<div style="display:flex;gap:6px;margin-bottom:10px">${cells.join('')}</div>`);
            }
        }

        const saleRows = [];
        if (comps.csd_sales && typeof comps.csd_sales === 'object') {
            const pushSale = (row, channel, grader) => {
                if (!row || typeof row !== 'object') return;
                const price = chanVal(row.price_cents != null ? { price_cents: row.price_cents } : ((row.price != null ? row.price : row.amount)));
                if (price == null) return;
                saleRows.push({
                    date: row.date || row.sold_at || row.sale_date || '',
                    grade: [grader, row.grade].filter(Boolean).join(' '),
                    price, channel,
                });
            };
            for (const ch of ['online', 'show', 'lcs']) {
                const rows = comps.csd_sales[ch];
                if (Array.isArray(rows)) rows.forEach(r => pushSale(r, ch));
            }
            if (comps.csd_sales.graded && typeof comps.csd_sales.graded === 'object') {
                for (const [g, rows] of Object.entries(comps.csd_sales.graded)) {
                    if (Array.isArray(rows)) rows.forEach(r => pushSale(r, 'graded', g.toUpperCase()));
                }
            }
            if (saleRows.length) srcSet.add('CardShowData');
        }
        let salesHtml = '';
        if (saleRows.length) {
            const lines = saleRows.slice(0, 10).map(s =>
                `<div style="display:flex;justify-content:space-between;gap:8px;padding:4px 0;border-bottom:1px solid var(--xg-border);font-size:11px">
                    <span style="color:var(--xg-muted);min-width:70px">${escapeHtml(String(s.date).slice(0, 10))}</span>
                    <span style="font-weight:700;color:var(--xg-text-2);flex:1">${escapeHtml(s.grade || 'raw')} <span style="font-weight:400;color:var(--xg-muted)">· ${escapeHtml(s.channel)}</span></span>
                    <strong style="color:var(--xg-text)">${fmtM(s.price)}</strong></div>`).join('');
            salesHtml = sec('Recent sales', box(lines));
        }

        let listingsHtml = '';
        if (comps.csd_listings && typeof comps.csd_listings === 'object') {
            const items = [];
            for (const [g, rows] of Object.entries(comps.csd_listings)) {
                if (!Array.isArray(rows)) continue;
                rows.slice(0, 5).forEach(r => {
                    const price = r && typeof r === 'object' ? chanVal(r.price_cents != null ? { price_cents: r.price_cents } : ((r.price != null ? r.price : r.ask))) : null;
                    if (price != null) items.push({ grader: g.toUpperCase(), grade: r.grade || '', price });
                });
            }
            if (items.length) {
                srcSet.add('CardShowData');
                const lines = items.slice(0, 8).map(i =>
                    `<div style="display:flex;justify-content:space-between;gap:8px;padding:4px 0;border-bottom:1px solid var(--xg-border);font-size:11px">
                        <span style="font-weight:700;color:var(--xg-text-2);flex:1">${escapeHtml(i.grader)} ${escapeHtml(String(i.grade))} <span style="font-weight:400;color:var(--xg-muted)">· listed now</span></span>
                        <strong style="color:var(--xg-text)">${fmtM(i.price)}</strong></div>`).join('');
                listingsHtml = sec('Slabs currently for sale', box(lines));
            }
        }

        // ── 7. Deterministic commentary ───────────────────────────────────
        const bullets = [];
        const rawNM = (() => {
            const t = comps.tcgpl_conditions || comps.csd_conditions || {};
            const nm = t.near_mint || t.mint || null;
            if (nm && num(nm.market)) return num(nm.market);
            if (tcgCard && Array.isArray(tcgCard.variants)) {
                for (const v of tcgCard.variants) {
                    const p = (v.prices || []).find(x => x.type === 'raw' && x.condition === 'NM');
                    if (p && num(p.market)) return num(p.market);
                }
            }
            return num(comps.fair_value);
        })();
        const bestOf = pred => {
            let b = null;
            for (const g of graderNames) for (const [gr, v] of Object.entries(graderMap[g])) {
                if (pred(gr) && (b == null || v > b)) b = v;
            }
            return b;
        };
        const gem = bestOf(gr => gradeNum(gr) === 10);
        if (gem && rawNM && gem > rawNM) {
            bullets.push(`A <strong>GEM 10</strong> sells for <strong>${fmtM(gem)}</strong> — <strong>${(gem / rawNM).toFixed(1)}×</strong> the raw NM price (${fmtM(rawNM)}).`);
        }
        // Cross-grader spread on a shared grade.
        for (const gr of allGrades) {
            const holders = graderNames.filter(g => g !== 'Market' && graderMap[g][gr] != null);
            if (holders.length >= 2) {
                const best = holders.reduce((a, b) => graderMap[a][gr] >= graderMap[b][gr] ? a : b);
                const others = holders.filter(h => h !== best)
                    .map(h => `${escapeHtml(h)} ${gr} ≈ ${Math.round(graderMap[h][gr] / graderMap[best][gr] * 100)}% of ${escapeHtml(best)}`);
                bullets.push(`Cross-grader spread at grade ${escapeHtml(gr)}: ${others.join(' · ')}.`);
                break;
            }
        }
        for (const g of popGraders) {
            const pm = popMap[g];
            const g10 = Object.entries(pm.byGrade).find(([gr]) => gradeNum(gr) === 10);
            if (g10 && pm.total > 0) {
                const pct = g10[1] / pm.total * 100;
                bullets.push(`${escapeHtml(g)} scarcity: <strong>${g10[1].toLocaleString()}</strong> grade-10s — ${pct < 1 ? 'under 1%' : pct.toFixed(1) + '%'} of ${pm.total.toLocaleString()} graded copies.`);
                break;
            }
        }
        if (num(comps.cl_pct_30d) != null && Math.abs(num(comps.cl_pct_30d)) >= 3) {
            const pct = num(comps.cl_pct_30d);
            bullets.push(`30-day momentum is <strong>${pct > 0 ? 'up' : 'down'} ${Math.abs(pct).toFixed(1)}%</strong> on recent graded sales.`);
        }
        if (comps.csd_sell && typeof comps.csd_sell === 'object') {
            const CH = [['online', 'online'], ['show', 'card shows'], ['lcs', 'local shops']];
            let best = null;
            for (const [k, lab] of CH) {
                const v = chanVal(comps.csd_sell[k]);
                if (v != null && (best == null || v > best.v)) best = { v, lab };
            }
            if (best) bullets.push(`Best expected return: <strong>${best.lab}</strong> at ${fmtM(best.v)} recommended ask.`);
        }
        const condVals = condSeries.length ? condSeries[0].vals : [];
        const nmV = condVals.find(x => x.lab === 'NM');
        const dmV = condVals.find(x => x.lab === 'DM');
        if (nmV && dmV && dmV.v > 0 && nmV.v / dmV.v >= 2) {
            bullets.push(`Condition is king — NM brings <strong>${(nmV.v / dmV.v).toFixed(1)}×</strong> the damaged price.`);
        }

        // ── Gate: need at least two data groups ───────────────────────────
        const groups = [ladderHtml, popHtml, condHtml, ebayHtml, trendsHtml, sellHtml, salesHtml, listingsHtml].filter(Boolean).length;
        if (groups < 2) return '';

        const commentHtml = bullets.length
            ? sec('What this means', `<div style="background:var(--xg-info-soft);border:1px solid var(--xg-info-line);border-radius:8px;padding:10px 14px;margin-bottom:12px">
                ${bullets.slice(0, 5).map(b => `<div style="font-size:12px;color:var(--xg-text-2);line-height:1.55;padding:2px 0">• ${b}</div>`).join('')}</div>`)
            : '';
        const srcLine = srcSet.size
            ? `<div style="font-size:10px;color:var(--xg-muted);margin-top:8px">Sources: ${escapeHtml([...srcSet].join(' · '))}${comps.csd_as_of ? ' · CSD data as of ' + escapeHtml(String(comps.csd_as_of).slice(0, 10)) : ''}</div>`
            : '';

        return `
        <div class="ximilar-result-card" style="
            background: var(--xg-panel); border-radius: 8px; padding: 20px;
            box-shadow: var(--xg-shadow); margin-bottom: 16px; border: 1px solid var(--xg-border);
        ">
            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 16px;">
                <div style="width: 44px; height: 44px; background: linear-gradient(135deg,#fef3c7,#fde68a); border: 1.5px solid #f59e0b55; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 22px; flex-shrink: 0; box-shadow: 0 2px 6px rgba(245,158,11,0.15);">🏅</div>
                <div>
                    <h3 style="font-size: 19px; font-weight: 800; color: var(--xg-text); margin: 0; line-height: 1.25; letter-spacing: -0.01em;">Graded Card Market</h3>
                    <p style="font-size: 12px; color: var(--xg-muted); margin: 2px 0 0 0; font-weight: 500;">Slab prices, population & trends — all providers combined</p>
                </div>
            </div>
            ${ladderHtml}
            ${popHtml}
            ${condHtml}
            ${ebayHtml}
            ${trendsHtml}
            ${sellHtml}
            ${salesHtml}
            ${listingsHtml}
            ${commentHtml}
            ${srcLine}
        </div>`;
    }

    // ── Subgrade combine (mirrors PHP FinalGrade) ─────────────────────────
    // BGS-faithful: unique lowest earns back up to +0.5 (+1.0/+1.5 on drastic
    // pillar gaps); tied lowest = the grade; gem-mint/pristine gating.
    function pgcCombineFinal(subs) {
        const vals = Object.entries(subs)
            .filter(([, v]) => typeof v === 'number' && !isNaN(v))
            .sort((a, b) => a[1] - b[1]);
        if (!vals.length) return null;
        if (vals.length === 1) return vals[0][1];
        const [lowP, low] = vals[0];
        if (vals[1][1] === low) return low; // two lowest equal -> that is the grade
        const second = vals.find(([, v]) => v > low)[1];
        const gap = second - low;
        let bump;
        if (lowP === 'edges' || lowP === 'surface') {
            bump = gap >= 1.0 ? 1.0 : 0.5;
            const hi = vals[vals.length - 1][1];
            if (bump >= 1.0 && (hi - low) <= 1.5 && hi < 10) bump = 0.5;
        } else if (lowP === 'centering') {
            bump = gap >= 4.0 ? 1.5 : (gap >= 2.0 ? 1.0 : 0.5);
        } else { // corners
            bump = gap >= 3.0 ? 1.0 : 0.5;
        }
        let f = low + bump;
        if ((lowP === 'edges' || lowP === 'surface') && bump >= 1.0 && Math.abs(f - 9.5) < 1e-9 && Math.abs(gap - 1.0) < 1e-9) {
            bump = 0.5;
            f = 9.0;
        }
        if (f >= 10.0) {
            const n10 = vals.filter(([, v]) => v >= 10.0).length;
            if (!(low >= 9.5 && n10 >= 3)) f = 9.5;
        }
        return Math.max(1, Math.min(10, f));
    }

    // CGC documented rule: unique lowest -> +0.5; two lowest equal -> lowest.
    function pgcCgcFinal(subs) {
        const vals = Object.values(subs).filter(v => typeof v === 'number' && !isNaN(v)).sort((a, b) => a - b);
        if (!vals.length) return null;
        if (vals.length >= 2 && vals[1] === vals[0]) return vals[0];
        return Math.min(10, vals[0] + 0.5);
    }

    // ── Hybrid Grade Forecast™ (mirrors PHP calculateHybridForecast) ───────────
    function calculateHybridForecast(grades) {
        if (!grades || grades.final == null) return null;
        const final    = parseFloat(grades.final);
        const corners  = parseFloat((grades.corners != null ? grades.corners : final));
        const edges    = parseFloat((grades.edges != null ? grades.edges : final));
        const surface  = parseFloat((grades.surface != null ? grades.surface : final));
        const centering = parseFloat((grades.centering != null ? grades.centering : final));

        // PSA — strict on centering
        let psaGrade = Math.floor(final);
        if (centering < 9.0) psaGrade = Math.min(psaGrade, 8);
        else if (centering < 9.5) psaGrade = Math.min(psaGrade, 9);

        // BGS — the real subgrade combine (same algorithm as our final)
        const lowest = Math.min(corners, edges, surface, centering);
        const subMap = { corners, edges, surface, centering };
        let bgsGrade = pgcCombineFinal(subMap);
        if (bgsGrade == null) bgsGrade = final;

        // SGC — vintage friendly, strict surface
        let sgcGrade = final;
        if (surface < 9.0) sgcGrade -= 1;
        sgcGrade = Math.max(1, Math.min(10, Math.floor(sgcGrade)));

        // CGC — documented rule (unique lowest +0.5, tied lowest = lowest)
        let cgcGrade = pgcCgcFinal(subMap);
        if (cgcGrade == null) cgcGrade = Math.floor(final * 2) / 2;

        return {
            PSA: { grade: psaGrade,   label: psaGrade  >= 10 ? 'Gem Mint' : (psaGrade  >= 9 ? 'Mint' : 'NM-MT'), color: '#dc2626', note: centering < 9.5 ? 'Centering risk for 10' : 'Strong candidate' },
            BGS: { grade: bgsGrade,   label: bgsGrade  >= 10 ? 'Pristine' : (bgsGrade  >= 9.5 ? 'Gem Mint' : 'Mint'), color: '#0ea5e9', note: lowest < 9.0 ? 'Lowest sub-grade limiting factor' : 'Solid sub-grades' },
            SGC: { grade: sgcGrade,   label: sgcGrade  >= 10 ? 'Pristine' : (sgcGrade  >= 9 ? 'Mint' : 'NM-MT'), color: '#16a34a', note: surface < 9.5 ? 'Surface wear detected' : 'Great eye appeal' },
            CGC: { grade: cgcGrade,   label: cgcGrade  >= 10 ? 'Pristine' : (cgcGrade  >= 9.5 ? 'Gem Mint' : 'Mint'), color: '#ca8a04', note: 'Technical accuracy high' }
        };
    }

    function calculateGradingROI(grades, id) {
        if (!grades || grades.final == null) return null;
        const pricing = (id != null ? id.pricing : void 0);
        if (!pricing || (!pricing.avg && !pricing.low && !pricing.high && !pricing.median)) return null;
        let rawPrice = 100;
        if ((pricing != null ? pricing.avg : void 0)) rawPrice = parseFloat(pricing.avg);
        else if ((pricing != null ? pricing.low : void 0)) rawPrice = parseFloat(pricing.low) * 1.5;

        const predictedGrade = parseFloat(grades.final);
        const multipliers = [[10, 8.5], [9.5, 4.2], [9, 2.1], [8.5, 1.4], [8, 1.1], [7, 0.8], [6, 0.6]];
        let multiplier = 0.5;
        for (const [g, m] of multipliers) {
            if (predictedGrade >= g) { multiplier = m; break; }
        }
        const gradedValue = rawPrice * multiplier;
        const gradingFee = 25;
        const profit = gradedValue - rawPrice - gradingFee;
        const roi = ((gradedValue - rawPrice - gradingFee) / (rawPrice + gradingFee)) * 100;
        return { raw_value: rawPrice, graded_value: gradedValue, grading_fee: gradingFee, profit, roi_percent: roi };
    }

    function renderHybridForecast(forecast) {
        if (!forecast) return '';
        const cards = Object.entries(forecast).map(([company, data]) => {
            const gradeDisplay = Number.isInteger(data.grade) ? data.grade : parseFloat(data.grade).toFixed(1);
            return `
            <div class="hybrid-forecast-card">
                <div class="hybrid-forecast-company" style="font-size: 10px; font-weight: 700; color: var(--xg-muted); text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 8px;">${company}</div>
                <div class="hybrid-forecast-grade" style="font-size: 2.6rem; font-weight: 700; color: ${data.color}; line-height: 1; margin-bottom: 4px;">${gradeDisplay}</div>
                <div class="hybrid-forecast-label" style="font-size: 12px; font-weight: 700; color: var(--xg-muted); text-transform: uppercase; margin-bottom: 10px;">${data.label}</div>
                <div class="hybrid-forecast-note" style="font-size: 11px; color: var(--xg-muted); font-style: italic; border-top: 1px solid var(--xg-border); padding-top: 8px; line-height: 1.3;">${data.note}</div>
            </div>`;
        }).join('');

        // Grade comparison bar chart
        const barChartHtml = (() => {
            const entries = Object.entries(forecast);
            if (entries.length < 2) return '';
            const maxGrade = 10;
            const bars = entries.map(([company, data]) => {
                const grade = parseFloat(data.grade);
                const pct = (grade / maxGrade) * 100;
                const gradeDisplay = Number.isInteger(data.grade) ? data.grade : grade.toFixed(1);
                return `<div style="display: flex; align-items: center; gap: 10px; margin-bottom: 8px;">
                    <div style="width: 48px; font-size: 11px; font-weight: 700; color: var(--xg-text); text-align: right; text-transform: uppercase; letter-spacing: 0.03em;">${escapeHtml(company)}</div>
                    <div style="flex: 1; background: var(--xg-subtle); border-radius: 6px; height: 26px; overflow: hidden; position: relative;">
                        <div style="width: ${pct}%; height: 100%; background: ${data.color}; border-radius: 6px; transition: width 0.4s; opacity: 0.9;"></div>
                    </div>
                    <div style="width: 36px; font-size: 14px; font-weight: 800; color: ${data.color}; text-align: left;">${gradeDisplay}</div>
                </div>`;
            }).join('');
            return `<div style="margin-top: 16px; padding: 14px 16px; background: var(--xg-subtle); border-radius: 8px; border: 1px solid var(--xg-border);">
                <div style="font-size: 12px; font-weight: 700; color: var(--xg-text); margin-bottom: 10px; text-transform: uppercase; letter-spacing: 0.04em;">Grade Forecast Comparison</div>
                ${bars}
            </div>`;
        })();

        return `
        <div class="hybrid-forecast-panel">
            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 6px;">
                <div style="width: 36px; height: 36px; background: var(--xg-info-soft); border: 1.5px solid var(--xg-info-line); border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 2px 6px rgba(14,165,233,0.12);">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                </div>
                <h4 style="margin: 0; font-size: 18px; font-weight: 800; color: var(--xg-text); letter-spacing: -0.01em;">Hybrid Grade Forecast™</h4>
            </div>
            <p style="font-size: 12px; color: var(--xg-muted); margin: 0 0 16px 0;">Based on our AI sub-grade analysis, here is how we expect this card to perform across major grading companies.</p>
            <div class="hybrid-forecast-grid">
                ${cards}
            </div>
            ${barChartHtml}
        </div>`;
    }

    function renderProfitabilityMatrix(roi, finalGrade) {
        if (!roi || roi.profit == null || roi.profit <= 0) return '';
        const fmt = (val, digits = 2) => (val != null && !Number.isNaN(parseFloat(val))) ? parseFloat(val).toFixed(digits) : 'N/A';
        return `
        <div class="xg-block" style="background: var(--xg-panel); border-radius: 8px; padding: 20px 24px; color: var(--xg-text); margin-top: 16px; border: 1px solid var(--xg-border); box-shadow: var(--xg-shadow);">
            <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 18px; flex-wrap: wrap; gap: 8px;">
                <div style="display: flex; align-items: center; gap: 12px;">
                    <div style="width: 36px; height: 36px; background: var(--xg-good-soft); border: 1.5px solid var(--xg-good-line); border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 2px 6px rgba(5,150,105,0.12);">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#059669" stroke-width="2.5"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                    </div>
                    <h4 style="margin: 0; font-size: 18px; font-weight: 800; color: var(--xg-text); letter-spacing: -0.01em;">Grading Profitability Matrix™</h4>
                </div>
                <span style="background: var(--xg-good-soft); color: var(--xg-good-strong); border: 1px solid var(--xg-good-line); font-size: 10px; font-weight: 700; padding: 4px 10px; border-radius: 100px; text-transform: uppercase; letter-spacing: 1px;">High ROI Potential</span>
            </div>
            <div class="xim-profit-grid">
                <div style="text-align: center; border-right: 1px solid var(--xg-border); padding-right: 16px;">
                    <div style="font-size: 10px; font-weight: 600; color: var(--xg-muted); text-transform: uppercase; margin-bottom: 6px;">Raw Value Est.</div>
                    <div style="font-size: 1.6rem; font-weight: 700; color: var(--xg-text);">$${fmt(roi.raw_value)}</div>
                </div>
                <div style="text-align: center; border-right: 1px solid var(--xg-border); padding-right: 16px;">
                    <div style="font-size: 10px; font-weight: 600; color: var(--xg-muted); text-transform: uppercase; margin-bottom: 6px;">Graded Market Value (Est.)</div>
                    <div style="font-size: 1.6rem; font-weight: 700; color: #059669;">$${fmt(roi.graded_value)}</div>
                    <div style="font-size: 11px; color: #059669; font-weight: 700;">+${roi.roi_percent != null ? Math.round(roi.roi_percent) : 0}% Growth</div>
                </div>
                <div style="text-align: center;">
                    <div style="font-size: 10px; font-weight: 600; color: var(--xg-muted); text-transform: uppercase; margin-bottom: 6px;">Net Grading Alpha</div>
                    <div style="font-size: 1.6rem; font-weight: 700; color: #2563eb;">+$${fmt(roi.profit)}</div>
                    <div style="font-size: 10px; color: var(--xg-muted); font-style: italic;">Incl. $${roi.grading_fee != null ? roi.grading_fee : 25} Fee</div>
                </div>
            </div>
            <div style="margin-top: 14px; padding-top: 12px; border-top: 1px solid var(--xg-border); display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px;">
                <p style="margin: 0; font-size: 10px; color: var(--xg-muted); font-style: italic;">*Estimates based on current auction data for <strong>${finalGrade != null ? parseFloat(finalGrade).toFixed(1) : 'N/A'}</strong> grade tier candidates.</p>
            </div>
        </div>`;
    }

    function renderEconomicsPanel(economics) {
        if (!economics || !economics.options || economics.options.length === 0) return '';
        const hasMarketData = economics.options.some(opt => parseFloat(opt.best_value) > 0);
        // No usable price anywhere (raw_value 0 and no graded comparables):
        // the Sell Raw/fee-savings math would be computed against $0 — show a
        // neutral no-data notice instead of a fabricated verdict.
        const rawOk = parseFloat(economics.raw_value) > 0;
        const sufficient = economics.market_data_sufficient != null
            ? !!economics.market_data_sufficient
            : (rawOk || hasMarketData);
        if (!sufficient) {
            return `
            <div class="ximilar-result-card xim-econ-card" style="background: var(--xg-panel); border-radius: 8px; padding: 20px; box-shadow: var(--xg-shadow); margin-bottom: 16px; border: 1px solid var(--xg-border);">
                <div style="display: flex; align-items: center; gap: 12px;">
                    <div style="width: 44px; height: 44px; background: var(--xg-subtle); border: 1.5px solid var(--xg-border); border-radius: 10px; display: flex; align-items: center; justify-content: center; color: var(--xg-muted); font-size: 22px; flex-shrink: 0;">💰</div>
                    <div>
                        <h3 style="font-size: 19px; font-weight: 800; color: var(--xg-text); margin: 0; line-height: 1.25; letter-spacing: -0.01em;">Submit or Sell Raw?</h3>
                        <p style="font-size: 12.5px; color: var(--xg-muted); margin: 4px 0 0 0; line-height: 1.5;">No market pricing found for this card — no recent sales or listings to compare against, so grading economics can't be estimated.</p>
                    </div>
                </div>
            </div>`;
        }
        // Pick the single best tier for each company so the comparison always shows CGC, BGS, PSA, SGC.
        const bestByCompany = {};
        economics.options.forEach(opt => {
            if (!bestByCompany[opt.company] || opt.net_profit > bestByCompany[opt.company].net_profit) {
                bestByCompany[opt.company] = opt;
            }
        });
        const options = Object.values(bestByCompany).sort((a, b) => b.net_profit - a.net_profit);
        const fmt = (val, digits = 2) => {
            if (val == null || Number.isNaN(parseFloat(val))) return 'N/A';
            const num = parseFloat(val);
            // Hide extreme/min-float sentinel values that leak through from the backend
            if (Math.abs(num) > 1e300 || Math.abs(num) < 1e-300) return 'N/A';
            return num.toFixed(digits);
        };
        const rows = options.map(opt => {
            const positive = opt.net_profit > 0;
            const pausedBadge = opt.is_paused ? '<span style="background:#fef3c7;color:#92400e;font-size:9px;font-weight:700;padding:2px 6px;border-radius:100px;margin-left:4px;text-transform:uppercase;">Paused</span>' : '';
            const turnLabel = opt.turnaround_days ? `<div style="font-size:10px;color:var(--xg-muted);margin-top:2px;">${opt.turnaround_days}</div>` : '';
            const tagsLabel = opt.tags && opt.tags !== 'active' && !opt.is_paused ? `<div style="font-size:10px;color:var(--xg-muted);margin-top:2px;">${opt.tags}</div>` : '';
            return `<tr style="border-bottom: 1px solid var(--xg-border);">
                <td data-label="Company" style="padding: 8px; font-weight: 600; font-size: 13px; border: 1px solid var(--xg-border);">${opt.company}${pausedBadge} <span style="font-weight: 500; color: var(--xg-muted);">${opt.tier}</span>${turnLabel}${tagsLabel}</td>
                <td data-label="Total Cost" style="padding: 8px; font-size: 13px; color: var(--xg-muted); border: 1px solid var(--xg-border);">$${fmt(opt.total_cost)}</td>
                <td data-label="Best Grade" style="padding: 8px; font-size: 13px; color: var(--xg-muted); border: 1px solid var(--xg-border);">${opt.best_grade || 'N/A'}</td>
                <td data-label="Net Profit" style="padding: 8px; font-size: 13px; font-weight: 700; color: ${positive ? '#059669' : '#ef4444'}; border: 1px solid var(--xg-border);">${positive ? '+' : ''}$${fmt(opt.net_profit)}</td>
                <td data-label="ROI" style="padding: 8px; font-size: 13px; font-weight: 700; color: ${positive ? '#059669' : '#64748b'}; border: 1px solid var(--xg-border);">${fmt(opt.roi_percent, 1)}%</td>
            </tr>`;
        }).join('');

        const badgeColor = economics.recommendation === 'Sell Raw' ? '#ef4444' : (economics.recommendation === 'Marginal / Coin Flip' ? '#f59e0b' : '#10b981');
        const bestOption = options[0] || {};

        // Reframe negative verdicts as fees avoided — a "Sell Raw" answer just
        // saved the user the cheapest all-in submission cost on a card that
        // wouldn't return it. Only meaningful when we have real tier pricing.
        const tierCosts = options.map(o => parseFloat(o.total_cost)).filter(c => isFinite(c) && c > 0);
        const cheapestCost = tierCosts.length ? Math.min(...tierCosts) : null;
        const cheapestOpt = cheapestCost != null
            ? (options.find(o => parseFloat(o.total_cost) === cheapestCost) || {})
            : {};
        const savedFeeHtml = (cheapestCost != null && (economics.recommendation === 'Sell Raw' || economics.recommendation === 'Marginal / Coin Flip'))
            ? `<div style="background: ${economics.recommendation === 'Sell Raw' ? '#f0fdf4' : '#fffbeb'}; border: 1px solid ${economics.recommendation === 'Sell Raw' ? '#bbf7d0' : '#fde68a'}; border-radius: 8px; padding: 10px 14px; margin-bottom: 12px; font-size: 12.5px; color: ${economics.recommendation === 'Sell Raw' ? '#166534' : '#92400e'}; line-height: 1.5;">
                ${economics.recommendation === 'Sell Raw'
                    ? `<strong>Smart move checking first.</strong> The cheapest pro submission here (~$${fmt(cheapestCost)} all-in at ${escapeHtml(cheapestOpt.company || 'the grader')} ${escapeHtml(cheapestOpt.tier || '')}) would cost more than this card returns graded — this scan just saved you that fee.`
                    : `<strong>This one's a coin flip.</strong> You'd be spending ~$${fmt(cheapestCost)} all-in (${escapeHtml(cheapestOpt.company || '')} ${escapeHtml(cheapestOpt.tier || '')}) on a maybe — worth knowing before you pay.`}
            </div>`
            : '';

        const gradedValue = bestOption.best_value || (economics.graded_prices != null ? economics.graded_prices[economics.predicted_grade] : void 0) || (economics.graded_prices != null ? economics.graded_prices.best : void 0) || 0;
        const gradeLabel = bestOption.best_grade || economics.predicted_grade || 'est.';
        const upsideGrade = bestOption.upside_grade;
        const upsideValue = bestOption.upside_value || 0;
        const upsideNet = bestOption.upside_net;

        // ── Interactive "What If It Grades…" explorer ──
        // Recomputes net profit client-side from the per-grade price curve and
        // per-tier all-in costs the PHP economics engine already produced.
        let explorerHtml = '';
        const gp = economics.graded_prices || {};
        const gradeKeys = Object.keys(gp).filter(k => !isNaN(parseFloat(k)) && parseFloat(k) >= 1 && parseFloat(k) <= 10 && gp[k] > 0);
        if (gradeKeys.length >= 2 && economics.options.length > 0) {
            const econId = 'econ-' + (++econIdCounter);
            econRegistry.set(econId, {
                prices: gp,
                options: economics.options,
                raw: parseFloat(economics.raw_value) || 0,
                predicted: parseFloat(economics.predicted_grade) || 7
            });
            const predicted = parseFloat(economics.predicted_grade) || 7;
            const tierOpts = economics.options.map((o, i) =>
                `<option value="${i}"${i === 0 ? ' selected' : ''}>${escapeHtml(o.company)} · ${escapeHtml(o.tier)} — $${fmt(o.total_cost)} all-in</option>`
            ).join('');
            explorerHtml = `
            <div class="xim-econ-explorer" data-econ-id="${econId}" style="margin-top: 16px; padding: 16px; background: var(--xg-subtle); border: 1px solid var(--xg-border); border-radius: 10px;">
                <div style="font-size: 13px; font-weight: 800; color: var(--xg-text); margin-bottom: 12px;">🎚️ What if it grades differently?</div>
                <div style="display: flex; flex-wrap: wrap; gap: 14px; align-items: flex-end; margin-bottom: 14px;">
                    <div style="flex: 1 1 240px; min-width: 200px;">
                        <div style="display: flex; justify-content: space-between; font-size: 11px; color: var(--xg-muted); margin-bottom: 4px;">
                            <span>Hypothetical grade</span>
                            <strong class="econ-grade-label" style="color: var(--xg-text); font-size: 13px;">${predicted.toFixed(1)}</strong>
                        </div>
                        <input type="range" class="econ-grade-slider" min="1" max="10" step="0.5" value="${predicted}" style="width: 100%; accent-color: #2563eb;">
                    </div>
                    <div style="flex: 1 1 0; min-width: 160px;">
                        <div style="font-size: 11px; color: var(--xg-muted); margin-bottom: 4px;">Grading company & tier</div>
                        <select class="econ-tier-select" style="width: 100%; padding: 8px 10px; border-radius: 8px; border: 1px solid var(--xg-border); background: var(--xg-panel); color: var(--xg-text); font-size: 12px; font-weight: 600;">${tierOpts}</select>
                    </div>
                </div>
                <div class="econ-explorer-out" style="display: flex; flex-wrap: wrap; gap: 10px; align-items: center;">
                    <div style="flex: 1 1 90px; text-align: center; padding: 10px; background: var(--xg-panel); border: 1px solid var(--xg-border); border-radius: 8px;">
                        <div style="font-size: 9px; color: var(--xg-muted); font-weight: 700; text-transform: uppercase;">Graded value</div>
                        <div class="econ-out-value" style="font-size: 17px; font-weight: 800; color: #059669;">—</div>
                    </div>
                    <div style="flex: 1 1 90px; text-align: center; padding: 10px; background: var(--xg-panel); border: 1px solid var(--xg-border); border-radius: 8px;">
                        <div style="font-size: 9px; color: var(--xg-muted); font-weight: 700; text-transform: uppercase;">Net after fees</div>
                        <div class="econ-out-net" style="font-size: 17px; font-weight: 800;">—</div>
                    </div>
                    <div style="flex: 1 1 90px; text-align: center; padding: 10px; background: var(--xg-panel); border: 1px solid var(--xg-border); border-radius: 8px;">
                        <div style="font-size: 9px; color: var(--xg-muted); font-weight: 700; text-transform: uppercase;">ROI</div>
                        <div class="econ-out-roi" style="font-size: 17px; font-weight: 800;">—</div>
                    </div>
                    <div style="flex: 2 1 140px; text-align: center;">
                        <span class="econ-out-verdict" style="display: inline-block; font-size: 12px; font-weight: 800; padding: 8px 16px; border-radius: 100px;"></span>
                    </div>
                </div>
            </div>`;
        }

        return `
        <div class="ximilar-result-card xim-econ-card" style="background: var(--xg-panel); border-radius: 8px; padding: 20px; box-shadow: var(--xg-shadow); margin-bottom: 16px; border: 1px solid var(--xg-border);">
            <div class="xim-econ-header" style="display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-bottom: 14px;">
                <div style="display: flex; align-items: center; gap: 12px;">
                    <div style="width: 44px; height: 44px; background: var(--xg-good-soft); border: 1.5px solid var(--xg-good-line); border-radius: 10px; display: flex; align-items: center; justify-content: center; color: var(--xg-good-strong); font-size: 22px; flex-shrink: 0; box-shadow: 0 2px 6px rgba(5,150,105,0.15);">💰</div>
                    <div>
                        <h3 style="font-size: 19px; font-weight: 800; color: var(--xg-text); margin: 0; line-height: 1.25; letter-spacing: -0.01em;">Submit or Sell Raw?</h3>
                        <p style="font-size: 12px; color: var(--xg-muted); margin: 2px 0 0 0; font-weight: 500;">Net profit after grading fees, shipping, and insurance</p>
                    </div>
                </div>
                <span style="background: ${badgeColor}15; color: ${badgeColor}; border: 1px solid ${badgeColor}30; font-size: 12px; font-weight: 700; padding: 6px 12px; border-radius: 100px; white-space: nowrap;">${economics.recommendation}</span>
            </div>
            <p style="font-size: 13px; color: var(--xg-text-2); margin-bottom: 12px; line-height: 1.5;">${economics.recommendation_reason}</p>
            ${savedFeeHtml}
            ${!hasMarketData ? `<div style="background: #fef3c7; border: 1px solid #fcd34d; border-radius: 8px; padding: 10px 14px; margin-bottom: 12px; font-size: 12px; color: #92400e; line-height: 1.5;"><strong>No graded comparables found.</strong> No recent auction data exists for this card in graded condition. The recommendation is based on raw value vs. grading fees alone — grading costs will exceed the card's market value.</div>` : ''}
            <div class="xim-econ-stats" style="display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 16px;">
                <div class="xim-econ-stat" style="background: var(--xg-subtle); border-radius: 10px; padding: 10px 14px; border: 1px solid var(--xg-border); min-width: 120px;">
                    <div style="font-size: 10px; color: var(--xg-muted); font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;">Raw Value Est.</div>
                    <div style="font-size: 15px; font-weight: 700; color: var(--xg-text);">$${fmt(economics.raw_value)}</div>
                </div>
                <div class="xim-econ-stat" style="background: var(--xg-subtle); border-radius: 10px; padding: 10px 14px; border: 1px solid var(--xg-border); min-width: 120px;">
                    <div style="font-size: 10px; color: var(--xg-muted); font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;">Graded Value Est. (${gradeLabel})</div>
                    <div style="font-size: 15px; font-weight: 700; color: var(--xg-text);">$${fmt(gradedValue)}</div>
                </div>
                ${upsideGrade && upsideValue > 0 ? `
                <div class="xim-econ-stat" style="background: #f0fdf4; border-radius: 10px; padding: 10px 14px; border: 1px solid #bbf7d0; min-width: 120px;">
                    <div style="font-size: 10px; color: #15803d; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;">Upside if ${upsideGrade}</div>
                    <div style="font-size: 15px; font-weight: 700; color: #15803d;">$${fmt(upsideValue)}</div>
                    ${upsideNet != null ? `<div style="font-size: 10px; color: #15803d; font-weight: 600;">Net $${fmt(upsideNet)}</div>` : ''}
                </div>` : ''}
            </div>
            <div class="xim-econ-table-wrap" style="overflow-x: auto;">
                <table class="xim-econ-table" style="width: 100%; min-width: 420px; border-collapse: collapse; font-size: 13px;">
                    <thead>
                        <tr style="background: var(--xg-subtle); border-bottom: 1px solid var(--xg-border); text-align: left;">
                            <th style="padding: 8px; font-size: 12px; font-weight: 600; color: var(--xg-text-2); border: 1px solid var(--xg-border);">Company</th>
                            <th style="padding: 8px; font-size: 12px; font-weight: 600; color: var(--xg-text-2); border: 1px solid var(--xg-border);">Total Cost</th>
                            <th style="padding: 8px; font-size: 12px; font-weight: 600; color: var(--xg-text-2); border: 1px solid var(--xg-border);">Best Grade</th>
                            <th style="padding: 8px; font-size: 12px; font-weight: 600; color: var(--xg-text-2); border: 1px solid var(--xg-border);">Net Profit</th>
                            <th style="padding: 8px; font-size: 12px; font-weight: 600; color: var(--xg-text-2); border: 1px solid var(--xg-border);">ROI</th>
                        </tr>
                    </thead>
                    <tbody>${rows}</tbody>
                </table>
            </div>
            ${explorerHtml}
            <p style="font-size: 11px; color: var(--xg-muted); margin-top: 12px; font-style: italic;">*Estimates use live marketplace data and current service-tier fees from each grading company. Paused tiers are marked. Turnaround times are estimates published by the grading companies and may vary.</p>
        </div>`;
    }

    // Ximilar corner/edge names are input only — each score is placed by name
    // into our fixed display squares: corners TL/TR/BL/BR, edges L/R/T/B.
    const _PGC_CORNER_LABELS = ['Top Left', 'Top Right', 'Bottom Left', 'Bottom Right'];
    const _PGC_EDGE_LABELS = ['Left Edge', 'Right Edge', 'Top Edge', 'Bottom Edge'];

    function _pgcCornerSquare(name) {
        const n = String(name || '').toUpperCase();
        const l = n.includes('LEFT')   || n === 'TL' || n === 'BL';
        const r = n.includes('RIGHT')  || n === 'TR' || n === 'BR';
        const t = n.includes('TOP')    || n.includes('UPPER') || n === 'TL' || n === 'TR';
        const b = n.includes('BOTTOM') || n.includes('DOWN') || n.includes('LOWER') || n === 'BL' || n === 'BR';
        if (t && l) return 0;
        if (t && r) return 1;
        if (b && l) return 2;
        if (b && r) return 3;
        return -1;
    }

    function _pgcEdgeSquare(name) {
        const n = String(name || '').toUpperCase();
        if (n.includes('LEFT') || n === 'L') return 0;
        if (n.includes('RIGHT') || n === 'R') return 1;
        if (n.includes('TOP') || n.includes('UPPER') || n === 'T') return 2;
        if (n.includes('BOTTOM') || n.includes('DOWN') || n.includes('LOWER') || n === 'B') return 3;
        return -1;
    }

    function _pgcSquareSort(list, sqFn, labels, fallback) {
        const keyed = list.map(item => ({ item, sq: sqFn(item && item.name) }));
        const counts = {};
        keyed.forEach(k => { if (k.sq >= 0) counts[k.sq] = (counts[k.sq] || 0) + 1; });
        keyed.sort((a, b) => (a.sq < 0 ? 99 : a.sq) - (b.sq < 0 ? 99 : b.sq));
        return keyed.map(k => ({
            item: k.item,
            label: (k.sq >= 0 && counts[k.sq] === 1) ? labels[k.sq] : String((k.item && k.item.name) || fallback),
        }));
    }

    // "Photo quality limited this grade" — shown whenever IqGradeCaps lowered
    // a pillar (grade_pgc.iq_caps). Honest about WHY, plus a retake nudge.
    function renderIqCapsNotice(record) {
        const caps = record && record.grade_pgc && record.grade_pgc.iq_caps;
        if (!Array.isArray(caps) || !caps.length) return '';
        const fmt = (v) => String(parseFloat(v).toFixed(1)).replace(/\.0$/, '');
        const parts = caps.filter(c => c && c.pillar).map(c =>
            `<b>${escapeHtml(String(c.pillar).charAt(0).toUpperCase() + String(c.pillar).slice(1))}</b> capped at <b>${fmt(c.to)}</b> &mdash; ${escapeHtml(String(c.reason || ''))}`);
        if (!parts.length) return '';
        return `<div style="margin:0 0 16px;padding:12px 16px;border-radius:10px;background:var(--xg-warn-soft);border:1px solid var(--xg-warn-line);display:flex;gap:10px;align-items:flex-start;">
            <span style="font-size:18px;line-height:1;flex-shrink:0;">📷</span>
            <div style="font-size:12.5px;line-height:1.55;color:var(--xg-text-2);">
                <div style="font-weight:800;color:var(--xg-warn-strong);margin-bottom:3px;">Photo quality limited this grade</div>
                <div>${parts.join('<br>')}</div>
                <div style="margin-top:6px;color:var(--xg-muted);">A grade is only as high as the photo can prove. Retake in sharp focus, out of the sleeve, with even light and no glare &mdash; the card may grade higher.</div>
            </div></div>`;
    }

    // Per-corner/per-edge values from the PGC grader (grade_pgc.front), in the
    // same {name, grade} list shape the Ximilar arrays use — the individual
    // sub-grade cards must match the grader that produced the headline. Null
    // when the record wasn't PGC-graded or the breakdown is incomplete.
    function pgcOwnSubgrades(record) {
        if (!record || record.grader !== 'pgc') return null;
        const front = record.grade_pgc && record.grade_pgc.front;
        if (!front) return null;
        const corners = [];
        [['ul', 'Top Left'], ['ur', 'Top Right'], ['ll', 'Bottom Left'], ['lr', 'Bottom Right']].forEach(([k, label]) => {
            const v = front.corners && front.corners[k];
            if (typeof v === 'number' && isFinite(v)) corners.push({ name: label, grade: v });
        });
        const edges = [];
        [['left', 'Left'], ['right', 'Right'], ['top', 'Top'], ['bottom', 'Bottom']].forEach(([k, label]) => {
            const v = front.edges && front.edges[k];
            if (typeof v === 'number' && isFinite(v)) edges.push({ name: label, grade: v });
        });
        if (corners.length < 4 && edges.length < 4) return null;
        return { corners: corners.length === 4 ? corners : [], edges: edges.length === 4 ? edges : [] };
    }

    function renderIndividualCorners(corners, edges) {
        if ((!Array.isArray(corners) || corners.length === 0) && (!Array.isArray(edges) || edges.length === 0)) return '';
        const subPanel = (title, list, sqFn, labels, fallback) => {
            if (!Array.isArray(list) || !list.length) return '';
            const items = _pgcSquareSort(list, sqFn, labels, fallback).map(({ item: g, label }) => `
                <div style="text-align: center; padding: 14px 10px; background: var(--xg-subtle); border-radius: 8px; border: 1px solid var(--xg-border);">
                    <div style="font-size: 11px; font-weight: 700; color: var(--xg-muted); text-transform: uppercase; letter-spacing: 0.03em; margin-bottom: 6px;">${label}</div>
                    <div style="font-size: 1.5rem; font-weight: 700; color: ${gradeColor(g.grade)};">${g.grade != null ? parseFloat(g.grade).toFixed(1) : 'N/A'}</div>
                    ${g.confidence ? `<div style="font-size: 10px; color: var(--xg-muted); margin-top: 3px;">${formatConfidence(g.confidence)}</div>` : ''}
                    <div style="margin-top: 6px;">${getSeverityBadge(g.grade)}</div>
                </div>`).join('');
            return `<div style="flex: 1; min-width: 280px;">
                <h4 style="font-size: 15px; font-weight: 800; color: var(--xg-text); text-transform: uppercase; letter-spacing: 0.05em; margin: 0 0 12px 0;">${title}</h4>
                <div class="xim-corner-grid" style="grid-template-columns: repeat(2, minmax(0, 1fr));">${items}</div>
            </div>`;
        };
        const cornerPanel = subPanel('Individual Corner Grades', corners, _pgcCornerSquare, _PGC_CORNER_LABELS, 'Corner');
        const edgePanel = subPanel('Individual Edge Grades', edges, _pgcEdgeSquare, _PGC_EDGE_LABELS, 'Edge');
        return `<div style="margin-top: 16px; padding-top: 16px; border-top: 1px solid var(--xg-border); display: flex; gap: 18px; flex-wrap: wrap;">${cornerPanel}${edgePanel}</div>`;
    }

    function gradeColor(grade) {
        const num = parseFloat(grade);
        if (isNaN(num)) return 'var(--xg-text)';
        if (num >= 9) return '#10b981';
        if (num >= 7) return '#84cc16';
        if (num >= 5) return '#f59e0b';
        return '#ef4444';
    }

    function hexToRgba(hex, alpha) {
        let h = hex.replace('#', '');
        if (h.length === 3) h = h.split('').map(c => c + c).join('');
        const r = parseInt(h.substring(0, 2), 16) || 0;
        const g = parseInt(h.substring(2, 4), 16) || 0;
        const b = parseInt(h.substring(4, 6), 16) || 0;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    function getSeverityLabel(grade) {
        const num = parseFloat(grade);
        if (isNaN(num)) return 'Unknown';
        if (num >= 10) return 'Pristine';
        if (num >= 9) return 'Mint';
        if (num >= 8) return 'Near Mint';
        if (num >= 7) return 'Excellent';
        if (num >= 6) return 'Very Good';
        if (num >= 5) return 'Good';
        if (num >= 4) return 'Fair';
        return 'Poor';
    }

    function getSeverityBadge(grade) {
        const label = getSeverityLabel(grade);
        const color = gradeColor(grade);
        return `<span class="xg-sev" style="background:${color};">${label}</span>`;
    }

    function renderLimitingFactor(grades) {
        if (!grades) return '';
        const factors = [
            { key: 'centering', label: pgcT('subgrade.centering', 'Centering'), weight: pgcT('rr.psa.is.strict.bgs.cgc.moderate', 'PSA is strict; BGS/CGC moderate') },
            { key: 'surface', label: pgcT('subgrade.surface', 'Surface'), weight: pgcT('rr.sgc.is.strict.hurts.all.companies', 'SGC is strict; hurts all companies') },
            { key: 'edges', label: pgcT('subgrade.edges', 'Edges'), weight: pgcT('rr.bgs.sub.grades.heavily.penalize.edge.wear', 'BGS sub-grades heavily penalize edge wear') },
            { key: 'corners', label: pgcT('subgrade.corners', 'Corners'), weight: pgcT('rr.universal.grade.killer.on.all.scales', 'Universal grade killer on all scales') }
        ].map(f => ({ ...f, value: parseFloat(grades[f.key]) })).filter(f => !isNaN(f.value));
        if (!factors.length) return '';
        const lowest = factors.reduce((min, f) => f.value < min.value ? f : min, factors[0]);
        const nextLowest = factors.filter(f => f.key !== lowest.key).sort((a, b) => a.value - b.value)[0];
        const lc = gradeColor(lowest.value);
        let line = `<strong style="color: var(--xg-text);">${lowest.label}</strong> is the weakest sub-grade at <strong style="color: ${lc};">${lowest.value.toFixed(1)}</strong>. ${lowest.weight}.`;
        if (nextLowest) {
            line += ` <span style="color: var(--xg-muted);">Next concern: <strong style="color: ${gradeColor(nextLowest.value)};">${nextLowest.label} ${nextLowest.value.toFixed(1)}</strong></span>`;
        }
        return `
            <div style="background: var(--xg-panel); border: 1px solid var(--xg-border); border-left: 4px solid ${lc}; border-radius: 10px; padding: 12px 16px; margin: -8px 0 16px; box-shadow: var(--xg-shadow);">
                <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 6px;">
                    <span style="font-size: 10px; font-weight: 800; letter-spacing: 0.09em; text-transform: uppercase; color: var(--xg-muted);">Limiting factor</span>
                </div>
                <p style="margin: 0; font-size: 13px; color: var(--xg-text-2); line-height: 1.55;">${line}</p>
            </div>`;
    }

    function renderSurfaceInspection(surface, grades) {
        const grade = (function(_o){return _o != null ? _o : null})((function(_o){return _o != null ? _o : (grades != null ? grades.surface : void 0)})((surface != null ? surface.grade : void 0)));
        if (grade == null) return '';
        const num = parseFloat(grade);
        return `
            <div style="background: var(--xg-panel); border: 1px solid var(--xg-border); border-radius: 8px; padding: 18px; margin-bottom: 16px; box-shadow: var(--xg-shadow);">
                <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 12px;">
                    <div style="width: 32px; height: 32px; background: #e0f2fe; border: 1.5px solid #bae6fd; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0284c7" stroke-width="2.5"><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                    </div>
                    <h4 style="margin: 0; font-size: 17px; font-weight: 800; color: #0c4a6e; letter-spacing: -0.01em;">Surface Inspection</h4>
                    <span style="margin-left: auto;">${getSeverityBadge(grade)}</span>
                </div>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 12px;">
                    <div style="text-align: center; padding: 14px; background: var(--xg-panel); border-radius: 12px; border: 1px solid #e0f2fe;">
                        <div style="font-size: 11px; font-weight: 700; color: var(--xg-muted); text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 6px;">Surface Grade</div>
                        <div style="font-size: 2rem; font-weight: 700; color: ${gradeColor(grade)}; line-height: 1;">${num.toFixed(1)}</div>
                    </div>
                    ${(surface != null ? surface.label : void 0) ? `<div style="text-align: center; padding: 14px; background: var(--xg-panel); border-radius: 12px; border: 1px solid #e0f2fe;"><div style="font-size: 11px; font-weight: 700; color: var(--xg-muted); text-transform: uppercase; margin-bottom: 6px;">Surface Label</div><div style="font-size: 1rem; font-weight: 700; color: #0f172a;">${surface.label}</div></div>` : ''}
                    ${(surface != null ? surface.details : void 0) ? `<div style="grid-column: 1 / -1; padding: 12px; background: var(--xg-panel); border-radius: 12px; border: 1px solid #e0f2fe;"><div style="font-size: 11px; font-weight: 700; color: var(--xg-muted); text-transform: uppercase; margin-bottom: 4px;">Details</div><div style="font-size: 13px; color: var(--xg-text-2); line-height: 1.5;">${surface.details}</div></div>` : ''}
                </div>
                <p style="margin: 12px 0 0 0; font-size: 12px; color: var(--xg-muted); line-height: 1.5;">
                    Surface is evaluated for scratches, print defects, gloss disruptions, dents, and stains on both the front and back of the card. A low surface grade is the most common reason a PSA 10 candidate falls to a 9 or 8.
                </p>
            </div>`;
    }

    function renderConditionResult(record, config) {
        const condition = record.condition || {};
        const damaged = record.damaged || {};
        const detection = record.detection || {};
        const category = record.category || {};
        const side = record.side || {};
        const autograph = record.autograph || {};
        
        const isDamaged = damaged.is_damaged;
        const color = isDamaged ? '#ef4444' : config.color;
        const bg = isDamaged ? '#fef2f2' : config.colorLight;
        
        const metrics = [
            { label: pgcT('rr.condition', 'Condition'), value: condition.label, color: color, sub: `Mode: ${condition.mode || record.mode || 'N/A'}` },
            { label: pgcT('rr.score', 'Score'), value: condition.value ? parseFloat(condition.value).toFixed(2) : null },
            { label: pgcT('metric.damaged', 'Damaged'), value: damaged.value || (isDamaged ? 'Yes' : 'No'), color: isDamaged ? '#ef4444' : '#10b981' },
            { label: pgcT('metric.category', 'Category'), value: normalizeCategory(category.name || record.card_type) },
            { label: pgcT('metric.side', 'Side'), value: side.value },
            { label: pgcT('metric.autograph', 'Autograph'), value: (!isTCGCard(record) && autograph.has_autograph && ((autograph.confidence != null ? autograph.confidence : 0)) >= 0.60) ? 'Yes' : 'No' },
            { label: pgcT('metric.detection', 'Detection'), value: detection.confidence ? formatConfidence(detection.confidence) : null },
            { label: pgcT('metric.bounding.box', 'Bounding Box'), value: detection.bound_box ? detection.bound_box.join(', ') : null }
        ];
        
        return renderMetricGrid(metrics);
    }

    // Reusable centering details panel — visual diagram + pixel measurements + offsets.
    // Used by both the centering service and the complete grade result.
    // Implements the G1-Sky variation: sky-colored border bars (bigger = more
    // saturated), L/R + T/B gauge bars with 50/50 tick, dimension arrows,
    // center crosshairs/dots, shift arrow, F3 quadrant highlight, compass rose.
    function renderCenteringDetailsPanel(centering, accentColor, record) {
        const px = centering.pixels || [];
        const offsets = centering.offsets || [];
        const hasPx = px.length >= 4;
        const hasOffsets = offsets.length >= 4;
        const color = accentColor || 'var(--xg-text)';
        const centImg = record && record._pgc_centering_url ? record._pgc_centering_url : null;
        const mkPhotoFig = (basis) => centImg
            ? `<figure class="xim-centering-fig" style="margin:0;flex:0 1 ${basis};min-width:140px;max-width:100%;display:flex;flex-direction:column;">
                <div class="ximilar-zoomable" data-zoom-src="${escapeHtml(centImg)}" data-zoom-label="Centering overlay from your upload" title="Click to zoom" style="cursor:zoom-in;flex:1 1 auto;display:flex;align-items:center;justify-content:center;background:var(--xg-subtle);border:1px solid var(--xg-border);border-radius:8px;overflow:hidden;">
                    <img src="${escapeHtml(centImg)}" alt="Centering overlay generated from your original photo" loading="lazy" onerror="this.closest('figure').style.display='none'" style="width:100%;height:auto;display:block;">
                </div>
                <figcaption style="font-size:11px;color:var(--xg-muted);margin-top:6px;font-weight:600;min-height:45px;">Centering overlay generated from your upload: guides mark the printed border on each side.</figcaption>
               </figure>`
            : '';
        const photoFig = mkPhotoFig('auto');
        // Crosshair-split centering overlay (owner 2026-11-02) — OUR px borders
        // on the clean crop via api/pgc-centering-overlay; replaces Ximilar's
        // burned-in overlay in the details panel as well as the tile.
        const pgcCtrImg = (record && record._pgc_centering_overlay_url) || null;
        const ximCentImg = (record && record._images && record._images.exact) || (record && record._exact_url_card) || null;
        // height:0 + padding-bottom instead of aspect-ratio — see ResultRenderer
        // mkXimFig: aspect-ratio collapses to ~0 inside a column flex figure on
        // iPhone (children are absolute, so content height is 0).
        const mkXimFig = (svgAspect, imgTopPct, imgHeightPct, captionTopPct) => pgcCtrImg
            ? `<figure class="xim-centering-fig" style="margin:0;flex:1 1 0;min-width:0;max-width:100%;display:flex;flex-direction:column;">
                <div class="ximilar-zoomable" data-zoom-src="${escapeHtml(pgcCtrImg)}" data-zoom-label="Centering overlay" title="Click to zoom" style="cursor:zoom-in;flex:none;position:relative;background:var(--xg-subtle);border:1px solid var(--xg-border);border-radius:8px;overflow:hidden;height:0;padding-bottom:${(100 / Math.max(0.01, svgAspect)).toFixed(4)}%;">
                    <div style="position:absolute;top:0;left:0;right:0;bottom:0;display:flex;align-items:center;justify-content:center;">
                        <img src="${escapeHtml(pgcCtrImg)}" alt="Centering overlay" loading="lazy" onerror="this.closest('figure').style.display='none'" style="max-width:100%;max-height:100%;object-fit:contain;display:block;">
                    </div>
                </div>
                <div style="display:flex;align-items:center;justify-content:center;padding:8px 10px 0;font-size:10.5px;color:var(--xg-muted);font-weight:600;line-height:1.3;text-align:center;">Centering overlay: crosshair marks the measured card center; labels are the saved printed borders.</div>
               </figure>`
            : ximCentImg
            ? `<figure class="xim-centering-fig" style="margin:0;flex:1 1 0;min-width:0;max-width:100%;display:flex;flex-direction:column;">
                <div class="ximilar-zoomable" data-zoom-src="${escapeHtml(ximCentImg)}" data-zoom-label="Centering overlay" title="Click to zoom" style="cursor:zoom-in;flex:none;position:relative;background:var(--xg-subtle);border:1px solid var(--xg-border);border-radius:8px;overflow:hidden;height:0;padding-bottom:${(100 / Math.max(0.01, svgAspect)).toFixed(4)}%;">
                    <div style="position:absolute;top:${imgTopPct}%;height:${imgHeightPct}%;left:0;right:0;display:flex;align-items:center;justify-content:center;">
                        <img src="${escapeHtml(ximCentImg)}" alt="Centering overlay" loading="lazy" onerror="this.closest('figure').style.display='none'" style="width:100%;height:100%;object-fit:contain;display:block;">
                    </div>
                    <div style="position:absolute;top:${captionTopPct}%;bottom:0;left:0;right:0;display:flex;align-items:center;justify-content:center;padding:0 10px;font-size:10.5px;color:var(--xg-muted);font-weight:600;line-height:1.3;text-align:center;">Centering overlay: guides mark the measured printed border on each side.</div>
                </div>
               </figure>`
            : '';

        let diagram = '';
        if (hasPx) {
            const [lPx, tPx, rPx, bPx] = px;
            const lrImbalance = lPx > rPx ? 'shifted right' : (rPx > lPx ? 'shifted left' : 'centered');
            const tbImbalance = (tPx + bPx === 0)
                ? 'not measurable'
                : (tPx > bPx ? 'shifted down' : (bPx > tPx ? 'shifted up' : 'centered'));

            // Representative card (2.5:3.5 TCG aspect) so the card is a real
            // rectangle and the shift is visible. Borders proportional to real px.
            const CARD_W = 320, CARD_H = 448;
            const frameW = CARD_W + lPx + rPx;
            const frameH = CARD_H + tPx + bPx;
            const cardLeft = lPx, cardTop = tPx;
            const cardCx = cardLeft + CARD_W / 2;
            const cardCy = cardTop + CARD_H / 2;
            const frameCx = frameW / 2;
            const frameCy = frameH / 2;
            const shiftX = cardCx - frameCx;
            const shiftY = cardCy - frameCy;
            // Guard zero-border axes: when the card touches both frame edges
            // (e.g. border clipped or undetectable), l+r or t+b sums to 0 and
            // naive division produces NaN — show '—' and skip that gauge.
            const lrSum = lPx + rPx;
            const tbSum = tPx + bPx;
            const lrValid = lrSum > 0;
            const tbValid = tbSum > 0;
            const lrL = lrValid ? lPx / lrSum : 0;
            const lrR = lrValid ? rPx / lrSum : 0;
            const tbT = tbValid ? tPx / tbSum : 0;
            const tbB = tbValid ? bPx / tbSum : 0;
            const lrPct = lrValid
                ? { l: (lrL * 100).toFixed(0), r: (lrR * 100).toFixed(0) }
                : { l: '—', r: '—' };
            const tbPct = tbValid
                ? { t: (tbT * 100).toFixed(0), b: (tbB * 100).toFixed(0) }
                : { t: '—', b: '—' };

            const pad = 58;
            const sw = Math.min(frameW, frameH);
            const fSize = sw * 0.052;
            const padR = 58 + fSize * 3.5; // extra right padding for vertical T/B gauge
            const strokeFrame = sw * 0.006;
            const strokeCard = sw * 0.012;
            const strokeAlign = sw * 0.004;
            const strokeDim = sw * 0.007;
            const inset = sw * 0.012;

            // Sky color scheme
            const skyAccent = '#38bdf8';
            const maxBorder = Math.max(lPx, rPx, tPx, bPx);
            const heat = (v) => {
                const t = v / maxBorder;
                return `rgba(14,165,233,${(0.20 + t * 0.55).toFixed(2)})`;
            };

            // Quadrant highlight (F3) — boosted contrast
            const inLeft = cardCx < frameCx;
            const inTop = cardCy < frameCy;
            const hx = inLeft ? 0 : frameCx;
            const hy = inTop ? 0 : frameCy;

            // Shift arrow (min length for visibility)
            const rawLen = Math.hypot(shiftX, shiftY);
            const minLen = sw * 0.06;
            const arrowScale = rawLen < minLen && rawLen > 0.001 ? minLen / rawLen : 1;
            const ex = frameCx + shiftX * arrowScale;
            const ey = frameCy + shiftY * arrowScale;

            // Compass rose (top-right corner, inside right padding)
            const roseR = sw * 0.06;
            const roseCx = frameW + padR - roseR * 1.3;
            const roseCy = -pad * 0.35 + roseR * 1.3;
            let nx = 0, ny = 1;
            if (rawLen > 0.001) { nx = shiftX / rawLen; ny = shiftY / rawLen; }
            const needleR = roseR * 0.78;

            // Gauge bar geometry — L/R horizontal below frame, T/B vertical right of frame
            const gaugeW = frameW * 0.72;
            const gaugeX = (frameW - gaugeW) / 2;
            const gaugeBarH = fSize * 1.5;
            const gaugeY1 = frameH + pad * 0.45;
            // T/B vertical gauge (right side)
            const gaugeX2 = frameW + pad * 0.55;
            const gaugeH2 = frameH * 0.7;
            const gaugeY2 = (frameH - gaugeH2) / 2;

            // Score headline first (mirrors ResultRenderer); photo + schematic
            // centered side by side on their own row, measurements below.
            // nowrap keeps the pair adjacent.
            const scoreBox = (centering.grade != null && centering.grade !== '') ? (() => {
                const cg = parseFloat(centering.grade);
                const cColor = gradeColor(centering.grade);
                const cValid = !isNaN(cg);
                return `<div style="display: flex; align-items: center; gap: 14px; padding: 14px 16px; background: var(--xg-subtle); border: 1px solid var(--xg-border); border-left: 4px solid ${cColor}; border-radius: 10px; margin-bottom: 12px;">
                    <div style="flex-shrink: 0; width: 56px; height: 56px; border-radius: 50%; background: ${cColor}; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px ${hexToRgba(cColor.startsWith('#') ? cColor : '#64748b', 0.35)};">
                        <span style="font-size: 22px; font-weight: 800; color: white; line-height: 1;">${cValid ? cg.toFixed(1) : '—'}</span>
                    </div>
                    <div>
                                <div style="font-size: 11px; font-weight: 700; color: var(--xg-muted); text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 2px;">Saved Centering Score</div>
                        <div style="font-size: 18px; font-weight: 800; color: var(--xg-text); line-height: 1.2;">${cValid ? cg.toFixed(1) + ' / 10' : 'Not scored'}</div>
                        <div style="font-size: 11px; color: var(--xg-muted); margin-top: 2px;">${cValid ? (cg >= 9 ? 'Excellent' : cg >= 7 ? 'Good' : cg >= 5 ? 'Fair' : 'Poor') + ' centering' : ''}</div>
                    </div>
                </div>`;
            })() : '';
            const offsetsCol = hasOffsets
                ? `<div style="margin-top: 12px; padding: 8px 10px; background: var(--xg-subtle); border-radius: 6px; border: 1px solid var(--xg-border); font-size: 11.5px; color: var(--xg-muted); line-height: 1.5;">
                    <strong style="color: var(--xg-text);">Normalized offsets:</strong> ${offsets.map(o => Number(o).toFixed(4)).join(', ')}
                    <div style="margin-top: 4px;">Border widths as a fraction of the card's full dimension (0.05 = 5%). Lower = tighter.</div>
                   </div>`
                : '';
            // Split the pair row so both CARDS render the same width (equal
            // widths = equal heights on the shared portrait aspect). The
            // centering webp is card + ~4.7% margin each side (card ≈ 91% of
            // image width); the svg's drawn frame is frameW/vbW of its
            // element. Photo basis = f/(f+0.91) of the row.
            const vbH = frameH + pad * 2 + gaugeBarH + fSize * 2.5;
            const vbW = frameW + pad + padR;
            const svgFrac = vbW > 0 ? frameW / vbW : 1;
            const photoShare = Math.round(1000 * svgFrac / (svgFrac + 0.91)) / 10;
            // Drop the drawing ~10 rendered px inside its box: viewBox units
            // scale with element width (~0.5 px/unit at the ~500px pair cap),
            // so a ~3.5% top extension lands close to 10px across widths.
            const vbY = -pad - Math.round(vbW * 0.035);
            diagram = `
                ${scoreBox}
                <div class="xim-centering-pair" style="display: flex; gap: 14px; align-items: stretch; justify-content: center; flex-wrap: nowrap; margin: 0 auto; max-width: 560px;">
                    ${mkXimFig(
                        (vbH > 0 ? vbW / vbH : 1).toFixed(4),
                        ((-vbY / vbH) * 100).toFixed(2),
                        ((frameH / vbH) * 100).toFixed(2),
                        (((frameH - vbY) / vbH) * 100).toFixed(2)
                    )}
                    <div class="xim-centering-svg" style="flex:1 1 0;min-width:0;max-width:min(560px,100%);display:flex;flex-direction:column;">
                    <svg viewBox="${-pad} ${vbY} ${vbW} ${vbH}" style="width: 100%; height: auto; background: var(--xg-subtle); border-radius: 8px; border: 1px solid var(--xg-border);" preserveAspectRatio="xMidYMid meet">
                        <defs>
                            <marker id="cdDim" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 Z" fill="var(--xg-text)"/></marker>
                            <marker id="cdArr" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 Z" fill="${skyAccent}"/></marker>
                        </defs>
                        <!-- Quadrant highlight -->
                        <rect x="${hx}" y="${hy}" width="${frameW / 2}" height="${frameH / 2}" fill="${skyAccent}" opacity="0.18"/>
                        <!-- Image frame (dashed) -->
                        <rect x="0" y="0" width="${frameW}" height="${frameH}" fill="none" stroke="var(--xg-muted)" stroke-width="${strokeFrame}" stroke-dasharray="${strokeFrame*3},${strokeFrame*2}" rx="2"/>
                        <!-- Sky border side-bars: bigger border = more saturated -->
                        <path d="M0 0 H${frameW} V${frameH} H0 Z M${cardLeft} ${cardTop} H${cardLeft + CARD_W} V${cardTop + CARD_H} H${cardLeft} Z" fill="rgba(56,189,248,0.30)" fill-rule="evenodd"/>
                        <!-- Card -->
                        <rect x="${cardLeft}" y="${cardTop}" width="${CARD_W}" height="${CARD_H}" fill="rgba(56,189,248,0.08)" stroke="${skyAccent}" stroke-width="${strokeCard}" rx="2"/>
                        <!-- Center crosshairs -->
                        <line x1="${frameCx - fSize*1.3}" y1="${frameCy}" x2="${frameCx + fSize*1.3}" y2="${frameCy}" stroke="var(--xg-muted)" stroke-width="${strokeAlign}"/>
                        <line x1="${frameCx}" y1="${frameCy - fSize*1.3}" x2="${frameCx}" y2="${frameCy + fSize*1.3}" stroke="var(--xg-muted)" stroke-width="${strokeAlign}"/>
                        <line x1="${cardCx - fSize*1.3}" y1="${cardCy}" x2="${cardCx + fSize*1.3}" y2="${cardCy}" stroke="${skyAccent}" stroke-width="${strokeAlign}"/>
                        <line x1="${cardCx}" y1="${cardCy - fSize*1.3}" x2="${cardCx}" y2="${cardCy + fSize*1.3}" stroke="${skyAccent}" stroke-width="${strokeAlign}"/>
                        <!-- Center dots -->
                        <circle cx="${frameCx}" cy="${frameCy}" r="${sw*0.007}" fill="var(--xg-muted)"/>
                        <circle cx="${cardCx}" cy="${cardCy}" r="${sw*0.007}" fill="${skyAccent}"/>
                        <!-- Shift arrow -->
                        <line x1="${frameCx}" y1="${frameCy}" x2="${ex}" y2="${ey}" stroke="${skyAccent}" stroke-width="${sw*0.008}" marker-end="url(#cdArr)"/>
                        <!-- Dimension arrows inside border gaps (thin lines, no labels) -->
                        ${tPx > inset * 2 ? `<line x1="${frameCx}" y1="${inset}" x2="${frameCx}" y2="${tPx - inset}" stroke="var(--xg-text)" stroke-width="${strokeDim}" marker-start="url(#cdDim)" marker-end="url(#cdDim)"/>` : ''}
                        ${bPx > inset * 2 ? `<line x1="${frameCx}" y1="${cardTop + CARD_H + inset}" x2="${frameCx}" y2="${frameH - inset}" stroke="var(--xg-text)" stroke-width="${strokeDim}" marker-start="url(#cdDim)" marker-end="url(#cdDim)"/>` : ''}
                        ${lPx > inset * 2 ? `<line x1="${inset}" y1="${cardCy}" x2="${lPx - inset}" y2="${cardCy}" stroke="var(--xg-text)" stroke-width="${strokeDim}" marker-start="url(#cdDim)" marker-end="url(#cdDim)"/>` : ''}
                        ${rPx > inset * 2 ? `<line x1="${cardLeft + CARD_W + inset}" y1="${cardCy}" x2="${frameW - inset}" y2="${cardCy}" stroke="var(--xg-text)" stroke-width="${strokeDim}" marker-start="url(#cdDim)" marker-end="url(#cdDim)"/>` : ''}
                        <!-- Border px labels: top/bottom inside gaps, left/right OUTSIDE frame -->
                        ${tPx > fSize ? `<rect x="${frameCx - fSize*0.35}" y="${tPx/2 - fSize*0.62}" width="${(tPx+'px').length*fSize*0.7}" height="${fSize*1.24}" fill="var(--xg-subtle)" rx="2"/>
                        <text x="${frameCx}" y="${tPx/2 + fSize*0.34}" text-anchor="middle" font-size="${fSize*0.95}" fill="var(--xg-text)" font-weight="700">${tPx}px</text>` : ''}
                        ${bPx > fSize ? `<rect x="${frameCx - fSize*0.35}" y="${cardTop + CARD_H + bPx/2 - fSize*0.62}" width="${(bPx+'px').length*fSize*0.7}" height="${fSize*1.24}" fill="var(--xg-subtle)" rx="2"/>
                        <text x="${frameCx}" y="${cardTop + CARD_H + bPx/2 + fSize*0.34}" text-anchor="middle" font-size="${fSize*0.95}" fill="var(--xg-text)" font-weight="700">${bPx}px</text>` : ''}
                        <text x="${-pad * 0.45}" y="${cardCy + fSize*0.34}" text-anchor="middle" font-size="${fSize*0.95}" fill="var(--xg-text)" font-weight="700">${lPx}px</text>
                        <text x="${frameW + pad * 0.45}" y="${cardCy + fSize*0.34}" text-anchor="middle" font-size="${fSize*0.95}" fill="var(--xg-text)" font-weight="700">${rPx}px</text>
                        <!-- L/R + T/B ratio gauges (dominant share in sky) -->
                        <rect x="${gaugeX}" y="${gaugeY1}" width="${gaugeW * lrL}" height="${gaugeBarH}" fill="${lrL >= 0.5 ? skyAccent : 'var(--xg-muted)'}" opacity="0.75" rx="2"/>
                        <rect x="${gaugeX + gaugeW * lrL}" y="${gaugeY1}" width="${gaugeW * lrR}" height="${gaugeBarH}" fill="${lrR > 0.5 ? skyAccent : 'var(--xg-muted)'}" opacity="0.75" rx="2"/>
                        <text x="${gaugeX - 6}" y="${gaugeY1 + gaugeBarH * 0.72}" text-anchor="end" font-size="${fSize * 0.72}" fill="var(--xg-muted)" font-weight="700">L/R</text>
                        <text x="${gaugeX + gaugeW / 2}" y="${gaugeY1 + gaugeBarH + fSize}" text-anchor="middle" font-size="${fSize * 0.72}" fill="var(--xg-text)" font-weight="700">${lrPct.l}/${lrPct.r}</text>
                        <rect x="${gaugeX2}" y="${gaugeY2}" width="${gaugeBarH}" height="${gaugeH2 * tbT}" fill="${tbT >= 0.5 ? skyAccent : 'var(--xg-muted)'}" opacity="0.75" rx="2"/>
                        <rect x="${gaugeX2}" y="${gaugeY2 + gaugeH2 * tbT}" width="${gaugeBarH}" height="${gaugeH2 * tbB}" fill="${tbB > 0.5 ? skyAccent : 'var(--xg-muted)'}" opacity="0.75" rx="2"/>
                        <text x="${gaugeX2 + gaugeBarH / 2}" y="${gaugeY2 - 8}" text-anchor="middle" font-size="${fSize * 0.72}" fill="var(--xg-muted)" font-weight="700">T/B</text>
                        <text x="${gaugeX2 + gaugeBarH / 2}" y="${gaugeY2 + gaugeH2 + fSize * 0.9}" text-anchor="middle" font-size="${fSize * 0.68}" fill="var(--xg-text)" font-weight="700">${tbPct.t}/${tbPct.b}</text>
                        <line x1="${roseCx}" y1="${roseCy - roseR}" x2="${roseCx}" y2="${roseCy + roseR}" stroke="var(--xg-muted)" stroke-width="${roseR*0.02}"/>
                        <line x1="${roseCx - roseR}" y1="${roseCy}" x2="${roseCx + roseR}" y2="${roseCy}" stroke="var(--xg-muted)" stroke-width="${roseR*0.02}"/>
                        <line x1="${roseCx}" y1="${roseCy}" x2="${roseCx + nx * needleR}" y2="${roseCy + ny * needleR}" stroke="${skyAccent}" stroke-width="${roseR*0.1}" marker-end="url(#cdArr)"/>
                        <circle cx="${roseCx}" cy="${roseCy}" r="${roseR*0.1}" fill="var(--xg-text)"/>
                        <text x="${roseCx}" y="${roseCy - roseR - roseR*0.1}" text-anchor="middle" font-size="${fSize*0.7}" fill="var(--xg-muted)">N</text>
                        <text x="${roseCx}" y="${roseCy + roseR + roseR*0.5}" text-anchor="middle" font-size="${fSize*0.75}" fill="var(--xg-text)" font-weight="700">${rawLen.toFixed(1)}px</text>
                    </svg>
                    </div>
                </div>
                <div class="xim-centering-info" style="max-width:560px;min-width:200px;margin:14px auto 0;">
                        <div style="font-size: 13px; font-weight: 700; color: var(--xg-text); margin-bottom: 8px;">Border Measurements</div>
                        <div class="xim-ratio-row" style="display: grid; grid-template-columns: 1fr 1fr !important; gap: 8px;">
                            <div class="xim-ratio-pill" style="background: var(--xg-subtle); border: 1px solid var(--xg-border); border-radius: 8px; padding: 6px 10px; font-size: 12px;"><span style="color: var(--xg-muted); font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; font-weight: 700;">Saved L/R Ratio</span><span style="color: var(--xg-text); font-weight: 800; font-size: 15px; display: block; margin-top: 1px;">${lrPct.l}/${lrPct.r}</span></div>
                            <div class="xim-ratio-pill" style="background: var(--xg-subtle); border: 1px solid var(--xg-border); border-radius: 8px; padding: 6px 10px; font-size: 12px;"><span style="color: var(--xg-muted); font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; font-weight: 700;">Saved T/B Ratio</span><span style="color: var(--xg-text); font-weight: 800; font-size: 15px; display: block; margin-top: 1px;">${tbPct.t}/${tbPct.b}</span></div>
                        </div>
                        <div class="xim-border-grid" style="display: grid; grid-template-columns: 1fr 1fr !important; gap: 8px; font-size: 13px; margin-top: 8px;">
                            <div class="xim-border-cell" style="background: var(--xg-subtle); padding: 6px 10px; border-radius: 6px; border: 1px solid var(--xg-border); display: flex; align-items: baseline; justify-content: space-between; gap: 6px;"><span style="color: var(--xg-muted); font-size: 11.5px;">Left</span><strong style="color: var(--xg-text);">${lPx}px</strong></div>
                            <div class="xim-border-cell" style="background: var(--xg-subtle); padding: 6px 10px; border-radius: 6px; border: 1px solid var(--xg-border); display: flex; align-items: baseline; justify-content: space-between; gap: 6px;"><span style="color: var(--xg-muted); font-size: 11.5px;">Top</span><strong style="color: var(--xg-text);">${tPx}px</strong></div>
                            <div class="xim-border-cell" style="background: var(--xg-subtle); padding: 6px 10px; border-radius: 6px; border: 1px solid var(--xg-border); display: flex; align-items: baseline; justify-content: space-between; gap: 6px;"><span style="color: var(--xg-muted); font-size: 11.5px;">Right</span><strong style="color: var(--xg-text);">${rPx}px</strong></div>
                            <div class="xim-border-cell" style="background: var(--xg-subtle); padding: 6px 10px; border-radius: 6px; border: 1px solid var(--xg-border); display: flex; align-items: baseline; justify-content: space-between; gap: 6px;"><span style="color: var(--xg-muted); font-size: 11.5px;">Bottom</span><strong style="color: var(--xg-text);">${bPx}px</strong></div>
                        </div>
                        <div class="xim-centering-desc" style="margin-top: 10px; font-size: 12px; color: var(--xg-muted); line-height: 1.5;">
                            The card is <strong style="color: var(--xg-text);">${lrImbalance}</strong> horizontally and <strong style="color: var(--xg-text);">${tbImbalance}</strong> vertically relative to the image frame.
                        </div>
                    ${offsetsCol}
                </div>`;
        }

        let offsetsBlock = '';
        if (!diagram && hasOffsets) {
            offsetsBlock = `
                <div style="margin-top: 14px; padding: 10px 12px; background: var(--xg-subtle); border-radius: 6px; border: 1px solid var(--xg-border); font-size: 12px; color: var(--xg-muted); line-height: 1.5;">
                    <strong style="color: var(--xg-text);">Normalized offsets:</strong> ${offsets.map(o => Number(o).toFixed(4)).join(', ')}
                    <div style="margin-top: 4px;">These are the border widths as a fraction of the card's full dimension (0.05 = 5% border). Lower = tighter centering.</div>
                </div>`;
        }

        if (!diagram && !offsetsBlock && photoFig) {
            offsetsBlock = `
                <div style="display: flex; gap: 16px; align-items: flex-start; flex-wrap: wrap; justify-content: center; margin-top: 14px;">
                    ${''/* photoFig — hidden 2026-10-24 pending rework, see pair row */}
                    <div style="flex:1 1 220px;min-width:200px;max-width:100%;">
                        <div style="font-size: 13px; font-weight: 700; color: var(--xg-text); margin-bottom: 8px;">Physical Card Cut</div>
                        <div style="font-size: 12px; color: var(--xg-muted); line-height: 1.5;">No saved print-border measurements are available for this record.</div>
                    </div>
                </div>`;
        }

        const detailsHtml = (diagram || offsetsBlock) ? diagram + offsetsBlock : '';
        return detailsHtml ? renderDataBlock('Centering Details', detailsHtml, color, 'var(--xg-subtle)') : '';
    }

    function renderCenteringResult(record, config) {
        const centering = record.centering || {};
        const card = record.card || {};
        const tags = card._tags || {};
        
        const metrics = [
            { label: pgcT('rr.centering.grade', 'Centering Grade'), value: centering.grade, color: gradeColor(centering.grade) },
            { label: pgcT('rr.left.right', 'Left / Right'), value: centering.left_right, sub: 'border ratio (%)' },
            { label: pgcT('rr.top.bottom', 'Top / Bottom'), value: centering.top_bottom, sub: 'border ratio (%)' },
            { label: pgcT('metric.side', 'Side'), value: capitalizeFirst((function(_o){return _o == null ? void 0 : _o.name})((tags.Side != null ? tags.Side[0] : void 0)) || (record.side != null ? record.side.value : void 0)) }
        ];
        
        return renderMetricGrid(metrics) + renderCenteringDetailsPanel(centering, config.color, record);
    }

    function renderCropResult(record, config) {
        const croppedSrc = formatImageSrc(record._img_url || record.cropped_image_base64);
        const originalSrc = formatImageSrc(record.original_image_url);
        // Build descriptive alt text from the record's identification
        const _cropId = (record.card && record.card.identification) || record.identification || {};
        const _cropAlt = buildAltText({
            year: _cropId.year,
            name: _cropId.name || _cropId.full_name,
            setName: _cropId.set_name || _cropId.set,
            setCode: _cropId.set_code,
            cardNumber: cleanCardNum(_cropId.card_number || _cropId.card_no),
            rarity: _cropId.rarity,
            gradeLabel: record.final_grade_label,
            grade: record.grades && record.grades.final
        });
        const _origAlt = _cropAlt + ' — original uploaded photo';
        const _cropAltFull = _cropAlt + ' — cropped card result';
        const metrics = [
            { label: pgcT('rr.width', 'Width'), value: record._width ? record._width + 'px' : null },
            { label: pgcT('rr.height', 'Height'), value: record._height ? record._height + 'px' : null },
            { label: pgcT('rr.crop.level', 'Crop Level'), value: record.crop_level },
            { label: pgcT('rr.card.detected', 'Card Detected'), value: record.card_detected ? 'Yes' : 'No' }
        ];
        
        let imageComparison = '';
        if (originalSrc && croppedSrc) {
            imageComparison = `
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px;">
                    <div style="text-align: center;">
                        <div style="font-size: 11px; color: var(--xg-muted); margin-bottom: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em;">Original Photo</div>
                        <div style="background: var(--xg-subtle); border-radius: 12px; padding: 12px; border: 1px solid var(--xg-border);">
                            <img src="${pgcThumb(originalSrc, 640)}" alt="${escapeHtml(_origAlt)}" style="width: 100%; max-height: 220px; object-fit: contain; border-radius: 8px;" loading="lazy">
                        </div>
                    </div>
                    <div style="text-align: center;">
                        <div style="font-size: 11px; color: ${config.color}; margin-bottom: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em;">Cropped Card</div>
                        <div style="background: var(--xg-subtle); border-radius: 12px; padding: 12px; border: 1px solid ${config.color};">
                            <img src="${pgcThumb(croppedSrc, 640)}" alt="${escapeHtml(_cropAltFull)}" style="width: auto; max-width: 100%; max-height: 220px; display: block; margin: 0 auto; border-radius: 5% / 3.6%;" loading="lazy">
                        </div>
                    </div>
                </div>
            `;
        }
        
        let download = '';
        if (croppedSrc) {
            download = `
                <div style="text-align: center; margin-top: 12px;">
                    <a href="${croppedSrc}" download="cropped-card.png" style="
                        display: inline-flex; align-items: center; gap: 8px;
                        padding: 12px 24px; background: ${config.color}; color: white;
                        border-radius: 10px; text-decoration: none; font-weight: 700; font-size: 14px;
                        transition: all 0.2s;
                    " onmouseover="this.style.opacity='0.9'" onmouseout="this.style.opacity='1'">
                        📥 Download Cropped Image
                    </a>
                </div>
            `;
        }
        
        return imageComparison + renderMetricGrid(metrics) + download;
    }

    function renderLocalizeResult(record, config) {
        const detection = record.detection || {};
        const card = record.card || {};
        const corners = record.corners || [];
        const edges = record.edges || [];
        
        const bb = detection.bound_box || (card != null ? card.bound_box : void 0) || [];
        const metrics = [
            { label: pgcT('rr.card.detected', 'Card Detected'), value: record.card_detected ? 'Yes' : 'No', color: record.card_detected ? '#10b981' : '#ef4444' },
            { label: pgcT('metric.confidence', 'Confidence'), value: detection.confidence ? formatConfidence(detection.confidence) : null },
            { label: pgcT('metric.bounding.box', 'Bounding Box'), value: bb.length === 4 ? bb.join(', ') : null },
            { label: pgcT('subgrade.corners', 'Corners'), value: corners.length > 0 ? corners.length + ' found' : null },
            { label: pgcT('subgrade.edges', 'Edges'), value: edges.length > 0 ? edges.length + ' found' : null },
            { label: pgcT('rr.image.size', 'Image Size'), value: record._width && record._height ? record._width + '×' + record._height : null }
        ];
        
        return renderMetricGrid(metrics);
    }

    function renderPossibleMatches(alternatives, config) {
        if (!Array.isArray(alternatives) || alternatives.length === 0) return '';

        const matches = alternatives.filter(match => match && typeof match === 'object').slice(0, 5);
        if (matches.length === 0) return '';

        const matchCards = matches.map((match, index) => {
            const details = [
                match.year ? { label: pgcT('rr.year', 'Year'), value: match.year } : null,
                (match.set_name || match.set) ? { label: pgcT('rr.set', 'Set'), value: match.set_name || match.set } : null,
                match.sub_set ? { label: pgcT('rr.subset', 'Subset'), value: match.sub_set } : null,
                cleanCardNum(match.card_number || match.card_no) ? { label: pgcT('rr.card', 'Card #'), value: cleanCardNum(match.card_number || match.card_no) } : null,
                match.company ? { label: pgcT('rr.company', 'Company'), value: match.company } : null,
                match.team ? { label: pgcT('rr.team', 'Team'), value: match.team } : null,
                (match.subcategory || match.series) ? { label: match.series ? 'Series' : 'Sport/Game', value: match.subcategory || match.series } : null,
                (match.card_type || match.type) ? { label: pgcT('rr.type', 'Type'), value: match.card_type || match.type } : null,
                match.rarity ? { label: pgcT('rr.rarity', 'Rarity'), value: match.rarity } : null
            ].filter(Boolean);
            const links = match.links && typeof match.links === 'object'
                ? Object.entries(match.links).filter(([, url]) => /^https?:\/\//i.test(String(url || '')))
                : [];
            const latestStat = Array.isArray(match.price_stats)
                ? match.price_stats.find(stat => (stat != null ? stat.stats_type : void 0) === 'overall' && (function(_o){return _o == null ? void 0 : _o.latest})((stat != null ? stat.value : void 0)) != null)
                    || match.price_stats.find(stat => (function(_o){return _o == null ? void 0 : _o.latest})((stat != null ? stat.value : void 0)) != null)
                : null;
            const latestValue = (function(_o){return _o == null ? void 0 : _o.latest})((latestStat != null ? latestStat.value : void 0));
            const saleCount = (function(_o){return _o == null ? void 0 : _o.volume})((latestStat != null ? latestStat.value : void 0));

            return `
                <article style="background: var(--xg-panel); border: 1px solid #fde68a; border-radius: 10px; padding: 16px; min-width: 0;">
                    <div style="display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 12px;">
                        <div style="min-width: 0;">
                            <div style="font-size: 10px; color: #92400e; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 4px;">Possible match ${index + 1}</div>
                            <div style="font-size: 16px; line-height: 1.35; font-weight: 800; color: var(--xg-text); overflow-wrap: anywhere;">${escapeHtml(String(match.full_name || match.name || 'Unspecified card'))}</div>
                        </div>
                        <span style="flex-shrink: 0; border-radius: 999px; background: #fef3c7; color: #92400e; padding: 4px 8px; font-size: 10px; font-weight: 800;">UNCONFIRMED</span>
                    </div>
                    ${details.length ? `<div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(110px, 1fr)); gap: 8px;">${details.map(detail => `
                        <div style="background: #fffbeb; border-radius: 6px; padding: 8px 10px; min-width: 0;">
                            <div style="font-size: 9px; color: #92400e; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 2px;">${escapeHtml(detail.label)}</div>
                            <div style="font-size: 12px; color: var(--xg-text-2); font-weight: 700; overflow-wrap: anywhere;">${escapeHtml(String(detail.value))}</div>
                        </div>`).join('')}</div>` : ''}
                    ${(latestValue != null || saleCount != null) ? `<div style="font-size: 12px; color: var(--xg-muted); margin-top: 10px;"><strong style="color: var(--xg-text-2);">Candidate market data:</strong>${latestValue != null ? ` latest sale $${Number(latestValue).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}${saleCount != null ? ` · ${escapeHtml(String(saleCount))} recorded sale${Number(saleCount) === 1 ? '' : 's'}` : ''}</div>` : ''}
                    ${links.length ? `<div style="display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px;">${links.map(([site, url]) => `<a href="${escapeHtml(String(url))}" target="_blank" rel="noopener" style="display: inline-flex; padding: 6px 9px; border: 1px solid #fcd34d; border-radius: 6px; color: #92400e; background: #fffbeb; text-decoration: none; font-size: 11px; font-weight: 700;">Check ${escapeHtml(String(site))}</a>`).join('')}</div>` : ''}
                </article>`;
        }).join('');

        return `
            <section class="ximilar-result-card" style="background: #fffbeb; border: 1px solid #fcd34d; border-left: 4px solid #f59e0b; border-radius: 8px; padding: 20px; margin-bottom: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.06);">
                <div style="display: flex; align-items: flex-start; gap: 12px; margin-bottom: 16px;">
                    <div aria-hidden="true" style="width: 30px; height: 30px; flex: 0 0 30px; border-radius: 50%; background: #fef3c7; color: #92400e; display: flex; align-items: center; justify-content: center; font-size: 16px; font-weight: 900;">?</div>
                    <div>
                        <h3 style="font-size: 16px; line-height: 1.3; font-weight: 800; color: #78350f; margin: 0 0 5px;">Possible card matches</h3>
                        <p style="font-size: 13px; line-height: 1.55; color: #92400e; margin: 0;">The API could not establish a high-confidence primary identity. These alternatives are possible results, not confirmed identifications. Compare the year, card number, set, player and team with the card before relying on pricing.</p>
                    </div>
                </div>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 12px;">${matchCards}</div>
            </section>`;
    }

    function renderSportIdResult(record, config) {
        const card = record.card || {};
        const id = card.identification || record.identification || {};
        const tags = card.tags || {};
        const listings = card.listings || record.listings || null;
        const priceStats = card.price_stats || record.price_stats || null;
        const alternatives = card.alternatives || record.alternatives || [];

        // ── Card Identity Metrics ──────────────────────────────────────────────
        const rawCategory = (tags.category != null ? tags.category.name : void 0) || record.category;
        const categoryValue = /^(Card\/Sport Card|Sport Card|Card)$/i.test(rawCategory) ? 'Card' : rawCategory;
        const metrics = [
            { label: pgcT('rr.year', 'Year'),      value: id.year },
            { label: pgcT('rr.set', 'Set'),       value: id.set_name || id.set },
            { label: pgcT('rr.card', 'Card #'),    value: cleanCardNum(id.card_number || id.card_no) },
            { label: pgcT('rr.company', 'Company'),   value: id.company },
            { label: pgcT('rr.team', 'Team'),      value: id.team },
            { label: pgcT('metric.category', 'Category'),  value: categoryValue },
            { label: pgcT('rr.type', 'Type'),      value: id.card_type },
            { label: pgcT('metric.side', 'Side'),      value: capitalizeFirst((tags.side != null ? tags.side.name : void 0)) },
            { label: pgcT('metric.autograph', 'Autograph'), value: ((tags.autograph != null ? tags.autograph.has_autograph : void 0) && ((function(_o){return _o != null ? _o : 0})((tags.autograph != null ? tags.autograph.confidence : void 0))) >= 0.60) ? '✅ Yes' : 'No' },
            { label: pgcT('rr.card.detection', 'Card Detection'), value: card.confidence ? formatConfidence(card.confidence) : null }
        ];

        // ── Price stats & listings rendered via shared module-level functions ──
        const priceStatsHtml = renderPriceStats(priceStats);
        const listingsHtml = renderListings(listings);

        // ── Marketplace Search Links rendered via renderMarketSearchLinks ──

        // ── Full name hero ─────────────────────────────────────────────────────
        let heroName = '';
        if (id.full_name || id.name) {
            heroName = `
            <div style="
                background: var(--xg-panel);
                border-radius: 8px; padding: 18px 20px; margin-bottom: 14px;
                border: 1px solid var(--xg-border);
                border-left: 4px solid ${config.color};
                box-shadow: var(--xg-shadow);
            ">
                <div style="font-size: 11px; color: var(--xg-muted); font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 6px;">Identified Card</div>
                <div style="font-size: 18px; font-weight: 700; color: var(--xg-text); line-height: 1.3;">${id.full_name || id.name}</div>
                ${id.sub_set ? `<div style="font-size: 13px; color: var(--xg-muted); margin-top: 4px;">${id.sub_set}</div>` : ''}
            </div>`;
        }

        const possibleMatchesHtml = (!id.full_name && !id.name)
            ? renderPossibleMatches(alternatives, config)
            : '';

        return heroName + possibleMatchesHtml + renderMetricGrid(metrics) + priceStatsHtml + listingsHtml;
    }

    function renderSportIdIdentification(record, config) {
        const card = record.card || {};
        const id = card.identification || record.identification || {};
        const tags = card.tags || {};
        const alternatives = card.alternatives || record.alternatives || [];
        const rawCategory = (tags.category != null ? tags.category.name : void 0) || record.category;
        const categoryValue = /^(Card\/Sport Card|Sport Card|Card)$/i.test(rawCategory) ? 'Card' : rawCategory;
        const metrics = [
            { label: pgcT('rr.year', 'Year'),      value: id.year },
            { label: pgcT('rr.set', 'Set'),       value: id.set_name || id.set },
            { label: pgcT('rr.card', 'Card #'),    value: cleanCardNum(id.card_number || id.card_no) },
            { label: pgcT('rr.company', 'Company'),   value: id.company },
            { label: pgcT('rr.team', 'Team'),      value: id.team },
            { label: pgcT('metric.category', 'Category'),  value: categoryValue },
            { label: pgcT('rr.type', 'Type'),      value: id.card_type },
            { label: pgcT('metric.side', 'Side'),      value: capitalizeFirst((tags.side != null ? tags.side.name : void 0)) },
            { label: pgcT('rr.card.detection', 'Card Detection'), value: card.confidence ? formatConfidence(card.confidence) : null }
        ];
        let heroName = '';
        if (id.full_name || id.name) {
            heroName = `
            <div class="xim-id-hero" style="
                background: var(--xg-panel);
                border-radius: 8px; padding: 18px 20px; margin-bottom: 14px;
                border: 1px solid var(--xg-border);
                border-left: 4px solid ${config.color};
                box-shadow: var(--xg-shadow);
            ">
                <div style="font-size: 11px; color: var(--xg-muted); font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 6px;">Identified Card</div>
                <div style="font-size: 18px; font-weight: 700; color: var(--xg-text); line-height: 1.3;">${id.full_name || id.name}</div>
                ${id.sub_set ? `<div style="font-size: 13px; color: var(--xg-muted); margin-top: 4px;">${id.sub_set}</div>` : ''}
            </div>`;
        }

        const possibleMatchesHtml = (!id.full_name && !id.name)
            ? renderPossibleMatches(alternatives, config)
            : '';
        return heroName + possibleMatchesHtml + renderMetricGrid(metrics);
    }

    function renderSportIdPriceData(record, config) {
        const card = record.card || {};
        const id = card.identification || record.identification || {};
        const listings = card.listings || record.listings || null;
        const priceStats = card.price_stats || record.price_stats || null;
        const priceStatsHtml = renderPriceStats(priceStats);
        // Marketplace listings now rendered inside renderLiveComps — no separate card here.
        // Marketplace search links rendered via renderMarketSearchLinks (above share section).
        return priceStatsHtml;
    }

    function renderTcgIdResult(record, config) {
        const card = record.card || {};
        const id = card.identification || record.identification || {};
        const tags = card.tags || {};
        const listings = card.listings || record.listings || null;
        const priceStats = card.price_stats || record.price_stats || null;
        const alternatives = card.alternatives || record.alternatives || [];

        const rawCategory = (tags.category != null ? tags.category.name : void 0) || (record.category != null ? record.category.value : void 0) || record.category;
        const categoryValue = /^(Card\/Trading Card Game|Trading Card Game|TCG)$/i.test(rawCategory) ? 'TCG' : rawCategory;
        const metrics = [
            { label: pgcT('rr.name', 'Name'),      value: id.name },
            { label: pgcT('rr.full.name', 'Full Name'), value: id.full_name },
            { label: pgcT('rr.set', 'Set'),       value: id.set_name || id.set },
            { label: pgcT('rr.set.code', 'Set Code'),  value: id.set_code },
            { label: pgcT('rr.set.series.code', 'Set Series Code'), value: id.set_series_code },
            { label: pgcT('rr.series', 'Series'),    value: id.series },
            { label: pgcT('rr.card', 'Card #'),    value: cleanCardNum(id.card_number || id.card_no) },
            { label: pgcT('rr.out.of', 'Out Of'),    value: id.out_of },
            { label: pgcT('rr.rarity', 'Rarity'),    value: id.rarity },
            { label: pgcT('rr.color', 'Color'),     value: id.color },
            { label: pgcT('rr.type', 'Type'),      value: id.type || id.card_type },
            { label: pgcT('rr.subcategory', 'Subcategory'), value: id.subcategory },
            { label: pgcT('metric.category', 'Category'),  value: categoryValue },
            { label: pgcT('metric.side', 'Side'),      value: capitalizeFirst((tags.side != null ? tags.side.name : void 0)) },
            { label: pgcT('rr.alphabet', 'Alphabet'),  value: capitalizeFirst((tags.alphabet != null ? tags.alphabet.name : void 0)) },
            { label: pgcT('metric.autograph', 'Autograph'), value: 'No' },
            { label: pgcT('rr.card.detection', 'Card Detection'), value: card.confidence ? formatConfidence(card.confidence) : null }
        ];

        const priceStatsHtml = renderPriceStats(priceStats);
        const listingsHtml = renderListings(listings);

        // Marketplace search links rendered via renderMarketSearchLinks (above share section).

        let heroName = '';
        if (id.full_name || id.name) {
            heroName = `
            <div style="
                background: var(--xg-panel);
                border-radius: 8px; padding: 18px 20px; margin-bottom: 14px;
                border: 1px solid var(--xg-border);
                border-left: 4px solid ${config.color};
                box-shadow: var(--xg-shadow);
            ">
                <div style="font-size: 11px; color: var(--xg-muted); font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 6px;">Identified Card</div>
                <div style="font-size: 18px; font-weight: 700; color: var(--xg-text); line-height: 1.3;">${id.full_name || id.name}</div>
                ${id.set_name || id.set ? `<div style="font-size: 13px; color: var(--xg-muted); margin-top: 4px;">${id.set_name || id.set}${id.rarity ? ` · ${id.rarity}` : ''}</div>` : ''}
            </div>`;
        }

        const possibleMatchesHtml = (!id.full_name && !id.name)
            ? renderPossibleMatches(alternatives, config)
            : '';

        return heroName + possibleMatchesHtml + renderMetricGrid(metrics) + priceStatsHtml + listingsHtml;
    }

    function renderTcgIdIdentification(record, config) {
        const card = record.card || {};
        const id = card.identification || record.identification || {};
        const tags = card.tags || {};
        const alternatives = card.alternatives || record.alternatives || [];
        const rawCategory = (tags.category != null ? tags.category.name : void 0) || (record.category != null ? record.category.value : void 0) || record.category;
        const categoryValue = /^(Card\/Trading Card Game|Trading Card Game|TCG)$/i.test(rawCategory) ? 'TCG' : rawCategory;
        const metrics = [
            { label: pgcT('rr.name', 'Name'),      value: id.name },
            { label: pgcT('rr.full.name', 'Full Name'), value: id.full_name },
            { label: pgcT('rr.set', 'Set'),       value: id.set_name || id.set },
            { label: pgcT('rr.set.code', 'Set Code'),  value: id.set_code },
            { label: pgcT('rr.set.series.code', 'Set Series Code'), value: id.set_series_code },
            { label: pgcT('rr.series', 'Series'),    value: id.series },
            { label: pgcT('rr.card', 'Card #'),    value: cleanCardNum(id.card_number || id.card_no) },
            { label: pgcT('rr.out.of', 'Out Of'),    value: id.out_of },
            { label: pgcT('rr.rarity', 'Rarity'),    value: id.rarity },
            { label: pgcT('rr.color', 'Color'),     value: id.color },
            { label: pgcT('rr.type', 'Type'),      value: id.type || id.card_type },
            { label: pgcT('rr.subcategory', 'Subcategory'), value: id.subcategory },
            { label: pgcT('metric.category', 'Category'),  value: categoryValue },
            { label: pgcT('metric.side', 'Side'),      value: capitalizeFirst((tags.side != null ? tags.side.name : void 0)) },
            { label: pgcT('rr.alphabet', 'Alphabet'),  value: capitalizeFirst((tags.alphabet != null ? tags.alphabet.name : void 0)) },
            { label: pgcT('rr.card.detection', 'Card Detection'), value: card.confidence ? formatConfidence(card.confidence) : null }
        ];
        let heroName = '';
        if (id.full_name || id.name) {
            heroName = `
            <div style="
                background: var(--xg-panel);
                border-radius: 8px; padding: 18px 20px; margin-bottom: 14px;
                border: 1px solid var(--xg-border);
                border-left: 4px solid ${config.color};
                box-shadow: var(--xg-shadow);
            ">
                <div style="font-size: 11px; color: var(--xg-muted); font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 6px;">Identified Card</div>
                <div style="font-size: 18px; font-weight: 700; color: var(--xg-text); line-height: 1.3;">${id.full_name || id.name}</div>
                ${id.set_name || id.set ? `<div style="font-size: 13px; color: var(--xg-muted); margin-top: 4px;">${id.set_name || id.set}${id.rarity ? ` · ${id.rarity}` : ''}</div>` : ''}
            </div>`;
        }
        const possibleMatchesHtml = (!id.full_name && !id.name)
            ? renderPossibleMatches(alternatives, config)
            : '';
        return heroName + possibleMatchesHtml + renderMetricGrid(metrics);
    }

    function renderTcgIdPriceData(record, config) {
        const card = record.card || {};
        const id = card.identification || record.identification || {};
        const listings = card.listings || record.listings || null;
        const priceStats = card.price_stats || record.price_stats || null;
        const priceStatsHtml = renderPriceStats(priceStats);
        // Marketplace listings now rendered inside renderLiveComps — no separate card here.
        // Marketplace search links rendered via renderMarketSearchLinks (above share section).
        return priceStatsHtml;
    }

    function renderUnifiedIdentifyResult(record, config) {
        const detectedType = record.detected_type || 'unknown';
        const sourceEndpoint = record.source_endpoint || '';
        const typeLabel = detectedType === 'tcg' ? 'Trading Card Game' : (detectedType === 'sport' ? 'Sports Card' : 'Card');
        const typeColor = detectedType === 'tcg' ? '#ec4899' : (detectedType === 'sport' ? '#8b5cf6' : config.color);
        const typeIcon = detectedType === 'tcg' ? '🎴' : (detectedType === 'sport' ? '🏆' : '🔍');

        const tcgScore = (record.tcg_score != null ? record.tcg_score : (function(_o){return _o != null ? _o : null})((record.detection != null ? record.detection.tcg_score : void 0)));
        const sportScore = (record.sport_score != null ? record.sport_score : (function(_o){return _o != null ? _o : null})((record.detection != null ? record.detection.sport_score : void 0)));
        const banner = `
            <div class="ximilar-result-card" style="
                background: var(--xg-panel);
                border-radius: 8px;
                padding: 18px 22px;
                margin-bottom: 20px;
                border: 1px solid var(--xg-border);
                border-left: 4px solid ${typeColor};
                display: flex;
                align-items: center;
                gap: 16px;
            ">
                <div style="font-size: 32px; line-height: 1;">${typeIcon}</div>
                <div style="flex: 1;">
                    <div style="font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: ${typeColor}; margin-bottom: 3px;">Auto-Detected Type</div>
                    <div style="font-size: 18px; font-weight: 700; color: var(--xg-text); line-height: 1.3;">${typeLabel}</div>
                    ${sourceEndpoint ? `<div style="font-size: 12px; color: var(--xg-muted); margin-top: 2px;">Matched via ${sourceEndpoint === 'tcg_id' ? 'TCG Card ID' : 'Sports Card ID'} model</div>` : ''}
                </div>
                <div style="text-align: right; font-size: 12px; color: var(--xg-muted); line-height: 1.4;">
                    <div>TCG score: ${(tcgScore != null ? tcgScore : '—')}</div>
                    <div>Sport score: ${(sportScore != null ? sportScore : '—')}</div>
                </div>
            </div>
        `;

        const marketConfig = detectedType === 'tcg'
            ? ((typeof SERVICE_CONFIG !== 'undefined' && SERVICE_CONFIG['tcg-id']) || config)
            : ((typeof SERVICE_CONFIG !== 'undefined' && SERVICE_CONFIG['sport-id']) || config);
        const marketContent = detectedType === 'tcg'
            ? renderTcgIdResult(record, marketConfig)
            : renderSportIdResult(record, marketConfig);
        const gradeCta = `
            <div class="ximilar-result-card" style="
                background: var(--xg-panel);
                border-radius: 8px;
                padding: 20px 24px;
                margin-top: 16px;
                color: var(--xg-text);
                border: 1px solid var(--xg-border);
                display: flex;
                align-items: center;
                justify-content: space-between;
                flex-wrap: wrap;
                gap: 12px;
            ">
                <div>
                    <div style="font-size: 15px; font-weight: 700; margin-bottom: 4px;">Want a grade prediction?</div>
                    <div style="font-size: 13px; color: var(--xg-muted);">Run the full AI grading analysis on this ${detectedType === 'tcg' ? 'TCG' : 'sports'} card.</div>
                </div>
                <a href="/services/card-grading" class="btn" style="
                    background: ${typeColor};
                    color: white;
                    border: none;
                    padding: 10px 20px;
                    border-radius: 6px;
                    font-weight: 600;
                    text-decoration: none;
                    font-size: 13px;
                ">Grade This Card →</a>
            </div>
        `;

        return banner + marketContent + gradeCta;
    }

    function renderSlabIdResult(record, config) {
        const id = record.identification || {};
        const slab = record.slab || {};
        const ocr = record.ocr || {};
        const card = record.card || {};
        
        const metrics = [
            { label: pgcT('rr.company', 'Company'), value: (slab.company != null ? slab.company.name : void 0) || id.company },
            { label: pgcT('rr.grade', 'Grade'), value: id.grade, color: gradeColor(id.grade) },
            { label: pgcT('rr.verbal.grade', 'Verbal Grade'), value: id.verbal_grade },
            { label: pgcT('rr.year', 'Year'), value: id.year },
            { label: pgcT('rr.set', 'Set'), value: id.set },
            { label: pgcT('rr.card', 'Card #'), value: id.card_no },
            { label: pgcT('rr.certificate', 'Certificate'), value: id.certificate_number },
            { label: pgcT('metric.confidence', 'Confidence'), value: slab.confidence ? formatConfidence(slab.confidence) : null }
        ];
        
        let ocrBlock = '';
        if (ocr.full_text) {
            ocrBlock = renderDataBlock('OCR Text Extraction', 
                `<div style="background: var(--xg-panel); padding: 10px; border-radius: 8px; font-family: monospace; font-size: 12px; border: 1px solid #f59e0b; margin-bottom: 8px;">${ocr.full_text}</div>
                 <div style="font-size: 11px; color: var(--xg-muted);">Language: ${ocr.lang_name || 'Unknown'} | Regions: ${(ocr.texts != null ? ocr.texts.length : void 0) || 0}</div>`,
                '#f59e0b', '#fef3c7'
            );
        }
        
        let links = '';
        if (id.links && Object.keys(id.links).length > 0) {
            links = `
                <div style="display: flex; flex-wrap: wrap; gap: 8px; justify-content: center;">
                    ${Object.entries(id.links).map(([site, url]) => `
                        <a href="${url}" target="_blank" rel="noopener" style="
                            background: var(--xg-info-soft); border: 1px solid var(--xg-info-line); border-radius: 8px; color: var(--xg-info); font-weight: 700;
                            font-size: 12px; transition: all 0.2s;
                        " onmouseover="this.style.borderColor='${config.color}'" onmouseout="this.style.borderColor='var(--xg-border)'">
                            🔗 ${site}
                        </a>
                    `).join('')}
                </div>
            `;
        }

        // Cert enrichment (Alt + CardLadder via Parse.bot) — attached to
        // record.cert_lookup by api/slab_id.php when the label carried a cert no.
        let certBlock = '';
        const cert = record.cert_lookup;
        if (cert && cert.sources) {
            const cl = cert.sources.cardladder || {};
            const alt = cert.sources.alt || {};
            const money = v => v == null ? null : '$' + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

            const valueRows = [];
            if (alt.alt_value != null) {
                const bounds = (alt.value_low != null && alt.value_high != null)
                    ? ` <span style="font-weight:400;color:var(--xg-muted)">(${money(alt.value_low)}–${money(alt.value_high)})</span>` : '';
                valueRows.push(`<div style="display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid var(--xg-border)"><span>Alt Value</span><strong>${money(alt.alt_value)}${bounds}</strong></div>`);
            }
            if (cl.cl_value != null) {
                valueRows.push(`<div style="display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid var(--xg-border)"><span>Card Ladder Value</span><strong>${money(cl.cl_value)}</strong></div>`);
            }
            if (cl.market_value != null) {
                valueRows.push(`<div style="display:flex;justify-content:space-between;padding:8px 0"><span>Market Value</span><strong>${money(cl.market_value)}</strong></div>`);
            }

            const saleRows = [];
            const seen = {};
            [...(alt.sales || []), ...(cl.recent_sales || [])].forEach(s => {
                if (s.price == null || !s.date) return;
                const key = s.date + '|' + s.price;
                if (seen[key]) return;
                seen[key] = true;
                saleRows.push({ date: s.date, price: s.price, platform: s.platform, url: s.url });
            });
            saleRows.sort((a, b) => String(b.date).localeCompare(String(a.date)));

            const salesHtml = saleRows.slice(0, 8).map(s => `
                <div style="display:flex;justify-content:space-between;gap:8px;padding:6px 0;border-bottom:1px solid var(--xg-border);font-size:12px">
                    <span style="color:var(--xg-muted)">${String(s.date).slice(0, 10)}</span>
                    ${s.url ? `<a href="${s.url}" target="_blank" rel="noopener" style="font-weight:700">${money(s.price)}</a>` : `<strong>${money(s.price)}</strong>`}
                    <span style="color:var(--xg-muted)">${s.platform || ''}</span>
                </div>`).join('');

            const listingsHtml = (cl.listings || []).slice(0, 6).map(l => `
                <div style="display:flex;justify-content:space-between;gap:8px;padding:6px 0;border-bottom:1px solid var(--xg-border);font-size:12px">
                    <span>${l.platform || 'Listing'}</span>
                    ${l.url ? `<a href="${l.url}" target="_blank" rel="noopener" style="font-weight:700">${money(l.price)}</a>` : `<strong>${money(l.price)}</strong>`}
                </div>`).join('');

            if (valueRows.length || salesHtml || listingsHtml) {
                certBlock = renderDataBlock('Certified Market Data',
                    (valueRows.length ? `<div style="margin-bottom:${salesHtml || listingsHtml ? '12px' : '0'}">${valueRows.join('')}</div>` : '')
                    + (salesHtml ? `<div style="font-size:11px;font-weight:800;color:var(--xg-muted);text-transform:uppercase;margin:8px 0 4px">Recent Sales</div>${salesHtml}` : '')
                    + (listingsHtml ? `<div style="font-size:11px;font-weight:800;color:var(--xg-muted);text-transform:uppercase;margin:8px 0 4px">Live Listings</div>${listingsHtml}` : ''),
                    config.color, config.colorLight);
            }
        }

        return renderMetricGrid(metrics) + certBlock + ocrBlock + (links ? renderDataBlock('Verify Your Card', links, config.color, config.colorLight) : '');
    }

    function renderSlabGradeResult(record, config) {
        const slab = record.slab || {};
        const gradedSlab = record.graded_slab || {};
        const card = record.card || {};
        
        const metrics = [
            { label: pgcT('rr.company', 'Company'), value: (slab.company != null ? slab.company.name : void 0) || (gradedSlab != null ? gradedSlab.company : void 0) },
            { label: pgcT('rr.grade', 'Grade'), value: (slab.grade != null ? slab.grade.value : void 0) || (gradedSlab != null ? gradedSlab.grade : void 0), color: gradeColor((slab.grade != null ? slab.grade.value : void 0) || (gradedSlab != null ? gradedSlab.grade : void 0)) },
            { label: pgcT('metric.category', 'Category'), value: (slab.category != null ? slab.category.name : void 0) || (gradedSlab != null ? gradedSlab.category : void 0) },
            { label: pgcT('metric.side', 'Side'), value: (slab.side != null ? slab.side.name : void 0) },
            { label: pgcT('rr.slab.detected', 'Slab Detected'), value: record.slab_detected ? 'Yes' : 'No', color: record.slab_detected ? '#10b981' : '#ef4444' },
            { label: pgcT('rr.card.detected', 'Card Detected'), value: (card != null ? card.bound_box : void 0) ? 'Yes' : 'No' },
            { label: pgcT('rr.grade.confidence', 'Grade Confidence'), value: (slab.grade != null ? slab.grade.confidence : void 0) ? formatConfidence(slab.grade.confidence) : null },
            { label: pgcT('rr.company.confidence', 'Company Confidence'), value: (slab.company != null ? slab.company.confidence : void 0) ? formatConfidence(slab.company.confidence) : null }
        ];
        
        return renderMetricGrid(metrics);
    }

    function renderCoinIdResult(record, config) {
        // History stores coin fields flat; the live API nests them under record.coin.
        const coin = (record && record.coin) ? record.coin : (record || {});
        const confidenceColors = { high: '#10b981', medium: '#f59e0b', low: '#ef4444' };
        const confKey = String(coin.confidence || '').toLowerCase();
        const confColor = confidenceColors[confKey] || '#94a3b8';
        const confLabel = confKey ? confKey.charAt(0).toUpperCase() + confKey.slice(1) + ' confidence' : null;

        const numistaUrl = coin.numista_query
            ? 'https://en.numista.com/catalogue/index.php?mode=simplifie&r=' + encodeURIComponent(coin.numista_query) + '&ct=coin&cat=y&tb=y&tc=y&tn=y&tp=y&tt=y'
            : null;

        const nameHtml = coin.name ? `
            <div style="text-align: center; margin-bottom: 20px;">
                ${confLabel ? `
                <div style="display: inline-flex; align-items: center; gap: 8px; margin-bottom: 10px;">
                    <span style="width: 10px; height: 10px; border-radius: 50%; background: ${confColor};"></span>
                    <span style="font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: ${confColor};">${escapeHtml(confLabel)}</span>
                </div>` : ''}
                <h2 style="font-size: 24px; font-weight: 800; color: var(--xg-text); margin: 0; line-height: 1.3;">${escapeHtml(coin.name)}</h2>
            </div>` : '';

        const metrics = renderMetricGrid([
            { label: pgcT('rr.country', 'Country'), value: coin.country, color: config.color },
            { label: pgcT('rr.denomination', 'Denomination'), value: coin.denomination, color: '#3b82f6' },
            { label: pgcT('rr.year', 'Year'), value: coin.year, color: '#8b5cf6' },
            { label: pgcT('rr.mint.mark', 'Mint Mark'), value: coin.mint_mark || 'Not visible', color: '#06b6d4' },
            { label: pgcT('rr.series', 'Series'), value: coin.series, color: '#ec4899' }
        ]);

        const commentaryHtml = coin.commentary ? `
            <div style="background: var(--xg-subtle); border-radius: 10px; padding: 16px 18px; margin-top: 16px; border: 1px solid var(--xg-border);">
                <div style="font-size: 13px; font-weight: 700; color: var(--xg-muted); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px;">About this coin</div>
                <p style="margin: 0; font-size: 14px; color: var(--xg-text-2); line-height: 1.7;">${escapeHtml(coin.commentary)}</p>
            </div>` : '';

        const footerHtml = `
            <div style="margin-top: 18px; padding-top: 16px; border-top: 1px solid var(--xg-border); display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap;">
                <p style="margin: 0; font-size: 12px; color: var(--xg-muted); line-height: 1.6; flex: 1; min-width: 220px;">
                    AI identification is a best-effort estimate — verify rare or high-value coins with a professional numismatist.
                </p>
                ${numistaUrl ? `
                <a href="${numistaUrl}" target="_blank" rel="noopener" style="
                    display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0;
                    background: ${config.color}; color: #ffffff; padding: 10px 18px;
                    border-radius: 8px; font-size: 13px; font-weight: 700; text-decoration: none;
                ">Look it up on Numista →</a>` : ''}
            </div>`;

        return `${nameHtml}${metrics}${commentaryHtml}${footerHtml}`;
    }

    function renderActions(service, record, resultId) {
        const canGenerateListing = ['grade', 'condition'].includes(service);
        const ebayBtn = canGenerateListing ? `
            <button class="xim-generate-ebay-listing" data-result-id="${resultId}" style="
                background: #f59e0b; color: white; padding: 12px 28px;
                border: none; border-radius: 10px; font-size: 14px; font-weight: 700;
                cursor: pointer; transition: all 0.2s;
            " onmouseover="this.style.background='#d97706'" onmouseout="this.style.background='#f59e0b'">
                🛒 eBay Listing
            </button>
        ` : '';

        return `
            <div class="ximilar-result-actions" style="display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; margin-bottom: 16px;" data-service="${service}">
                <button type="button" data-xim-action="scan-next" style="
                    background: #22c55e; color: white; padding: 12px 28px;
                    border: none; border-radius: 10px; font-size: 14px; font-weight: 700;
                    cursor: pointer; transition: all 0.2s;
                " onmouseover="this.style.background='#16a34a'" onmouseout="this.style.background='#22c55e'">
                    📸 Scan Next Card
                </button>
                ${ebayBtn}
                <button type="button" data-xim-action="download-pdf" style="
                    background: #ef4444; color: white; padding: 12px 28px;
                    border: none; border-radius: 10px; font-size: 14px; font-weight: 700;
                    cursor: pointer; transition: all 0.2s;
                " onmouseover="this.style.background='#dc2626'" onmouseout="this.style.background='#ef4444'">
                    📄 Download PDF
                </button>
                <button type="button" data-xim-action="print" style="
                    background: #3b82f6; color: white; padding: 12px 28px;
                    border: none; border-radius: 10px; font-size: 14px; font-weight: 700;
                    cursor: pointer; transition: all 0.2s;
                " onmouseover="this.style.background='#2563eb'" onmouseout="this.style.background='#3b82f6'">
                    🖨️ Print
                </button>
            </div>
        `;
    }

    function ensureHtml2Pdf() {
        return new Promise((resolve, reject) => {
            if (typeof html2pdf !== 'undefined') return resolve();
            const script = document.createElement('script');
            script.src = '/assets/js/vendor/html2pdf.bundle.min.js';
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Could not load PDF generator'));
            document.head.appendChild(script);
        });
    }

    function downloadReport() {
        console.log('[XimilarResults] downloadReport called');
        const original = document.querySelector('.ximilar-result');
        if (!original) {
            alert('Result not available to export.');
            return;
        }

        const btn = document.querySelector('button[data-xim-action="download-pdf"]') || document.activeElement;
        if (btn && btn.disabled !== undefined) {
            btn.disabled = true;
            btn.textContent = 'Generating PDF...';
        }

        ensureHtml2Pdf()
            .then(() => {
                console.log('[XimilarResults] html2pdf ready, building PDF');
                return buildAndSavePdf(original);
            })
            .catch(err => {
                console.error('[XimilarResults] PDF generation failed:', err);
                alert('PDF download failed: ' + (err.message || 'Unknown error'));
            })
            .finally(() => {
                if (btn && btn.disabled !== undefined) {
                    btn.disabled = false;
                    btn.textContent = '📄 Download PDF';
                }
            });
    }

    function showPdfLoading() {
        let overlay = document.getElementById('ximilar-pdf-loading');
        if (!overlay) {
            overlay = document.createElement('div');
            overlay.id = 'ximilar-pdf-loading';
            overlay.style.cssText = 'position:fixed;inset:0;background:rgba(15,23,42,0.75);display:flex;align-items:center;justify-content:center;z-index:99999;backdrop-filter:blur(2px);';
            overlay.innerHTML = `
                <div style="background:#fff;border-radius:16px;padding:32px 40px;text-align:center;box-shadow:0 25px 50px rgba(0,0,0,0.25);max-width:320px;">
                    <svg width="48" height="48" viewBox="0 0 48 48" style="margin:0 auto 16px;transform-origin:center;">
                        <circle cx="24" cy="24" r="20" stroke="#e2e8f0" stroke-width="4" fill="none"/>
                        <circle cx="24" cy="24" r="20" stroke="#3b82f6" stroke-width="4" fill="none" stroke-linecap="round" stroke-dasharray="80" stroke-dashoffset="60">
                            <animateTransform attributeName="transform" type="rotate" from="0 24 24" to="360 24 24" dur="1s" repeatCount="indefinite"/>
                        </circle>
                    </svg>
                    <div style="font-size:18px;font-weight:700;color:#1f2937;margin-bottom:6px;">Preparing PDF</div>
                    <div style="font-size:13px;color:#6b7280;">This may take a few seconds...</div>
                </div>
            `;
            document.body.appendChild(overlay);
        } else {
            overlay.style.display = 'flex';
        }
    }

    function hidePdfLoading() {
        const overlay = document.getElementById('ximilar-pdf-loading');
        if (overlay) overlay.style.display = 'none';
    }

    function buildAndSavePdf(original) {
        showPdfLoading();

        const stageId = 'ximilar-pdf-stage';
        let stage = document.getElementById(stageId);
        if (!stage) {
            stage = document.createElement('div');
            stage.id = stageId;
            stage.style.cssText = 'position:absolute;left:-9999px;top:0;width:8.5in;background:#fff;overflow:visible;';
            document.body.appendChild(stage);
        }
        stage.innerHTML = '';

        const serviceEl = original.querySelector('[data-service]');
        const serviceName = serviceEl ? serviceEl.getAttribute('data-service') : 'report';

        // Grade results get a dedicated single-page certificate document
        let docNode;
        if (serviceName === 'grade' && lastRenderContext && lastRenderContext.record) {
            docNode = buildCertificateDocument(lastRenderContext);
        } else {
            docNode = buildLegacyReportDocument(original, serviceName);
        }

        stage.appendChild(docNode);

        const filename = (serviceName === 'grade' && lastRenderContext && lastRenderContext.certNo)
            ? 'PreGradeCards-Certificate-' + lastRenderContext.certNo + '.pdf'
            : 'PreGradeCards-' + serviceName + '-' + new Date().toISOString().slice(0, 10) + '.pdf';

        // Measure only after every image has loaded — the box (and so the
        // capture height below) grows when they do.
        const imgsReady = Promise.all(Array.from(docNode.querySelectorAll('img')).map(im =>
            im.complete ? null : new Promise(res => { im.addEventListener('load', res, { once: true }); im.addEventListener('error', res, { once: true }); setTimeout(res, 8000); })));

        return imgsReady.then(() => {
            const box = docNode.getBoundingClientRect();
            const h = Math.ceil(Math.max(box.height, docNode.scrollHeight));
            const opt = {
                margin: 0.35,
                filename: filename,
                image: { type: 'jpeg', quality: 0.92 },
                // Capture the document's own box in a clone window at least as
                // wide as the real one: a fixed 816px clone window shifted the
                // certificate left on wide screens (clipped), phones laid it
                // out at screen width (2–3 pages), and anything below the
                // window height was cut off.
                html2canvas: { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff', scrollX: 0, scrollY: 0, width: Math.ceil(box.width) || 749, height: h || undefined, windowHeight: Math.max(h, window.innerHeight), imageTimeout: 15000, windowWidth: Math.max(window.innerWidth, 816) },
                jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait', compress: true }
            };
            return html2pdf().set(opt).from(docNode).save();
        }).then(() => {
            stage.innerHTML = '';
            hidePdfLoading();
        }).catch(err => {
            stage.innerHTML = '';
            hidePdfLoading();
            throw err;
        });
    }

    // ── Certificate PDF (grade service) ─────────────────────────────────────
    // Single-page, official certificate of AI grading analysis. Condition data
    // only — deliberately excludes market pricing, forecasts and account info.
    function buildCertificateDocument(ctx) {
        const record = ctx.record || {};
        const grades = record.grades || {};
        const centering = record.centering || {};
        let corners = Array.isArray(record.corners) ? record.corners : [];
        let edges = Array.isArray(record.edges) ? record.edges : [];
        // Certificate sub-grades must match the grader that produced the
        // headline (same mismatch as the record page — record 10877).
        const _certPgcSub = pgcOwnSubgrades(record);
        if (_certPgcSub) { corners = _certPgcSub.corners; edges = _certPgcSub.edges; }
        const surface = record.surface || {};
        const detection = record.detection || {};
        const damaged = record.damaged || {};
        const tags = (record.card && record.card._tags) || {};
        const id = (record.card && record.card.identification) || record.identification || {};
        const certNo = String(record._id || (record._status && record._status.request_id) || ('PGC-' + Date.now())).toUpperCase();
        ctx.certNo = certNo;

        const fmtDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        const esc = (t) => String(t == null ? '' : t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

        // Card identity — intentionally excludes any personal/player name
        const titleParts = [id.year, (id.set_name || id.set), cleanCardNum(id.card_number || id.card_no) ? '#' + cleanCardNum(id.card_number || id.card_no) : ''].filter(Boolean).join(' ');
        const cardTitle = titleParts.length ? esc(titleParts) : 'Certified Trading Card';
        const metaParts = [];
        if (id.company) metaParts.push('Company: <b>' + esc(id.company) + '</b>');
        if (id.card_type) metaParts.push('Type: <b>' + esc(id.card_type) + '</b>');
        if (id.set_name || id.set) metaParts.push('Set: <b>' + esc(id.set_name || id.set) + '</b>');
        if (id.year) metaParts.push('Year: <b>' + esc(id.year) + '</b>');
        const sideVal = capitalizeFirst((tags.Side && tags.Side[0] && tags.Side[0].name) || (record.side && record.side.value));
        if (sideVal) metaParts.push('Side: <b>' + esc(sideVal) + '</b>');
        if (detection.confidence != null) metaParts.push('Detection: <b>' + Math.round(parseFloat(detection.confidence) * 100) + '%</b>');
        const metaHtml = metaParts.join(' &nbsp;·&nbsp; ');

        const condition = grades.condition || 'AI Predicted';
        const overall = grades.final != null ? grades.final : 'N/A';
        const issueDate = new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).toUpperCase();
        const meterColor = (v) => { const n = parseFloat(v); if (isNaN(n)) return '#9a948a'; if (n >= 9) return '#166534'; if (n >= 8) return '#a16207'; return '#b91c1c'; };
        const sevLabel = (v) => { const n = parseFloat(v); if (isNaN(n)) return '—'; if (n >= 9) return 'Mint'; if (n >= 8) return 'Near Mint'; if (n >= 7) return 'Excellent'; if (n >= 6) return 'Very Good'; if (n >= 5) return 'Good'; return 'Below market grade'; };

        const subRows = [
            { label: pgcT('subgrade.corners', 'Corners'), desc: 'Sharpness &amp; whitening', value: grades.corners, note: '' },
            { label: pgcT('subgrade.edges', 'Edges'), desc: 'Chipping &amp; silvering', value: grades.edges, note: '' },
            { label: pgcT('subgrade.surface', 'Surface'), desc: 'Scratches, print lines, clouding', value: grades.surface, note: surface.label || surface.details || '' },
            { label: pgcT('subgrade.centering', 'Centering'), desc: 'Border balance, all edges', value: grades.centering, note: centering.left_right ? (esc(centering.left_right) + ' left-right' + (centering.top_bottom ? ' · ' + esc(centering.top_bottom) + ' top-bottom' : '')) : '' }
        ];

        const subRowsHtml = subRows.map(r => {
            const v = parseFloat(r.value);
            const pct = isNaN(v) ? 0 : Math.min(v / 10 * 100, 100);
            return '<tr>' +
                '<td><div class="crit">' + r.label + '<small>' + r.desc + '</small></div></td>' +
                '<td><div class="meter"><i style="width:' + pct + '%;background:' + meterColor(v) + ';"></i></div>' + (r.note ? '<div class="note">' + esc(r.note) + '</div>' : '') + '</td>' +
                '<td><div class="score">' + (isNaN(v) ? 'N/A' : v.toFixed(1)) + '</div><div class="sev">' + sevLabel(v) + '</div></td>' +
                '</tr>';
        }).join('');

        // PGC sub-grades are named 'Top Left' / 'Top' (pgcOwnSubgrades), which
        // the provider-name matching below never found — read them directly.
        const _pf = _certPgcSub && record.grade_pgc && record.grade_pgc.front;
        const _cell = v => (v != null && !isNaN(parseFloat(v))) ? parseFloat(v).toFixed(1) : '—';
        const cornerCells = _pf ? [_pf.corners.ul, _pf.corners.ur, _pf.corners.lr, _pf.corners.ll].map(_cell) : ['UPPER_LEFT', 'UPPER_RIGHT', 'DOWN_RIGHT', 'DOWN_LEFT'].map(key => {
            const c = corners.find(x => String(x.name || '').toUpperCase().replace(/[-\s]/g, '_').includes(key)) || corners.find(x => String(x.name || '').toUpperCase().includes(key.split('_')[0]) && String(x.name || '').toUpperCase().includes(key.split('_')[1]));
            return c ? (c.grade != null ? parseFloat(c.grade).toFixed(1) : '—') : '—';
        });
        const edgeKeys = ['LEFT', 'RIGHT', 'UPPER', 'DOWN'];
        const edgeCells = _pf ? [_pf.edges.left, _pf.edges.right, _pf.edges.top, _pf.edges.bottom].map(_cell) : edgeKeys.map(key => {
            const e = edges.find(x => String(x.name || '').toUpperCase().replace(/[-\s]/g, '_').includes(key));
            return e && e.grade != null ? parseFloat(e.grade).toFixed(1) : '—';
        });

        const lr = centering.left_right ? String(centering.left_right).split('/') : null;
        const tb = centering.top_bottom ? String(centering.top_bottom).split('/') : null;

        const imgSrc = pgcThumb(record._images && (record._images.clean || record._images.cropped) || record.original_image_url || '', 640);
        // PGC-drawn overlay image (PgcOverlay.php) replaces the generic
        // centering diagram — it shows our own corner/edge/centering numbers.
        const pgcFullImg = record._pgc_slab_url
            || (record.grader === 'pgc' && record._images && record._images.pgc_full ? record._images.pgc_full : null);
        const pgcCertCap = record._pgc_slab_url ? 'PGC slab' : 'PGC analysis overlay';
        // record.surface is the provider's raw read (Ximilar 6.5 on 10923);
        // the headline surface pillar is grades.surface.
        const surfaceGrade = !isNaN(parseFloat(grades.surface)) ? parseFloat(grades.surface) : (!isNaN(parseFloat(surface.grade)) ? parseFloat(surface.grade) : null);

        const cert = document.createElement('div');
        cert.className = 'pgc-cert';
        cert.innerHTML = `
            <style>
                .pgc-cert { width: 7.8in; min-width: 7.8in; max-width: none !important; background: #fdfcf8; color: #26241f; font-family: Georgia, 'Times New Roman', serif; box-sizing: border-box; border: 1px solid #d9d2c0; outline: 4px double #b08d3e; outline-offset: -10px; padding: 0.34in 0.42in 0.3in; }
                .pgc-cert * { box-sizing: border-box; }
                .pgc-mast { text-align: center; border-bottom: 2px solid #26241f; padding-bottom: 12px; }
                .pgc-brand { font-size: 10px; font-weight: 800; letter-spacing: 0.3em; text-transform: uppercase; color: #8a6d1f; }
                .pgc-mast h1 { font-family: Georgia, 'Times New Roman', serif; font-size: 25px; font-weight: 400; margin: 7px 0 5px; letter-spacing: 0.02em; }
                .pgc-cert-no { font-size: 9.5px; letter-spacing: 0.12em; color: #7a756a; text-transform: uppercase; }
                .pgc-slab { display: flex; margin: 16px auto 0; max-width: 7in; border: 2px solid #111; background: #fff; }
                .pgc-slab-img { width: 0.95in; border-right: 2px solid #111; display: flex; align-items: center; justify-content: center; padding: 6px; background: #f6f3ea; }
                .pgc-slab-img img { max-width: 100%; max-height: 0.95in; object-fit: contain; display: block; }
                .pgc-slab-info { flex: 1; padding: 10px 14px; border-right: 2px solid #111; display: flex; flex-direction: column; justify-content: center; }
                .pgc-co { font-size: 8.5px; font-weight: 800; letter-spacing: 0.2em; color: #b91c1c; text-transform: uppercase; margin-bottom: 3px; }
                .pgc-cardname { font-family: Georgia, serif; font-size: 14.5px; font-weight: 700; line-height: 1.25; }
                .pgc-cardmeta { font-size: 9.5px; color: #57534a; margin-top: 4px; }
                .pgc-cardmeta b { color: #26241f; }
                .pgc-slab-grade { background: #111; color: #fff; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 10px 12px; min-width: 1.15in; }
                .pgc-slab-grade .g { font-family: Georgia, serif; font-size: 40px; font-weight: 700; line-height: 1; }
                .pgc-slab-grade .l { font-size: 8.5px; font-weight: 800; letter-spacing: 0.16em; text-transform: uppercase; color: #d4b96a; margin-top: 4px; }
                .pgc-slab-grade .s { font-size: 7.5px; color: #9ca3af; margin-top: 3px; letter-spacing: 0.05em; }
                .pgc-sec { margin-top: 16px; }
                .pgc-sec-head { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
                .pgc-sec-head h2 { font-family: Georgia, serif; font-size: 11px; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase; white-space: nowrap; margin: 0; }
                .pgc-sec-head .rule { flex: 1; height: 1px; background: #c9c0ab; }
                .pgc-sec-head .num { font-family: Georgia, serif; font-size: 10px; color: #8a6d1f; font-style: italic; }
                table.pgc-formal { width: 100%; border-collapse: collapse; font-size: 11px; }
                table.pgc-formal th { text-align: left; font-size: 8.5px; letter-spacing: 0.12em; text-transform: uppercase; color: #7a756a; padding: 5px 8px; border-bottom: 2px solid #26241f; font-weight: 700; }
                table.pgc-formal td { padding: 7px 8px; border-bottom: 1px solid #e5dfcf; vertical-align: middle; }
                table.pgc-formal tr:last-child td { border-bottom: 2px solid #26241f; }
                .pgc-crit { font-family: Georgia, serif; font-weight: 700; font-size: 11.5px; }
                .pgc-crit small { display: block; font-family: 'Segoe UI', sans-serif; font-weight: 400; font-size: 8.5px; color: #7a756a; margin-top: 1px; }
                .pgc-cert .crit { font-family: Georgia, serif; font-weight: 700; font-size: 11.5px; }
                .pgc-cert .crit small { display: block; font-family: 'Segoe UI', sans-serif; font-weight: 400; font-size: 8.5px; color: #7a756a; margin-top: 1px; }
                .pgc-cert .meter { position: relative; height: 6px; background: #ece7d8; border-radius: 999px; overflow: hidden; }
                .pgc-cert .meter i { position: absolute; top: 0; bottom: 0; left: 0; border-radius: 999px; }
                .pgc-cert .score { font-family: Georgia, serif; font-size: 15px; font-weight: 700; text-align: right; line-height: 1.1; }
                .pgc-cert .sev { font-size: 7.5px; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; color: #7a756a; }
                .pgc-cert .note { font-size: 9px; color: #7a756a; font-style: italic; margin-top: 3px; }
                .pgc-center-grid { display: flex; gap: 20px; align-items: center; }
                .pgc-center-diagram { width: 1.35in; height: 1.85in; border: 1px solid #26241f; position: relative; background: #fff; flex-shrink: 0; }
                .pgc-center-diagram .inner { position: absolute; top: 7%; bottom: 7%; left: 6.5%; right: 6.5%; border: 1.5px dashed #b91c1c; background: rgba(185,28,28,0.04); }
                .pgc-center-diagram .axis-x { position: absolute; left: 0; right: 0; top: 50%; height: 1px; background: rgba(38,36,31,0.25); }
                .pgc-center-diagram .axis-y { position: absolute; top: 0; bottom: 0; left: 50%; width: 1px; background: rgba(38,36,31,0.25); }
                .pgc-center-diagram .lbl { position: absolute; font-size: 7.5px; font-weight: 700; color: #b91c1c; background: #fdfcf8; padding: 1px 4px; }
                .pgc-ov-fig { width: 1.2in; flex-shrink: 0; text-align: center; }
                .pgc-ov-fig img { width: 100%; height: auto; display: block; border: 1px solid #26241f; }
                .pgc-ov-fig .cap { font-size: 7.5px; letter-spacing: 0.12em; text-transform: uppercase; color: #7a756a; margin-top: 4px; }
                .pgc-kv { flex: 1; display: grid; grid-template-columns: 1fr 1fr; gap: 0 22px; }
                .pgc-kv .row { display: flex; justify-content: space-between; align-items: baseline; padding: 5px 2px; border-bottom: 1px dotted #c9c0ab; font-size: 10px; }
                .pgc-kv .row .k { color: #57534a; }
                .pgc-kv .row .v { font-family: Georgia, serif; font-weight: 700; font-size: 10.5px; text-align: right; }
                .pgc-foot { margin-top: 18px; display: flex; justify-content: space-between; align-items: flex-end; gap: 24px; }
                .pgc-sig { flex: 1; }
                .pgc-sig .line { border-bottom: 1px solid #57534a; height: 26px; font-family: Georgia, serif; font-style: italic; font-size: 13px; color: #3f3c34; display: flex; align-items: flex-end; padding-bottom: 3px; }
                .pgc-sig .cap { font-size: 8px; letter-spacing: 0.12em; text-transform: uppercase; color: #7a756a; margin-top: 5px; }
                .pgc-seal { width: 84px; height: 84px; border-radius: 50%; border: 2px solid #b08d3e; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; color: #8a6d1f; flex-shrink: 0; box-shadow: inset 0 0 0 3px #fdfcf8, inset 0 0 0 4.5px #b08d3e; transform: rotate(-8deg); background: radial-gradient(circle at 35% 30%, rgba(176,141,62,0.12), transparent 60%); }
                .pgc-seal .t1 { font-size: 7px; font-weight: 800; letter-spacing: 0.16em; }
                .pgc-seal .t2 { font-family: Georgia, serif; font-size: 12px; font-weight: 700; margin: 2px 0; }
                .pgc-seal .t3 { font-size: 6.5px; letter-spacing: 0.12em; }
                .pgc-fine { margin-top: 18px; font-size: 8px; color: #9a948a; line-height: 1.55; border-top: 1px solid #e5dfcf; padding-top: 9px; }
            </style>
            <div class="pgc-mast">
                <div class="pgc-brand">PreGrade Cards · AI Grading Laboratory</div>
                <h1>Certificate of AI Grading Analysis</h1>
                <div class="pgc-cert-no">Certificate No. ${esc(certNo)} &nbsp;·&nbsp; Issued ${fmtDate}</div>
            </div>
            <div class="pgc-slab">
                ${imgSrc ? '<div class="pgc-slab-img"><img src="' + imgSrc + '" alt="' + esc(buildAltText({ year: id.year, name: id.name || id.full_name, setName: id.set_name || id.set, setCode: id.set_code, cardNumber: cleanCardNum(id.card_number || id.card_no), rarity: id.rarity, gradeLabel: grades.condition || record.final_grade_label, grade: grades.final, fallback: 'Certified trading card' })) + '" onerror="this.parentNode.style.display=\'none\'"></div>' : ''}
                <div class="pgc-slab-info">
                    <div class="pgc-co">Certified AI Grade</div>
                    <div class="pgc-cardname">${cardTitle}</div>
                    <div class="pgc-cardmeta">${metaHtml}</div>
                </div>
                <div class="pgc-slab-grade">
                    <div class="g">${overall}</div>
                    <div class="l">${esc(grades.condition || 'AI Predicted')}</div>
                    <div class="s">of 10 · AI Predicted</div>
                </div>
            </div>
            <div class="pgc-sec">
                <div class="pgc-sec-head"><span class="num">I.</span><h2>Subgrade Breakdown</h2><span class="rule"></span></div>
                <table class="pgc-formal">
                    <thead><tr><th style="width:26%">Criterion</th><th>Assessment</th><th style="width:24%">Score</th></tr></thead>
                    <tbody>${subRowsHtml}</tbody>
                </table>
            </div>
            <div class="pgc-sec">
                <div class="pgc-sec-head"><span class="num">II.</span><h2>Technical Measurements</h2><span class="rule"></span></div>
                <div class="pgc-center-grid">
                    ${pgcFullImg ? '<div class="pgc-ov-fig"><img src="' + esc(pgcFullImg) + '" alt="' + esc(pgcCertCap) + '"><div class="cap">' + esc(pgcCertCap) + '</div></div>' : `<div class="pgc-center-diagram">
                        <div class="axis-x"></div><div class="axis-y"></div>
                        <div class="inner"></div>
                        ${tb && tb[0] != null ? '<span class="lbl" style="top:3%; left:50%; transform:translateX(-50%);">T ' + esc(tb[0]) + '</span>' : ''}
                        ${tb && tb[1] != null ? '<span class="lbl" style="bottom:3%; left:50%; transform:translateX(-50%);">B ' + esc(tb[1]) + '</span>' : ''}
                        ${lr && lr[0] != null ? '<span class="lbl" style="left:3%; top:50%; transform:translateY(-50%);">L ' + esc(lr[0]) + '</span>' : ''}
                        ${lr && lr[1] != null ? '<span class="lbl" style="right:3%; top:50%; transform:translateY(-50%);">R ' + esc(lr[1]) + '</span>' : ''}
                    </div>`}
                    <div class="pgc-kv">
                        <div class="row"><span class="k">Left / Right split</span><span class="v">${centering.left_right ? esc(centering.left_right) : 'N/A'}</span></div>
                        <div class="row"><span class="k">Top / Bottom split</span><span class="v">${centering.top_bottom ? esc(centering.top_bottom) : 'N/A'}</span></div>
                        <div class="row"><span class="k">Centering box (px)</span><span class="v">${centering.bound_box ? esc('[' + centering.bound_box.join(', ') + ']') : '—'}</span></div>
                        <div class="row"><span class="k">Detection confidence</span><span class="v">${detection.confidence ? Math.round(parseFloat(detection.confidence) * 100) + '%' : 'N/A'}</span></div>
                        <div class="row"><span class="k">Surface grade</span><span class="v">${surfaceGrade != null ? surfaceGrade.toFixed(1) : 'N/A'}${surface.label ? ' — ' + esc(surface.label) : ''}</span></div>
                        <div class="row"><span class="k">Corners detail</span><span class="v">TL ${cornerCells[0]} · TR ${cornerCells[1]} · BL ${cornerCells[3]} · BR ${cornerCells[2]}</span></div>
                        <div class="row"><span class="k">Edges detail</span><span class="v">L ${edgeCells[0]} · R ${edgeCells[1]} · T ${edgeCells[2]} · B ${edgeCells[3]}</span></div>
                        <div class="row"><span class="k">Image / Engine</span><span class="v">${record._width && record._height ? record._width + '×' + record._height + ' px' : 'N/A'} · v2</span></div>
                    </div>
                </div>
            </div>
            <div class="pgc-foot">
                <div class="pgc-sig">
                    <div class="line">PreGrade Cards Grading Engine</div>
                    <div class="cap">Automated AI Analysis · Engine v2 · ${esc(ctx.procTime || '')}</div>
                </div>
                <div class="pgc-seal">
                    <div class="t1">PreGrade</div>
                    <div class="t2">AI Verified</div>
                    <div class="t3">${issueDate}</div>
                </div>
            </div>
            <div class="pgc-fine">
                This certificate reflects an automated pre-grade estimate produced by computer-vision analysis of the submitted image on ${fmtDate}. It is an analytical estimate of condition — not a professional authentication, not an encapsulated grade, and not a guarantee of any grade assigned by a third-party grading company. Certificate ${esc(certNo)} · Verify at pregradecards.com/verify.
            </div>
        `;
        return cert;
    }

    // Legacy full-report PDF (non-grade services): clone the rendered result
    function buildLegacyReportDocument(original, serviceName) {
        const stageId = 'ximilar-pdf-stage';
        let stage = document.getElementById(stageId);
        if (!stage) {
            stage = document.createElement('div');
            stage.id = stageId;
            stage.style.cssText = 'position:absolute;left:-9999px;top:0;width:8.5in;background:#fff;overflow:visible;';
            document.body.appendChild(stage);
        }
        stage.innerHTML = '';

        const clone = original.cloneNode(true);
        clone.querySelectorAll('.ximilar-result-header, .ximilar-result-actions, .ximilar-report-section, .ximilar-sportsdb-lookup, .ximilar-tcg-lookup, .ximilar-post-result-upsell, .ximilar-technical-details, .ximilar-share-section, .ximilar-lightbox, #ximilar-lightbox, button, .ximilar-zoom-icon').forEach(el => el.remove());
        clone.querySelectorAll('.ximilar-zoomable > div').forEach(el => el.remove());
        clone.querySelectorAll('a').forEach(a => { a.style.textDecoration = 'none'; a.style.color = '#1f2937'; });
        const banner = clone.querySelector('.ximilar-print-banner');
        if (banner) {
            banner.style.display = 'block';
            clone.insertBefore(banner, clone.firstChild);
        }
        clone.querySelectorAll('.ximilar-result-header, .ximilar-result-card, .ximilar-results-body, .ximilar-images-grid, .ximilar-image-item, .grade-result').forEach(el => {
            el.style.breakInside = 'avoid-page';
            el.style.pageBreakInside = 'avoid';
        });
        clone.querySelectorAll('img').forEach(img => {
            img.style.maxHeight = '1.8in';
            img.style.width = 'auto';
            img.style.height = 'auto';
            img.style.objectFit = 'contain';
        });
        clone.querySelectorAll('.ximilar-images-grid').forEach(grid => {
            grid.style.gridTemplateColumns = 'repeat(3, 1fr)';
            grid.style.gap = '10px';
        });
        clone.querySelectorAll('.ximilar-image-item').forEach(item => {
            item.style.breakInside = 'avoid';
            item.style.pageBreakInside = 'avoid';
        });
        clone.querySelectorAll('.ximilar-image-corner-detail img, .ximilar-image-edge-detail img').forEach(img => {
            img.style.maxHeight = '1.5in';
        });
        clone.style.width = '7.5in';
        clone.style.maxWidth = '7.5in';
        clone.style.padding = '0';
        clone.style.margin = '0';
        clone.style.background = '#fff';
        clone.style.color = '#1f2937';
        clone.style.fontSize = '12px';
        clone.style.lineHeight = '1.45';

        stage.appendChild(clone);
        return clone;
    }

    function linkifyCommentary(text) {
        if (!text) return '';
        return text
            // Strip the retired grade-cap sentence baked into older stored
            // commentary — grades are never capped for photo quality anymore.
            .replace(/\s*Note: the photo is too soft to verify fine edge\/corner\/surface detail, so the grade is capped — reshoot in sharper focus with even lighting for a full evaluation\./gi, '')
            // Link "Slab Identification tool" / "Slab ID tool" to the slab-id page
            .replace(/\bSlab Identification tool\b/gi, '<a href="/services/slab-id" style="color: var(--xg-info); font-weight: 600; text-decoration: underline;">Slab Identification tool</a>')
            .replace(/\bSlab ID tool\b/gi, '<a href="/services/slab-id" style="color: var(--xg-info); font-weight: 600; text-decoration: underline;">Slab ID tool</a>');
    }

    function renderImageQualityAnalysis(record) {
        const iq = record && record.image_quality;
        if (!iq || iq.error || !iq.commentary) return '';

        const score = iq.score || 0;
        let badgeColor, badgeBg, badgeText;
        if (score >= 75) { badgeBg = '#dcfce7'; badgeColor = '#166534'; badgeText = 'Good'; }
        else if (score >= 50) { badgeBg = '#fef3c7'; badgeColor = '#92400e'; badgeText = 'Fair'; }
        else { badgeBg = '#fee2e2'; badgeColor = '#991b1b'; badgeText = 'Poor'; }

        // Build issue tags
        const tags = [];
        const issueMap = [
            ['is_slabbed', 'Graded Slab', '#fee2e2', '#991b1b'],
            ['has_sleeve', 'Sleeve/Holder', '#fee2e2', '#991b1b'],
            ['has_hands', 'Hands Visible', '#fee2e2', '#991b1b'],
            ['has_glare', 'Glare', '#fef3c7', '#92400e'],
            ['has_shadows', 'Shadows', '#fef3c7', '#92400e'],
            ['is_blurry', 'Blurry', '#fef3c7', '#92400e'],
        ];
        for (const [key, label, bg, color] of issueMap) {
            if (iq[key]) tags.push(`<span style="padding:3px 10px;border-radius:6px;font-size:11px;font-weight:600;background:${bg};color:${color};">${label}</span>`);
        }
        // Framing/lighting/background tags
        if (iq.edge_clipped || iq.framing === 'partial_cutoff') {
            const sides = Array.isArray(iq.clipped_sides) && iq.clipped_sides.length ? ` (${iq.clipped_sides.join(', ')})` : '';
            tags.push(`<span style="padding:3px 10px;border-radius:6px;font-size:11px;font-weight:600;background:#fee2e2;color:#991b1b;">Card edge cut off${sides} — grade may be inaccurate</span>`);
        } else if (iq.framing && iq.framing !== 'good') {
            tags.push(`<span style="padding:3px 10px;border-radius:6px;font-size:11px;font-weight:600;background:#fef3c7;color:#92400e;">Framing: ${iq.framing.replace(/_/g,' ')}</span>`);
        }
        if (iq.lighting && iq.lighting !== 'good') {
            tags.push(`<span style="padding:3px 10px;border-radius:6px;font-size:11px;font-weight:600;background:#fef3c7;color:#92400e;">Lighting: ${iq.lighting.replace(/_/g,' ')}</span>`);
        }
        if (iq.background && iq.background !== 'clean' && iq.background !== 'good') {
            tags.push(`<span style="padding:3px 10px;border-radius:6px;font-size:11px;font-weight:600;background:#fef3c7;color:#92400e;">Background: ${escapeHtml(iq.background_detail || iq.background)}</span>`);
        }
        // Card feature tags (relics/autographs are notable features, not issues)
        if (iq.has_relic) {
            const rt = (iq.relic_type && iq.relic_type !== 'none') ? iq.relic_type : 'memorabilia';
            tags.push(`<span style="padding:3px 10px;border-radius:6px;font-size:11px;font-weight:600;background:#e0e7ff;color:#3730a3;">Relic: ${escapeHtml(String(rt))}</span>`);
        }
        if (iq.has_autograph) {
            tags.push(`<span style="padding:3px 10px;border-radius:6px;font-size:11px;font-weight:600;background:#e0e7ff;color:#3730a3;">Autograph</span>`);
        }
        if (iq.is_numbered && !isCollectorNumberNotSerial(iq, record)) {
            const serialLabel = iq.serial ? `#${escapeHtml(String(iq.serial))}` : 'numbered';
            tags.push(`<span style="padding:3px 10px;border-radius:6px;font-size:11px;font-weight:600;background:#e0e7ff;color:#3730a3;">Serial ${serialLabel}</span>`);
        }

        const tagsHtml = tags.length ? `<div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:10px;">${tags.join('')}</div>` : '';

        // Filter out placeholder junk the model occasionally emits instead of an
        // empty array ("empty", "none", "n/a", etc.)
        const isJunk = v => !v || ['empty', 'none', 'null', 'n/a', 'na', 'no issues', 'nothing'].includes(String(v).trim().toLowerCase());
        const issues = (iq.issues_found || []).filter(i => !isJunk(i));
        const defects = (iq.observed_defects || []).filter(d => !isJunk(d));

        // Issues list
        let issuesHtml = '';
        if (issues.length) {
            issuesHtml = `<div style="margin-top:8px;">
                <div style="font-size:11px;font-weight:700;color:#dc2626;text-transform:uppercase;letter-spacing:0.4px;">Photo issues</div>
                <ul style="margin:6px 0 0 0;padding-left:20px;font-size:13px;color:#dc2626;">${issues.map(i => `<li>${escapeHtml(String(i))}</li>`).join('')}</ul>
            </div>`;
        }

        // Visible card condition issues (defects on the card itself, not the photo)
        let defectsHtml = '';
        if (defects.length) {
            defectsHtml = `<div style="margin-top:8px;">
                <div style="font-size:11px;font-weight:700;color:#b45309;text-transform:uppercase;letter-spacing:0.4px;">Visible card condition</div>
                <ul style="margin:6px 0 0 0;padding-left:20px;font-size:13px;color:#b45309;">${defects.map(d => `<li>${escapeHtml(String(d))}</li>`).join('')}</ul>
            </div>`;
        }

        // Notable card features (relic/memorabilia, autograph) — positive, not issues
        const features = [];
        if (iq.has_relic) {
            const rt = (iq.relic_type && iq.relic_type !== 'none') ? iq.relic_type : 'memorabilia piece';
            const colors = (iq.relic_colors && iq.relic_colors.length) ? ` (${iq.relic_colors.map(c => escapeHtml(String(c))).join(', ')})` : '';
            features.push(`Relic / memorabilia: ${escapeHtml(String(rt))}${colors}`);
        }
        if (iq.has_autograph) features.push('Autograph visible on card');
        if (iq.is_numbered && !isCollectorNumberNotSerial(iq, record)) {
            features.push(iq.serial
                ? `Serial numbered: ${escapeHtml(String(iq.serial))}`
                : 'Serial numbered (number not legible in photo)');
        }
        let featuresHtml = '';
        if (features.length) {
            featuresHtml = `<div style="margin-top:8px;">
                <div style="font-size:11px;font-weight:700;color:#166534;text-transform:uppercase;letter-spacing:0.4px;">Card features detected</div>
                <ul style="margin:6px 0 0 0;padding-left:20px;font-size:13px;color:#166534;">${features.map(f => `<li>${f}</li>`).join('')}</ul>
            </div>`;
        }

        // Uploaded image thumbnail
        const imgSrc = record.original_image_url || (record._images && (record._images.original || record._images.front)) || null;
        const imgHtml = imgSrc ? `
            <div class="xim-iq-thumb" style="flex-shrink: 0; margin-right: 16px;">
                <img src="${pgcThumb(imgSrc, 320)}" alt="Your uploaded card" style="width: 160px; height: auto; border-radius: 8px; border: 1px solid var(--xg-border); object-fit: cover;" />
            </div>` : '';

        // Lighting zone map — paint measured blown/shadow cells over the card
        // crop so "blown-out on the left" is visible, not just stated. Zones are
        // card-relative so prefer the cropped image (whole photo would misalign).
        const zoneMapHtml = renderIqZoneMap(iq, record);

        return `
        <div class="ximilar-result-card ximilar-image-quality" style="background: var(--xg-subtle); border-radius: 12px; padding: 22px 24px; margin-top: 20px; border: 1px solid var(--xg-border); box-shadow: var(--xg-shadow);">
            <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 16px;">
                <span style="font-size: 22px;">📸</span>
                <h3 style="margin: 0; font-size: 17px; font-weight: 800; color: var(--xg-text);">Image Quality Analysis</h3>
                <span style="padding: 4px 12px; border-radius: 999px; font-size: 13px; font-weight: 700; background: ${badgeBg}; color: ${badgeColor}; margin-left: auto;">${badgeText} — ${score}/100</span>
            </div>
            <div class="xim-iq-layout" style="display: flex; align-items: flex-start; gap: 0; flex-wrap: wrap;">
                ${imgHtml}
                ${zoneMapHtml}
                <div style="flex: 1 1 0; min-width: 160px;">
                    ${tagsHtml}
                    ${issuesHtml}
                    ${defectsHtml}
                    ${featuresHtml}
                </div>
            </div>
            <div style="margin-top: 12px; padding: 14px 16px; background: var(--xg-panel); border-radius: 10px; border-left: 3px solid #6366f1; font-size: 14px; line-height: 1.6; color: var(--xg-text-2);">
                ${linkifyCommentary(iq.commentary)}
            </div>
        </div>`;
    }

    // Paint the measured zone-lighting grid over the card crop. Cells flagged
    // blown render amber, shadow cells render slate — the user sees WHERE the
    // lighting failed instead of only reading "glare on the left".
    function renderIqZoneMap(iq, record) {
        const zm = iq.zone_lighting_measured || null;
        if (!zm || ((!zm.blown_zones || !zm.blown_zones.length) && (!zm.shadow_zones || !zm.shadow_zones.length))) return '';
        // Grid orientation comes from the card's bound_box (landscape cards
        // were measured on a 7x5 grid, portrait on 5x7 — see _iqZoneLighting).
        const bb = (record.card && record.card.bound_box) || null;
        const bw = bb ? Math.abs(bb[2] - bb[0]) : (record._width || 0);
        const bh = bb ? Math.abs(bb[3] - bb[1]) : (record._height || 0);
        const landscape = bw > 0 && bh > 0 && bw > bh;
        const CX = zm.cx || (landscape ? 7 : 5), CY = zm.cy || (landscape ? 5 : 7);
        const blown = new Set(), shadow = new Set();
        if (Array.isArray(zm.cells) && zm.cells.length) {
            const med = Math.max(zm.median || 1, 1);
            zm.cells.forEach(c => {
                if (c.blown >= 0.08) blown.add(c.cx + ',' + c.cy);
                else if (c.crushed >= 0.35 && c.mean < 0.35 * med) shadow.add(c.cx + ',' + c.cy);
            });
        } else {
            // Pre-cells records: only side names were persisted — expand each
            // name to the grid region it describes (left = column 0, etc.).
            const expand = (names, set) => (names || []).forEach(n => {
                for (let cy = 0; cy < CY; cy++) for (let cx = 0; cx < CX; cx++) {
                    const isL = cx === 0, isR = cx === CX - 1, isT = cy === 0, isB = cy === CY - 1;
                    const hit = n === 'center' ? (!isL && !isR && !isT && !isB)
                        : n === 'left' ? (isL && !isT && !isB)
                        : n === 'right' ? (isR && !isT && !isB)
                        : n === 'top' ? (isT && !isL && !isR)
                        : n === 'bottom' ? (isB && !isL && !isR)
                        : n === 'top-left' ? (isT && isL)
                        : n === 'top-right' ? (isT && isR)
                        : n === 'bottom-left' ? (isB && isL)
                        : n === 'bottom-right' ? (isB && isR) : false;
                    if (hit) set.add(cx + ',' + cy);
                }
            });
            expand(zm.blown_zones, blown); expand(zm.shadow_zones, shadow);
        }
        if (!blown.size && !shadow.size) return '';
        let cellsHtml = '';
        for (let cy = 0; cy < CY; cy++) for (let cx = 0; cx < CX; cx++) {
            const k = cx + ',' + cy;
            if (!blown.has(k) && !shadow.has(k)) continue;
            const bg = blown.has(k) ? 'rgba(245,158,11,0.42)' : 'rgba(30,41,59,0.5)';
            const bd = blown.has(k) ? 'rgba(245,158,11,0.85)' : 'rgba(51,65,85,0.9)';
            cellsHtml += `<div style="position: absolute; left: ${(cx / CX * 100).toFixed(2)}%; top: ${(cy / CY * 100).toFixed(2)}%; width: ${(100 / CX).toFixed(2)}%; height: ${(100 / CY).toFixed(2)}%; background: ${bg}; border: 1px dashed ${bd}; box-sizing: border-box;"></div>`;
        }
        const cropSrc = (record._images && (record._images.cropped || record._images.clean || record._images.exact)) || record.original_image_url || '';
        // Container starts at bound_box ratio (keeps the frame from collapsing
        // pre-load) then snaps to the image's REAL ratio onload — bound_box is
        // the photo-space axis bbox of a skewed card, and forcing it over the
        // rectified crop made object-fit:cover lop off the card's top and
        // bottom (record 12111: bbox 0.7517 vs crop 0.7195).
        const ar = (bw > 0 && bh > 0) ? (bw / bh).toFixed(4) : (CX / CY).toFixed(4);
        const inner = (cropSrc
            ? `<img src="${pgcThumb(cropSrc, 320)}" alt="Lighting zones on your card" style="position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; display: block; border-radius: 5% / 3.6%;" onload="var v=this.closest('.xim-iq-zone-visual');if(v&&this.naturalHeight>0)v.style.aspectRatio=(this.naturalWidth/this.naturalHeight).toFixed(4);" />`
            : `<div style="position: absolute; inset: 0; background: var(--xg-panel);"></div>`) + cellsHtml;
        return `
            <div class="xim-iq-zonemap" style="flex-shrink: 0; margin-right: 16px; width: 132px;">
                <div style="position: relative; overflow: hidden; border-radius: 6px; border: 1px solid var(--xg-border); aspect-ratio: ${ar};">${inner}</div>
                <div style="margin-top: 6px; font-size: 10.5px; line-height: 1.4; color: var(--xg-muted);">
                    <div style="display: flex; align-items: center; gap: 4px; margin-top: 2px;"><span style="display: inline-block; width: 9px; height: 9px; border-radius: 2px; background: rgba(245,158,11,0.42); border: 1px dashed rgba(245,158,11,0.85);"></span>Blown / glare</div>
                    <div style="display: flex; align-items: center; gap: 4px; margin-top: 2px;"><span style="display: inline-block; width: 9px; height: 9px; border-radius: 2px; background: rgba(30,41,59,0.5); border: 1px dashed rgba(51,65,85,0.9);"></span>Deep shadow</div>
                </div>
            </div>`;
    }

    function renderGradeTipsExplainer(service) {
        if (service === 'coin-id') {
            const coinTips = [
                ['🖼️', 'Use a plain background', 'Place the coin on a solid white or light-colored surface. Patterned backgrounds make it harder for the AI to read legends and design details.'],
                ['🔍', 'Fill the frame — get close', 'The more of the photo the coin occupies, the more detail the AI can read. Crop in so the coin fills most of the frame without cutting off the rim.'],
                ['🔄', 'Photograph both sides', 'The date and mint mark are often on the opposite side from the main design. Adding the reverse photo dramatically improves identification.'],
                ['💡', 'Avoid flash glare', 'Direct flash creates hot spots that wash out the date and mint mark. Use soft, indirect light — daylight near a window works well.'],
                ['📐', 'Keep the coin flat', 'Shoot straight down with the coin lying flat. Angled shots distort the lettering the AI relies on for the date and legends.']
            ];
            return `
            <div class="ximilar-result-card ximilar-grade-tips" style="background: var(--xg-subtle); border-radius: 12px; padding: 22px 24px; margin-top: 20px; border: 1px solid var(--xg-border); box-shadow: var(--xg-shadow);">
                <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 16px;">
                    <span style="font-size: 22px;">💡</span>
                    <h3 style="margin: 0; font-size: 17px; font-weight: 800; color: var(--xg-text);">Not getting the identification you expected?</h3>
                </div>
                <p style="margin: 0 0 16px 0; font-size: 14px; color: var(--xg-text-2); line-height: 1.6;">Photo quality directly affects the AI's ability to read your coin. The most common fixes:</p>
                <div style="display: grid; gap: 14px;">
                    ${coinTips.map(t => `
                    <div style="display: flex; gap: 14px; align-items: flex-start; background: var(--xg-panel); border-radius: 10px; padding: 14px 16px; border: 1px solid var(--xg-border);">
                        <div style="flex-shrink: 0; width: 36px; height: 36px; background: var(--xg-info-soft); border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 18px;">${t[0]}</div>
                        <div>
                            <div style="font-size: 14px; font-weight: 700; color: var(--xg-text); margin-bottom: 4px;">${t[1]}</div>
                            <div style="font-size: 13px; color: var(--xg-muted); line-height: 1.5;">${t[2]}</div>
                        </div>
                    </div>`).join('')}
                </div>
            </div>`;
        }
        const serviceLabels = {
            'grade': 'grade',
            'centering': 'centering measurement',
            'condition': 'condition assessment',
            'crop': 'crop result',
            'localize': 'localization',
            'sport-id': 'identification',
            'tcg-id': 'identification',
            'unified-identify': 'identification',
            'slab-id': 'slab identification',
            'slab-grade': 'slab grade'
        };
        const label = serviceLabels[service] || 'results';
        return `
        <div class="ximilar-result-card ximilar-grade-tips" style="background: var(--xg-subtle); border-radius: 12px; padding: 22px 24px; margin-top: 20px; border: 1px solid var(--xg-border); box-shadow: var(--xg-shadow);">
            <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 16px;">
                <span style="font-size: 22px;">💡</span>
                <h3 style="margin: 0; font-size: 17px; font-weight: 800; color: var(--xg-text);">Not getting the ${label} you expected?</h3>
            </div>
            <p style="margin: 0 0 16px 0; font-size: 14px; color: var(--xg-text-2); line-height: 1.6;">Your photo quality directly affects the AI's ability to read your card accurately. Here are the most common things that can affect your ${label} — and how to fix them:</p>
            <div style="display: grid; gap: 14px;">
                <div style="display: flex; gap: 14px; align-items: flex-start; background: var(--xg-panel); border-radius: 10px; padding: 14px 16px; border: 1px solid var(--xg-border);">
                    <div style="flex-shrink: 0; width: 36px; height: 36px; background: var(--xg-info-soft); border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 18px;">🖼️</div>
                    <div>
                        <div style="font-size: 14px; font-weight: 700; color: var(--xg-text); margin-bottom: 4px;">Use a plain background</div>
                        <div style="font-size: 13px; color: var(--xg-muted); line-height: 1.5;">Patterned or colorful backgrounds make it harder for the AI to detect your card's true edges. Place your card on a solid white or light-colored surface before photographing.</div>
                    </div>
                </div>
                <div style="display: flex; gap: 14px; align-items: flex-start; background: var(--xg-panel); border-radius: 10px; padding: 14px 16px; border: 1px solid var(--xg-border);">
                    <div style="flex-shrink: 0; width: 36px; height: 36px; background: var(--xg-good-soft); border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 18px;">🃏</div>
                    <div>
                        <div style="font-size: 14px; font-weight: 700; color: var(--xg-text); margin-bottom: 4px;">Scan raw cards — no sleeves or toploaders</div>
                        <div style="font-size: 13px; color: var(--xg-muted); line-height: 1.5;">Penny sleeves and toploaders reflect light and blur fine details. The AI can't accurately assess corners, edges, or surface through plastic. Remove all holders before scanning.</div>
                    </div>
                </div>
                <div style="display: flex; gap: 14px; align-items: flex-start; background: var(--xg-panel); border-radius: 10px; padding: 14px 16px; border: 1px solid var(--xg-border);">
                    <div style="flex-shrink: 0; width: 36px; height: 36px; background: var(--xg-warn-soft); border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 18px;">🔍</div>
                    <div>
                        <div style="font-size: 14px; font-weight: 700; color: var(--xg-text); margin-bottom: 4px;">Fill the frame — get close</div>
                        <div style="font-size: 13px; color: var(--xg-muted); line-height: 1.5;">The more of the image your card occupies, the more detail the AI can see. Small or distant cards lose corner sharpness and surface detail. Crop in so the card fills most of the photo.</div>
                    </div>
                </div>
                <div style="display: flex; gap: 14px; align-items: flex-start; background: var(--xg-panel); border-radius: 10px; padding: 14px 16px; border: 1px solid var(--xg-border);">
                    <div style="flex-shrink: 0; width: 36px; height: 36px; background: var(--xg-bad-soft); border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 18px;">💡</div>
                    <div>
                        <div style="font-size: 14px; font-weight: 700; color: var(--xg-text); margin-bottom: 4px;">Use even, shadow-free lighting</div>
                        <div style="font-size: 13px; color: var(--xg-muted); line-height: 1.5;">Shadows, glare, and camera flash create artifacts that look like surface defects to the AI. Use soft, diffused lighting from multiple angles — or scan on a flatbed scanner for the most consistent results.</div>
                    </div>
                </div>
                <div style="display: flex; gap: 14px; align-items: flex-start; background: var(--xg-panel); border-radius: 10px; padding: 14px 16px; border: 1px solid var(--xg-border);">
                    <div style="flex-shrink: 0; width: 36px; height: 36px; background: var(--xg-info-soft); border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 18px;">📐</div>
                    <div>
                        <div style="font-size: 14px; font-weight: 700; color: var(--xg-text); margin-bottom: 4px;">Keep the card flat and straight</div>
                        <div style="font-size: 13px; color: var(--xg-muted); line-height: 1.5;">A tilted or bent card distorts centering measurements and can create false edge wear readings. Place the card flat on a solid surface and shoot directly from above.</div>
                    </div>
                </div>
            </div>
            <div style="margin-top: 16px; padding: 14px 16px; background: var(--xg-info-soft); border-radius: 10px; border: 1px solid var(--xg-info-line);">
                <div style="font-size: 13px; color: var(--xg-info); font-weight: 600; line-height: 1.5;">✅ Fix these issues and rescan — you'll often see better ${label} on the second attempt. Every credit saves you from wasting a $25–$80 grading submission fee.</div>
            </div>
        </div>`;
    }

    // Extract player and/or team names from the API identification result
    function extractPlayerTeam(record) {
        const card = record.card || {};
        const id = card.identification || record.identification || {};
        let player = id.player || null;
        let team = id.team || null;

        // If the identification has a full name like "Grant Hill 2021 Panini Prizm Black #90",
        // try to extract the player name from the common "Player Name Year Brand #" pattern.
        const fullName = id.full_name || id.name || '';
        if (!player && fullName) {
            // Capture everything before the first 4-digit year
            const match = fullName.match(/^(.+?)\s+\d{4}\s+/);
            if (match && match[1]) {
                player = match[1].trim();
            }
        }

        // Collect alternative player names from the alternatives list.
        // Ximilar sometimes misspells the primary name; alternatives often have the correct spelling.
        const altPlayers = [];
        const alts = record.alternatives || card.alternatives || [];
        if (Array.isArray(alts)) {
            for (const a of alts) {
                // a.name is usually just the player name; a.full_name needs parsing
                const altPlayer = a.name || extractPlayerFromFullName(a.full_name || '');
                if (altPlayer && altPlayer !== player && !altPlayers.includes(altPlayer)) {
                    altPlayers.push(altPlayer);
                }
            }
        }

        return { player, team, altPlayers };
    }

    function extractPlayerFromFullName(fullName) {
        if (!fullName) return null;
        const match = fullName.match(/^(.+?)\s+\d{4}\s+/);
        return match && match[1] ? match[1].trim() : null;
    }

    // Generate a unique-ish ID for the sportsdb lookup container
    let sportsDbCounter = 0;
    function sportsDbContainerId() {
        return 'ximilar-sportsdb-' + (++sportsDbCounter) + '-' + Date.now();
    }

    // Check if the record is a TCG card (not a sports card)
    function isTCGCard(record) {
        // Check set_name for known TCG game names first — this is the most
        // reliable signal. Sports cards never have "Pokemon", "Magic", etc.
        // as their set name.
        const id = (record.card || {}).identification || record.identification || {};
        const setNameLower = (id.set_name || '').toLowerCase();
        const tcgSetNames = ['pokemon', 'magic', 'yu-gi-oh', 'yugioh', 'lorcana', 'one piece', 'digimon', 'flesh and blood'];
        const isTCGBySetName = tcgSetNames.some(tcg => setNameLower.includes(tcg));
        if (isTCGBySetName) return true;

        // If sport_id identified the card, this is a sports card even if
        // Ximilar's category tag misclassified it as TCG. Judge success by
        // identification, not sport_id_error — that error only means pricing
        // data was missing, even when identification succeeded.
        const sportIdHasId = !!(record.sport_id && record.sport_id.card && record.sport_id.card.identification
            && (record.sport_id.card.identification.name || record.sport_id.card.identification.full_name
                || record.sport_id.card.identification.set_name));
        if (sportIdHasId) return false;
        // If identification.subcategory is a known sport, treat as sports card.
        const sub = (id.subcategory || '').toLowerCase();
        if (['baseball','basketball','football','hockey','soccer','boxing','golf','tennis','mma','wrestling','nascar','f1','cricket','rugby','australian rules football'].includes(sub)) return false;
        // If card_type contains "rookie", treat as sports — but ONLY when
        // we don't have a clear TCG identification (tcg_id failed/empty). Rookie
        // cards are exclusively sports, so if tcg_id found nothing, this is
        // likely a misclassified sports card.
        const tcgIdHasId = !!(record.tcg_id && record.tcg_id.card && record.tcg_id.card.identification
            && (record.tcg_id.card.identification.name || record.tcg_id.card.identification.full_name
                || record.tcg_id.card.identification.set_name));
        if (id.card_type && /rookie/i.test(id.card_type) && !tcgIdHasId) return false;

        // tcg_id's own tags are stronger evidence than the grader's coarse
        // category tag — the grader tags the image before identification runs.
        // A real tcg_id hit with a TCG subcategory/category means TCG even when
        // the grader guessed "Card/Sport Card" (e.g. foreign-language sets like
        // "Paradox Veil" that match no English set-name keyword). Guarded by
        // !sportIdHasId so tcg_id hallucinations on real sports cards can't
        // flip them (sport_id only misses when there's no sports match).
        if (tcgIdHasId && !sportIdHasId) {
            const tcgTags = (record.tcg_id.card && record.tcg_id.card.tags) || {};
            const tcgSub = (tcgTags.subcategory && tcgTags.subcategory.name || id.subcategory || '').toLowerCase();
            const tcgCat = (tcgTags.category && tcgTags.category.name || '').toLowerCase();
            const tcgGames = ['pokemon','pokémon','magic the gathering','magic: the gathering','yu-gi-oh','yugioh','lorcana','one piece','digimon','flesh and blood','gundam','riftbound','vanguard','weiss schwarz','dragon ball','dragon ball super','other game','trading card game'];
            if (tcgCat.indexOf('trading card game') !== -1 || tcgGames.indexOf(tcgSub) !== -1) return true;
        }

        const card = record.card || {};
        const tags = card._tags || card.tags || {};
        const rawCategory = (function(_o){return _o == null ? void 0 : _o.name})((tags.Category != null ? tags.Category[0] : void 0)) || (record.category != null ? record.category.value : void 0) || (record.category != null ? record.category.name : void 0) || record.category;
        if (!rawCategory) return false;
        const str = String(rawCategory).trim();
        return /^(Card\/Trading Card Game|Trading Card Game|TCG)$/i.test(str);
    }

    // A "serial" that is really the printed collector number — TCG cards print
    // NNN/NNN in the bottom info strip on every copy. The grade API clears
    // is_numbered for new records; this guard hides the wrong Serial chip on
    // historical records stored before that suppression existed.
    function isCollectorNumberNotSerial(iq, record) {
        if (!iq || !iq.is_numbered) return false;
        if (isTCGCard(record)) return true;
        const m = String(iq.serial || '').match(/(\d{1,5})\s*\//);
        if (!m) return false;
        const id = (record.card || {}).identification || record.identification || {};
        const num = parseInt(String(id.card_number || id.card_no || ''), 10);
        return Number.isFinite(num) && parseInt(m[1], 10) === num;
    }

    // Serial embedded in the provider's special_features prose ("1st Bowman,
    // Serial Numbered 4/5, Red Shimmer Parallel") — fallback for records where
    // the IQ pass didn't set is_numbered. Returns "4/5" or "/99" (denominator
    // only) or null. Kept in sync with the grade-v3.php special_features parse.
    function serialFromSpecialFeatures(sf) {
        if (!sf) return null;
        for (const seg of String(sf).split(/[;,]/)) {
            if (!/serial|numbered|print\s*run/i.test(seg)) continue;
            const m = seg.match(/\b(\d{1,4})\s*\/\s*(\d{1,4})\b/);
            if (m && parseInt(m[1], 10) <= parseInt(m[2], 10)) return parseInt(m[1], 10) + '/' + parseInt(m[2], 10);
            const md = seg.match(/\/\s*(\d{1,4})\b/);
            if (md) return '/' + parseInt(md[1], 10);
        }
        return null;
    }

    // Serial from any source the record carries — IQ flag first, then the
    // special_features prose fallback. null for TCG collector numbers.
    function serialForRecord(record) {
        const iq = record && record.image_quality;
        if (iq && iq.is_numbered && !isCollectorNumberNotSerial(iq, record)) {
            return iq.serial || 'numbered';
        }
        const id = (record && record.sport_id && (record.sport_id.card || {}).identification)
            || (record && record.card && record.card.identification)
            || (record && record.identification) || {};
        return serialFromSpecialFeatures(id.special_features);
    }

    // Provider sentinel for an unreadable card number ("not visible") — never
    // render or search it as a real number.
    // JS port of pgc_split_card_subjects()/pgc_looks_like_person_name() in
    // includes/functions.php — keep the heuristics in sync. Splits composite
    // subject strings ("Joe Torre / Vida Blue") so multi-player cards render
    // every printed name; insert tails like "Jordan/MJ Retro 93-94 UD" fail
    // the name-shape test and are dropped.
    function pgcLooksLikePersonName(s) {
        s = (s || '').trim();
        if (s.length < 5 || s.length > 50) return false;
        if (/\d/.test(s)) return false;
        if (/[^\p{L} '\-.]/u.test(s)) return false;
        const words = s.split(/\s+/).filter(Boolean);
        if (words.length < 2 || words.length > 5) return false;
        const stop = ['the','a','an','of','and','card','cards','topps','panini','upper','deck','bowman','fleer','donruss','score','leaders','leader','mvp','stars','star','rookies','rookie','prospects','prospect','team','league','insert','subset','series','collection','edition','set','base','gold','silver','refractor','parallel','chrome','prizm','optic','select','mosaic','finest','stadium','club','certified','mini','jumbo','box','topper','vs','versus','feat','featuring','with'];
        let cap = 0;
        for (const w of words) {
            if (stop.indexOf(w.toLowerCase()) !== -1) return false;
            if (/^\p{Lu}/u.test(w) || /^([A-Z]\.){1,3}$/.test(w)) { cap++; continue; }
            return false;
        }
        return cap >= 2;
    }

    function pgcSplitCardSubjects(s, isTeam) {
        s = (s || '').trim();
        if (!s) return [];
        const pm = s.match(/\(([^()]*(?:\/|&|\band\b)[^()]*)\)/i);
        if (pm) s = pm[1];
        const di = s.indexOf(' - ');
        if (di !== -1 && /\b(leaders?|stars?|mvp|rankings?|checklist|highlights?|vs)\b/i.test(s.slice(0, di))) {
            s = s.slice(di + 3).trim();
        }
        const parts = s.split(/\s*(?:\/|&|\+|\band\b)\s*/i);
        const out = [];
        for (const raw of parts) {
            let p = raw.trim().replace(/^\((.*)\)$/, '$1').replace(/\s*\([^)]*\)\s*/g, ' ').trim();
            if (!p) continue;
            if (!isTeam && !pgcLooksLikePersonName(p)) continue;
            if (isTeam && (p.length < 3 || /\d/.test(p) || /^(multiple|various|unknown|n\/?a|tbd)\b/i.test(p))) continue;
            if (out.indexOf(p) === -1) out.push(p);
        }
        return out;
    }

    function cleanCardNum(v) {
        const s = String(v || '').trim();
        if (!s || /not visible|n\/a|none|unknown|unnumbered|illegible/i.test(s) || !/[a-z0-9]/i.test(s)) return '';
        return s;
    }

    // Parallel name from special_features prose ("Red Shimmer Parallel" →
    // "Red Shimmer"). Used for the numbered-search marketplace links.
    function parallelFromSpecialFeatures(sf) {
        if (!sf) return '';
        for (const seg of String(sf).split(/[;,]/)) {
            const m = seg.trim().match(/([a-z0-9][a-z0-9 '\u2019-]{1,40}?)\s*parallel\b/i);
            if (m) return m[1].trim();
        }
        return '';
    }

    // True when a SportsData alias resolution is a genuine franchise
    // rename/relocation ("Washington Bullets" → "Washington Wizards") rather
    // than a mascot- or city-only fragment of the same name ("Thunder" →
    // "Oklahoma City Thunder", "Sacramento" → "Sacramento Kings"). Stored
    // alias_type (new records) wins; older records fall back to a substring
    // check — mascot/city aliases always resolve to a name containing the
    // fragment ("Wolves" → "Minnesota Timberwolves" too).
    function isTeamRename(orig, resolved, aliasType) {
        if (!orig || !resolved || orig === resolved) return false;
        if (aliasType) return aliasType === 'rename';
        const o = String(orig).toLowerCase().trim(), n = String(resolved).toLowerCase().trim();
        return !(n.startsWith(o) || n.endsWith(o));
    }

    // Extract the game/sport subcategory from the record.
    // Ximilar stores it in tcg_id.card.tags.subcategory.name or sport_id.card.tags.subcategory.name,
    // NOT in the identification object.
    function extractSubcategory(record) {
        // If set_name indicates TCG, return the game name directly
        const id = (record.card || {}).identification || record.identification || {};
        const setNameLower = (id.set_name || '').toLowerCase();
        const tcgSetNames = ['pokemon', 'magic', 'yu-gi-oh', 'yugioh', 'lorcana', 'one piece', 'digimon', 'flesh and blood'];
        if (tcgSetNames.some(tcg => setNameLower.includes(tcg))) {
            // Check tcg_id response first
            const tcgSub = (function(_o){return _o == null ? void 0 : _o.name})((function(_o){return _o == null ? void 0 : _o.subcategory})((function(_o){return _o == null ? void 0 : _o.tags})((record.tcg_id != null ? record.tcg_id.card : void 0))));
            if (tcgSub) return tcgSub;
            // Infer from set_name
            if (setNameLower.includes('pokemon')) return 'Pokemon';
            if (setNameLower.includes('magic')) return 'Magic The Gathering';
            if (setNameLower.includes('yu-gi-oh') || setNameLower.includes('yugioh')) return 'Yu-Gi-Oh!';
            if (setNameLower.includes('lorcana')) return 'Lorcana';
            if (setNameLower.includes('one piece')) return 'One Piece';
            if (setNameLower.includes('digimon')) return 'Digimon';
            if (setNameLower.includes('flesh and blood')) return 'Flesh and Blood';
        }
        // For sports cards, check sport_id first (tcg_id may have a wrong "Other game" result).
        // Judge by identification, not sport_id_error (missing pricing ≠ failed ID).
        const knownSports = PGC_SPORTS_OR_LEAGUES;

        // Non-sport collectible hard rule — when the authoritative
        // identification names a non-sport game/franchise (Star Wars, Garbage
        // Pail Kids, Marvel...), sport_id's low-confidence tag is a
        // hallucination ("Hockey" @ 0.59 on a Star Wars card → Gary Kurt bio,
        // record 7621). Return the real name; never fall back to the tag.
        const nonSport = nonSportCollectibleLabel(record);
        if (nonSport) return nonSport;
        const sportIdIdentified = !!(record.sport_id && record.sport_id.card && record.sport_id.card.identification
            && (record.sport_id.card.identification.name || record.sport_id.card.identification.full_name
                || record.sport_id.card.identification.set_name));
        const isSports = sportIdIdentified || knownSports.includes((id.subcategory || '').toLowerCase());
        if (isSports) {
            // identification.subcategory is the identification model's own
            // call — stronger than the low-confidence sport_id tag. Prefer it
            // when it names a real sport.
            if (knownSports.includes((id.subcategory || '').toLowerCase())) return id.subcategory;
            const sportSub = (function(_o){return _o == null ? void 0 : _o.name})((function(_o){return _o == null ? void 0 : _o.subcategory})((function(_o){return _o == null ? void 0 : _o.tags})((record.sport_id != null ? record.sport_id.card : void 0))));
            if (sportSub) return sportSub;
        }
        // Check tcg_id response (TCG cards)
        const tcgSub = (function(_o){return _o == null ? void 0 : _o.name})((function(_o){return _o == null ? void 0 : _o.subcategory})((function(_o){return _o == null ? void 0 : _o.tags})((record.tcg_id != null ? record.tcg_id.card : void 0))));
        if (tcgSub) return tcgSub;
        // Fall back to identification (may not have it, but check anyway)
        return id.subcategory || id.category || '';
    }

    // Sports + sports-league names. The identification's `subcategory` /
    // `sport_or_game` sometimes carries a league (NBA, UFC) instead of the
    // sport name — both count as sports.
    const PGC_SPORTS_OR_LEAGUES = ['baseball','basketball','football','hockey','ice hockey','field hockey','american football','soccer','boxing','golf','tennis','mma','wrestling','nascar','f1','cricket','rugby','rugby league','rugby union','australian rules football','motorsport','auto racing','lacrosse','volleyball','softball',
        'nba','nfl','mlb','nhl','mls','wnba','ncaa','cfl','xfl','usfl','ufl','afl','epl','premier league','la liga','bundesliga','serie a','ligue 1','ligue1',
        'ufc','wwe','aew','pga','pga tour','atp','wta','formula 1','indycar','motogp','olympics','ipl','mll','nll'];
    const PGC_VAGUE_CATEGORY = ['', 'other', 'unknown', 'n/a', 'none', 'null', 'card/sport card'];

    // Returns the identification's non-sport game/franchise name (e.g. 'Star
    // Wars') when the authoritative identification names something outside
    // sports — else null. Non-sport collectibles (Star Wars, Garbage Pail
    // Kids, Marvel cards...) must never be treated as sports: sport_id's
    // low-confidence tag is unreliable on them (record 7621: Star Wars card
    // tagged "Hockey" @ 0.59 → Gary Kurt bio served for Luke Skywalker).
    function nonSportCollectibleLabel(record) {
        if (isTCGCard(record)) return null; // TCG handled by its own path
        const id = (record.card || {}).identification || record.identification || {};
        const subLow = (id.subcategory || '').toString().toLowerCase();
        const gameLow = (id.sport_or_game || '').toString().toLowerCase();
        const subVague = PGC_VAGUE_CATEGORY.includes(subLow);
        const gameVague = PGC_VAGUE_CATEGORY.includes(gameLow);
        if (subVague && gameVague) {
            // Same fallback as ResultRenderer: records stored before the
            // write-time enrichment carry no identification classification,
            // but the IQ pass's card_identity read the franchise off the
            // face (record 10578: Star Wars card rendered "Player").
            const iqGame = ((((record.image_quality || {}).card_identity || {}).sport_or_game) || '').toString().trim();
            const iqGameLow = iqGame.toLowerCase();
            if (iqGameLow === '' || PGC_VAGUE_CATEGORY.includes(iqGameLow)
                || PGC_SPORTS_OR_LEAGUES.includes(iqGameLow)) return null;
            return iqGame;
        }
        if (PGC_SPORTS_OR_LEAGUES.includes(subLow) || PGC_SPORTS_OR_LEAGUES.includes(gameLow)) return null;
        return !subVague ? id.subcategory : id.sport_or_game;
    }

    // Sports profile renders from record.sports_profile (fetched at grade
    // time) inside the unified identification card. Records are snapshots —
    // there is intentionally no lazy /api/sportsdb-lookup path: a record
    // without a stored profile simply omits the section.
    function renderSportsDbLookup(record, config) {
        return '';
    }

    // Escape HTML for data attributes
    function escapeHtml(text) {
        if (text == null) return '';
        return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
    }

    // Records are snapshots — image URLs are already resolved server-side:
    // external enrichment art points at static /cache/img-proxy/* files and
    // local uploads at /uploads/*. Nothing routes through /api/img-proxy
    // or /api/img-thumb from the browser anymore; both helpers pass through.
    function proxyImg(url) {
        return url;
    }

    // Server-side embed builds record._thumb_map (srcPath → 640px webp under
    // /cache/thumbs/). render() merges it into thumbMap; the small-grid and
    // detail images swap src while data-zoom-src keeps the full-res path.
    const thumbMap = {};
    function pgcThumb(src, w) {
        if (!src || typeof src !== 'string') return src;
        let p = src;
        if (/^https?:\/\//i.test(p)) {
            try {
                const u = new URL(p);
                if (u.origin !== location.origin) return src;
                p = u.pathname;
            } catch (e) { return src; }
        }
        return thumbMap[p] || src;
    }
    // Build a descriptive alt string for a card image from available context.
    // Produces e.g. "Pikachu ex #277, Ascended Heroes, Special Illustration Rare — Near Mint 8"
    // or "1999 Pokemon Challenge from the Darkness #006 Blaine's Charizard — Excellent 5"
    // Falls back gracefully when fields are missing.
    function buildAltText(opts) {
        opts = opts || {};
        const parts = [];
        if (opts.year) parts.push(String(opts.year));
        if (opts.name) parts.push(String(opts.name));
        const setNum = [];
        if (opts.setName) setNum.push(opts.setName);
        if (opts.cardNumber) setNum.push('#' + opts.cardNumber);
        if (setNum.length) parts.push(setNum.join(' '));
        if (opts.rarity && opts.rarity !== opts.name) parts.push(opts.rarity);
        let base = parts.filter(Boolean).join(', ');
        if (opts.gradeLabel || opts.grade) {
            const g = opts.gradeLabel || (opts.grade != null ? 'Grade ' + opts.grade : '');
            if (g) base = base ? base + ' — ' + g : g;
        }
        if (opts.suffix) base = base ? base + ' ' + opts.suffix : opts.suffix;
        return base || opts.fallback || 'Card image';
    }
    // Ensure a URL has a protocol — TheSportsDB returns bare domains like "www.facebook.com/Mets"
    function ensureUrl(url) {
        if (!url) return '';
        if (/^https?:\/\//i.test(url) || /^mailto:/i.test(url)) return url;
        return 'https://' + url;
    }

    // fetchSportsDbData was removed — records are grade-time snapshots and
    // never trigger /api/sportsdb-lookup from the page.

    // Render cool player/team cards from multi-source sports data
    function renderSportsDbContent(data, cardTeam) {
        // Format ISO date (YYYY-MM-DD) as "Mon DD, YYYY"
        function fmtDate(d) {
            if (!d) return null;
            const dt = new Date(d);
            if (isNaN(dt)) return d;
            return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        }

        const playerHtml = (data.player || []).slice(0, 1).map(p => {
            const img = p.thumbnail || p.render || null;

            // Use the card-era team for the player bio (the team on the card),
            // not the current team from TheSportsDB (which may say "_Retired Baseball")
            const playerTeam = cardTeam || p.team || '';

            // Get the current team name from the team data (if available)
            const teamData = (data.team || [])[0] || {};
            const currentTeamName = teamData.name || '';
            const teamRenamed = isTeamRename(teamData.original_name, currentTeamName, teamData.alias_type);

            // Group bio facts into a clean definition list
            const bioFacts = [];
            if (p.position) bioFacts.push(['Position', p.position]);
            if (playerTeam) bioFacts.push(['Team (on card)', playerTeam]);
            // Show resolved/current team if different from card team
            if (currentTeamName && currentTeamName !== playerTeam) {
                bioFacts.push([teamRenamed ? 'Team (current)' : 'Team (full name)', currentTeamName + (teamRenamed ? ' (renamed)' : '')]);
            }
            if (p.sport) bioFacts.push(['Sport', p.sport]);
            if (p.number) bioFacts.push(['Number', '#' + p.number]);
            if (p.height) bioFacts.push(['Height', p.height]);
            if (p.weight) bioFacts.push(['Weight', p.weight + ' lb']);
            if (p.batSide) bioFacts.push(['Bats', p.batSide]);
            if (p.throwArm) bioFacts.push(['Throws', p.throwArm]);
            if (p.birth) bioFacts.push(['Born', fmtDate(p.birth)]);
            if (p.age != null) bioFacts.push(['Age', String(p.age)]);
            if (p.debut) bioFacts.push(['Debut', fmtDate(p.debut)]);
            if (p.deathDate) bioFacts.push(['Died', fmtDate(p.deathDate)]);
            if (p.draftYear) bioFacts.push(['Draft', String(p.draftYear)]);
            const birthplace = [p.birthCity, p.birthState].filter(Boolean).join(', ');
            if (birthplace) bioFacts.push(['Birthplace', birthplace]);
            if (p.nationality) bioFacts.push(['Nationality', p.nationality]);
            if (p.active !== null && p.active !== undefined) bioFacts.push(['Status', p.active ? 'Active' : 'Retired']);

            // Career stats
            const cs = p.career_stats || {};
            const ss = p.season_stats || {};
            const seasonLabel = new Date().getFullYear() + ' Season';

            function renderStatBlock(title, statData, fields) {
                if (!statData) return '';
                const stats = fields.filter(([,v]) => v != null);
                if (!stats.length) return '';
                return `<div style="padding: 10px 0; border-top: 1px solid var(--xg-border);">
                    <div style="font-size: 11px; font-weight: 700; color: var(--xg-text-2); margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;">${escapeHtml(title)}</div>
                    <div style="display: flex; flex-wrap: wrap; gap: 4px;">
                        ${stats.map(([k,v]) => `<div style="text-align: center; padding: 4px 8px; background: var(--xg-subtle); border-radius: 6px; min-width: 48px;"><div style="font-size: 9px; color: var(--xg-muted); font-weight: 600; text-transform: uppercase; letter-spacing: 0.3px;">${escapeHtml(k)}</div><div style="font-size: 15px; font-weight: 800; color: var(--xg-text); margin-top: 1px;">${escapeHtml(String(v))}</div></div>`).join('')}
                    </div>
                </div>`;
            }

            const hittingFields = [['AVG', (cs.hitting != null ? cs.hitting.avg : void 0)], ['HR', (cs.hitting != null ? cs.hitting.homeRuns : void 0)], ['RBI', (cs.hitting != null ? cs.hitting.rbi : void 0)], ['H', (cs.hitting != null ? cs.hitting.hits : void 0)],
                ['2B', (cs.hitting != null ? cs.hitting.doubles : void 0)], ['3B', (cs.hitting != null ? cs.hitting.triples : void 0)], ['SB', (cs.hitting != null ? cs.hitting.stolenBases : void 0)], ['R', (cs.hitting != null ? cs.hitting.runs : void 0)],
                ['OBP', (cs.hitting != null ? cs.hitting.obp : void 0)], ['SLG', (cs.hitting != null ? cs.hitting.slg : void 0)], ['OPS', (cs.hitting != null ? cs.hitting.ops : void 0)], ['GP', (cs.hitting != null ? cs.hitting.gamesPlayed : void 0)]];
            const pitchingFields = [['ERA', (cs.pitching != null ? cs.pitching.era : void 0)], ['W', (cs.pitching != null ? cs.pitching.wins : void 0)], ['L', (cs.pitching != null ? cs.pitching.losses : void 0)], ['SV', (cs.pitching != null ? cs.pitching.saves : void 0)],
                ['G', (cs.pitching != null ? cs.pitching.gamesPlayed : void 0)], ['GS', (cs.pitching != null ? cs.pitching.gamesStarted : void 0)], ['IP', (cs.pitching != null ? cs.pitching.inningsPitched : void 0)],
                ['SO', (cs.pitching != null ? cs.pitching.strikeOuts : void 0)], ['WHIP', (cs.pitching != null ? cs.pitching.whip : void 0)], ['CG', (cs.pitching != null ? cs.pitching.completeGames : void 0)], ['SHO', (cs.pitching != null ? cs.pitching.shutouts : void 0)]];

            const isPitcher = p.position && /pitcher|p/i.test(p.position);
            let statBlocks = '';
            if (cs.pitching) {
                statBlocks += renderStatBlock('Career Pitching', cs.pitching, pitchingFields);
            }
            if (cs.hitting) {
                statBlocks += renderStatBlock('Career Hitting', cs.hitting, hittingFields);
            }

            // Current season stats
            if (ss.pitching) {
                const sPitching = [['ERA', ss.pitching.era], ['W', ss.pitching.wins], ['L', ss.pitching.losses], ['SV', ss.pitching.saves],
                    ['IP', ss.pitching.inningsPitched], ['SO', ss.pitching.strikeOuts], ['WHIP', ss.pitching.whip]];
                statBlocks += renderStatBlock(seasonLabel + ' Pitching', ss.pitching, sPitching);
            }
            if (ss.hitting) {
                const sHitting = [['AVG', ss.hitting.avg], ['HR', ss.hitting.homeRuns], ['RBI', ss.hitting.rbi], ['H', ss.hitting.hits],
                    ['SB', ss.hitting.stolenBases], ['OPS', ss.hitting.ops], ['GP', ss.hitting.gamesPlayed]];
                statBlocks += renderStatBlock(seasonLabel + ' Hitting', ss.hitting, sHitting);
            }

            // NBA Basketball stats (ESPN career averages / BALLDONTLIE season)
            if (cs.basketball) {
                const bballFields = [['PPG', cs.basketball.pts], ['RPG', cs.basketball.reb], ['APG', cs.basketball.ast],
                    ['SPG', cs.basketball.stl], ['BPG', cs.basketball.blk], ['FG%', cs.basketball.fg_pct],
                    ['3P%', cs.basketball.fg3_pct], ['FT%', cs.basketball.ft_pct],
                    ['GP', cs.basketball.games_played], ['MIN', cs.basketball.minutes_per_game]];
                statBlocks += renderStatBlock(cs.basketball_label || 'NBA Season Averages', cs.basketball, bballFields);
            }

            const statsHtml = statBlocks ? `<div style="padding: 10px 0; border-top: 1px solid var(--xg-border);">${statBlocks}</div>` : '';

            // Awards
            let awardsHtml = '';
            if (p.awards && p.awards.length) {
                const maxShow = 8;
                const topAwards = p.awards.slice(0, maxShow);
                const extraCount = p.awards.length - maxShow;
                awardsHtml = `<div style="padding: 10px 0; border-top: 1px solid var(--xg-border);">
                    <div style="font-size: 11px; font-weight: 700; color: var(--xg-text-2); margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;">Awards & Honors (${p.awards.length})</div>
                    <div style="display: flex; flex-wrap: wrap; gap: 4px;">
                        ${topAwards.map(a => `<span style="background: var(--xg-good-soft, #e8f5e9); padding: 3px 8px; border-radius: 6px; font-size: 10px; font-weight: 600; color: var(--xg-text-2);">${escapeHtml(a.name)}${a.season ? ' <span style="color: var(--xg-muted); font-weight: 400;">' + escapeHtml(a.season) + '</span>' : ''}</span>`).join('')}
                        ${extraCount > 0 ? `<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 6px; font-size: 10px; font-weight: 600; color: var(--xg-muted);">+${extraCount} more</span>` : ''}
                    </div>
                </div>`;
            }

            // Social links
            let socialHtml = '';
            const socials = [];
            if (p.website) socials.push(`<a href="${escapeHtml(ensureUrl(p.website))}" target="_blank" rel="noopener" style="display: inline-flex; align-items: center; gap: 4px; padding: 5px 12px; background: var(--xg-panel); border: 1px solid var(--xg-border); border-radius: 100px; color: var(--xg-text-2); text-decoration: none; font-size: 11px; font-weight: 600; transition: all .15s;" onmouseover="this.style.background='var(--xg-subtle)'" onmouseout="this.style.background='var(--xg-panel)'">🌐 Website</a>`);
            if (p.facebook) socials.push(`<a href="${escapeHtml(ensureUrl(p.facebook))}" target="_blank" rel="noopener" style="display: inline-flex; align-items: center; gap: 4px; padding: 5px 12px; background: #1877f2; border-radius: 100px; color: white; text-decoration: none; font-size: 11px; font-weight: 600; transition: opacity .15s;" onmouseover="this.style.opacity='0.85'" onmouseout="this.style.opacity='1'">f Facebook</a>`);
            if (p.twitter) socials.push(`<a href="${escapeHtml(ensureUrl(p.twitter))}" target="_blank" rel="noopener" style="display: inline-flex; align-items: center; gap: 4px; padding: 5px 12px; background: #000; border-radius: 100px; color: white; text-decoration: none; font-size: 11px; font-weight: 600; transition: opacity .15s;" onmouseover="this.style.opacity='0.85'" onmouseout="this.style.opacity='1'">𝕏 Twitter</a>`);
            if (p.instagram) socials.push(`<a href="${escapeHtml(ensureUrl(p.instagram))}" target="_blank" rel="noopener" style="display: inline-flex; align-items: center; gap: 4px; padding: 5px 12px; background: linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888); border-radius: 100px; color: white; text-decoration: none; font-size: 11px; font-weight: 600; transition: opacity .15s;" onmouseover="this.style.opacity='0.85'" onmouseout="this.style.opacity='1'">📷 Instagram</a>`);
            if (p.wiki_url) socials.push(`<a href="${escapeHtml(ensureUrl(p.wiki_url))}" target="_blank" rel="noopener" style="display: inline-flex; align-items: center; gap: 4px; padding: 5px 12px; background: var(--xg-panel); border: 1px solid var(--xg-border); border-radius: 100px; color: var(--xg-text-2); text-decoration: none; font-size: 11px; font-weight: 600; transition: all .15s;" onmouseover="this.style.background='var(--xg-subtle)'" onmouseout="this.style.background='var(--xg-panel)'">📖 Wikipedia</a>`);
            if (socials.length) {
                socialHtml = `<div style="margin-top: 12px; display: flex; gap: 8px; flex-wrap: wrap;">${socials.join('')}</div>`;
            }

            // Bio facts as a clean grid (2 cols on wide, 1 col on narrow)
            const bioHtml = bioFacts.length ? `<div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(100px, 1fr)); gap: 6px; margin-top: 10px; padding: 10px; background: var(--xg-panel); border-radius: 8px; border: 1px solid var(--xg-border);">
                ${bioFacts.map(([k, v]) => `<div style="display: flex; flex-direction: column; gap: 1px;"><span style="font-size: 9px; color: var(--xg-muted); font-weight: 600; text-transform: uppercase; letter-spacing: 0.3px;">${escapeHtml(k)}</span><span style="font-size: 12px; color: var(--xg-text); font-weight: 600;">${escapeHtml(String(v))}</span></div>`).join('')}
            </div>` : '';

            return `
                <div style="background: var(--xg-subtle); border-radius: 10px; padding: 16px; border: 1px solid var(--xg-border);">
                    <div style="display: flex; gap: 14px; align-items: center; margin-bottom: 4px;">
                        ${img ? `<img src="${escapeHtml(proxyImg(img))}" alt="${escapeHtml(p.name)}" style="width: 64px; height: 64px; object-fit: cover; border-radius: 10px; background: var(--xg-panel); border: 1px solid var(--xg-border); flex-shrink: 0;" onerror="this.style.display='none'">` : ''}
                        <div style="min-width: 0; flex: 1;">
                            <div style="font-size: 18px; font-weight: 800; color: var(--xg-text); line-height: 1.2;">${escapeHtml(p.name)}</div>
                            ${p.team ? `<div style="font-size: 13px; color: var(--xg-muted); margin-top: 2px;">${escapeHtml(p.team)}</div>` : ''}
                            ${p.active !== null && p.active !== undefined ? `<span style="display: inline-block; margin-top: 4px; padding: 2px 8px; border-radius: 100px; font-size: 10px; font-weight: 700; background: ${p.active ? 'var(--xg-good-soft, #e8f5e9)' : 'var(--xg-chip-bg)'}; color: ${p.active ? 'var(--xg-good-strong, #2e7d32)' : 'var(--xg-text-2)'};">${p.active ? '● Active' : '○ Retired'}</span>` : ''}
                        </div>
                    </div>
                    ${bioHtml}
                    ${p.description ? `<p style="font-size: 12px; color: var(--xg-muted); margin: 12px 0 0 0; line-height: 1.5; display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden;">${escapeHtml(p.description)}</p>` : ''}
                    ${(statsHtml || awardsHtml) ? `<div style="margin-top: 10px; padding: 0 12px; background: var(--xg-panel); border-radius: 8px; border: 1px solid var(--xg-border);">${statsHtml}${awardsHtml}</div>` : ''}
                    ${socialHtml}
                </div>
            `;
        }).join('');

        const teamHtml = (data.team || []).slice(0, 1).map(t => {
            const img = t.logo || t.badge || null;

            // Team facts
            const teamFacts = [];
            if (t.league) teamFacts.push(['League', t.league]);
            if (t.sport) teamFacts.push(['Sport', t.sport]);
            if (t.founded) teamFacts.push(['Founded', String(t.founded)]);
            if (t.stadium) teamFacts.push(['Stadium', t.stadium]);
            if (t.country) teamFacts.push(['Country', t.country]);
            if (t.abbreviation) teamFacts.push(['Abbr', t.abbreviation]);

            const teamBioHtml = teamFacts.length ? `<div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(100px, 1fr)); gap: 6px; margin-top: 10px; padding: 10px; background: var(--xg-panel); border-radius: 8px; border: 1px solid var(--xg-border);">
                ${teamFacts.map(([k, v]) => `<div style="display: flex; flex-direction: column; gap: 1px;"><span style="font-size: 9px; color: var(--xg-muted); font-weight: 600; text-transform: uppercase; letter-spacing: 0.3px;">${escapeHtml(k)}</span><span style="font-size: 12px; color: var(--xg-text); font-weight: 600;">${escapeHtml(String(v))}</span></div>`).join('')}
            </div>` : '';

            // Team social links
            const teamSocials = [];
            if (t.website) teamSocials.push(`<a href="${escapeHtml(ensureUrl(t.website))}" target="_blank" rel="noopener" style="display: inline-flex; align-items: center; gap: 4px; padding: 5px 12px; background: var(--xg-panel); border: 1px solid var(--xg-border); border-radius: 100px; color: var(--xg-text-2); text-decoration: none; font-size: 11px; font-weight: 600; transition: all .15s;" onmouseover="this.style.background='var(--xg-subtle)'" onmouseout="this.style.background='var(--xg-panel)'">🌐 Website</a>`);
            if (t.facebook) teamSocials.push(`<a href="${escapeHtml(ensureUrl(t.facebook))}" target="_blank" rel="noopener" style="display: inline-flex; align-items: center; gap: 4px; padding: 5px 12px; background: #1877f2; border-radius: 100px; color: white; text-decoration: none; font-size: 11px; font-weight: 600; transition: opacity .15s;" onmouseover="this.style.opacity='0.85'" onmouseout="this.style.opacity='1'">f Facebook</a>`);
            if (t.twitter) teamSocials.push(`<a href="${escapeHtml(ensureUrl(t.twitter))}" target="_blank" rel="noopener" style="display: inline-flex; align-items: center; gap: 4px; padding: 5px 12px; background: #000; border-radius: 100px; color: white; text-decoration: none; font-size: 11px; font-weight: 600; transition: opacity .15s;" onmouseover="this.style.opacity='0.85'" onmouseout="this.style.opacity='1'">𝕏 Twitter</a>`);
            if (t.instagram) teamSocials.push(`<a href="${escapeHtml(ensureUrl(t.instagram))}" target="_blank" rel="noopener" style="display: inline-flex; align-items: center; gap: 4px; padding: 5px 12px; background: linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888); border-radius: 100px; color: white; text-decoration: none; font-size: 11px; font-weight: 600; transition: opacity .15s;" onmouseover="this.style.opacity='0.85'" onmouseout="this.style.opacity='1'">📷 Instagram</a>`);
            if (t.youtube) teamSocials.push(`<a href="${escapeHtml(ensureUrl(t.youtube))}" target="_blank" rel="noopener" style="display: inline-flex; align-items: center; gap: 4px; padding: 5px 12px; background: #ff0000; border-radius: 100px; color: white; text-decoration: none; font-size: 11px; font-weight: 600; transition: opacity .15s;" onmouseover="this.style.opacity='0.85'" onmouseout="this.style.opacity='1'">▶ YouTube</a>`);
            const teamSocialHtml = teamSocials.length ? `<div style="margin-top: 12px; display: flex; gap: 8px; flex-wrap: wrap;">${teamSocials.join('')}</div>` : '';

            return `
                <div style="background: var(--xg-subtle); border-radius: 10px; padding: 16px; border: 1px solid var(--xg-border);">
                    <div style="display: flex; gap: 14px; align-items: center; margin-bottom: 4px;">
                        ${img ? `<img src="${escapeHtml(proxyImg(img))}" alt="${escapeHtml(t.name)}" style="width: 64px; height: 64px; object-fit: contain; border-radius: 10px; background: var(--xg-panel); border: 1px solid var(--xg-border); padding: 6px; flex-shrink: 0;" onerror="this.style.display='none'">` : ''}
                        <div style="min-width: 0; flex: 1;">
                            <div style="font-size: 18px; font-weight: 800; color: var(--xg-text); line-height: 1.2;">${escapeHtml(t.name)}</div>
                            ${isTeamRename(t.original_name, t.name, t.alias_type) ? `<div style="font-size: 12px; color: var(--xg-muted); margin-top: 3px; font-style: italic;">Formerly: ${escapeHtml(t.original_name)}</div>` : ''}
                            ${t.alternate ? `<div style="font-size: 13px; color: var(--xg-muted); margin-top: 2px;">${escapeHtml(t.alternate)}</div>` : ''}
                        </div>
                    </div>
                    ${teamBioHtml}
                    ${t.description ? `<p style="font-size: 12px; color: var(--xg-muted); margin: 12px 0 0 0; line-height: 1.5; display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden;">${escapeHtml(t.description)}</p>` : ''}
                    ${teamSocialHtml}
                </div>
            `;
        }).join('');

        if (!playerHtml && !teamHtml) {
            return `<div style="color: var(--xg-muted); font-size: 13px;">No matching player or team profile found.</div>`;
        }

        return playerHtml + teamHtml;
    }

    // Legacy hook kept as a no-op for older callers — snapshot records never
    // emit .ximilar-sportsdb-lookup containers, so there is nothing to init.
    function initSportsDbLookups() {}

    // ── TCG Card Lookup (multi-source: Pokemon, YuGiOh, MTG, Lorcana, FaB, SWU) ──

    function tcgContainerId() {
        return 'ximilar-tcg-lookup-' + Math.random().toString(36).slice(2, 9);
    }

    function renderTcgLookup(record, config) {
        if ((record.identification_resolution != null ? record.identification_resolution.status : void 0) === 'partial' || !isTCGCard(record)) return '';
        const id = (record.card || {}).identification || record.identification || {};
        const name = id.name || '';
        const game = extractSubcategory(record);
        const setName = id.set_name || '';
        const cardNumber = id.card_number || '';
        if (!name || !game) return '';

        const cid = tcgContainerId();

        // Render only the grade-time snapshot — no lazy /api/tcg-lookup call.
        // A record without stored tcg_card_data omits the section entirely.
        const savedData = record.tcg_card_data || null;
        if (!(savedData && savedData.success && savedData.card)) return '';
        const contentHtml = renderTcgContent(savedData);
        const dataAttrs = '';

        return `
            <div class="ximilar-result-card ximilar-tcg-lookup" id="${cid}"${dataAttrs} style="
                background: var(--xg-panel);
                border-radius: 8px;
                padding: 20px;
                box-shadow: var(--xg-shadow);
                border: 1px solid var(--xg-border);
                margin-bottom: 20px;
            ">
                <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 14px;">
                    <span style="font-size: 18px;">🃏</span>
                    <h3 style="font-size: 15px; font-weight: 700; margin: 0; color: var(--xg-text);">Card Database: ${escapeHtml(name)}</h3>
                </div>
                <div class="ximilar-tcg-content" style="color: var(--xg-muted); font-size: 13px;">
                    ${contentHtml}
                </div>
            </div>
        `;
    }

    // fetchTcgData was removed — records render the stored tcg_card_data
    // snapshot and never trigger /api/tcg-lookup from the page.

    // ── Scrydex market data ─────────────────────────────────────────
    // Renders prices-by-condition, trend pills, graded price ladder, PSA
    // population report, and a lazy-loaded price-history chart — all from
    // tcgCard.variants (populated when source = Scrydex).

    function renderScrydexMarket(tcgCard, record) {
        if (!tcgCard || !Array.isArray(tcgCard.variants) || !tcgCard.variants.length) return '';

        const fmt = (v, cur) => {
            if (v == null || isNaN(v)) return '—';
            const sym = (cur === 'USD' || !cur) ? '$' : (cur === 'JPY' ? '¥' : cur + ' ');
            return sym + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        };

        // Primary variant = most price records
        let primary = tcgCard.variants[0];
        for (const v of tcgCard.variants) {
            if ((v.prices || []).length > (primary.prices || []).length) primary = v;
        }
        const prices = primary.prices || [];
        const raw = prices.filter(p => p.type === 'raw');
        const graded = prices.filter(p => p.type === 'graded');
        const cur = (raw[0] || graded[0] || {}).currency || 'USD';

        const parts = [];

        // Variant label + other-printings line
        const variantName = (primary.name || 'normal').replace(/([a-z])([A-Z])/g, '$1 $2');
        const others = tcgCard.variants.filter(v => v !== primary)
            .map(v => {
                const nm = (v.prices || []).find(p => p.type === 'raw' && p.condition === 'NM');
                return `${escapeHtml((v.name || 'normal').replace(/([a-z])([A-Z])/g, '$1 $2'))}${nm && nm.market != null ? ' ' + fmt(nm.market, nm.currency) : ''}`;
            });

        // ── Raw prices by condition ──
        const condOrder = ['NM', 'LP', 'MP', 'HP', 'DM'];
        const condCells = condOrder.map(cond => {
            const p = raw.find(r => r.condition === cond);
            if (!p) return '';
            return `<div style="background:var(--xg-panel);border:1px solid var(--xg-border);border-radius:8px;padding:8px 10px;text-align:center;min-width:0">
                <div style="font-size:9px;font-weight:700;color:var(--xg-muted);text-transform:uppercase;letter-spacing:0.05em">${cond}</div>
                <div style="font-size:15px;font-weight:800;color:var(--xg-text)">${fmt(p.market, p.currency)}</div>
                ${p.low != null ? `<div style="font-size:10px;color:var(--xg-muted)">low ${fmt(p.low, p.currency)}</div>` : ''}
            </div>`;
        }).filter(Boolean);
        if (condCells.length) {
            parts.push(`<div style="font-size:10px;font-weight:700;color:var(--xg-muted);text-transform:uppercase;letter-spacing:0.06em;margin-bottom:6px">Raw prices${tcgCard.variants.length > 1 ? ' · ' + escapeHtml(variantName) : ''} (${escapeHtml(cur)})</div>
                <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(72px,1fr));gap:6px;margin-bottom:10px">${condCells.join('')}</div>`);
        }

        // ── Trend pills (from the NM raw price) ──
        const nm = raw.find(r => r.condition === 'NM') || raw[0];
        if (nm && nm.trends) {
            const t = nm.trends;
            const pills = [['days_1','1D'],['days_7','7D'],['days_14','14D'],['days_30','30D'],['days_90','90D'],['days_180','180D']]
                .map(([k, label]) => {
                    const d = t[k];
                    if (!d || d.percent_change == null) return '';
                    const pct = Number(d.percent_change);
                    const up = pct > 0;
                    const c = up ? '#059669' : (pct < 0 ? '#dc2626' : '#64748b');
                    const arrow = up ? '▲' : (pct < 0 ? '▼' : '—');
                    return `<span style="display:inline-flex;align-items:center;gap:4px;background:${c}12;border:1px solid ${c}40;color:${c};border-radius:100px;padding:3px 9px;font-size:11px;font-weight:700">${label} ${arrow} ${Math.abs(pct).toFixed(1)}%</span>`;
                }).filter(Boolean);
            if (pills.length) {
                parts.push(`<div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:12px">${pills.join('')}</div>`);
            }
        }

        // ── Graded price ladder (per company, top grades) ──
        if (graded.length) {
            const byCompany = {};
            for (const p of graded) {
                const co = p.company || 'Other';
                (byCompany[co] = byCompany[co] || []).push(p);
            }
            const companies = Object.keys(byCompany).sort((a, b) => (a === 'PSA' ? -1 : b === 'PSA' ? 1 : a.localeCompare(b)));
            const rows = companies.slice(0, 3).map(co => {
                const cells = byCompany[co]
                    .filter(p => p.grade != null && p.market != null && !isNaN(parseFloat(p.grade)))
                    .sort((a, b) => parseFloat(b.grade) - parseFloat(a.grade))
                    .slice(0, 6)
                    .map(p => `<div style="text-align:center;min-width:52px">
                        <div style="font-size:9px;font-weight:700;color:var(--xg-muted)">${escapeHtml(p.grade)}</div>
                        <div style="font-size:13px;font-weight:800;color:${parseFloat(p.grade) >= 10 ? '#b45309' : 'var(--xg-text)'}">${fmt(p.market, p.currency)}</div>
                    </div>`).join('');
                if (!cells) return '';
                return `<div style="display:flex;align-items:center;gap:4px;flex-wrap:wrap;margin-bottom:6px">
                    <span style="font-size:11px;font-weight:800;color:var(--xg-text-2);min-width:34px">${escapeHtml(co)}</span>
                    <div style="display:flex;gap:10px;flex-wrap:wrap">${cells}</div>
                </div>`;
            }).filter(Boolean);
            if (rows.length) {
                parts.push(`<div style="font-size:10px;font-weight:700;color:var(--xg-muted);text-transform:uppercase;letter-spacing:0.06em;margin-bottom:6px">Graded market prices</div>
                    <div style="background:var(--xg-panel);border:1px solid var(--xg-border);border-radius:8px;padding:10px 12px;margin-bottom:10px">${rows.join('')}</div>`);
            }

            // ── Grade ROI callout ──
            const psa10 = graded.find(p => p.company === 'PSA' && p.grade === '10')
                       || graded.filter(p => p.grade === '10').sort((a,b) => (b.market||0)-(a.market||0))[0];
            if (psa10 && nm && nm.market > 0 && psa10.market > nm.market) {
                const mult = psa10.market / nm.market;
                parts.push(`<div style="background:linear-gradient(135deg,#fef3c7,#fde68a);border:1px solid #f59e0b55;border-radius:8px;padding:10px 14px;margin-bottom:10px;display:flex;align-items:center;gap:10px">
                    <span style="font-size:18px">📈</span>
                    <div style="font-size:12px;color:#92400e;line-height:1.5">
                        <strong>Grading upside:</strong> a ${escapeHtml(psa10.company)} 10 sells for <strong>${fmt(psa10.market, psa10.currency)}</strong> —
                        <strong>${mult.toFixed(1)}×</strong> the raw NM price (${fmt(nm.market, nm.currency)}).
                    </div>
                </div>`);
            }
        }

        // ── PSA population report ──
        const pop = (primary.pop_reports || []).find(r => (r.company || '').toUpperCase() === 'PSA') || (primary.pop_reports || [])[0];
        if (pop && Array.isArray(pop.grades) && pop.grades.length) {
            const numeric = pop.grades
                .filter(g => g.grade != null && !isNaN(parseFloat(g.grade)) && (g.count || 0) > 0)
                .sort((a, b) => parseFloat(a.grade) - parseFloat(b.grade));
            const maxCount = Math.max(...numeric.map(g => g.count));
            const total = pop.total || numeric.reduce((s, g) => s + (g.count || 0), 0);
            const g10 = numeric.find(g => parseFloat(g.grade) === 10);
            const bars = numeric.map(g => {
                const w = Math.max(2, Math.round((g.count / maxCount) * 100));
                const is10 = parseFloat(g.grade) === 10;
                return `<div style="display:flex;align-items:center;gap:8px;margin-bottom:3px">
                    <span style="font-size:10px;font-weight:700;color:var(--xg-muted);width:26px;text-align:right">${escapeHtml(String(g.grade))}</span>
                    <div style="flex:1;background:var(--xg-subtle);border-radius:3px;height:12px;overflow:hidden">
                        <div style="width:${w}%;height:100%;background:${is10 ? '#f59e0b' : '#0d9488'};border-radius:3px"></div>
                    </div>
                    <span style="font-size:10px;color:var(--xg-text-2);width:44px">${Number(g.count).toLocaleString()}</span>
                </div>`;
            }).join('');
            const scarcity = g10 && total ? `<div style="font-size:11px;color:var(--xg-text-2);margin-top:6px">
                Only <strong>${Number(g10.count).toLocaleString()}</strong> PSA 10s exist — <strong>${(g10.count / total * 100).toFixed(1)}%</strong> of ${Number(total).toLocaleString()} graded copies.
            </div>` : '';
            parts.push(`<div style="font-size:10px;font-weight:700;color:var(--xg-muted);text-transform:uppercase;letter-spacing:0.06em;margin-bottom:6px">${escapeHtml(pop.company || 'PSA')} population report${total ? ' · ' + Number(total).toLocaleString() + ' graded' : ''}</div>
                <div style="background:var(--xg-panel);border:1px solid var(--xg-border);border-radius:8px;padding:10px 12px;margin-bottom:10px">${bars}${scarcity}</div>`);
        }

        // ── Price-history chart — static only ──
        // Renders only when a history series was embedded into the record
        // (grade-time snapshot or the page's tcg_card_cache resolution). The
        // lazy /api/card-price-history fetch is gone: a paid Scrydex call must
        // never fire from a page view.
        const hist = tcgCard.price_history || (record && record.price_history) || null;
        if (Array.isArray(hist) && hist.length > 1) {
            parts.push(`<div class="scrydex-history">
                <div style="font-size:10px;font-weight:700;color:var(--xg-muted);text-transform:uppercase;letter-spacing:0.06em;margin-bottom:6px">Price history</div>
                <div class="scrydex-history-body" style="background:var(--xg-panel);border:1px solid var(--xg-border);border-radius:8px;padding:10px 12px;display:block">
                    ${renderScrydexHistoryChart(hist)}
                </div>
            </div>`);
        }

        // ── Buy-this-card CTAs ──
        // TCGplayer purchase URL comes from the Scrydex card payload; the eBay
        // link is a pre-filled search for this exact printing.
        const buyBtns = [];
        if (tcgCard.tcgplayer_url) {
            buyBtns.push(`<a href="${escapeHtml(tcgCard.tcgplayer_url)}" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:6px;background:#2563eb;color:#fff;padding:9px 16px;border-radius:8px;text-decoration:none;font-size:13px;font-weight:700">Buy on TCGplayer ↗</a>`);
        }
        const ebayQ = [tcgCard.name, tcgCard.set_name, tcgCard.number ? '#' + tcgCard.number : ''].filter(Boolean).join(' ');
        if (ebayQ) {
            buyBtns.push(`<a href="https://www.ebay.com/sch/i.html?_nkw=${encodeURIComponent(ebayQ)}" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:6px;background:var(--xg-panel);color:var(--xg-text);padding:9px 16px;border-radius:8px;border:1px solid var(--xg-border);text-decoration:none;font-size:13px;font-weight:700">Search eBay ↗</a>`);
        }
        if (buyBtns.length) {
            parts.push(`<div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:4px;margin-bottom:10px">${buyBtns.join('')}</div>`);
        }

        if (others.length) {
            parts.push(`<div style="font-size:11px;color:var(--xg-muted);margin-top:8px">Other printings: ${others.join(' · ')}</div>`);
        }
        if (!parts.length) return '';

        return `<div class="ximilar-result-card" style="background:var(--xg-panel);border-radius:8px;padding:20px;box-shadow:var(--xg-shadow);margin-bottom:16px;border:1px solid var(--xg-border);">
            <div style="display:flex;align-items:center;gap:12px;margin-bottom:16px;">
                <div style="width:44px;height:44px;background:var(--xg-good-soft);border:1.5px solid var(--xg-good-line);border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:22px;flex-shrink:0;box-shadow:0 2px 6px rgba(13,148,136,0.15);">📈</div>
                <div>
                    <h3 style="font-size:19px;font-weight:800;color:var(--xg-text);margin:0;line-height:1.25;letter-spacing:-0.01em;">Market Data</h3>
                    <p style="font-size:12px;color:var(--xg-muted);margin:2px 0 0 0;font-weight:500;">Scrydex</p>
                </div>
            </div>
            ${parts.join('')}
        </div>`;
    }

    // Standalone Market Data card — resolves the verified enrichment card.
    // Grade results place it just above Live Market Comps; other services
    // render it right after the identification card.
    function renderScrydexMarketCard(record) {
        const { tcgCard } = _resolveTcgEnrichment(record);
        return renderScrydexMarket(tcgCard, record);
    }

    // Snapshot-rendered population panels: render() stashes embedded records
    // here and emits [data-population-static] mounts — population-report.js
    // binds them with zero network calls.
    const popStaticEmbed = {};
    let popStaticSeq = 0;
    function getPopStatic(pid) { return popStaticEmbed[pid] || null; }

    // Inline SVG line chart (no deps — survives html2canvas PDF export).
    function renderScrydexHistoryChart(days) {
        const W = 560, H = 140, PAD = 8;
        const series = [
            { key: 'raw_nm', color: '#0d9488', label: pgcT('rr.raw.nm', 'Raw NM') },
            { key: 'psa10', color: '#b45309', label: pgcT('rr.psa.10', 'PSA 10') },
        ];
        const pts = {};
        let min = Infinity, max = -Infinity;
        for (const s of series) {
            pts[s.key] = days.map(d => d[s.key]).filter(v => v != null && v > 0);
            if (pts[s.key].length) {
                min = Math.min(min, ...pts[s.key]);
                max = Math.max(max, ...pts[s.key]);
            }
        }
        if (!isFinite(min) || !isFinite(max)) return '';
        if (min === max) { min *= 0.9; max *= 1.1; }
        const n = days.length;
        const x = i => PAD + (i / Math.max(1, n - 1)) * (W - 2 * PAD);
        const y = v => H - PAD - ((v - min) / (max - min)) * (H - 2 * PAD);
        const lines = series.map(s => {
            const coords = [];
            days.forEach((d, i) => { if (d[s.key] != null && d[s.key] > 0) coords.push(x(i).toFixed(1) + ',' + y(d[s.key]).toFixed(1)); });
            if (coords.length < 2) return '';
            return `<polyline points="${coords.join(' ')}" fill="none" stroke="${s.color}" stroke-width="2" stroke-linejoin="round"/>`;
        }).join('');
        const legend = series.map(s => `<span style="display:inline-flex;align-items:center;gap:4px;font-size:10px;color:var(--xg-text-2);font-weight:600;white-space:nowrap">
            <span style="width:10px;height:3px;background:${s.color};border-radius:2px;display:inline-block"></span>${s.label}</span>`).join('<span style="width:10px"></span>');
        const last = days[days.length - 1];
        const curCode = last.cur || 'USD';
        const sym = curCode === 'USD' ? '$' : (curCode === 'JPY' ? '¥' : curCode + ' ');
        const fmtP = v => v == null ? '—' : sym + Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
        return `<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px"><span style="white-space:nowrap">${legend}</span>
                <span style="font-size:10px;color:var(--xg-muted);white-space:nowrap">${escapeHtml(days[0].date || '')} → ${escapeHtml(last.date || '')}</span></div>
            <svg viewBox="0 0 ${W} ${H}" style="width:100%;height:auto;display:block" preserveAspectRatio="none">${lines}</svg>
            <div style="display:flex;gap:14px;font-size:11px;color:var(--xg-text-2);margin-top:4px">
                <span>Raw NM: <strong>${fmtP(last.raw_nm)}</strong></span>
                <span>PSA 10: <strong>${fmtP(last.psa10)}</strong></span>
            </div>`;
    }

    function renderTcgContent(data) {
        const cards = data.card || [];
        if (!cards || cards.length === 0) {
            return `<div style="color: var(--xg-muted); font-size: 13px;">No card data found in TCG databases.</div>`;
        }
        const c = cards[0];
        const img = c.image || c.thumbnail || null;
        const chips = [];
        if (c.game) chips.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">${escapeHtml(c.game)}</span>`);
        if (c.rarity) chips.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">${escapeHtml(c.rarity)}</span>`);
        if (c.set_name) chips.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">${escapeHtml(c.set_name)}</span>`);
        if (c.number) chips.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">#${escapeHtml(c.number)}</span>`);
        if (c.year) chips.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">${escapeHtml(String(c.year).slice(0,4))}</span>`);
        if (c.artist) chips.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">Art: ${escapeHtml(c.artist)}</span>`);

        // Game-specific stats
        const stats = [];
        if (c.hp) stats.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">HP ${escapeHtml(String(c.hp))}</span>`);
        if (c.types) stats.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">${escapeHtml(c.types.join('/'))}</span>`);
        if (c.subtypes) stats.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">${escapeHtml(c.subtypes.join('/'))}</span>`);
        if (c.supertype) stats.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">${escapeHtml(c.supertype)}</span>`);
        if (c.atk != null) stats.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">ATK ${escapeHtml(String(c.atk))}</span>`);
        if (c.def != null) stats.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">DEF ${escapeHtml(String(c.def))}</span>`);
        if (c.level != null) stats.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">LV ${escapeHtml(String(c.level))}</span>`);
        if (c.attribute) stats.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">${escapeHtml(c.attribute)}</span>`);
        if (c.mana_cost) stats.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">${escapeHtml(c.mana_cost)}</span>`);
        if (c.cmc != null) stats.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">CMC ${escapeHtml(String(c.cmc))}</span>`);
        if (c.type_line || c.type) stats.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">${escapeHtml(c.type_line || c.type)}</span>`);
        if (c.power) stats.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">Power ${escapeHtml(String(c.power))}</span>`);
        if (c.toughness) stats.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">Toughness ${escapeHtml(String(c.toughness))}</span>`);
        if (c.cost != null) stats.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">Cost ${escapeHtml(String(c.cost))}</span>`);
        if (c.ink) stats.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">${escapeHtml(c.ink)}</span>`);
        if (c.strength) stats.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">Strength ${escapeHtml(String(c.strength))}</span>`);
        if (c.willpower) stats.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">Willpower ${escapeHtml(String(c.willpower))}</span>`);
        if (c.lore) stats.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">Lore ${escapeHtml(String(c.lore))}</span>`);
        if (c.pitch) stats.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">Pitch ${escapeHtml(String(c.pitch))}</span>`);
        if (c.defense) stats.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">Defense ${escapeHtml(String(c.defense))}</span>`);
        if (c.class) stats.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">${escapeHtml(c.class)}</span>`);
        if (c.aspects && c.aspects.length) {
            const aspectStr = c.aspects.map(a => typeof a === 'object' ? Object.values(a)[0] : a).join('/');
            stats.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">${escapeHtml(aspectStr)}</span>`);
        }
        if (c.arenas && c.arenas.length) stats.push(`<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">${escapeHtml(c.arenas.join('/'))}</span>`);

        // Prices
        const priceEntries = Object.entries(c.prices || {});
        const priceHtml = priceEntries.length
            ? `<div style="margin-top: 10px; padding: 8px 12px; background: var(--xg-panel); border-radius: 6px; border: 1px solid var(--xg-border);">
                <div style="font-size: 11px; font-weight: 600; color: var(--xg-text-2); margin-bottom: 4px;">Market Prices</div>
                <div style="display: flex; flex-wrap: wrap; gap: 6px; font-size: 11px;">
                    ${priceEntries.map(([k, v]) => `<span style="background: var(--xg-chip-bg); padding: 3px 8px; border-radius: 100px;">${escapeHtml(k)}: ${escapeHtml(v)}</span>`).join('')}
                </div>
              </div>`
            : '';

        // Attacks/abilities (Pokemon)
        const attacksHtml = (c.attacks || []).length
            ? `<div style="margin-top: 10px;"><div style="font-size: 11px; font-weight: 600; color: var(--xg-text-2); margin-bottom: 4px;">Attacks</div>${c.attacks.map(a => `
                <div style="font-size: 11px; color: var(--xg-text); margin-bottom: 4px; padding: 6px 10px; background: var(--xg-panel); border-radius: 6px; border: 1px solid var(--xg-border);">
                    <strong>${escapeHtml(a.name || '')}</strong>${a.damage ? ` — ${escapeHtml(a.damage)}` : ''}${a.cost && a.cost.length ? ` (${a.cost.join('/')})` : ''}
                    ${a.text ? `<div style="color: var(--xg-muted); margin-top: 2px;">${escapeHtml(a.text)}</div>` : ''}
                </div>`).join('')}</div>`
            : '';

        const abilitiesHtml = (c.abilities || []).length
            ? `<div style="margin-top: 10px;"><div style="font-size: 11px; font-weight: 600; color: var(--xg-text-2); margin-bottom: 4px;">Abilities</div>${c.abilities.map(a => `
                <div style="font-size: 11px; color: var(--xg-text); margin-bottom: 4px; padding: 6px 10px; background: var(--xg-panel); border-radius: 6px; border: 1px solid var(--xg-border);">
                    <strong>${escapeHtml(a.name || '')}</strong>
                    ${a.text ? `<div style="color: var(--xg-muted); margin-top: 2px;">${escapeHtml(a.text)}</div>` : ''}
                </div>`).join('')}</div>`
            : '';

        // Card text (YuGiOh, MTG, etc.)
        const textHtml = (c.oracle_text || c.desc || c.text || c.flavor_text)
            ? `<div style="margin-top: 10px; font-size: 11px; color: var(--xg-muted); line-height: 1.5; padding: 8px 12px; background: var(--xg-panel); border-radius: 6px; border: 1px solid var(--xg-border);">
                ${c.oracle_text || c.desc || c.text ? `<div style="margin-bottom: 6px;">${escapeHtml(c.oracle_text || c.desc || c.text)}</div>` : ''}
                ${c.flavor_text ? `<div style="font-style: italic; opacity: 0.8;">${escapeHtml(c.flavor_text)}</div>` : ''}
               </div>`
            : '';

        // Evolution chain (Pokemon)
        const evolvesHtml = (c.evolves_from || (c.evolves_to && c.evolves_to.length))
            ? `<div style="margin-top: 8px; font-size: 11px; color: var(--xg-muted); display: flex; flex-wrap: wrap; gap: 6px; align-items: center;">
                ${c.evolves_from ? `<span style="padding: 3px 8px; background: var(--xg-panel); border-radius: 100px; border: 1px solid var(--xg-border);">Evolves from: <strong style="color: var(--xg-text);">${escapeHtml(c.evolves_from)}</strong></span>` : ''}
                ${c.evolves_to && c.evolves_to.length ? `<span style="padding: 3px 8px; background: var(--xg-panel); border-radius: 100px; border: 1px solid var(--xg-border);">Evolves to: <strong style="color: var(--xg-text);">${c.evolves_to.map(escapeHtml).join(', ')}</strong></span>` : ''}
               </div>`
            : '';

        // Keywords (MTG)
        const keywordsHtml = (c.keywords && c.keywords.length)
            ? `<div style="margin-top: 8px; display: flex; flex-wrap: wrap; gap: 4px;">${c.keywords.map(k => `<span style="background: var(--xg-warn-soft); color: var(--xg-warn-strong); padding: 2px 8px; border-radius: 100px; font-size: 10px; font-weight: 600;">${escapeHtml(k)}</span>`).join('')}</div>`
            : '';

        // Finishes (MTG)
        const finishesHtml = (c.finishes && c.finishes.length)
            ? `<div style="margin-top: 6px; display: flex; flex-wrap: wrap; gap: 4px; font-size: 10px;">${c.finishes.map(f => `<span style="background: var(--xg-chip-bg); padding: 2px 6px; border-radius: 4px; text-transform: capitalize;">${escapeHtml(f)}</span>`).join('')}</div>`
            : '';

        // Printings (YuGiOh — all sets this card appeared in)
        const printingsHtml = (c.printings && c.printings.length > 1)
            ? `<div style="margin-top: 10px;"><div style="font-size: 11px; font-weight: 600; color: var(--xg-text-2); margin-bottom: 4px;">Printings (${c.printings.length})</div>
                <div style="max-height: 120px; overflow-y: auto; font-size: 10px;">
                    ${c.printings.slice(0, 15).map(p => `<div style="padding: 3px 8px; background: var(--xg-panel); border-radius: 4px; border: 1px solid var(--xg-border); margin-bottom: 3px; display: flex; justify-content: space-between; gap: 8px;">
                        <span style="color: var(--xg-text);">${escapeHtml(p.set_name || '')} <span style="color: var(--xg-muted);">${escapeHtml(p.set_code || '')}</span></span>
                        <span style="color: var(--xg-muted); white-space: nowrap;">${escapeHtml(p.rarity || '')}${p.price ? ' · ' + escapeHtml(p.price) : ''}</span>
                    </div>`).join('')}
                    ${c.printings.length > 15 ? `<div style="color: var(--xg-muted); font-size: 10px; padding: 2px 8px;">+${c.printings.length - 15} more...</div>` : ''}
                </div></div>`
            : '';

        // Purchase links
        const links = [];
        if (c.tcgplayer_url) links.push(`<a href="${escapeHtml(c.tcgplayer_url)}" target="_blank" rel="noopener" style="display: inline-flex; align-items: center; gap: 4px; background: #2563eb; color: white; padding: 4px 10px; border-radius: 6px; text-decoration: none; font-size: 11px; font-weight: 600;">TCGPlayer ↗</a>`);
        if (c.cardmarket_url) links.push(`<a href="${escapeHtml(c.cardmarket_url)}" target="_blank" rel="noopener" style="display: inline-flex; align-items: center; gap: 4px; background: #7c3aed; color: white; padding: 4px 10px; border-radius: 6px; text-decoration: none; font-size: 11px; font-weight: 600;">Cardmarket ↗</a>`);
        if (c.scryfall_url) links.push(`<a href="${escapeHtml(c.scryfall_url)}" target="_blank" rel="noopener" style="display: inline-flex; align-items: center; gap: 4px; background: #db2777; color: white; padding: 4px 10px; border-radius: 6px; text-decoration: none; font-size: 11px; font-weight: 600;">Scryfall ↗</a>`);
        if (c.ygoprodeck_url) links.push(`<a href="${escapeHtml(c.ygoprodeck_url)}" target="_blank" rel="noopener" style="display: inline-flex; align-items: center; gap: 4px; background: #059669; color: white; padding: 4px 10px; border-radius: 6px; text-decoration: none; font-size: 11px; font-weight: 600;">YGOPRODeck ↗</a>`);
        if (c.detail_url) links.push(`<a href="${escapeHtml(c.detail_url)}" target="_blank" rel="noopener" style="display: inline-flex; align-items: center; gap: 4px; background: #4b5563; color: white; padding: 4px 10px; border-radius: 6px; text-decoration: none; font-size: 11px; font-weight: 600;">Details ↗</a>`);
        const linksHtml = links.length ? `<div style="margin-top: 10px; display: flex; flex-wrap: wrap; gap: 6px;">${links.join('')}</div>` : '';

        // Set logo
        const setLogoHtml = (c.set_logo || c.set_symbol || c.set_icon)
            ? `<img src="${escapeHtml(proxyImg(c.set_logo || c.set_symbol || c.set_icon))}" alt="${escapeHtml((c.set_name || '') + ' set logo')}" style="height: 48px; max-width: 160px; object-fit: contain;" onerror="this.style.display='none'">`
            : '';

        return `
            <div style="min-width: 0; width: 100%; background: var(--xg-subtle); border-radius: 8px; padding: 14px; border: 1px solid var(--xg-border);">
                <div style="display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 14px; align-items: flex-start;">
                    ${img ? `<img src="${escapeHtml(proxyImg(img))}" alt="${escapeHtml(buildAltText({ name: c.name, setName: c.set_name, setCode: c.set_code, cardNumber: c.card_number || c.number, rarity: c.rarity, suffix: c.version && c.version !== 'Non-Foil' ? '(' + c.version + ')' : '' }))}" style="width: 120px; height: 168px; object-fit: contain; border-radius: 8px; background: var(--xg-panel); border: 1px solid var(--xg-border);" onerror="this.style.display='none'">` : ''}
                    <div style="min-width: 0;">
                        <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
                            <div style="font-size: 16px; font-weight: 700; color: var(--xg-text); overflow-wrap: break-word;">${escapeHtml(c.name)}${c.version ? ` <span style="font-weight: 400; color: var(--xg-muted); font-size: 13px;">— ${escapeHtml(c.version)}</span>` : ''}</div>
                            ${setLogoHtml}
                        </div>
                        <div style="display: flex; flex-wrap: wrap; gap: 6px; font-size: 11px; color: var(--xg-text-2); margin-bottom: 6px;">
                            ${chips.join('')}
                        </div>
                        <div style="display: flex; flex-wrap: wrap; gap: 6px; font-size: 11px; color: var(--xg-text-2);">
                            ${stats.join('')}
                        </div>
                        ${priceHtml}
                        ${evolvesHtml}
                        ${keywordsHtml}
                        ${finishesHtml}
                        ${attacksHtml}
                        ${abilitiesHtml}
                        ${printingsHtml}
                        ${textHtml}
                        ${linksHtml}
                    </div>
                </div>
            </div>
        `;
    }

    // No-op — tcg lookup containers render fully from the stored snapshot.
    function initTcgLookups() {}

    function renderMarketSearchLinks(record, config) {
        if (!record) return '';
        // Gemini identification (top-level record.identification) takes priority
        // over Ximilar's tcg_id/sport_id.
        let id = null;
        let isTcg = false;
        if (record.identification && record.identification.source === 'gemini') {
            id = record.identification;
            isTcg = !!(record.tcg_id);
        } else if (record.tcg_id && ((record.tcg_id.card != null ? record.tcg_id.card.identification : void 0) || record.tcg_id.identification)) {
            id = (record.tcg_id.card != null ? record.tcg_id.card.identification : void 0) || record.tcg_id.identification;
            isTcg = true;
        } else if (record.sport_id && ((record.sport_id.card != null ? record.sport_id.card.identification : void 0) || record.sport_id.identification)) {
            id = (record.sport_id.card != null ? record.sport_id.card.identification : void 0) || record.sport_id.identification;
        } else if ((record.card != null ? record.card.identification : void 0) || record.identification) {
            id = (record.card != null ? record.card.identification : void 0) || record.identification;
            isTcg = !!(record.tcg_id);
        }
        if (!id) id = {};

        // TCG records: provider identifications rarely carry marketplace links,
        // so build the row from the tcg_card_data enrichment URLs + an eBay search.
        if (isTcg) {
            const tcgData = record.tcg_card_data || null;
            let tcgCard = (tcgData && tcgData.success && tcgData.card)
                ? (Array.isArray(tcgData.card) ? tcgData.card[0] : tcgData.card) : null;
            if (tcgCard && typeof tcgCard !== 'object') tcgCard = null;
            let tcgNum = String((tcgCard && tcgCard.number) || id.card_number || id.number || '');
            if (tcgNum.indexOf('/') !== -1) tcgNum = tcgNum.split('/')[0];
            const ebayQ = [id.name || (tcgCard && tcgCard.name), id.set_name || id.set, tcgNum]
                .filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
            if (!id.links) id.links = {};
            if (ebayQ && !id.links['ebay.com']) {
                id.links['ebay.com'] = `https://www.ebay.com/sch/i.html?_nkw=${encodeURIComponent(ebayQ)}&_sacat=2536`;
            }
            const directMap = {
                'tcgplayer.com': 'tcgplayer_url', 'cardmarket.com': 'cardmarket_url',
                'scryfall.com': 'scryfall_url', 'ygoprodeck.com': 'ygoprodeck_url',
            };
            if (tcgCard) {
                for (const [site, field] of Object.entries(directMap)) {
                    if (!id.links[site] && tcgCard[field]) id.links[site] = tcgCard[field];
                }
            }
        }
        if (!id.links || Object.keys(id.links).length === 0) return '';

        const linkIcons = isTcg
            ? { 'ebay.com': '🛒', 'tcgplayer.com': '📋', 'cardmarket.com': '🇪🇺', 'trollandtoad.com': '🎲', 'scryfall.com': '🔮', 'ygoprodeck.com': '🃏' }
            : { 'ebay.com': '🛒', 'comc.com': '📋', 'beckett.com': '📊' };
        const color = (config != null ? config.color : void 0) || '#2563eb';

        // Numbered/parallel search variant — serial'd parallels sell under
        // titles that carry the print run ("Red Shimmer /5"). The numerator is
        // unique to this copy, so only the denominator is searchable.
        const serTok = serialForRecord(record);
        const parTok = parallelFromSpecialFeatures(id.special_features || (record.identification && record.identification.special_features))
            || id.sub_set || '';
        const serDm = String(serTok || '').match(/\/\s*(\d{1,4})\b/);
        const serQueryTok = serDm ? '/' + parseInt(serDm[1], 10) : '';
        const mktLink = (site, url) =>
            `<a href="${url}" target="_blank" rel="noopener" style="color: var(--xg-info); font-weight: 600; font-size: 12.5px; text-decoration: none; white-space: nowrap;" onmouseover="this.style.textDecoration='underline'" onmouseout="this.style.textDecoration='none'">${linkIcons[site] || '🔗'} ${site.replace('.com', '')}</a>`;
        const sep = '<span style="color:var(--xg-border);">·</span>';

        let numberedRow = '';
        if (serQueryTok || parTok) {
            const nq = [id.name, id.year, id.set_name || id.set, parTok, serQueryTok]
                .filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
            if (nq) {
                const enc = encodeURIComponent(nq);
                const variantUrls = {
                    'ebay.com': `https://www.ebay.com/sch/i.html?_nkw=${enc}&_sacat=${isTcg ? 2536 : 212}`,
                    'comc.com': `https://www.comc.com/Cards,=${enc}`,
                    'beckett.com': `https://marketplace.beckett.com/search_new/?term=${enc}`,
                };
                const variants = Object.entries(variantUrls).filter(([site]) => id.links[site]);
                if (variants.length) {
                    numberedRow = `
                        <span style="font-size:10.5px;font-weight:700;color:var(--xg-muted);text-transform:uppercase;letter-spacing:0.04em;margin-left:6px;"
                              title="${escapeHtml(nq)}">Numbered${serQueryTok ? ` ${escapeHtml(serQueryTok)}` : ''}:</span>
                        ${variants.map(([site, url]) => mktLink(site, url)).join(sep)}`;
                }
            }
        }

        return `
            <div class="ximilar-result-card" style="
                background: var(--xg-panel); border-radius: 8px; padding: 10px 14px;
                margin-bottom: 16px; border: 1px solid var(--xg-border);
            ">
                <div style="display: flex; flex-wrap: wrap; align-items: center; column-gap: 10px; row-gap: 4px;">
                    <span style="font-size: 10.5px; font-weight: 700; color: var(--xg-muted); text-transform: uppercase; letter-spacing: 0.04em;">Find this card:</span>
                    ${Object.entries(id.links).map(([site, url]) => mktLink(site, url)).join(sep)}
                    ${numberedRow}
                </div>
            </div>`;
    }

    function renderShareSection(meta) {
        if (!meta) meta = {};
        const shareUrl = meta.share_url || '';
        if (!shareUrl) return '';
        const encodedUrl = encodeURIComponent(shareUrl);
        const tweetText = encodeURIComponent('Check out my AI card grading result from PreGradeCards:');
        const fbQuote = encodeURIComponent('My AI card grading result');
        const redditTitle = encodeURIComponent('AI Card Grading Result - PreGradeCards');
        const whatsappText = encodeURIComponent('Check out my AI card grading result: ');
        const emailSubject = encodeURIComponent('My AI Card Grading Result');
        const emailBody = encodeURIComponent('I just got my card graded with AI on PreGradeCards. See the result: ' + shareUrl);
        return `
        <div class="ximilar-result-card ximilar-share-section" style="background: var(--xg-panel); border-radius: 8px; padding: 20px; box-shadow: var(--xg-shadow); margin-bottom: 16px; border: 1px solid var(--xg-border);">
            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 16px;">
                <div style="width: 36px; height: 36px; background: #eff6ff; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: #2563eb;">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                </div>
                <div>
                    <h3 style="font-size: 16px; font-weight: 700; color: var(--xg-text); margin: 0;">Share Your Results</h3>
                    <p style="font-size: 12px; color: var(--xg-muted); margin: 0;">Post your AI grade to social media or copy the link for eBay listings.</p>
                </div>
            </div>
            <div class="xim-share-buttons" style="display: flex; gap: 8px; flex-wrap: wrap; justify-content: center; margin-bottom: 14px;">
                <a href="https://twitter.com/intent/tweet?text=${tweetText}&url=${encodedUrl}" target="_blank" rel="noopener" class="xim-share-btn" style="display:flex;align-items:center;gap:6px;padding:10px 14px;border-radius:10px;background:#000;color:#fff;text-decoration:none;font-size:13px;font-weight:600;transition:opacity .15s;" onmouseover="this.style.opacity='0.85'" onmouseout="this.style.opacity='1'">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    X
                </a>
                <a href="https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${fbQuote}" target="_blank" rel="noopener" class="xim-share-btn" style="display:flex;align-items:center;gap:6px;padding:10px 14px;border-radius:10px;background:#1877f2;color:#fff;text-decoration:none;font-size:13px;font-weight:600;transition:opacity .15s;" onmouseover="this.style.opacity='0.85'" onmouseout="this.style.opacity='1'">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    Facebook
                </a>
                <!-- Reddit share removed per owner 2026-10-29 -->
                <a href="https://wa.me/?text=${whatsappText}${encodedUrl}" target="_blank" rel="noopener" class="xim-share-btn" style="display:flex;align-items:center;gap:6px;padding:10px 14px;border-radius:10px;background:#25d366;color:#fff;text-decoration:none;font-size:13px;font-weight:600;transition:opacity .15s;" onmouseover="this.style.opacity='0.85'" onmouseout="this.style.opacity='1'">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    WhatsApp
                </a>
                <a href="mailto:?subject=${emailSubject}&body=${emailBody}" style="display:flex;align-items:center;gap:6px;padding:10px 14px;border-radius:10px;background:#6b7280;color:#fff;text-decoration:none;font-size:13px;font-weight:600;transition:opacity .15s;" onmouseover="this.style.opacity='0.85'" onmouseout="this.style.opacity='1'">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    Email
                </a>
                <button type="button" onclick="navigator.clipboard.writeText('${shareUrl}'); this.innerHTML='&#10003; Copied!'; setTimeout(()=>{this.innerHTML='Copy Link'},1500);" style="display:flex;align-items:center;gap:6px;padding:10px 14px;border-radius:10px;background:#2563eb;color:#fff;border:none;font-size:13px;font-weight:600;cursor:pointer;transition:opacity .15s;" onmouseover="this.style.opacity='0.85'" onmouseout="this.style.opacity='1'">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                    Copy Link
                </button>
            </div>
            <div style="display: flex; gap: 10px; flex-wrap: wrap;">
                <input type="text" value="${shareUrl}" readonly id="xim-share-url" style="flex: 1; min-width: 220px; padding: 10px 14px; border: 1px solid var(--xg-border); border-radius: 10px; font-size: 13px; background: var(--xg-subtle); color: var(--xg-muted);">
            </div>
        </div>`;
    }

    function renderReferralPromo(position) {
        const bonus = (typeof window !== 'undefined' && window.REFERRAL_SHARE_BONUS) ? window.REFERRAL_SHARE_BONUS : 50;
        const refCode = (typeof window !== 'undefined' && window.REFERRAL_CODE) ? window.REFERRAL_CODE : '';
        const siteUrl = (typeof window !== 'undefined' && window.SITE_URL) ? window.SITE_URL : 'https://pregradecards.com';
        const isLoggedIn = (typeof window !== 'undefined' && window.IS_LOGGED_IN) ? window.IS_LOGGED_IN : false;
        const pos = position || 'top';

        if (!isLoggedIn || !refCode) return '';

        const refLink = siteUrl + '/auth/register?ref=' + refCode;
        const encodedRefLink = encodeURIComponent(refLink);
        const tweetText = encodeURIComponent('I just AI-graded my sports card with PreGradeCards — get ' + bonus + ' bonus credits with your first purchase:');
        const fbQuote = encodeURIComponent('AI card grading — get free credits');
        const redditTitle = encodeURIComponent('Free AI Card Grading - PreGradeCards');
        const whatsappText = encodeURIComponent('Check out this AI card grading tool — get free credits: ');
        const emailSubject = encodeURIComponent('Get free AI card grading credits');
        const emailBody = encodeURIComponent('I\'ve been using PreGradeCards to AI-grade my cards before sending them to PSA/BGS. Sign up with my link and get bonus credits: ' + refLink);

        const pulseAnim = pos === 'top' ? 'referralPulseTop' : 'referralPulseBottom';

        return `
        <div class="ximilar-referral-promo ximilar-referral-promo--${pos}" style="
            background: var(--xg-panel);
            border-radius: 8px;
            padding: 20px;
            margin: ${pos === 'top' ? '0 0 20px 0' : '20px 0 0 0'};
            color: var(--xg-text);
            box-shadow: var(--xg-shadow);
            border: 1px solid var(--xg-border);
        ">
            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 16px;">
                <div style="width: 36px; height: 36px; background: #ecfdf5; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: #059669;">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/></svg>
                </div>
                <div>
                    <h3 style="font-size: 16px; font-weight: 700; color: var(--xg-text); margin: 0;">Share &amp; Earn ${bonus} Free Credits</h3>
                    <p style="font-size: 12px; color: var(--xg-muted); margin: 0;">For each friend who makes their first purchase</p>
                </div>
                <div style="margin-left: auto; background: #ecfdf5; border-radius: 10px; padding: 6px 14px; font-size: 12px; font-weight: 700; color: #059669; text-transform: uppercase; letter-spacing: 0.05em; white-space: nowrap;">
                    ${bonus} credits / friend
                </div>
            </div>

            <div class="xim-share-buttons" style="display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 14px;">
                <a href="https://twitter.com/intent/tweet?text=${tweetText}&url=${encodedRefLink}" target="_blank" rel="noopener" style="display:flex;align-items:center;gap:6px;padding:10px 14px;border-radius:10px;background:#000;color:#fff;text-decoration:none;font-size:13px;font-weight:600;transition:opacity .15s;" onmouseover="this.style.opacity='0.85'" onmouseout="this.style.opacity='1'">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    Post on X
                </a>
                <a href="https://www.facebook.com/sharer/sharer.php?u=${encodedRefLink}&quote=${fbQuote}" target="_blank" rel="noopener" style="display:flex;align-items:center;gap:6px;padding:10px 14px;border-radius:10px;background:#1877f2;color:#fff;text-decoration:none;font-size:13px;font-weight:600;transition:opacity .15s;" onmouseover="this.style.opacity='0.85'" onmouseout="this.style.opacity='1'">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    Facebook
                </a>
                <!-- Reddit share removed per owner 2026-10-29 -->
                <a href="https://wa.me/?text=${whatsappText}${encodedRefLink}" target="_blank" rel="noopener" style="display:flex;align-items:center;gap:6px;padding:10px 14px;border-radius:10px;background:#25d366;color:#fff;text-decoration:none;font-size:13px;font-weight:600;transition:opacity .15s;" onmouseover="this.style.opacity='0.85'" onmouseout="this.style.opacity='1'">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    WhatsApp
                </a>
                <a href="mailto:?subject=${emailSubject}&body=${emailBody}" style="display:flex;align-items:center;gap:6px;padding:10px 14px;border-radius:10px;background:#6b7280;color:#fff;text-decoration:none;font-size:13px;font-weight:600;transition:opacity .15s;" onmouseover="this.style.opacity='0.85'" onmouseout="this.style.opacity='1'">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    Email
                </a>
                <button type="button" onclick="navigator.clipboard.writeText('${refLink}'); this.innerHTML='&#10003; Copied!'; setTimeout(()=>{this.innerHTML='<svg width=\\'16\\' height=\\'16\\' viewBox=\\'0 0 24 24\\' fill=\\'none\\' stroke=\\'currentColor\\' stroke-width=\\'2\\'><rect x=\\'9\\' y=\\'9\\' width=\\'13\\' height=\\'13\\' rx=\\'2\\' ry=\\'2\\'/><path d=\\'M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1\\'/></svg> Copy Link'},2000);" style="display:flex;align-items:center;gap:6px;padding:10px 14px;border-radius:10px;background:#2563eb;color:#fff;border:none;font-size:13px;font-weight:600;cursor:pointer;transition:opacity .15s;" onmouseover="this.style.opacity='0.85'" onmouseout="this.style.opacity='1'">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                    Copy Link
                </button>
            </div>

            <div style="display: flex; gap: 10px; flex-wrap: wrap; align-items: center;">
                <input type="text" value="${refLink}" readonly style="flex: 1; min-width: 200px; padding: 10px 14px; border: 1px solid var(--xg-border); border-radius: 10px; font-size: 12px; background: var(--xg-subtle); color: var(--xg-muted); outline: none;" onclick="this.select()">
            </div>
        </div>`;
    }

    function getSavedTheme() {
        try { return localStorage.getItem('xim-theme') || 'light'; } catch (e) { return 'light'; }
    }
    // Ensure default is light on first visit (no stored preference yet)
    try { if (!localStorage.getItem('xim-theme')) localStorage.setItem('xim-theme', 'light'); } catch (e) {}

    function renderResult(service, record, originalImageUrl, creditsUsed, statistics, meta) {
        const config = SERVICE_CONFIG[service] || SERVICE_CONFIG.grade;
        injectStyles();
        meta = meta || {};

        // Expose credits + processing time to section renderers (KPI strip)
        meta.creditsUsed = (creditsUsed != null) ? creditsUsed : config.credits;
        meta.procTime = formatProcessingTime(record, statistics) || 'N/A';
        const savedTheme = getSavedTheme();

        // Make sure the original image URL is available inside the record for downstream tools
        if (record && originalImageUrl) {
            record.original_image_url = originalImageUrl;
        }

        const resultId = 'xim-res-' + (++resultIdCounter);
        resultRegistry.set(resultId, record || {});
        lastRenderContext = { service: service, record: record || {}, creditsUsed: meta.creditsUsed, procTime: meta.procTime, certNo: null };

        // Static thumb paths resolved server-side — merge into the shared map.
        if (record && record._thumb_map) {
            for (const k in record._thumb_map) thumbMap[k] = record._thumb_map[k];
        }

        let content = '';
        let gradeMarketHtml = '';
        let topHtml = '';
        switch (service) {
            case 'grade': {
                const gradeResult = renderGradeResult(record, config, meta);
                content = gradeResult.html;
                gradeMarketHtml = gradeResult.marketHtml || '';
                topHtml = gradeResult.topHtml || '';
                break;
            }
            case 'condition':
                content = renderConditionResult(record, config);
                break;
            case 'centering':
                content = renderCenteringResult(record, config);
                break;
            case 'crop':
                content = renderCropResult(record, config);
                break;
            case 'localize':
                content = renderLocalizeResult(record, config);
                break;
            case 'sport-id':
                content = renderSportIdResult(record, config);
                break;
            case 'tcg-id':
                content = renderTcgIdResult(record, config);
                break;
            case 'unified-identify':
                content = renderUnifiedIdentifyResult(record, config);
                break;
            case 'slab-id':
                content = renderSlabIdResult(record, config);
                break;
            case 'slab-grade':
                content = renderSlabGradeResult(record, config);
                break;
            case 'coin-id':
                content = renderCoinIdResult(record, config);
                break;
            default:
                content = renderMetricGrid([
                    { label: pgcT('rr.service', 'Service'), value: service },
                    { label: pgcT('rr.status', 'Status'), value: 'Completed' }
                ]);
        }

        const printBanner = `
            <div class="ximilar-print-banner" style="display: none; padding: 24px; border-bottom: 3px solid #1f2937; margin-bottom: 24px;">
                <div style="display: flex; align-items: center; justify-content: space-between; gap: 16px;">
                    <div>
                        <div style="font-size: 24px; font-weight: 800; color: var(--xg-text);">PreGradeCards</div>
                        <div style="font-size: 12px; color: var(--xg-muted); font-weight: 600;">AI-Powered Card Grading Analysis Report</div>
                    </div>
                    <div style="text-align: right; font-size: 11px; color: var(--xg-muted);">
                        <div>${new Date().toLocaleDateString()}</div>
                        <div>pregradecards.com</div>
                    </div>
                </div>
            </div>
        `;

        const visibleSection = `
            <div class="ximilar-result-card" style="
                background: var(--xg-panel);
                border-radius: 8px;
                padding: 14px 20px;
                box-shadow: var(--xg-shadow);
                margin-bottom: 16px;
                border: 1px solid var(--xg-border);
            ">
                <div style="display: flex; align-items: center; justify-content: center; gap: 16px;">
                    <h3 style="font-size: 16px; font-weight: 600; color: var(--xg-text); margin: 0;">Theme Color</h3>
                    <button type="button" class="xim-theme-toggle" title="Toggle light / dark mode" aria-label="Toggle light or dark mode">
                        <span class="xt-light">☀ Light</span>
                        <span class="xt-dark">🌙 Dark</span>
                    </button>
                </div>
            </div>
            ${renderHeader(service, record, config, creditsUsed, statistics)}
            ${renderUnifiedCardId(record, config)}
            ${renderSportsDbLookup(record, config)}
            ${service !== 'grade' ? renderScrydexMarketCard(record) : ''}
            ${topHtml}
            ${renderImages(record, originalImageUrl, service, meta)}
            ${printBanner}
            <div class="ximilar-result-card ximilar-results-body" style="
                background: var(--xg-panel);
                border-radius: 8px;
                padding: 20px;
                box-shadow: var(--xg-shadow);
                margin-bottom: 20px;
                border: 1px solid var(--xg-border);
            ">
                ${content}
            </div>
        `;

        const referralPromoTop = renderReferralPromo('top');
        const referralPromoBottom = renderReferralPromo('bottom');

        // AJAX mounts land after DOMContentLoaded, when ensureReportStyles'
        // one-shot scan already ran and found no .ximilar-result — so on the
        // fresh-grade view the canonical sheet never loads and the injected
        // snapshot alone styles the report. Re-scan a tick after the caller
        // inserts our markup: the retry link fetches the real file, which
        // replaces the snapshot on load.
        setTimeout(ensureReportStyles, 0);
        // Same defer as ensureReportStyles — the caller injects our markup
        // after renderResult returns; a synchronous scan finds no
        // [data-surface-lab] and returns early.
        setTimeout(ensureSurfaceLab, 0);

        const lockedSection = `
            ${renderMarketSearchLinks(record, config)}
            ${renderShareSection(meta)}
            ${renderActions(service, record, resultId)}
            ${renderReportSection(meta.action_id, service)}
            <div data-promo-mount="post_result" data-service="${service}" data-cost="${config.credits || 1}"></div>
            ${renderGradeTipsExplainer(service)}
        `;

        return `
            <div class="ximilar-result" data-xim-theme="${savedTheme}" style="max-width: 100%; margin: 0 auto; font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.5;">
                ${referralPromoTop}
                ${visibleSection}
                ${lockedSection}
                ${referralPromoBottom}
                ${renderDisclaimer()}
            </div>
        `;
    }

    function renderDisclaimer() {
        return `
            <div class="xim-disclaimer" style="text-align: center; margin: 20px auto 10px; max-width: 820px; padding: 12px 16px; font-size: 11px; line-height: 1.55; color: var(--xg-muted); opacity: 0.85; border-top: 1px solid var(--xg-border);">
                <strong>Disclaimer:</strong> PreGradeCards reports, AI pre-grade estimates, sub-grades, and market values are provided for entertainment, estimation, and evaluation purposes only. Card identification, market comps, pricing, and population data are sourced from or supplemented by third parties. We make no guarantees, promises, or warranties—implied or expressed—as to grade accuracy in comparison to any other grading organization or regarding any other information in this report. Users should independently corroborate all information with additional sources before making any decisions or taking any actions.
            </div>
        `;
    }

    function isActionReported(actionId) {
        try {
            const reported = JSON.parse(sessionStorage.getItem('ximilarReportedActions') || '[]');
            return Array.isArray(reported) && reported.includes(String(actionId));
        } catch (e) {
            return false;
        }
    }

    function markActionReported(actionId) {
        try {
            const reported = JSON.parse(sessionStorage.getItem('ximilarReportedActions') || '[]');
            if (!Array.isArray(reported)) return;
            const key = String(actionId);
            if (!reported.includes(key)) reported.push(key);
            sessionStorage.setItem('ximilarReportedActions', JSON.stringify(reported));
        } catch (e) {}
    }

    function renderReportSection(actionId, service) {
        if (!actionId || typeof window === 'undefined' || !window.IS_LOGGED_IN) return '';
        const reported = isActionReported(actionId);
        return `
            <div class="ximilar-report-section" style="text-align: center; margin-top: 16px; padding-top: 16px; border-top: 1px dashed var(--xg-border);">
                ${reported ? `
                    <span style="display: inline-flex; align-items: center; gap: 6px; color: var(--xg-good-strong); font-size: 14px; font-weight: 700;">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
                        Report submitted — we'll review it shortly.
                    </span>
                ` : `
                    <button class="ximilar-report-trigger" data-action-id="${actionId}" data-service="${service}" style="background: var(--xg-info-soft); border: 1px solid var(--xg-info-line); border-radius: 8px; color: var(--xg-info); font-size: 14px; font-weight: 700; cursor: pointer; padding: 8px 16px; line-height: 1.4;">
                        Result not correct?
                    </button>
                    <p style="margin: 8px 0 0; color: var(--xg-text-2); font-size: 13px; line-height: 1.45; max-width: 420px; margin-left: auto; margin-right: auto;">
                        Sometimes our AI isn't perfect. Let us know — if we agree, you'll get your credits back.
                    </p>
                `}
            </div>
        `;
    }

    let reportModalOverlay = null;
    function ensureReportModal() {
        if (reportModalOverlay) return reportModalOverlay;
        const overlay = document.createElement('div');
        overlay.className = 'ximilar-report-modal';
        overlay.style.cssText = 'position: fixed; inset: 0; z-index: 100000; display: none; align-items: center; justify-content: center; background: rgba(15,23,42,0.55); padding: 20px;';
        overlay.innerHTML = `
            <div style="background: var(--xg-panel); border-radius: 14px; padding: 24px; width: 100%; max-width: 460px; box-shadow: 0 20px 50px rgba(0,0,0,0.25);">
                <h3 style="margin: 0 0 6px; font-size: 17px; font-weight: 700; color: var(--xg-text);">Report an incorrect result</h3>
                <p style="margin: 0 0 14px; color: var(--xg-muted); font-size: 13px; line-height: 1.5;">Tell us what the result got wrong (wrong card, missed auto/number, etc.). If we agree, we'll refund the credits.</p>
                <textarea class="ximilar-report-reason" placeholder="e.g., It's a numbered /25 parallel, but the AI didn't detect the serial number..." style="width: 100%; min-height: 100px; padding: 12px; border: 1px solid var(--xg-border); border-radius: 10px; font-family: inherit; font-size: 13px; resize: vertical; box-sizing: border-box;"></textarea>
                <div class="ximilar-report-error" style="color: #dc2626; font-size: 12px; margin-top: 8px; display: none;"></div>
                <div class="ximilar-report-success" style="color: #059669; font-size: 13px; margin-top: 8px; display: none; font-weight: 600;">Report submitted. We'll review it soon.</div>
                <div style="display: flex; justify-content: flex-end; gap: 10px; margin-top: 16px;">
                    <button class="ximilar-report-cancel" style="padding: 9px 18px; border-radius: 8px; border: 1px solid var(--xg-border); background: var(--xg-panel); color: var(--xg-text-2); font-weight: 600; font-size: 13px; cursor: pointer;">Cancel</button>
                    <button class="ximilar-report-submit" style="padding: 9px 18px; border-radius: 8px; border: none; background: #3b82f6; color: white; font-weight: 600; font-size: 13px; cursor: pointer;">Submit Report</button>
                </div>
            </div>
        `;
        document.body.appendChild(overlay);

        const close = () => {
            overlay.style.display = 'none';
            overlay.querySelector('.ximilar-report-reason').value = '';
            overlay.querySelector('.ximilar-report-error').style.display = 'none';
            overlay.querySelector('.ximilar-report-success').style.display = 'none';
            overlay.querySelector('.ximilar-report-submit').disabled = false;
            overlay.querySelector('.ximilar-report-submit').textContent = 'Submit Report';
            overlay.querySelector('.ximilar-report-cancel').textContent = 'Cancel';
        };

        overlay.querySelector('.ximilar-report-cancel').addEventListener('click', close);
        overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

        reportModalOverlay = overlay;
        return overlay;
    }

    function openReportModal(actionId, service, triggerEl) {
        const overlay = ensureReportModal();
        overlay.style.display = 'flex';
        overlay.dataset.actionId = actionId;
        const reasonEl = overlay.querySelector('.ximilar-report-reason');
        const errorEl = overlay.querySelector('.ximilar-report-error');
        const successEl = overlay.querySelector('.ximilar-report-success');
        const submitBtn = overlay.querySelector('.ximilar-report-submit');
        reasonEl.focus();

        const onSubmit = async () => {
            const reason = reasonEl.value.trim();
            if (!reason) {
                errorEl.textContent = 'Please describe what was wrong.';
                errorEl.style.display = 'block';
                return;
            }
            errorEl.style.display = 'none';
            submitBtn.disabled = true;
            submitBtn.textContent = 'Submitting...';

            try {
                const response = await fetch('/api/report-result', {
                    method: 'POST',
                    headers: { 'Accept': 'application/json' },
                    body: new URLSearchParams({ action_id: actionId, reason: reason })
                });
                const data = await response.json();
                if (data.success) {
                    successEl.style.display = 'block';
                    markActionReported(actionId);
                    if (triggerEl) {
                        const section = triggerEl.closest('.ximilar-report-section');
                        if (section) {
                            section.innerHTML = `
                                <span style="display: inline-flex; align-items: center; gap: 6px; color: #059669; font-size: 13px; font-weight: 600;">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
                                    Report submitted — we'll review it shortly.
                                </span>`;
                        }
                    }
                    const cancelBtn = overlay.querySelector('.ximilar-report-cancel');
                    if (cancelBtn) cancelBtn.textContent = 'Close';
                } else {
                    errorEl.textContent = data.error || 'Something went wrong. Please try again.';
                    errorEl.style.display = 'block';
                    submitBtn.disabled = false;
                    submitBtn.textContent = 'Submit Report';
                }
            } catch (err) {
                errorEl.textContent = 'Network error. Please try again.';
                errorEl.style.display = 'block';
                submitBtn.disabled = false;
                submitBtn.textContent = 'Submit Report';
            }
        };

        // Avoid stacking listeners
        submitBtn.replaceWith(submitBtn.cloneNode(true));
        overlay.querySelector('.ximilar-report-submit').addEventListener('click', onSubmit);
        reasonEl.onkeydown = (e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) onSubmit(); };
    }
    /**
     * Shared image lightbox — delegated to assets/js/lightbox.js
     * (window.PGCLightbox). This renderer used to carry its own copy, which is
     * why the /services ?result= view kept the old "Reset" bottom pill and the
     * title drawn over the card: both copies bound .ximilar-zoomable and the
     * older one won. One implementation, one place to fix.
     */
    const Lightbox = {
        init: function () {
            if (window.PGCLightbox) window.PGCLightbox.init();
            else missingLightboxWarning();
        },
        open: function (src, label, target, keepList) {
            if (window.PGCLightbox) return window.PGCLightbox.open(src, label, target, keepList);
            missingLightboxWarning();
            if (src) window.open(src, '_blank', 'noopener');
        },
        close: function () {
            if (window.PGCLightbox) window.PGCLightbox.close();
        }
    };

    let lightboxWarned = false;
    function missingLightboxWarning() {
        if (lightboxWarned) return;
        lightboxWarned = true;
        try {
            if (window.pgcTrack) window.pgcTrack('js_error', { message: 'PGCLightbox module missing — lightbox include not loaded' }, 'error');
        } catch (_) {}
        if (window.console && console.warn) console.warn('PGCLightbox missing: add <script src="/assets/js/lightbox.js">');
    }

    /**
     * Inline OpenSeadragon viewers for detail card grids (corner / edge details).
     * Initialized lazily once per element and watched via MutationObserver so
     * new results inserted after DOMContentLoaded still get viewers.
     */
    const DetailViewers = (function () {
        const osdMap = new Map();

        function ensureOsd() {
            return typeof OpenSeadragon !== 'undefined'
                ? Promise.resolve()
                : new Promise((resolve, reject) => {
                    if (document.querySelector('script[src*="openseadragon"]')) {
                        // Wait for the existing script to finish loading.
                        const check = setInterval(() => {
                            if (typeof OpenSeadragon !== 'undefined') {
                                clearInterval(check);
                                resolve();
                            }
                        }, 50);
                        setTimeout(() => { clearInterval(check); reject(new Error('OpenSeadragon load timeout')); }, 10000);
                        return;
                    }
                    const script = document.createElement('script');
                    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/openseadragon/4.1.1/openseadragon.min.js';
                    script.async = true;
                    script.onload = resolve;
                    script.onerror = reject;
                    document.head.appendChild(script);
                });
        }

        function initViewer(el) {
            if (el.dataset.osdReady === '1' || osdMap.has(el)) return;
            const src = el.getAttribute('data-osd-src');
            if (!src) return;
            el.dataset.osdReady = '1';

            ensureOsd().then(() => {
                if (osdMap.has(el)) return;
                const viewer = OpenSeadragon({
                    element: el,
                    prefixUrl: 'https://cdnjs.cloudflare.com/ajax/libs/openseadragon/4.1.1/images/',
                    tileSources: { type: 'image', url: src },
                    showNavigator: false,
                    minZoomLevel: 0.1,
                    maxZoomLevel: 20,
                    defaultZoomLevel: 0.6,
                    zoomPerClick: 1.4,
                    zoomPerScroll: 1.2,
                    homeFillsViewer: false
                });
                osdMap.set(el, viewer);

            }).catch(err => {
                // Fall back to a plain image if OpenSeadragon cannot be loaded.
                console.warn('[DetailViewers] OpenSeadragon failed to load:', err);
                el.innerHTML = `<img src="${src}" alt="Card detail view" style="max-width:100%;max-height:100%;object-fit:contain;display:block;margin:0 auto;">`;
            });
        }

        function init(container) {
            const root = container || document;
            root.querySelectorAll('[data-osd-viewer]:not([data-osd-ready="1"])').forEach(initViewer);
        }

        function scan() { init(document); }

        return { init: init, scan: scan };
    })();

    // ── Canonical-stylesheet self-heal ─────────────────────────────────
    // ResultRenderer embeds the report CSS inline (#ximilar-critical-css)
    // so a server-rendered report stays readable if the linked stylesheet
    // never applies — a bad response can sit in the 30-day browser/edge
    // cache. Here we detect that case, upgrade to the bundled copy, and
    // re-fetch the real file under a fresh URL (a never-seen query bypasses
    // both cache layers and repopulates them with good bytes).
    function canonicalReportCssLoaded() {
        // Any sheet pointing at the file with parsed rules counts — the
        // original link may sit empty beside a healthy retry link.
        for (let i = 0; i < document.styleSheets.length; i++) {
            const s = document.styleSheets[i];
            if (!s.href || s.href.indexOf('ximilar-results.css') === -1) continue;
            try { if (s.cssRules && s.cssRules.length) return true; } catch (e) {}
        }
        return false;
    }

    function dropEmbeddedReportCss() {
        const crit = document.getElementById('ximilar-critical-css');
        if (crit && crit.parentNode) crit.parentNode.removeChild(crit);
    }

    // Surface Lab lazy-load: the rendered markup carries a <script src>
    // inside the component HTML, which is INERT under innerHTML injection —
    // on AJAX/result= mounts surface-lab.js never loads and the tiles stay
    // skeletons until a manual refresh. Append a real script element (which
    // does execute) when a container exists but the pipeline isn't loaded.
    let slLoadState = '';
    function ensureSurfaceLab() {
        if (!document.querySelector('[data-surface-lab]')) return;
        if (window.PGCSurfaceLabScan) { window.PGCSurfaceLabScan(); return; }
        if (slLoadState === 'loading') return;
        slLoadState = 'loading';
        const s = document.createElement('script');
        s.src = '/assets/js/surface-lab.js?v=' + Date.now();
        s.onload = function () { slLoadState = 'loaded'; window.PGCSurfaceLabScan && window.PGCSurfaceLabScan(); };
        s.onerror = function () { slLoadState = ''; };
        document.head.appendChild(s);
    }

    function ensureReportStyles() {
        if (!document.querySelector('.ximilar-result')) return;
        if (canonicalReportCssLoaded()) {
            // Canonical sheet applied — the embedded snapshot did its job;
            // drop it so future file edits always win.
            dropEmbeddedReportCss();
            return;
        }
        // Linked sheet missing/empty — paint with the bundled copy now,
        // then pull the authoritative file under a never-cached URL.
        injectStyles();
        if (document.getElementById('ximilar-results-css-retry')) return;
        const l = document.createElement('link');
        l.id = 'ximilar-results-css-retry';
        l.rel = 'stylesheet';
        l.href = '/assets/css/ximilar-results.css?r=' + Date.now();
        l.onload = function () {
            const inj = document.getElementById('ximilar-result-styles');
            if (inj && inj.parentNode) inj.parentNode.removeChild(inj);
            dropEmbeddedReportCss();
        };
        document.head.appendChild(l);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => { Lightbox.init(); DetailViewers.scan(); ensureReportStyles(); });
    } else {
        Lightbox.init();
        DetailViewers.scan();
        ensureReportStyles();
    }

    window.XimilarResults = {
        render: renderResult,
        ensureStyles: ensureReportStyles,
        downloadReport: downloadReport,
        services: SERVICE_CONFIG,
        openLightbox: Lightbox.open,
        initSportsDbLookups: initSportsDbLookups,
        getPopStatic: getPopStatic,
        setTheme: function (theme) {
            const t = (theme === 'light') ? 'light' : 'dark';
            try { localStorage.setItem('xim-theme', t); } catch (e) {}
            document.querySelectorAll('.ximilar-result[data-xim-theme]').forEach(function (root) {
                root.setAttribute('data-xim-theme', t);
            });
        },
        getTheme: getSavedTheme,
        buildCertificate: buildCertificateDocument
    };

    // Theme toggle (light/dark) — event delegation so it works for every result card.
    // record-interactions.js binds an identical delegated handler on ?result=
    // and /r/ pages — the flag keeps a click from flipping twice (net zero).
    document.addEventListener('click', function (e) {
        const tbtn = e.target.closest('.xim-theme-toggle');
        if (!tbtn) return;
        const root = tbtn.closest('.ximilar-result');
        if (!root) return;
        e.__pgcXimThemeHandled = true;
        const next = root.getAttribute('data-xim-theme') === 'light' ? 'dark' : 'light';
        root.setAttribute('data-xim-theme', next);
        try { localStorage.setItem('xim-theme', next); } catch (err) {}
    });

    // Event delegation for eBay listing generator buttons
    document.addEventListener('click', function (e) {
        const btn = e.target.closest('.xim-generate-ebay-listing');
        if (!btn) return;
        const resultId = btn.dataset.resultId;
        const record = resultRegistry.get(resultId);
        if (!record) return;
        if (window.EbayListingGenerator) {
            const images = {
                cropped: (record._images != null ? record._images.cropped : void 0) || record._img_url || null,
                exact: (record._images != null ? record._images.exact : void 0) || record._exact_url_card || null,
                full: (record._images != null ? record._images.full : void 0) || record._full_url_card || null,
                clean: (record._images != null ? record._images.clean : void 0) || record._clean_url_card || null,
                original: record.original_image_url || null
            };
            window.EbayListingGenerator.open(record, 'Graded Card', images);
        }
    });

    // Event delegation for incorrect-result report links
    document.addEventListener('click', function (e) {
        const trigger = e.target.closest('.ximilar-report-trigger');
        if (!trigger) return;
        e.preventDefault();
        const actionId = trigger.getAttribute('data-action-id');
        const service = trigger.getAttribute('data-service');
        if (actionId) openReportModal(actionId, service, trigger);
    });

    // Event delegation for table expander buttons (View all sold/active comps).
    // record-interactions.js registers the SAME delegation on server-rendered
    // record pages where both files load (?result=, /r/) — two listeners each
    // toggling produced a net-zero double-flip (buttons looked dead). First
    // script to evaluate binds; the other skips via the shared flag.
    if (!window.__pgcExpandBound) {
        window.__pgcExpandBound = true;
        document.addEventListener('click', function (e) {
            const btn = e.target.closest('.xim-expand-btn');
            if (!btn) return;
            const container = btn.closest('div');
            const tbody = container ? container.querySelector('.xim-expandable-rows') : null;
            if (!tbody) return;
            const isExpanded = tbody.classList.toggle('xim-expanded');
            const labelSpan = btn.querySelector('span');
            const svg = btn.querySelector('svg');
            if (isExpanded) {
                if (labelSpan) labelSpan.textContent = labelSpan.textContent.replace(/^View all/, 'Show fewer');
                if (svg) svg.style.transform = 'rotate(180deg)';
            } else {
                if (labelSpan) labelSpan.textContent = labelSpan.textContent.replace(/^Show fewer/, 'View all');
                if (svg) svg.style.transform = 'rotate(0deg)';
            }
        });
    }

    // ── "What If It Grades…" economics explorer ──
    function updateEconExplorer(root) {
        const data = econRegistry.get(root.dataset.econId);
        if (!data) return;
        const slider = root.querySelector('.econ-grade-slider');
        const select = root.querySelector('.econ-tier-select');
        if (!slider || !select) return;
        const g = parseFloat(slider.value);
        const opt = data.options[parseInt(select.value, 10)] || data.options[0];
        if (!opt) return;
        const price = (function(_o){return _o != null ? _o : 0})((function(_o){return _o != null ? _o : data.prices[String(g.toFixed(0))]})((function(_o){return _o != null ? _o : data.prices[String(Math.floor(g))]})((function(_o){return _o != null ? _o : data.prices[g.toFixed(1)]})(data.prices[String(g)]))));
        const cost = parseFloat(opt.total_cost) || 0;
        const net = price - cost;
        const roi = cost > 0 ? (net / cost) * 100 : 0;
        const deltaVsRaw = data.raw > 0 ? net - data.raw : null;

        let verdict, color;
        if (net > 0) { verdict = 'Submit to ' + opt.company + ' (' + opt.tier + ')'; color = '#10b981'; }
        else if (net > -20) { verdict = 'Marginal / Coin Flip'; color = '#f59e0b'; }
        else { verdict = 'Sell Raw'; color = '#ef4444'; }

        const labelEl = root.querySelector('.econ-grade-label');
        const valEl = root.querySelector('.econ-out-value');
        const netEl = root.querySelector('.econ-out-net');
        const roiEl = root.querySelector('.econ-out-roi');
        const verdEl = root.querySelector('.econ-out-verdict');
        if (labelEl) labelEl.textContent = g.toFixed(1);
        if (valEl) valEl.textContent = price > 0 ? '$' + price.toFixed(2) : 'N/A';
        if (netEl) {
            netEl.textContent = (net > 0 ? '+' : net < 0 ? '−' : '') + '$' + Math.abs(net).toFixed(2);
            netEl.style.color = net > 0 ? '#059669' : '#ef4444';
            netEl.title = deltaVsRaw !== null ? 'vs selling raw: ' + (deltaVsRaw > 0 ? '+' : '−') + '$' + Math.abs(deltaVsRaw).toFixed(2) : '';
        }
        if (roiEl) {
            roiEl.textContent = isFinite(roi) ? (roi > 0 ? '+' : '') + roi.toFixed(0) + '%' : 'N/A';
            roiEl.style.color = roi > 0 ? '#059669' : '#64748b';
        }
        if (verdEl) {
            verdEl.textContent = verdict;
            verdEl.style.background = color + '15';
            verdEl.style.color = color;
            verdEl.style.border = '1px solid ' + color + '30';
        }
    }

    ['input', 'change'].forEach(function (evt) {
        document.addEventListener(evt, function (e) {
            const root = e.target && e.target.closest && e.target.closest('.xim-econ-explorer');
            if (root) updateEconExplorer(root);
        });
    });

    // Auto-initialize detail OpenSeadragon viewers whenever a new result card
    // is inserted into the DOM. (Record enrichment is snapshot-only — there is
    // deliberately no observer branch for API lookups.)
    if (window.MutationObserver) {
        const observer = new MutationObserver(function (mutations) {
            let shouldInitDetailViewers = false;
            const econRoots = [];
            for (const mutation of mutations) {
                for (const node of mutation.addedNodes) {
                    if (node.nodeType !== 1) continue;
                    if (
                        (node.classList && (node.classList.contains('ximilar-detail-section') || node.classList.contains('ximilar-detail-viewer'))) ||
                        (node.querySelector && node.querySelector('.ximilar-detail-section, .ximilar-detail-viewer'))
                    ) {
                        shouldInitDetailViewers = true;
                    }
                    if (node.classList && node.classList.contains('xim-econ-explorer')) {
                        econRoots.push(node);
                    } else if (node.querySelector) {
                        node.querySelectorAll('.xim-econ-explorer').forEach(el => econRoots.push(el));
                    }
                    if (shouldInitDetailViewers) break;
                }
                if (shouldInitDetailViewers) break;
            }
            econRoots.forEach(updateEconExplorer);
            if (shouldInitDetailViewers) {
                DetailViewers.scan();
            }
        });
        observer.observe(document.documentElement, { childList: true, subtree: true });
    }

    // Event delegation for result action buttons (print, PDF download, reload)
    document.addEventListener('click', function (e) {
        const btn = e.target.closest('button[data-xim-action]');
        if (!btn) return;
        const action = btn.getAttribute('data-xim-action');
        if (action === 'download-pdf') {
            e.preventDefault();
            downloadReport();
        } else if (action === 'print') {
            e.preventDefault();
            window.print();
        } else if (action === 'scan-next' || action === 'reload') {
            e.preventDefault();
            const serviceEl = btn.closest('[data-service]');
            const serviceKey = serviceEl ? serviceEl.getAttribute('data-service') : null;
            // Binder-triage: when the upload form is already on the page (fresh
            // scan flow) just scroll back to it — no reload, the form was
            // auto-reset after the result rendered. On history/report pages
            // fall through to the service start URL.
            if (action === 'scan-next') {
                const gradingForm = document.getElementById('grading-form');
                if (gradingForm) {
                    gradingForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    return;
                }
            }
            const serviceStartUrl = {
                grade: '/services/card-grading',
                condition: '/services/condition-check',
                centering: '/services/centering-analysis',
                crop: '/services/card-cropping',
                localize: '/services/card-detection',
                'sport-id': '/services/sports-card-id',
                'tcg-id': '/services/tcg-id',
                'slab-id': '/services/slab-reader',
                'slab-grade': '/services/slab-grade',
                'unified-identify': '/services/card-identification'
            }[serviceKey] || '/services';
            window.location.href = serviceStartUrl + '#grading-form';
        }
    });

})(window);

window.PgcJS=window.PgcJS||{};PgcJS["ximilar-results"]=1;
